import { useLocalizedStringFormatter, I18nProvider, useNumberFormatter } from '@react-aria/i18n';
export { I18nProvider, useCollator, useDateFormatter, useFilter, useListFormatter, useLocale, useLocalizedStringFormatter, useNumberFormatter } from '@react-aria/i18n';
export { SSRProvider, useIsSSR } from '@react-aria/ssr';
import { rsiAsmHorizontal, rsiBrandTrustHorizontal, rsiCertificatesHorizontal, rsiOndmarcHorizontal, rsiPulseHorizontal, rsiRadarHorizontal, rsiRojoDsHorizontal, rsiVendorSecureHorizontal, rsiRedSiftHorizontal, mdiClose as mdiClose$1, mdiAlert, mdiCheckCircle, mdiAlertCircle, mdiInformation, mdiMenu, mdiChevronDown, mdiMenuOpen, mdiChevronUp, mdiCheckboxMarked, mdiMinusBox, mdiCheckboxBlankOutline, mdiPageLast, mdiKeyboardCaps, mdiRadioboxMarked, mdiRadioboxBlank } from '@redsift/icons';
import * as React from 'react';
import React__default, { useState, useEffect, forwardRef, useRef, useCallback, useContext, useReducer, useMemo, useLayoutEffect } from 'react';
import classNames from 'classnames';
import styled, { css, keyframes } from 'styled-components';
import { useFocusRing } from '@react-aria/focus';
import * as ReactDOM from 'react-dom';
import { createPortal } from 'react-dom';
import { useFocusWithin } from '@react-aria/interactions';

/**
 * Font.
 */
const FontFamily = {
  poppins: 'poppins',
  'source-code-pro': 'source-code-pro'
};

const Product = {
  asm: 'asm',
  'brand-trust': 'brand-trust',
  certificates: 'certificates',
  ondmarc: 'ondmarc',
  pulse: 'pulse',
  radar: 'radar',
  'rojo-ds': 'rojo-ds',
  'vendor-secure': 'vendor-secure',
  website: 'website'
};
const ProductName = {
  asm: 'ASM',
  'brand-trust': 'Brand Trust',
  certificates: 'Certificates',
  ondmarc: 'OnDMARC',
  pulse: 'Pulse',
  radar: 'RADAR',
  'rojo-ds': 'ROJO DS',
  'vendor-secure': 'Vendor Secure',
  website: 'Website'
};
const ProductLogo = {
  asm: rsiAsmHorizontal,
  'brand-trust': rsiBrandTrustHorizontal,
  certificates: rsiCertificatesHorizontal,
  ondmarc: rsiOndmarcHorizontal,
  pulse: rsiPulseHorizontal,
  radar: rsiRadarHorizontal,
  'rojo-ds': rsiRojoDsHorizontal,
  'vendor-secure': rsiVendorSecureHorizontal,
  website: rsiRedSiftHorizontal
};

/**
 * Color palette.
 */
const CtasColorPalette = {
  primary: 'primary',
  secondary: 'secondary'
};
const NotificationsColorPalette = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  question: 'question',
  'no-data': 'no-data'
};
const ProductColorPalette = Product;
const PresentationColorPalette = {
  green: 'green',
  pink: 'pink',
  aqua: 'aqua',
  brown: 'brown',
  red: 'red',
  yellow: 'yellow',
  purple: 'purple',
  orange: 'orange',
  blue: 'blue',
  grey: 'grey'
};
const NeutralColorPalette = {
  black: 'black',
  darkgrey: 'darkgrey',
  midgrey: 'midgrey',
  lightgrey: 'lightgrey',
  xlightgrey: 'xlightgrey',
  white: 'white'
};

const AlignSelf = {
  auto: 'auto',
  normal: 'normal',
  start: 'start',
  end: 'end',
  center: 'center',
  'flex-start': 'flex-start',
  'flex-end': 'flex-end',
  'self-start': 'self-start',
  'self-end': 'self-end',
  baseline: 'baseline',
  stretch: 'stretch'
};
const JustifySelf = {
  auto: 'auto',
  normal: 'normal',
  start: 'start',
  end: 'end',
  center: 'center',
  'flex-start': 'flex-start',
  'flex-end': 'flex-end',
  'self-start': 'self-start',
  'self-end': 'self-end',
  left: 'left',
  right: 'right',
  stretch: 'stretch'
};
const AlignContent = {
  'flex-start': 'flex-start',
  'flex-end': 'flex-end',
  center: 'center',
  'space-between': 'space-between',
  'space-around': 'space-around',
  'space-evenly': 'space-evenly',
  stretch: 'stretch',
  start: 'start',
  end: 'end',
  baseline: 'baseline',
  'first baseline': 'first baseline',
  'last baseline': 'last baseline'
};
const AlignItems = {
  stretch: 'stretch',
  'flex-start': 'flex-start',
  'flex-end': 'flex-end',
  center: 'center',
  baseline: 'baseline',
  'first baseline': 'first baseline',
  'last baseline': 'last baseline',
  start: 'start',
  end: 'end',
  'self-start': 'self-start',
  'self-end': 'self-end'
};
const FlexDirection = {
  row: 'row',
  'row-reverse': 'row-reverse',
  column: 'column',
  'column-reverse': 'column-reverse'
};
const FlexWrap = {
  nowrap: 'nowrap',
  wrap: 'wrap',
  'wrap-reverse': 'wrap-reverse'
};
const JustifyContent = {
  'flex-start': 'flex-start',
  'flex-end': 'flex-end',
  center: 'center',
  'space-between': 'space-between',
  'space-around': 'space-around',
  'space-evenly': 'space-evenly',
  start: 'start',
  end: 'end',
  left: 'left',
  right: 'right',
  baseline: 'baseline',
  'first baseline': 'first baseline',
  'last baseline': 'last baseline',
  stretch: 'stretch'
};
const JustifyItems = {
  start: 'start',
  end: 'end',
  center: 'center',
  stretch: 'stretch'
};

/**
 * Create a predicate function that checks if a ReactNode is a react element from the given component.
 *
 * @param  component React function component or the component name
 * @return predicate returning true if value is instance of the component
 */
const isComponent = component => instance => {
  const componentName = typeof component === 'string' ? component : component.displayName;
  return /*#__PURE__*/React__default.isValidElement(instance) && instance.type.displayName === componentName;
};

/**
 * Create an array containing only valid and specific components.
 *
 * @param  component React function component or the component name
 * @return an array of components
 */
const filterComponents = component => children => {
  const components = Array.isArray(component) ? component : [component];
  const childArray = [];
  React__default.Children.forEach(children, child => {
    components.forEach(comp => {
      if (isComponent(comp)(child)) {
        childArray.push(child);
      }
    });
  });
  return childArray;
};

/**
 * Return components based on list of predicates.
 *
 * @param  components Array of components
 * @param  predicates Array of predicates
 * @return an array of arrays of components based on the given array of predicates
 */
function partitionComponents(components, predicates) {
  const elements = [...predicates.map(() => []), []];
  components.forEach(component => {
    let found = false;
    predicates.forEach((predicate, index) => {
      if (predicate(component)) {
        elements[index].push(component);
        found = true;
      }
    });
    if (!found) {
      elements[elements.length - 1].push(component);
    }
  });
  return elements;
}

/**
 * Do not edit directly
 * Generated on Wed, 20 Dec 2023 08:32:38 GMT
 */

const RedsiftColorRedsift = '#e11010';
const RedsiftColorProductAsm = '#8361ea';
const RedsiftColorProductBrandTrust = '#7ec2fa';
const RedsiftColorProductCertificates = '#e8712e';
const RedsiftColorProductOndmarc = '#96d36e';
const RedsiftColorProductRadar = '#51b7a4';
const RedsiftColorProductRojoDs = '#e11010';
const RedsiftColorProductVendorSecure = '#f7c948';
const RedsiftColorProductWebsite = '#e11010';
const RedsiftColorNeutralBlack = '#1c1c1c';
const RedsiftColorNeutralDarkgrey = '#333333';
const RedsiftColorNeutralMidgrey = '#666666';
const RedsiftColorNeutralLightgrey = '#b6b6b6';
const RedsiftColorNeutralXlightgrey = '#f2f2f2';
const RedsiftColorNeutralWhite = '#ffffff';
const RedsiftAppBarColorBackground = '#ffffff';
const RedsiftAppBarColorText = '#1c1c1c';
const RedsiftAppBarColorExpandIconResting = '#333333';
const RedsiftAppBarColorExpandIconHover = '#1c1c1c';
const RedsiftAppBarColorExpandIconBackgroundHover = '#ffffff';
const RedsiftColorCtasPrimaryPrimary = '#4285F4';
const RedsiftColorCtasPrimaryHover = '#D9E7FD';
const RedsiftColorCtasPrimaryActive = '#B3CEFB';
const RedsiftColorCtasPrimaryActiveHover = '#8EB6F8';
const RedsiftColorCtasPrimaryButtonHover = '#356AC3';
const RedsiftColorCtasPrimaryButtonActive = '#285092';
const RedsiftColorCtasPrimaryButtonActiveHover = '#1A3562';
const RedsiftColorCtasSecondaryPrimary = '#51B7A4';
const RedsiftColorCtasSecondaryHover = '#DCF1ED';
const RedsiftColorCtasSecondaryActive = '#B9E2DB';
const RedsiftColorCtasSecondaryActiveHover = '#97D4C8';
const RedsiftColorCtasSecondaryButtonHover = '#419283';
const RedsiftColorCtasSecondaryButtonActive = '#316E62';
const RedsiftColorCtasSecondaryButtonActiveHover = '#204942';
const RedsiftColorNotificationsSuccessPrimary = '#02AC61';
const RedsiftColorNotificationsSuccessHover = '#CCEEDF';
const RedsiftColorNotificationsSuccessActive = '#9ADEC0';
const RedsiftColorNotificationsSuccessActiveHover = '#67CDA0';
const RedsiftColorNotificationsSuccessButtonHover = '#028A4E';
const RedsiftColorNotificationsSuccessButtonActive = '#01673A';
const RedsiftColorNotificationsSuccessButtonActiveHover = '#014527';
const RedsiftColorNotificationsErrorPrimary = '#E11010';
const RedsiftColorNotificationsErrorHover = '#F9CFCF';
const RedsiftColorNotificationsErrorActive = '#F39F9F';
const RedsiftColorNotificationsErrorActiveHover = '#ED7070';
const RedsiftColorNotificationsErrorButtonHover = '#B40D0D';
const RedsiftColorNotificationsErrorButtonActive = '#870A0A';
const RedsiftColorNotificationsErrorButtonActiveHover = '#5A0606';
const RedsiftColorNotificationsWarningPrimary = '#FCBB54';
const RedsiftColorNotificationsWarningHover = '#FEF1DD';
const RedsiftColorNotificationsWarningActive = '#FEE4BB';
const RedsiftColorNotificationsWarningActiveHover = '#FDD698';
const RedsiftColorNotificationsWarningButtonHover = '#CA9643';
const RedsiftColorNotificationsWarningButtonActive = '#977032';
const RedsiftColorNotificationsWarningButtonActiveHover = '#654B22';
const RedsiftColorNotificationsInfoPrimary = '#4285F4';
const RedsiftColorNotificationsInfoHover = '#D9E7FD';
const RedsiftColorNotificationsInfoActive = '#B3CEFB';
const RedsiftColorNotificationsInfoActiveHover = '#8EB6F8';
const RedsiftColorNotificationsInfoButtonHover = '#356AC3';
const RedsiftColorNotificationsInfoButtonActive = '#285092';
const RedsiftColorNotificationsInfoButtonActiveHover = '#1A3562';
const RedsiftColorNotificationsQuestionPrimary = '#666666';
const RedsiftColorNotificationsQuestionHover = '#B2B2B2';
const RedsiftColorNotificationsQuestionActive = '#A3A3A3';
const RedsiftColorNotificationsQuestionActiveHover = '#949494';
const RedsiftColorNotificationsQuestionButtonHover = '#525252';
const RedsiftColorNotificationsQuestionButtonActive = '#474747';
const RedsiftColorNotificationsQuestionButtonActiveHover = '#3D3D3D';
const RedsiftColorNotificationsNoDataPrimary = '#BDBDBD';
const RedsiftColorNotificationsNoDataHover = '#DEDEDE';
const RedsiftColorNotificationsNoDataActive = '#D7D7D7';
const RedsiftColorNotificationsNoDataActiveHover = '#D1D1D1';
const RedsiftColorNotificationsNoDataButtonHover = '#979797';
const RedsiftColorNotificationsNoDataButtonActive = '#848484';
const RedsiftColorNotificationsNoDataButtonActiveHover = '#717171';
const RedsiftColorPresentationGreenDarkerer = '#078833';
const RedsiftColorPresentationGreenDarker = '#07AA45';
const RedsiftColorPresentationGreenDark = '#00CE59';
const RedsiftColorPresentationGreenDefault = '#56D58E';
const RedsiftColorPresentationGreenLight = '#A2E7BA';
const RedsiftColorPresentationGreenLighter = '#C2EFD1';
const RedsiftColorPresentationGreenLighterer = '#E1F7E8';
const RedsiftColorPresentationPinkDarkerer = '#B10078';
const RedsiftColorPresentationPinkDarker = '#C5008C';
const RedsiftColorPresentationPinkDark = '#D800A2';
const RedsiftColorPresentationPinkDefault = '#D95CBA';
const RedsiftColorPresentationPinkLight = '#ECA0D6';
const RedsiftColorPresentationPinkLighter = '#F4C0E3';
const RedsiftColorPresentationPinkLighterer = '#FAE0F1';
const RedsiftColorPresentationAquaDarkerer = '#0B9287';
const RedsiftColorPresentationAquaDarker = '#0AB5AB';
const RedsiftColorPresentationAquaDark = '#00D9D2';
const RedsiftColorPresentationAquaDefault = '#63EAE4';
const RedsiftColorPresentationAquaLight = '#9AF8F4';
const RedsiftColorPresentationAquaLighter = '#CAF7F4';
const RedsiftColorPresentationAquaLighterer = '#E5FBFA';
const RedsiftColorPresentationBrownDarkerer = '#701205';
const RedsiftColorPresentationBrownDarker = '#903107';
const RedsiftColorPresentationBrownDark = '#AF5100';
const RedsiftColorPresentationBrownDefault = '#C78348';
const RedsiftColorPresentationBrownLight = '#EDC19C';
const RedsiftColorPresentationBrownLighter = '#F6D9C3';
const RedsiftColorPresentationBrownLighterer = '#FAE6D7';
const RedsiftColorPresentationRedDarkerer = '#B10004';
const RedsiftColorPresentationRedDarker = '#C70003';
const RedsiftColorPresentationRedDark = '#DE0000';
const RedsiftColorPresentationRedDefault = '#E06363';
const RedsiftColorPresentationRedLight = '#F3A39E';
const RedsiftColorPresentationRedLighter = '#F8BBBA';
const RedsiftColorPresentationRedLighterer = '#FDDDDC';
const RedsiftColorPresentationYellowDarkerer = '#8A7F18';
const RedsiftColorPresentationYellowDarker = '#BCAD14';
const RedsiftColorPresentationYellowDark = '#F0DE00';
const RedsiftColorPresentationYellowDefault = '#FFF741';
const RedsiftColorPresentationYellowLight = '#F7EFC3';
const RedsiftColorPresentationYellowLighter = '#FBF5DB';
const RedsiftColorPresentationYellowLighterer = '#FCF9E7';
const RedsiftColorPresentationPurpleDarkerer = '#4702C1';
const RedsiftColorPresentationPurpleDarker = '#7902EB';
const RedsiftColorPresentationPurpleDark = '#9200FF';
const RedsiftColorPresentationPurpleDefault = '#965EDE';
const RedsiftColorPresentationPurpleLight = '#C6A8EF';
const RedsiftColorPresentationPurpleLighter = '#DECAF6';
const RedsiftColorPresentationPurpleLighterer = '#E9DCF9';
const RedsiftColorPresentationOrangeDarkerer = '#B45310';
const RedsiftColorPresentationOrangeDarker = '#D2710B';
const RedsiftColorPresentationOrangeDark = '#ED9200';
const RedsiftColorPresentationOrangeDefault = '#FCBB54';
const RedsiftColorPresentationOrangeLight = '#F8D296';
const RedsiftColorPresentationOrangeLighter = '#FDE4C0';
const RedsiftColorPresentationOrangeLighterer = '#FEEDD5';
const RedsiftColorPresentationBlueDarkerer = '#0082F8';
const RedsiftColorPresentationBlueDarker = '#0099FD';
const RedsiftColorPresentationBlueDark = '#00AEFF';
const RedsiftColorPresentationBlueDefault = '#73C5EB';
const RedsiftColorPresentationBlueLight = '#B1DCF3';
const RedsiftColorPresentationBlueLighter = '#CCE8F7';
const RedsiftColorPresentationBlueLighterer = '#E6F3FB';
const RedsiftColorPresentationGreyDarkerer = '#6E6E6E';
const RedsiftColorPresentationGreyDarker = '#959595';
const RedsiftColorPresentationGreyDark = '#BFBFBF';
const RedsiftColorPresentationGreyDefault = '#D6D6D6';
const RedsiftColorPresentationGreyLight = '#E6E6E6';
const RedsiftColorPresentationGreyLighter = '#EEEEEE';
const RedsiftColorPresentationGreyLighterer = '#F7F7F7';
const RedsiftSideNavigationColorBackground = '#f2f2f2';
const RedsiftSideNavigationColorMenuItemTextResting = '#333333';
const RedsiftSideNavigationColorMenuItemTextDisabled = '#666666';
const RedsiftSideNavigationColorMenuItemTextHover = '#1c1c1c';
const RedsiftSideNavigationColorMenuItemBackgroundHover = '#E6E6E6';
const RedsiftSideNavigationColorMenuItemBackgroundSecondary = 'transparent';
const RedsiftSideNavigationColorMenuItemBackgroundActive = '#b6b6b6';
const RedsiftSideNavigationColorMenuItemActive = '#1c1c1c';
const RedsiftSideNavigationColorScrollbarResting = '#b6b6b6';
const RedsiftSideNavigationColorScrollbarHover = '#666666';
const RedsiftLayoutZIndexFooter = '1000';
const RedsiftLayoutZIndexHeader = '1010';
const RedsiftLayoutZIndexSidePanel = '1050';
const RedsiftLayoutZIndexDropdown = '1100';
const RedsiftLayoutZIndexOverlay = '1150';
const RedsiftLayoutZIndexDialog = '1200';
const RedsiftLayoutZIndexPopover = '1250';
const RedsiftLayoutZIndexTooltip = '1300';
const RedsiftTypographyFontFamilyPoppins = "'Poppins', sans-serif";
const RedsiftTypographyFontFamilySourceCodePro = "'Source Code Pro', sans-serif";
const RedsiftTypographyFontWeightThin = '100';
const RedsiftTypographyFontWeightExtraLight = '200';
const RedsiftTypographyFontWeightLight = '300';
const RedsiftTypographyFontWeightRegular = '400';
const RedsiftTypographyFontWeightMedium = '500';
const RedsiftTypographyFontWeightSemiBold = '600';
const RedsiftTypographyFontWeightBold = '700';
const RedsiftTypographyFontWeightExtraBold = '800';
const RedsiftTypographyFontWeightBlack = '900';
const RedsiftTypographyH1FontFamily = "'Poppins', sans-serif";
const RedsiftTypographyH1FontSize = '24px';
const RedsiftTypographyH1FontWeight = '600';
const RedsiftTypographyH1LineHeight = '36px';
const RedsiftTypographyH1TextTransform = 'unset';
const RedsiftTypographyH2FontFamily = "'Poppins', sans-serif";
const RedsiftTypographyH2FontSize = '22px';
const RedsiftTypographyH2FontWeight = '600';
const RedsiftTypographyH2LineHeight = '30px';
const RedsiftTypographyH2TextTransform = 'unset';
const RedsiftTypographyH3FontFamily = "'Poppins', sans-serif";
const RedsiftTypographyH3FontSize = '20px';
const RedsiftTypographyH3FontWeight = '600';
const RedsiftTypographyH3LineHeight = '30px';
const RedsiftTypographyH3TextTransform = 'unset';
const RedsiftTypographyH4FontFamily = "'Poppins', sans-serif";
const RedsiftTypographyH4FontSize = '16px';
const RedsiftTypographyH4FontWeight = '600';
const RedsiftTypographyH4LineHeight = '24px';
const RedsiftTypographyH4TextTransform = 'unset';
const RedsiftTypographyH5FontFamily = "'Poppins', sans-serif";
const RedsiftTypographyH5FontSize = '12px';
const RedsiftTypographyH5FontWeight = '600';
const RedsiftTypographyH5LineHeight = '18px';
const RedsiftTypographyH5TextTransform = 'unset';
const RedsiftTypographyBodyFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyBodyFontSize = '15px';
const RedsiftTypographyBodyFontWeight = '400';
const RedsiftTypographyBodyLineHeight = '24px';
const RedsiftTypographyBodyTextTransform = 'unset';
const RedsiftTypographyLinkFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyLinkFontSize = '15px';
const RedsiftTypographyLinkFontWeight = '400';
const RedsiftTypographyLinkLineHeight = '26px';
const RedsiftTypographyInputTextFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyInputTextFontSize = '15px';
const RedsiftTypographyInputTextFontWeight = '400';
const RedsiftTypographyInputTextLineHeight = '24px';
const RedsiftTypographyButtonFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyButtonFontSize = '16px';
const RedsiftTypographyButtonFontWeight = '500';
const RedsiftTypographyButtonLineHeight = '24px';
const RedsiftTypographyButtonTextTransform = 'uppercase';
const RedsiftTypographyCaptionFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyCaptionFontSize = '13px';
const RedsiftTypographyCaptionFontWeight = '400';
const RedsiftTypographyCaptionLineHeight = '18px';
const RedsiftTypographyCaptionTextTransform = 'unset';
const RedsiftTypographyHelperFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyHelperFontSize = '16px';
const RedsiftTypographyHelperFontWeight = '400';
const RedsiftTypographyHelperLineHeight = '24px';
const RedsiftTypographyHelperTextTransform = 'unset';
const RedsiftTypographyBadgeFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyBadgeFontSize = '12px';
const RedsiftTypographyBadgeFontWeight = '400';
const RedsiftTypographyBadgeLineHeight = '12px';
const RedsiftTypographyPillFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyPillFontSize = '10px';
const RedsiftTypographyPillFontWeight = '400';
const RedsiftTypographyPillLineHeight = '15px';
const RedsiftTypographyChipFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyChipFontSize = '12px';
const RedsiftTypographyChipFontWeight = '400';
const RedsiftTypographyChipLineHeight = '18px';
const RedsiftTypographyTooltipFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyTooltipFontSize = '12px';
const RedsiftTypographyTooltipFontWeight = '400';
const RedsiftTypographyTooltipLineHeight = '18px';
const RedsiftTypographyDatagridHeaderFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyDatagridHeaderFontSize = '14px';
const RedsiftTypographyDatagridHeaderFontWeight = '600';
const RedsiftTypographyDatagridHeaderLineHeight = '21px';
const RedsiftTypographyDatagridCellFontFamily = "'Poppins', sans-serif";
const RedsiftTypographyDatagridCellFontSize = '16px';
const RedsiftTypographyDatagridCellFontWeight = '400';
const RedsiftTypographyDatagridCellLineHeight = '24px';

let globalPrefix = 'rsi';
let lastId = 0;
const nextId = localPrefix => {
  lastId++;
  return `${localPrefix || globalPrefix}${lastId}`;
};
const resetId = () => {
  lastId = 0;
};
const setPrefix = newPrefix => {
  globalPrefix = newPrefix;
};
const getIds = (count, prefix) => {
  const ids = [];
  for (let i = 0; i < count; i++) {
    ids.push(nextId(prefix));
  }
  return ids;
};
function usePrevious$1(value) {
  const ref = React__default.useRef();
  React__default.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
const useId$1 = function () {
  let count = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
  let prefix = arguments.length > 1 ? arguments[1] : undefined;
  const idsListRef = React__default.useRef([]);
  const prevCount = usePrevious$1(count);
  const prevPrefix = usePrevious$1(prefix);
  if (count !== prevCount || prevPrefix !== prefix) {
    idsListRef.current = getIds(count, prefix);
  }
  return idsListRef.current;
};

/**
 * Display a warning if no label was found for screen readers and assistive technologies.
 *
 * @param props Component props containing the aria-label and aria-labelledby params to check for.
 * @param additionalKeysToCheck Additional props to check that can act as a label for screen readers (i.e. children).
 */
const warnIfNoAccessibleLabelFound = (props, additionalKeysToCheck) => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby
  } = props;
  const hasText = additionalKeysToCheck && additionalKeysToCheck.filter(key => !!key).length > 0;
  const hasAriaLabel = Boolean(ariaLabel || ariaLabelledby);
  if (!hasAriaLabel && !hasText) {
    console.warn('You must provide a label for accessibility', props);
  }
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
    size: undefined
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize(windowSize => {
        if (window.innerWidth === windowSize.width && window.innerHeight === windowSize.height) {
          return windowSize;
        }
        let size;
        if (window.innerWidth <= 480) {
          size = 'xs';
        } else if (window.innerWidth <= 768) {
          size = 'sm';
        } else if (window.innerWidth <= 1024) {
          size = 'md';
        } else if (window.innerWidth <= 1200) {
          size = 'lg';
        } else {
          size = 'xl';
        }
        return {
          width: window.innerWidth,
          height: window.innerHeight,
          size
        };
      });
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
};

/**
 * Get the bounding rect of a component.
 */
const useBoundingClientRect = (ref, deps) => {
  const [boundingRect, setBoundingRect] = useState({});
  useEffect(() => {
    if (ref.current) {
      setBoundingRect(ref.current.getBoundingClientRect());
    }
  }, deps);
  return boundingRect;
};

/**
 * Component variant.
 */
const AlertVariant = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info'
};

/**
 * Component props.
 */

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

var close$1 = "Close";
var enUS$6 = {
	close: close$1
};

var close = "Fermer";
var frFR$6 = {
	close: close
};

var intlMessages$6 = {
  'en-US': enUS$6,
  'fr-FR': frFR$6
};

/**
 * Component variant.
 */
const IconButtonVariant = {
  primary: 'primary',
  secondary: 'secondary',
  unstyled: 'unstyled'
};

/**
 * Component color.
 */

/**
 * Component props.
 */

/**
 * Component size.
 */
const IconSize = {
  xsmall: 'xsmall',
  small: 'small',
  medium: 'medium',
  large: 'large',
  xlarge: 'xlarge',
  xxlarge: 'xxlarge'
};

/**
 * Component props.
 */

const baseLayout = css`
  ${_ref => {
  let {
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    alignSelf,
    justifySelf,
    order,
    gridArea,
    gridColumn,
    gridRow,
    gridColumnStart,
    gridColumnEnd,
    gridRowStart,
    gridRowEnd
  } = _ref;
  return css`
      ${flex ? `flex: ${flex};` : ''}
      ${flexGrow !== undefined ? `flex-grow: ${flexGrow};` : ''}
      ${flexShrink !== undefined ? `flex-shrink: ${flexShrink};` : ''}
      ${flexBasis ? `flex-basis: ${flexBasis};` : ''}
      ${alignSelf ? `align-self: ${alignSelf};` : ''}
      ${justifySelf ? `justify-self: ${justifySelf};` : ''}
      ${order !== undefined ? `order: ${order};` : ''}
      ${gridArea ? `grid-area: ${gridArea};` : ''}
      ${gridColumn ? `grid-column: ${gridColumn};` : ''}
      ${gridRow ? `grid-row: ${gridRow};` : ''}
      ${gridColumnStart ? `grid-column-start: ${gridColumnStart};` : ''}
      ${gridColumnEnd ? `grid-column-end: ${gridColumnEnd};` : ''}
      ${gridRowStart ? `grid-row-start: ${gridRowStart};` : ''}
      ${gridRowEnd ? `grid-row-end: ${gridRowEnd};` : ''}
    `;
}}
`;
const baseSpacing = css`
  ${_ref2 => {
  let {
    margin,
    marginBottom,
    marginLeft,
    marginRight,
    marginTop
  } = _ref2;
  return css`
      ${margin ? `margin: ${margin};` : ''}
      ${marginBottom ? `margin-bottom: ${marginBottom};` : ''}
      ${marginLeft ? `margin-left: ${marginLeft};` : ''}
      ${marginRight ? `margin-right: ${marginRight};` : ''}
      ${marginTop ? `margin-top: ${marginTop};` : ''}
    `;
}}
`;
const baseInternalSpacing = css`
  ${_ref3 => {
  let {
    padding,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop
  } = _ref3;
  return css`
      ${padding ? `padding: ${padding};` : ''}
      ${paddingBottom ? `padding-bottom: ${paddingBottom};` : ''}
      ${paddingLeft ? `padding-left: ${paddingLeft};` : ''}
      ${paddingRight ? `padding-right: ${paddingRight};` : ''}
      ${paddingTop ? `padding-top: ${paddingTop};` : ''}
    `;
}}
`;
const baseSizing = css`
  ${_ref4 => {
  let {
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    width
  } = _ref4;
  return css`
      ${height !== undefined ? `height: ${typeof height === 'number' ? `${height}px` : height};` : ''}
      ${maxHeight ? `max-height: ${maxHeight};` : ''}
      ${maxWidth ? `max-width: ${maxWidth};` : ''}
      ${minHeight ? `min-height: ${minHeight};` : ''}
      ${minWidth ? `min-width: ${minWidth};` : ''}
      ${width !== undefined ? `width: ${typeof width === 'number' ? `${width}px` : width};` : ''}
    `;
}}
`;
const basePositioning = css`
  ${_ref5 => {
  let {
    position,
    top,
    bottom,
    left,
    right,
    zIndex
  } = _ref5;
  return css`
      ${position ? `position: ${position};` : ''}
      ${top ? `top: ${top};` : ''}
      ${bottom ? `bottom: ${bottom};` : ''}
      ${left ? `left: ${left};` : ''}
      ${right ? `right: ${right};` : ''}
      ${zIndex ? `z-index: ${zIndex};` : ''}
    `;
}}
`;
const baseFlexbox = css`
  ${_ref6 => {
  let {
    alignContent,
    alignItems,
    flexDirection,
    flexWrap,
    gap,
    justifyContent
  } = _ref6;
  return css`
      ${alignContent ? `align-content: ${alignContent};` : ''}
      ${alignItems ? `align-items: ${alignItems};` : ''}
      ${flexDirection ? `flex-direction: ${flexDirection};` : ''}
      ${flexWrap ? `flex-wrap: ${flexWrap};` : ''}
      gap: ${gap};
      ${justifyContent ? `justify-content: ${justifyContent};` : ''}
    `;
}}
`;
const baseGrid = css`
  ${_ref7 => {
  let {
    alignContent,
    alignItems,
    gap,
    gridAutoColumns,
    gridAutoRows,
    gridTemplateAreas,
    gridTemplateColumns,
    gridTemplateRows,
    justifyContent,
    justifyItems
  } = _ref7;
  return css`
      ${alignContent ? `align-content: ${alignContent};` : ''}
      ${alignItems ? `align-items: ${alignItems};` : ''}
      ${gap ? `gap: ${gap};` : ''}
      ${gridAutoColumns ? `grid-auto-columns: ${gridAutoColumns};` : ''}
      ${gridAutoRows ? `grid-auto-rows: ${gridAutoRows};` : ''}
      ${gridTemplateAreas ? `grid-template-areas: ${gridTemplateAreas};` : ''}
      ${gridTemplateColumns ? `grid-template-columns: ${gridTemplateColumns};` : ''}
      ${gridTemplateRows ? `grid-template-rows: ${gridTemplateRows};` : ''}
      ${justifyContent ? `justify-content: ${justifyContent};` : ''}
      ${justifyItems ? `justify-items: ${justifyItems};` : ''}
    `;
}}
`;
const baseStyling = css`
  font-family: var(--redsift-typography-body-font-family);
  font-size: var(--redsift-typography-body-font-size);
  font-weight: var(--redsift-typography-body-font-weight);
  line-height: var(--redsift-typography-body-line-height);
  color: var(--redsift-color-neutral-black);

  ${baseLayout}
  ${baseSpacing}
  ${baseSizing}
  ${basePositioning}
`;
const baseContainer = css`
  ${_ref8 => {
  let {
    display
  } = _ref8;
  return display ? `display: ${display};` : '';
}}
  font-family: var(--redsift-typography-body-font-family);
  font-size: var(--redsift-typography-body-font-size);
  font-weight: var(--redsift-typography-body-font-weight);
  line-height: 20px;
  color: var(--redsift-color-neutral-black);

  ${baseInternalSpacing}
  ${baseSpacing}
  ${baseSizing}
  ${basePositioning}
  ${baseGrid}
  ${baseFlexbox}
`;
const focusRing = css`
  @media (prefers-reduced-motion: no-preference) {
    :focus-visible {
      outline: 2px solid var(--redsift-color-ctas-primary-primary);
      transition: outline-offset 75ms ease-out;
    }

    :not(:active):focus-visible {
      transition-duration: 0.25s;
    }
  }

  :not(:active):focus-visible {
    outline-offset: 0.2rem;
  }
`;

/**
 * Component style.
 */
const StyledIcon = styled.span`
  position: relative;
  display: inline-grid;
  ${baseStyling}

  font-style: normal;

  svg {
    vertical-align: -0.125em;
    grid-column: 1;
    grid-row: 1;
  }

  ${_ref => {
  let {
    $color
  } = _ref;
  return $color && Object.keys(NotificationsColorPalette).indexOf($color) !== -1 ? css`
          color: var(--redsift-color-notifications-${$color}-primary);
        ` : $color && Object.keys(ProductColorPalette).indexOf($color) !== -1 ? css`
          color: var(--redsift-color-product-${$color});
        ` : css`
          color: ${$color || css`var(--redsift-color-neutral-darkgrey)`};
        `;
}}

  ${_ref2 => {
  let {
    $width,
    $height,
    $fontSize,
    $lineHeight
  } = _ref2;
  return css`
    ${$width ? css`
          width: ${$width}px;
        ` : ''}
    ${$height ? css`
          height: ${$height}px;
        ` : ''}
    ${$fontSize ? css`
          font-size: ${$fontSize}px;
        ` : ''}
    ${$lineHeight ? css`
          line-height: ${$lineHeight}px;
        ` : ''}

    & .redsift-badge-standard {
      grid-column: 1;
      grid-row: 1;
      margin-right: unset;
      ${$width ? css`
            margin-left: ${$width * 0.75}px;
          ` : ''}
      ${$height ? css`
            margin-top: ${$height * 0.1 - 4}px;
          ` : ''}
    }

    & .redsift-badge-dot {
      grid-column: 1;
      grid-row: 1;
      margin-right: unset;
      ${$width ? css`
            margin-left: ${$width * 0.8}px;
          ` : ''}
      ${$height ? css`
            margin-top: ${$height * 0.2 - 3}px;
          ` : ''}
    }
  `;
}}
`;

const _excluded$P = ["aria-hidden", "aria-label", "badge", "className", "color", "icon", "size", "svgProps"];
const COMPONENT_NAME$P = 'Icon';
const CLASSNAME$P = 'redsift-icon';
const DEFAULT_PROPS$P = {
  size: IconSize.medium
};
const sizeToDimension$1 = size => {
  if (typeof size !== 'string') {
    return size;
  }
  switch (size) {
    case IconSize.xsmall:
      return {
        width: 16,
        height: 16,
        fontSize: 16,
        lineHeight: 16
      };
    case IconSize.small:
      return {
        width: 20,
        height: 20,
        fontSize: 20,
        lineHeight: 20
      };
    case IconSize.medium:
    default:
      return {
        width: 24,
        height: 24,
        fontSize: 24,
        lineHeight: 24
      };
    case IconSize.large:
      return {
        width: 30,
        height: 30,
        fontSize: 30,
        lineHeight: 30
      };
    case IconSize.xlarge:
      return {
        width: 40,
        height: 40,
        fontSize: 40,
        lineHeight: 40
      };
    case IconSize.xxlarge:
      return {
        width: 55,
        height: 55,
        fontSize: 55,
        lineHeight: 55
      };
  }
};

/**
 * The Icon component.
 */
const Icon = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-hidden': ariaHidden,
      'aria-label': ariaLabel,
      badge,
      className,
      color,
      icon,
      size,
      svgProps
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$P);
  const {
    width,
    height,
    fontSize,
    lineHeight
  } = sizeToDimension$1(size);
  return /*#__PURE__*/React__default.createElement(StyledIcon, _extends$1({}, forwardedProps, {
    $color: color,
    $width: width,
    $height: height,
    $fontSize: fontSize,
    $lineHeight: lineHeight,
    "aria-hidden": ariaLabel ? ariaHidden ? ariaHidden : undefined : true,
    "aria-label": ariaLabel,
    className: classNames(Icon.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.cloneElement(typeof icon === 'string' || Array.isArray(icon) && icon.every(it => typeof it === 'string') ? /*#__PURE__*/React__default.createElement("svg", {
    viewBox: "0 0 24 24"
  }, (Array.isArray(icon) ? icon : [icon]).map(path => /*#__PURE__*/React__default.createElement("path", {
    key: path,
    d: path,
    fill: "currentColor"
  }))) : /*#__PURE__*/React__default.isValidElement(icon) ? icon : /*#__PURE__*/React__default.createElement(React__default.Fragment, null), _objectSpread2({
    'aria-hidden': ariaLabel ? ariaHidden ? ariaHidden : undefined : true,
    'aria-label': ariaLabel,
    focusable: 'false',
    height,
    preserveAspectRatio: 'xMidYMid meet',
    role: 'img',
    width
  }, svgProps)), badge ? badge : null);
});
Icon.className = CLASSNAME$P;
Icon.defaultProps = DEFAULT_PROPS$P;
Icon.displayName = COMPONENT_NAME$P;

/**
 * Component variant.
 */
const ButtonVariant = {
  primary: 'primary',
  secondary: 'secondary',
  unstyled: 'unstyled'
};

/**
 * Component color.
 */

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledButton = styled.button`
  ${baseStyling}

  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  display: inline-flex;
  font-family: var(--redsift-typography-button-font-family);
  font-size: var(--redsift-typography-button-font-size);
  font-weight: var(--redsift-typography-button-font-weight);
  gap: 8px;
  justify-content: center;
  line-height: var(--redsift-typography-button-line-height);
  text-decoration: none;
  text-transform: var(--redsift-typography-button-text-transform);
  vertical-align: middle;
  -webkit-font-smoothing: antialiased;

  ${_ref => {
  let {
    $isLoading
  } = _ref;
  return $isLoading ? css`
          & > span {
            visibility: hidden;
          }

          & > .redsift-shield {
            position: absolute;
          }
        ` : '';
}}

  ${_ref2 => {
  let {
    $variant,
    $color,
    $isActive,
    $isDisabled,
    $isHovered
  } = _ref2;
  return css`
    padding: ${$variant === ButtonVariant.secondary ? '8px 15px' : '9px 16px'};

    ${$variant === ButtonVariant.secondary ? css`
          background-color: ${$isActive && $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active-hover)` : $isActive ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active)` : $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-hover)` : $isDisabled ? 'var(--redsift-color-neutral-white)' : 'var(--redsift-color-neutral-white)'};
          &,
          .redsift-icon {
            color: ${$isActive || $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)` : $isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-primary)`};
          }
        ` : $variant === ButtonVariant.unstyled ? css`
          background-color: ${$isActive && $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active-hover)` : $isActive ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active)` : $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-hover)` : $isDisabled ? 'none' : 'none'};
          &,
          .redsift-icon {
            color: ${$isActive || $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)` : $isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-primary)`};
          }
        ` : css`
          background-color: ${$isActive && $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active-hover)` : $isActive ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)` : $isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-hover)` : $isDisabled ? '#ECECEC' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-primary)`};
          &,
          .redsift-icon {
            color: ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : 'var(--redsift-color-neutral-white)'};
          }
        `}

    ${$variant === ButtonVariant.secondary ? css`
          border: 1px solid
            ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-primary)`};
        ` : ''}
  `;
}};

  &:hover,
  &:focus-visible {
    outline: none;

    ${_ref3 => {
  let {
    $variant,
    $color,
    $isActive,
    $isDisabled
  } = _ref3;
  return css`
      cursor: ${$isDisabled ? 'default' : 'pointer'};

      ${$variant === ButtonVariant.secondary ? css`
            background-color: ${$isActive ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active-hover)` : $isDisabled ? 'var(--redsift-color-neutral-white)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-hover)`};
            &,
            .redsift-icon {
              color: ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)`};
            }
          ` : $variant === ButtonVariant.unstyled ? css`
            background-color: ${$isActive ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active-hover)` : $isDisabled ? 'none' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-hover)`};
            &,
            .redsift-icon {
              color: ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)`};
            }
          ` : css`
            background-color: ${$isActive ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active-hover)` : $isDisabled ? '#ECECEC' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-hover)`};
            &,
            .redsift-icon {
              color: ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : 'var(--redsift-color-neutral-white)'};
            }
          `}

      ${$variant === ButtonVariant.secondary ? css`
            border: 1px solid
              ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-primary)`};
          ` : `
        border: none;
      `}
    `;
}};
  }

  &:active {
    outline: none;

    ${_ref4 => {
  let {
    $variant,
    $color,
    $isHovered,
    $isDisabled
  } = _ref4;
  return css`
      ${$variant === ButtonVariant.secondary ? css`
            background-color: ${$isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active-hover)` : $isDisabled ? 'var(--redsift-color-neutral-white)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active)`};
            &,
            .redsift-icon {
              color: ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)`};
            }
          ` : $variant === ButtonVariant.unstyled ? css`
            background-color: ${$isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active-hover)` : $isDisabled ? 'none' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active)`};
            &,
            .redsift-icon {
              color: ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)`};
            }
          ` : css`
            background-color: ${$isHovered ? `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active-hover)` : $isDisabled ? '#ECECEC' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-active)`};
            &,
            .redsift-icon {
              color: ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : 'var(--redsift-color-neutral-white)'};
            }
          `}

      ${$variant === ButtonVariant.secondary ? css`
            border: 1px solid
              ${$isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : `var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-primary)`};
          ` : `
        border: none;
      `}
    `;
}};
  }

  ${_ref5 => {
  let {
    $fullWidth
  } = _ref5;
  return $fullWidth ? `
        width: 100%;
        flex-grow: 1;
      ` : '';
}}

  .redsift-icon.left {
    margin-right: unset;
    align-items: center;
  }

  .redsift-icon.right {
    margin-left: unset;
    align-items: center;
  }

  ${focusRing}
`;

var loading$1 = "Loading...";
var enUS$5 = {
	loading: loading$1
};

var loading = "Chargement...";
var frFR$5 = {
	loading: loading
};

var intlMessages$5 = {
  'en-US': enUS$5,
  'fr-FR': frFR$5
};

/**
 * Component size.
 */
const SpinnerSize = {
  xsmall: 'xsmall',
  small: 'small',
  medium: 'medium',
  large: 'large'
};

/**
 * Component props.
 */

const StyledSpinner = styled.img`
  ${baseStyling}

  display: inline;
  line-height: 0px;
  vertical-align: -0.125em;
`;

var spinner = 'data:image/svg+xml;base64,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';

var uncoloredSpinner = 'data:image/svg+xml;base64,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';

const _excluded$O = ["aria-hidden", "aria-label", "className", "isColored", "size"];
const COMPONENT_NAME$O = 'Spinner';
const CLASSNAME$O = 'redsift-shield';
const DEFAULT_PROPS$O = {
  isColored: true,
  size: SpinnerSize.medium
};
const sizeToDimension = size => {
  switch (size) {
    case SpinnerSize.xsmall:
      return {
        width: 15,
        height: 15
      };
    case SpinnerSize.small:
      return {
        width: 24,
        height: 24
      };
    case SpinnerSize.large:
      return {
        width: 56,
        height: 56
      };
    case SpinnerSize.medium:
    default:
      return {
        width: 40,
        height: 40
      };
  }
};

/**
 * The Spinner component.
 */
const Spinner = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-hidden': ariaHidden,
      'aria-label': ariaLabel,
      className,
      isColored,
      size
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$O);
  const {
    width,
    height
  } = sizeToDimension(size);
  const stringFormatter = useLocalizedStringFormatter(intlMessages$5);
  return /*#__PURE__*/React__default.createElement(StyledSpinner, _extends$1({
    alt: stringFormatter.format('loading')
  }, forwardedProps, {
    "aria-hidden": ariaLabel ? ariaHidden ? ariaHidden : undefined : true,
    "aria-label": ariaLabel,
    className: classNames(Spinner.className, className),
    height: height,
    ref: ref,
    src: isColored ? spinner : uncoloredSpinner,
    width: width,
    $size: size
  }));
});
Spinner.className = CLASSNAME$O;
Spinner.defaultProps = DEFAULT_PROPS$O;
Spinner.displayName = COMPONENT_NAME$O;

const _excluded$N = ["children", "className", "color", "disabled", "fullWidth", "isActive", "isDisabled", "isHovered", "isLoading", "leftIcon", "leftIconProps", "rightIcon", "rightIconProps", "variant"];
const COMPONENT_NAME$N = 'Button';
const CLASSNAME$N = 'redsift-button';
const DEFAULT_PROPS$N = {
  color: CtasColorPalette.primary,
  height: 'fit-content',
  variant: ButtonVariant.primary
};

/**
 * The Button is a semantic button that looks like a button.
 *
 * For a semantic link that looks like a button, please use the ButtonLink component.
 * For a semantic link that looks like a link, please use the Link component.
 * For a semantic button that looks like a link, please use the LinkButton component.
 */
const Button = /*#__PURE__*/forwardRef((props, ref) => {
  const _ref = useRef();
  const buttonRef = ref || _ref;
  const {
      children,
      className,
      color,
      disabled,
      fullWidth,
      isActive,
      isDisabled: propsIsDisabled,
      isHovered,
      isLoading,
      leftIcon,
      leftIconProps,
      rightIcon,
      rightIconProps,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$N);
  const isDisabled = isLoading || propsIsDisabled || disabled;
  return /*#__PURE__*/React__default.createElement(StyledButton, _extends$1({}, forwardedProps, {
    $color: color,
    $fullWidth: fullWidth,
    $isActive: isActive,
    $isDisabled: isDisabled,
    $isHovered: isHovered,
    $isLoading: isLoading,
    $variant: variant,
    "aria-disabled": isDisabled,
    className: classNames(Button.className, className),
    disabled: isDisabled,
    ref: buttonRef
  }), leftIcon ? /*#__PURE__*/React__default.createElement(Icon, _extends$1({
    icon: leftIcon,
    "aria-hidden": "true",
    className: "left"
  }, leftIconProps)) : null, isLoading ? /*#__PURE__*/React__default.createElement(Spinner, {
    size: "small",
    isColored: false
  }) : null, /*#__PURE__*/React__default.createElement("span", null, children), rightIcon ? /*#__PURE__*/React__default.createElement(Icon, _extends$1({
    icon: rightIcon,
    "aria-hidden": "true",
    className: "right"
  }, rightIconProps)) : null);
});
Button.className = CLASSNAME$N;
Button.defaultProps = DEFAULT_PROPS$N;
Button.displayName = COMPONENT_NAME$N;

/**
 * Component style.
 */
const StyledIconButton = styled(StyledButton)`
  ${_ref => {
  let {
    $variant
  } = _ref;
  return css`
    padding: ${$variant === IconButtonVariant.secondary ? '8px' : '9px'};
  `;
}};

  ${_ref2 => {
  let {
    $isLoading
  } = _ref2;
  return $isLoading ? css`
          & > .redsift-shield {
            position: relative;
          }
        ` : '';
}}
`;

const _excluded$M = ["className", "color", "disabled", "icon", "iconProps", "isActive", "isDisabled", "isHovered", "isLoading", "variant"];
const COMPONENT_NAME$M = 'IconButton';
const CLASSNAME$M = 'redsift-icon-button';
const DEFAULT_PROPS$M = {
  color: CtasColorPalette.primary,
  height: 'fit-content',
  variant: IconButtonVariant.unstyled
};

/**
 * The Icon Button is a component that contains only one icon as a child, without text.
 *
 * This component should use aria-label, aria-labelledby or aria-describedby to make this
 * component usable with a screen reader.
 */
const IconButton = /*#__PURE__*/forwardRef((props, ref) => {
  const buttonRef = ref || useRef();
  const {
      className,
      color,
      disabled,
      icon,
      iconProps,
      isActive,
      isDisabled: propsIsDisabled,
      isHovered,
      isLoading,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$M);
  const isDisabled = props.isLoading || propsIsDisabled || disabled;
  warnIfNoAccessibleLabelFound(props);
  return /*#__PURE__*/React__default.createElement(StyledIconButton, _extends$1({}, forwardedProps, {
    $color: isDisabled ? undefined : color,
    $isActive: isActive,
    $isDisabled: isDisabled,
    $isHovered: isHovered,
    $isLoading: isLoading,
    $variant: variant,
    "aria-disabled": isDisabled,
    className: classNames(IconButton.className, className),
    disabled: isDisabled,
    ref: buttonRef
  }), isLoading ? /*#__PURE__*/React__default.createElement(Spinner, {
    size: "small"
  }) : /*#__PURE__*/React__default.createElement(Icon, _extends$1({}, iconProps, {
    icon: icon,
    color: isDisabled ? undefined : color
  })));
});
IconButton.className = CLASSNAME$M;
IconButton.defaultProps = DEFAULT_PROPS$M;
IconButton.displayName = COMPONENT_NAME$M;

/**
 * Component style.
 */
const StyledAlert = styled.div`
  ${baseStyling}

  padding: 16px;
  border-radius: 4px;
  ${_ref => {
  let {
    $hasTitle,
    $isClosable,
    $isClosed,
    $variant
  } = _ref;
  return css`
    display: ${!$isClosed ? 'block' : 'none'};
    background-color: var(--redsift-color-notifications-${$variant}-hover);

    .redsift-alert-header__icon-button {
      padding: 0px;
      .redsift-icon {
        color: var(--redsift-color-neutral-black);
      }

      &:hover,
      &:focus-visible {
        background-color: var(--redsift-color-notifications-${$variant}-active);
      }
    }

    .redsift-alert__content {
      margin-top: ${$hasTitle ? '8px' : '-24px'};
      margin-left: 38px;
      ${$isClosable ? css`
            margin-right: 24px;
          ` : ''};
    }
  `;
}}
`;

/**
 * Component variant.
 */
const TextVariant = {
  body: 'body',
  button: 'button',
  caption: 'caption',
  helper: 'helper',
  inherit: 'inherit'
};
const TextComponent = {
  p: 'p',
  b: 'b',
  i: 'i',
  u: 'u',
  abbr: 'abbr',
  cite: 'cite',
  del: 'del',
  em: 'em',
  ins: 'ins',
  kbd: 'kbd',
  mark: 'mark',
  s: 's',
  samp: 'samp',
  sub: 'sub',
  sup: 'sup'
};

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledText = styled.span`
  ${baseStyling}

  ${_ref => {
  let {
    $color
  } = _ref;
  return $color && Object.keys(NotificationsColorPalette).indexOf($color) !== -1 ? css`
          color: var(--redsift-color-notifications-${$color}-primary);
        ` : Object.keys(NeutralColorPalette).indexOf($color) !== -1 ? css`
          color: var(--redsift-color-neutral-${$color});
        ` : css`
          color: ${$color || css`inherit`};
        `;
}}

  ${_ref2 => {
  let {
    $as
  } = _ref2;
  return css`
    ${$as === TextComponent.i || $as === TextComponent.cite || $as === TextComponent.em ? css`
          font-style: italic;
        ` : $as === TextComponent.sub ? css`
          vertical-align: sub;
        ` : $as === TextComponent.sup ? css`
          vertical-align: super;
        ` : ''}
  `;
}}

  ${_ref3 => {
  let {
    $as,
    $fontFamily,
    $fontSize,
    $fontWeight,
    $lineHeight,
    $variant
  } = _ref3;
  return !$variant ? css`
          ${$fontFamily ? css`
                font-family: var(--redsift-typography-font-family-${$fontFamily});
              ` : ''}
          ${$fontSize ? css`
                font-size: ${$fontSize};
              ` : ''}
          ${$fontWeight ? css`
                font-weight: ${$fontWeight};
              ` : ''}
          ${$lineHeight ? css`
                line-height: ${$lineHeight};
              ` : ''}
          ${$as === TextComponent.b ? css`
                font-weight: ${$fontWeight ? $fontWeight : 'var(--redsift-typography-font-weight-bold)'};
              ` : $as === TextComponent.sup || $as === TextComponent.sub ? css`
                font-size: ${$fontSize ? $fontSize : '12px'};
              ` : ''}
        ` : $variant === TextVariant.inherit ? css`
          font-family: ${$fontFamily ? css`var(--redsift-typography-font-family-${$fontFamily})` : css`inherit`};
          font-size: ${$fontSize ? $fontSize : $as === TextComponent.sup || $as === TextComponent.sub ? '12px' : css`inherit`};
          font-weight: ${$fontWeight ? $fontWeight : $as === TextComponent.b ? 'var(--redsift-typography-font-weight-bold)' : 'inherit'};
          line-height: ${$lineHeight ? $lineHeight : css`inherit`};
          text-transform: inherit;
        ` : css`
          font-family: ${$fontFamily ? css`var(--redsift-typography-font-family-${$fontFamily})` : css`var(--redsift-typography-${$variant}-font-family)`};
          font-size: ${$fontSize ? $fontSize : css`var(--redsift-typography-${$variant}-font-size)`};
          font-weight: ${$fontWeight ? $fontWeight : css`var(--redsift-typography-${$variant}-font-weight)`};
          line-height: ${$lineHeight ? $lineHeight : css`var(--redsift-typography-${$variant}-line-height)`};
          text-transform: var(--redsift-typography-${$variant}-text-transform);
        `;
}}
  
  ${_ref4 => {
  let {
    $noWrap
  } = _ref4;
  return $noWrap ? css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        ` : '';
}}
`;

const _excluded$L = ["as", "children", "className", "color", "fontFamily", "fontSize", "fontWeight", "lineHeight", "noWrap", "variant"];
const COMPONENT_NAME$L = 'Text';
const CLASSNAME$L = 'redsift-text';
const DEFAULT_PROPS$L = {
  fontFamily: FontFamily.poppins
};

/**
 * The Text component.
 */
const Text = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as,
      children,
      className,
      color,
      fontFamily,
      fontSize,
      fontWeight,
      lineHeight,
      noWrap,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$L);
  return /*#__PURE__*/React__default.createElement(StyledText, _extends$1({
    as: as
  }, forwardedProps, {
    className: classNames(Text.className, className),
    ref: ref,
    $as: as,
    $color: color,
    $fontFamily: fontFamily,
    $fontSize: fontSize,
    $fontWeight: fontWeight,
    $lineHeight: lineHeight,
    $noWrap: noWrap,
    $variant: variant
  }), children);
});
Text.className = CLASSNAME$L;
Text.defaultProps = DEFAULT_PROPS$L;
Text.displayName = COMPONENT_NAME$L;

/**
 * Component style.
 */
const StyledFlexbox = styled.div`
  display: ${_ref => {
  let {
    inline
  } = _ref;
  return inline ? 'inline-flex' : 'flex';
}};

  ${baseStyling}
  ${baseInternalSpacing}
  ${baseFlexbox}

  ${_ref2 => {
  let {
    $divider
  } = _ref2;
  return $divider ? css`
          overflow: hidden;
          > *:not(:empty) {
            position: relative;

            ::before {
              content: '';
              position: absolute;
              background-color: var(--redsift-color-neutral-lightgrey);
              inline-size: 1px;
              block-size: 100%;
              inset-inline-start: calc(${Math.max(1, $divider.colGap / 2)}px * -1);
            }

            ::after {
              content: '';
              position: absolute;
              background-color: var(--redsift-color-neutral-lightgrey);
              inline-size: 200vw;
              block-size: 1px;
              inset-inline-start: -100vw;
              inset-block-start: calc(${Math.max(1, $divider.rowGap / 2)}px * -1);
              z-index: 0;
            }
          }
        ` : '';
}}
`;

const _excluded$K = ["children", "className", "divider", "gap"];
const COMPONENT_NAME$K = 'Flexbox';
const CLASSNAME$K = 'redsift-flex-box';
const DEFAULT_PROPS$K = {
  gap: '16px'
};

/**
 * The Flexbox component.
 */
const Flexbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      divider,
      gap
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$K);
  return /*#__PURE__*/React__default.createElement(StyledFlexbox, _extends$1({}, forwardedProps, {
    gap: divider ? `${divider.rowGap}px ${divider.colGap}px` : gap,
    className: classNames(Flexbox.className, className),
    ref: ref,
    $divider: divider
  }), children);
});
Flexbox.className = CLASSNAME$K;
Flexbox.defaultProps = DEFAULT_PROPS$K;
Flexbox.displayName = COMPONENT_NAME$K;

const _excluded$J = ["children", "className", "isClosable", "isClosed", "onClose", "title", "variant"];
const COMPONENT_NAME$J = 'Alert';
const CLASSNAME$J = 'redsift-alert';
const DEFAULT_PROPS$J = {
  variant: 'info'
};
const getVariant$1 = variant => {
  switch (variant) {
    case AlertVariant.info:
    default:
      return {
        color: NotificationsColorPalette.info,
        icon: mdiInformation
      };
    case AlertVariant.error:
      return {
        color: NotificationsColorPalette.error,
        icon: mdiAlertCircle
      };
    case AlertVariant.success:
      return {
        color: NotificationsColorPalette.success,
        icon: mdiCheckCircle
      };
    case AlertVariant.warning:
      return {
        color: NotificationsColorPalette.warning,
        icon: mdiAlert
      };
  }
};

/**
 * The Alert component.
 */
const Alert = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      isClosable,
      isClosed: propsIsClosed,
      onClose,
      title,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$J);
  const stringFormatter = useLocalizedStringFormatter(intlMessages$6);
  const {
    color,
    icon
  } = getVariant$1(variant);
  const [isClosed, setIsClosed] = useState(propsIsClosed);
  useEffect(() => {
    setIsClosed(propsIsClosed);
  }, [propsIsClosed]);
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose(true);
    }
    if (propsIsClosed === undefined || propsIsClosed === null) {
      setIsClosed(true);
    }
  }, [onClose]);
  return /*#__PURE__*/React__default.createElement(StyledAlert, _extends$1({}, forwardedProps, {
    className: classNames(Alert.className, className),
    ref: ref,
    $hasTitle: Boolean(title),
    $isClosable: isClosable,
    $isClosed: isClosed,
    $variant: variant
  }), /*#__PURE__*/React__default.createElement(Flexbox, {
    className: `${Alert.className}__header`,
    justifyContent: "space-between"
  }, /*#__PURE__*/React__default.createElement(Flexbox, {
    className: `${Alert.className}-header__title`,
    gap: "14px",
    alignItems: "center"
  }, /*#__PURE__*/React__default.createElement(Icon, {
    icon: icon,
    color: color
  }), title ? /*#__PURE__*/React__default.createElement(Text, {
    fontWeight: RedsiftTypographyFontWeightSemiBold
  }, title) : null), isClosable ? /*#__PURE__*/React__default.createElement(IconButton, {
    "aria-label": stringFormatter.format('close'),
    className: `${Alert.className}-header__icon-button`,
    icon: mdiClose$1,
    onClick: handleClose
  }) : null), children ? /*#__PURE__*/React__default.createElement("div", {
    className: `${Alert.className}__content`
  }, children) : null);
});
Alert.className = CLASSNAME$J;
Alert.defaultProps = DEFAULT_PROPS$J;
Alert.displayName = COMPONENT_NAME$J;

var expand$7 = "Expand left side panel";
var collapse$7 = "Collapse left side panel";
var enUS$4 = {
	expand: expand$7,
	collapse: collapse$7
};

var expand$6 = "Développer le panneau latéral gauche";
var collapse$6 = "Réduire le panneau latéral gauche";
var frFR$4 = {
	expand: expand$6,
	collapse: collapse$6
};

var intlMessages$4 = {
  'en-US': enUS$4,
  'fr-FR': frFR$4
};

const AppContainerContext = /*#__PURE__*/React__default.createContext(null);

const useIsLoaded = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    setIsLoaded(true);
  });
  return {
    isLoaded
  };
};

/**
 * Component style.
 */
const StyledAppBar = styled.header`
  align-content: center;
  align-items: center;
  background-color: var(--redsift-color-neutral-white);
  color: var(--redsift-color-neutral-darkgrey);
  display: flex;
  flex-direction: row;

  height: 92px;
  @media (max-width: 1080px) {
    height: 72px;
  }

  justify-content: space-between;
  left: 0;
  position: sticky;
  top: 0;
  z-index: var(--redsift-layout-z-index-header);

  ${_ref => {
  let {
    $hasBorder
  } = _ref;
  return $hasBorder ? css`
          border-bottom: 2px solid #e1e1e1;
        ` : css`
          border-bottom: none;
        `;
}}

  ${_ref2 => {
  let {
    $isSidePanelCollapsed
  } = _ref2;
  return !$isSidePanelCollapsed ? css`
          transform: translate(254px);
          width: calc(100% - 254px);
        ` : css`
          transform: translate(64px);
          width: calc(100% - 64px);
        `;
}}

  ${_ref3 => {
  let {
    $isLoaded
  } = _ref3;
  return $isLoaded ? css`
          transition: transform 0.25s ease-out, width 0.25s ease-out;
        ` : '';
}}

  h1 {
    margin: unset;
    padding: unset;
  }

  .redsift-icon-button {
    > span,
    > span > svg {
      width: 30px;
      height: 30px;
    }
  }

  .redsift-app-bar__left {
    align-content: center;
    align-items: center;
    display: inline-flex;
    flex-direction: row;
    gap: 8px;
    justify-content: center;
    margin-left: 19px;
  }

  .redsift-app-bar-left__expand-button,
  .redsift-app-bar-left__collapse-button {
    margin-right: 3px;
    padding: 2px;
    margin-left: -2px;
  }

  .redsift-app-bar__right {
    display: inline-flex;
    margin: 16px;
  }

  .redsift-breadcrumb-item,
  .redsift-breadcrumbs__separator {
    font-family: var(--redsift-typography-h1-font-family) !important;
    font-size: var(--redsift-typography-h1-font-size) !important;
    font-weight: var(--redsift-typography-body-font-weight) !important;
    line-height: var(--redsift-typography-h1-line-height) !important;
    color: var(--redsift-color-neutral-black) !important;
  }

  h1.redsift-breadcrumb-item {
    font-weight: var(--redsift-typography-h1-font-weight) !important;
  }
`;

/**
 * Component variant.
 */
const HeadingVariant = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  body: 'body'
};
const HeadingComponent = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  span: 'span'
};

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledHeading = styled.span`
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;

  ${baseStyling}

  ${_ref => {
  let {
    $variant
  } = _ref;
  return $variant === 'h1' ? css`
          color: var(--redsift-color-neutral-black);
          font-family: var(--redsift-typography-h1-font-family);
          font-size: var(--redsift-typography-h1-font-size);
          font-weight: var(--redsift-typography-h1-font-weight);
          line-height: var(--redsift-typography-h1-line-height);
        ` : $variant === 'h2' ? css`
          color: var(--redsift-color-neutral-black);
          font-family: var(--redsift-typography-h2-font-family);
          font-size: var(--redsift-typography-h2-font-size);
          font-weight: var(--redsift-typography-h2-font-weight);
          line-height: var(--redsift-typography-h2-line-height);
        ` : $variant === 'h3' ? css`
          color: var(--redsift-color-neutral-black);
          font-family: var(--redsift-typography-h3-font-family);
          font-size: var(--redsift-typography-h3-font-size);
          font-weight: var(--redsift-typography-h3-font-weight);
          line-height: var(--redsift-typography-h3-line-height);
        ` : $variant === 'h4' ? css`
          color: var(--redsift-color-neutral-black);
          font-family: var(--redsift-typography-h4-font-family);
          font-size: var(--redsift-typography-h4-font-size);
          font-weight: var(--redsift-typography-h4-font-weight);
          line-height: var(--redsift-typography-h4-line-height);
        ` : $variant === 'h5' ? css`
          color: var(--redsift-color-neutral-black);
          font-family: var(--redsift-typography-h5-font-family);
          font-size: var(--redsift-typography-h5-font-size);
          font-weight: var(--redsift-typography-h5-font-weight);
          line-height: var(--redsift-typography-h5-line-height);
        ` : css`
          color: var(--redsift-color-neutral-black);
          font-family: var(--redsift-typography-body-font-family);
          font-size: var(--redsift-typography-body-font-size);
          font-weight: var(--redsift-typography-body-font-weight);
          line-height: var(--redsift-typography-body-line-height);
        `;
}}

  ${_ref2 => {
  let {
    $noWrap
  } = _ref2;
  return $noWrap ? css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        ` : '';
}}
`;

const _excluded$I = ["as", "children", "className", "noWrap", "variant"];
const COMPONENT_NAME$I = 'Heading';
const CLASSNAME$I = 'redsift-heading';
const DEFAULT_PROPS$I = {};

/**
 * The Heading component.
 */
const Heading = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as,
      children,
      className,
      noWrap,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$I);
  return /*#__PURE__*/React__default.createElement(StyledHeading, _extends$1({
    as: as
  }, forwardedProps, {
    className: classNames(Heading.className, className),
    ref: ref,
    $noWrap: noWrap,
    $variant: variant ? variant : as === 'span' ? 'body' : as
  }), children);
});
Heading.className = CLASSNAME$I;
Heading.defaultProps = DEFAULT_PROPS$I;
Heading.displayName = COMPONENT_NAME$I;

/**
 * Component variant.
 */
const AppSidePanelVariant = {
  shrinked: 'shrinked',
  standard: 'standard'
};

/**
 * Component props.
 */

var expand$5 = "Expand left side panel";
var collapse$5 = "Collapse left side panel";
var enUS$3 = {
	expand: expand$5,
	collapse: collapse$5
};

var expand$4 = "Développer le panneau latéral gauche";
var collapse$4 = "Réduire le panneau latéral gauche";
var frFR$3 = {
	expand: expand$4,
	collapse: collapse$4
};

var intlMessages$3 = {
  'en-US': enUS$3,
  'fr-FR': frFR$3
};

/**
 * Component style.
 */
const StyledAppSidePanel = styled.div`
  align-items: center;
  background-color: var(--redsift-side-navigation-color-background);
  border-right: 4px solid #e1e1e1;
  box-sizing: border-box;
  flex-direction: column;
  height: 100%;
  left: 0px;
  overflow: hidden;
  position: fixed;
  top: 0;
  transition: width 300ms ease-out;
  width: ${_ref => {
  let {
    $variant
  } = _ref;
  return $variant === AppSidePanelVariant.shrinked ? '68px' : '254px';
}};
  z-index: var(--redsift-layout-z-index-side-panel);

  .redsift-app-side-panel__header {
    box-sizing: border-box;
    transition: padding 300ms ease-out;
    padding: 18px ${_ref2 => {
  let {
    $variant
  } = _ref2;
  return $variant === AppSidePanelVariant.shrinked ? '16px' : '24px';
}};
    margin-bottom: 16px;
    width: 100%;

    height: 92px;
    @media (max-width: 1080px) {
      height: 72px;
    }

    display: grid;
    justify-items: start;
    align-items: center;

    .redsift-icon-button {
      > span,
      > span > svg {
        width: 30px;
        height: 30px;
      }
    }
  }

  .redsift-app-side-panel-header__logo {
    align-items: center;
    display: flex;
    grid-column: 1;
    grid-row: 1;
    transition: opacity 300ms ease-out;
    opacity: ${_ref3 => {
  let {
    $variant
  } = _ref3;
  return $variant === AppSidePanelVariant.shrinked ? '0' : '1';
}};

    height: 56px;
    @media (max-width: 1080px) {
      height: 36px;
    }
  }

  .redsift-app-side-panel-header__logo > img {
    max-height: 56px;
    @media (max-width: 1080px) {
      max-height: 36px;
    }
    max-width: 195px;
  }

  .redsift-app-side-panel-header__expand-button {
    grid-column: 1;
    grid-row: 1;
    transition: opacity 300ms ease-out;
    opacity: ${_ref4 => {
  let {
    $variant
  } = _ref4;
  return $variant === AppSidePanelVariant.shrinked ? '1' : '0';
}};
    padding: 2px;
    margin-left: -1px;
  }

  .redsift-app-side-panel__featured {
    box-sizing: border-box;
    border-left: 4px solid #e1e1e1;
    border-right: unset;
    margin-bottom: 16px;
    padding: 0px 16px;
    width: 100%;
  }

  [dir='rtl'] & {
    left: unset;
    right: 0px;
    transform: unset;
    transition: unset;
  }

  scrollbar-color: var(--redsift-side-navigation-color-scrollbar-resting) transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    block-size: 9px;
    inline-size: 9px;
  }

  &::-webkit-scrollbar-thumb {
    background-clip: padding-box;
    background-color: var(--redsift-side-navigation-color-scrollbar-resting);
    border-radius: 0px;
    border-left: 2px solid transparent;
    border-right: 2px solid transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb:horizontal,
  &::-webkit-scrollbar-thumb:vertical {
    &:hover {
      background-color: var(--redsift-side-navigation-color-scrollbar-hover);
    }
  }

  &::-webkit-scrollbar-corner {
    visibility: hidden;
  }
`;

const _excluded$H = ["children", "className", "featuredElements", "logo", "variant", "onOpen", "iconButtonProps", "iconButtonRef"];
const COMPONENT_NAME$H = 'AppSidePanel';
const CLASSNAME$H = 'redsift-app-side-panel';
const DEFAULT_PROPS$H = {
  variant: AppSidePanelVariant.standard
};

/**
 * The AppSidePanel component.
 */
const AppSidePanel = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      featuredElements,
      logo,
      variant,
      onOpen,
      iconButtonProps,
      iconButtonRef
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$H);
  const {
    width
  } = useWindowSize();
  const appContainerState = useContext(AppContainerContext);
  const stringFormatter = useLocalizedStringFormatter(intlMessages$3);
  useEffect(() => {
    if (appContainerState && variant && appContainerState.sidePanelVariant !== variant) {
      appContainerState.setSidePanelVariant(variant);
    }
  }, [variant]);
  const handleClose = event => {
    onOpen === null || onOpen === void 0 ? void 0 : onOpen(event);
    appContainerState === null || appContainerState === void 0 ? void 0 : appContainerState.expandSidePanel();
  };
  const computedVariant = appContainerState ? appContainerState.sidePanelVariant : variant;
  const product = appContainerState && appContainerState.product;
  return /*#__PURE__*/React__default.createElement(StyledAppSidePanel, _extends$1({}, forwardedProps, {
    $variant: computedVariant,
    className: classNames(AppSidePanel.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${AppSidePanel.className}__header`
  }, /*#__PURE__*/React__default.createElement("div", {
    className: `${AppSidePanel.className}-header__logo`
  }, /*#__PURE__*/React__default.isValidElement(logo) ? logo : logo ? /*#__PURE__*/React__default.createElement("img", {
    src: logo.src,
    alt: logo.alt
  }) : product && (typeof product === 'string' || product.logo) ? /*#__PURE__*/React__default.createElement(Icon, {
    icon: typeof product === 'string' ? ProductLogo[product] : product.logo,
    size: {
      height: width && width > 1080 ? 41 : 36
    }
  }) : null), /*#__PURE__*/React__default.createElement(IconButton, _extends$1({
    className: `${AppSidePanel.className}-header__expand-button`
  }, iconButtonProps, {
    "aria-label": stringFormatter.format('expand'),
    icon: mdiMenu,
    onClick: handleClose,
    ref: iconButtonRef,
    color: "question",
    isDisabled: computedVariant === 'standard'
  }))), featuredElements ? /*#__PURE__*/React__default.createElement("div", {
    className: `${AppSidePanel.className}__featured`
  }, featuredElements) : null, children);
});
AppSidePanel.className = CLASSNAME$H;
AppSidePanel.defaultProps = DEFAULT_PROPS$H;
AppSidePanel.displayName = COMPONENT_NAME$H;

/**
 * Component variant.
 */
const SideNavigationMenuBarVariant = {
  shrinked: 'shrinked',
  standard: 'standard'
};

/**
 * Context props.
 */

/**
 * Component props.
 */

/**
 * Hook props.
 */

/**
 * Context props.
 */

/**
 * Reducer props.
 */

let SideNavigationMenuReducerActionType = /*#__PURE__*/function (SideNavigationMenuReducerActionType) {
  SideNavigationMenuReducerActionType["Expand"] = "expand";
  SideNavigationMenuReducerActionType["Collapse"] = "collapse";
  SideNavigationMenuReducerActionType["Move"] = "move";
  return SideNavigationMenuReducerActionType;
}({});

/**
 * Component props.
 */

const SideNavigationMenuBarContext = /*#__PURE__*/React__default.createContext(null);

const SideNavigationMenuContext = /*#__PURE__*/React__default.createContext(null);

const StyledSideNavigationMenuItemIndicatorContainer = styled.div`
  min-width: 4px;
`;
const StyledSideNavigationMenuItemIndicator = styled.div`
  position: relative;
  width: 4px;
  height: 38px;
  border-radius: 0px 4px 4px 0px;
  background-color: var(--redsift-color-neutral-black);
`;
const StyledSideNavigationMenuItemBorder = styled.div`
  position: relative;
  width: 100%;
  height: 1px;
  background-color: var(--redsift-color-neutral-lightgrey);
  margin-left: 62px;
  margin-top: -1px;
`;

/**
 * Component style.
 */
const StyledSideNavigationMenuItem = styled.a`
  /**
   * Common style
   */
  ${_ref => {
  let {
    $isDisabled
  } = _ref;
  return !$isDisabled ? css`
          & {
            color: var(--redsift-side-navigation-color-menu-item-text-resting);
          }
        ` : css`
          & {
            color: var(--redsift-side-navigation-color-menu-item-text-disabled);
          }
        `;
}}
  align-items: center;
  border-radius: 0 4px 4px 0;
  display: flex;
  font-family: var(--redsift-typography-body-font-family);
  font-size: var(--redsift-typography-body-font-size);
  font-weight: var(--redsift-typography-body-font-weight);
  gap: 8px;
  line-height: var(--redsift-typography-body-line-height);
  overflow: hidden;
  text-decoration: none;
  text-transform: uppercase;
  width: 100%;

  &:hover,
  &:focus-visible {
    outline: none;

    ${_ref2 => {
  let {
    $isDisabled
  } = _ref2;
  return !$isDisabled ? css`
            background-color: var(--redsift-side-navigation-color-menu-item-background-hover);
            & {
              color: var(--redsift-side-navigation-color-menu-item-text-hover);
            }
          ` : '';
}}
  }

  :active {
    ${_ref3 => {
  let {
    $isDisabled
  } = _ref3;
  return !$isDisabled ? css`
            background-color: var(--redsift-side-navigation-color-menu-item-background-active);
          ` : '';
}}
  }

  ${_ref4 => {
  let {
    $variant
  } = _ref4;
  return css`
    @media (prefers-reduced-motion: no-preference) {
      :focus-visible {
        > span${$variant === SideNavigationMenuBarVariant.shrinked ? '.first' : '.content'} {
          outline: 2px solid var(--redsift-color-ctas-primary-primary);
          transition: outline-offset 75ms ease-out;
        }

        :not(:active):focus-visible {
          > span${$variant === SideNavigationMenuBarVariant.shrinked ? '.first' : '.content'} {
            transition-duration: 0.25s;
          }
        }
      }

      :not(:active):focus-visible {
        > span${$variant === SideNavigationMenuBarVariant.shrinked ? '.first' : '.content'} {
          outline-offset: 0.25rem;
        }
      }
    }

    .content {
      transition: opacity 300ms ease-out;
      opacity: ${$variant === SideNavigationMenuBarVariant.shrinked ? '0' : '1'};
      overflow: hidden;
      white-space: nowrap;
    }
  `;
}}

  ${_ref5 => {
  let {
    $isSecondLevel,
    $variant
  } = _ref5;
  return !$isSecondLevel ? css`
          /**
           * First-level items
           */

          background-color: var(--redsift-side-navigation-color-background);
          height: 38px;
          margin-bottom: 16px;
          margin-left: -4px;
          transition: padding 300ms ease-out;
          padding: 0 16px 0 ${$variant === SideNavigationMenuBarVariant.shrinked ? '16px' : '24px'};

          .redsift-icon.first {
            box-sizing: unset;
          }
        ` : `
    /**
     * Second-level items
     */

    background-color: var(--redsift-side-navigation-color-menu-item-background-secondary);
    font-size: var(--redsift-typography-caption-font-size);
    padding-bottom: 7px;
    padding-left: 62px;
    padding-right: 8px;
    padding-top: 7px;
    border-radius: 0;


    .redsift-icon.first {
      box-sizing: unset;
      margin-right: 0;
    }
  `;
}}
`;

/**
 * Component variant.
 */
const BadgeVariant = {
  dot: 'dot',
  standard: 'standard'
};

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledBadge = styled.div`
  ${baseStyling}
  box-sizing: content-box;

  ${_ref => {
  let {
    $color,
    $isReversed
  } = _ref;
  if ($isReversed) {
    return css`
        background-color: var(--redsift-color-neutral-white);

        &,
        .redsift-icon {
          color: ${Object.keys(CtasColorPalette).includes($color) ? `var(--redsift-color-ctas-${$color}-primary);` : Object.keys(PresentationColorPalette).includes($color) ? `var(--redsift-color-presentation-${$color}-darker);` : Object.keys(NotificationsColorPalette).includes($color) ? `var(--redsift-color-notifications-${$color}-primary);` : css`var(--redsift-color-neutral-darkgrey);`};
        }
      `;
  } else {
    return css`
        background-color: ${Object.keys(CtasColorPalette).includes($color) ? `var(--redsift-color-ctas-${$color}-primary);` : Object.keys(PresentationColorPalette).includes($color) ? `var(--redsift-color-presentation-${$color}-darker);` : Object.keys(NotificationsColorPalette).includes($color) ? `var(--redsift-color-notifications-${$color}-primary);` : css`var(--redsift-color-neutral-darkgrey);`};
        &,
        .redsift-icon {
          color: var(--redsift-color-neutral-white);
        }
      `;
  }
}}

  ${_ref2 => {
  let {
    $variant,
    $autoBreak
  } = _ref2;
  switch ($variant) {
    case BadgeVariant.dot:
      return css`
          border-radius: 50%;
          height: 8px;
          margin-bottom: 12px;
          margin-right: 12px;
          width: 8px;
        `;
    case BadgeVariant.standard:
    default:
      return css`
          align-items: center;
          border-radius: 64px;
          display: inline-flex;
          gap: 4px;
          height: fit-content;
          justify-content: center;
          min-width: 8px;
          padding: 4px 6px;
          text-align: center;
          width: fit-content;
          word-break: ${$autoBreak ? 'break-word' : 'normal'};

          &,
          .redsift-icon,
          .redsift-number,
          .redsift-text {
            font-family: var(--redsift-typography-badge-font-family);
            font-size: var(--redsift-typography-badge-font-size);
            font-weight: var(--redsift-typography-badge-font-weight);
            line-height: var(--redsift-typography-badge-line-height);
          }

          .redsift-icon {
            width: unset;
            height: unset;

            > svg {
              width: 1em;
              height: 1em;
            }
          }
        `;
  }
}}
`;

const _excluded$G = ["autoBreak", "children", "className", "color", "isReversed", "variant"];
const COMPONENT_NAME$G = 'Badge';
const CLASSNAME$G = 'redsift-badge';
const DEFAULT_PROPS$G = {
  color: NotificationsColorPalette.question,
  variant: BadgeVariant.dot
};

/**
 * The Badge component.
 */
const Badge = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      autoBreak,
      children,
      className,
      color,
      isReversed,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$G);
  return /*#__PURE__*/React__default.createElement(StyledBadge, _extends$1({}, forwardedProps, {
    $autoBreak: autoBreak,
    $color: color,
    $isReversed: isReversed,
    $variant: variant,
    className: classNames(Badge.className, `${Badge.className}-${variant}`, className),
    ref: ref
  }), variant === BadgeVariant.standard ? children : null);
});
Badge.className = CLASSNAME$G;
Badge.defaultProps = DEFAULT_PROPS$G;
Badge.displayName = COMPONENT_NAME$G;

const _excluded$F = ["as", "badge", "badgeProps", "children", "className", "hasBorder", "href", "icon", "iconProps", "iconRef", "isCurrent", "isDisabled", "isSecondLevel", "onClick", "onKeyDown", "tabIndex"];
const COMPONENT_NAME$F = 'SideNavigationMenuItem';
const CLASSNAME$F = 'redsift-side-navigation-menu-item';
const DEFAULT_PROPS$F = {};

/**
 * The SideNavigationMenuItem component.
 */
const SideNavigationMenuItem = /*#__PURE__*/forwardRef((props, ref) => {
  const menuItemRef = ref || useRef();
  const {
      as,
      badge,
      badgeProps,
      children,
      className,
      hasBorder,
      href,
      icon,
      iconProps,
      iconRef = useRef(),
      isCurrent,
      isDisabled,
      isSecondLevel,
      onClick,
      onKeyDown,
      tabIndex
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$F);
  warnIfNoAccessibleLabelFound(props, [children]);
  const sideNavigationMenuBarContext = useContext(SideNavigationMenuBarContext);
  const sideNavigationMenuContext = useContext(SideNavigationMenuContext);
  const [isFirstChild, setIsFirstChild] = useState(false);
  const {
    menuItems
  } = sideNavigationMenuContext || sideNavigationMenuBarContext || {};
  useEffect(() => {
    if (!(sideNavigationMenuContext || sideNavigationMenuBarContext)) {
      setIsFirstChild(true);
      return;
    }
    const menuItemNode = menuItemRef.current;
    if (menuItemNode) {
      if (!menuItems.size) {
        setIsFirstChild(true);
      }
      menuItems.add(menuItemNode);
    }
    return () => {
      menuItems.delete(menuItemNode);
    };
  }, [menuItems]);
  const sideNavigationVariant = sideNavigationMenuBarContext === null || sideNavigationMenuBarContext === void 0 ? void 0 : sideNavigationMenuBarContext.sideNavigationMenuBarVariant;
  return /*#__PURE__*/React__default.createElement(Flexbox, {
    flexDirection: "column",
    gap: "0px"
  }, /*#__PURE__*/React__default.createElement(Flexbox, {
    flexDirection: "row",
    gap: "0px"
  }, !isSecondLevel ? /*#__PURE__*/React__default.createElement(StyledSideNavigationMenuItemIndicatorContainer, null, isCurrent ? /*#__PURE__*/React__default.createElement(StyledSideNavigationMenuItemIndicator, null) : null) : null, /*#__PURE__*/React__default.createElement(StyledSideNavigationMenuItem, _extends$1({
    as: as,
    role: "menuitem"
  }, forwardedProps, {
    $isCurrent: isCurrent,
    $isDisabled: isDisabled,
    $isSecondLevel: isSecondLevel,
    $variant: sideNavigationVariant,
    "aria-current": isCurrent ? 'page' : undefined,
    "aria-disabled": isDisabled,
    className: classNames(SideNavigationMenuItem.className, className),
    href: !isDisabled ? href : undefined,
    onClick: isDisabled ? undefined : onClick,
    onKeyDown: isDisabled ? undefined : onKeyDown,
    ref: menuItemRef,
    tabIndex: tabIndex !== undefined ? tabIndex : !sideNavigationMenuContext && isFirstChild ? 0 : -1
  }), !isSecondLevel || icon ? /*#__PURE__*/React__default.createElement(Icon, _extends$1({
    color: `var(--redsift-side-navigation-color-menu-item-text-${isSecondLevel || isDisabled ? 'disabled' : 'resting'})`
  }, iconProps, {
    icon: icon,
    ref: iconRef,
    className: "first",
    size: isSecondLevel ? IconSize.xsmall : IconSize.large
  })) : null, /*#__PURE__*/React__default.createElement("span", {
    className: "content"
  }, children), badge ? /*#__PURE__*/React__default.createElement(Badge, _extends$1({
    variant: BadgeVariant.standard,
    color: "error"
  }, badgeProps), badge) : null)), isSecondLevel && hasBorder ? /*#__PURE__*/React__default.createElement(StyledSideNavigationMenuItemBorder, null) : null);
});
SideNavigationMenuItem.className = CLASSNAME$F;
SideNavigationMenuItem.defaultProps = DEFAULT_PROPS$F;
SideNavigationMenuItem.displayName = COMPONENT_NAME$F;

/**
 * Component style.
 */
const StyledSideNavigationMenu = styled.div`
  .redsift-side-navigation-menu__current-indicator-container {
    min-width: 4px;
  }

  .redsift-side-navigation-menu__menu-container {
    width: 100%;
  }

  ${_ref => {
  let {
    $isDisabled,
    $variant
  } = _ref;
  return css`
    .redsift-side-navigation-menu__menu-container > button {
      ${!$isDisabled ? css`
            & {
              color: var(--redsift-side-navigation-color-menu-item-text-resting);
            }
          ` : css`
            & {
              color: var(--redsift-side-navigation-color-menu-item-text-disabled);
            }
          `}
      align-items: center;
      background-color: var(--redsift-side-navigation-color-background);
      border: none;
      border-radius: 0 4px 4px 0;
      display: flex;
      font-family: var(--redsift-typography-body-font-family);
      font-size: var(--redsift-typography-body-font-size);
      font-weight: var(--redsift-typography-body-font-weight);
      gap: 0;
      height: 38px;
      line-height: var(--redsift-typography-body-line-height);
      margin-left: -4px;
      transition: padding 300ms ease-out;
      padding: 0 16px 0 ${$variant === SideNavigationMenuBarVariant.shrinked ? '16px' : '24px'};
      text-decoration: none;
      text-transform: uppercase;
      width: calc(100% + ${$variant === SideNavigationMenuBarVariant.shrinked ? '0px' : '4px'});

      :hover,
      :focus-visible {
        outline: none;

        ${!$isDisabled ? css`
              background-color: var(--redsift-side-navigation-color-menu-item-background-hover);
              &,
              .redsift-icon.first,
              .redsift-side-navigation-menu__expand-icon {
                color: var(--redsift-side-navigation-color-menu-item-text-hover);
              }
              cursor: pointer;
            ` : ''}
      }

      :active {
        ${!$isDisabled ? css`
              background-color: var(--redsift-side-navigation-color-menu-item-background-active);
            ` : ''}
      }

      @media (prefers-reduced-motion: no-preference) {
        :focus-visible {
          > span${$variant === SideNavigationMenuBarVariant.shrinked ? '.first' : '.content'} {
            outline: 2px solid var(--redsift-color-ctas-primary-primary);
            transition: outline-offset 75ms ease-out;
          }

          :not(:active):focus-visible {
            > span${$variant === SideNavigationMenuBarVariant.shrinked ? '.first' : '.content'} {
              transition-duration: 0.25s;
            }
          }
        }

        :not(:active):focus-visible {
          > span${$variant === SideNavigationMenuBarVariant.shrinked ? '.first' : '.content'} {
            outline-offset: 0.25rem;
          }
        }
      }
    }

    .content {
      transition: opacity 300ms ease-out;
      opacity: ${$variant === SideNavigationMenuBarVariant.shrinked ? '0' : '1'};
      overflow: hidden;
      white-space: nowrap;
    }
  `;
}}

  .first {
    box-sizing: unset;
    margin-right: 8px;
  }

  .redsift-side-navigation-menu__expand-icon {
    transition: transform 300ms ease-out;
    ${_ref2 => {
  let {
    $isDisabled
  } = _ref2;
  return !$isDisabled ? css`
            &,
            .redsift-icon.first {
              color: var(--redsift-side-navigation-color-menu-item-text-resting);
            }
          ` : css`
            &,
            .redsift-icon.first {
              color: var(--redsift-side-navigation-color-menu-item-text-disabled);
            }
          `;
}}
    ${_ref3 => {
  let {
    $isExpanded
  } = _ref3;
  return !$isExpanded ? css`
            transform: rotate(0);
          ` : css`
            transform: rotate(-180deg);
          `;
}}
    margin-left: auto;

    transition: opacity 300ms ease-out;
    opacity: ${_ref4 => {
  let {
    $variant
  } = _ref4;
  return $variant === SideNavigationMenuBarVariant.standard ? '1' : '0';
}};
  }

  [dir='rtl'] & {
    .redsift-side-navigation-menu__expand-icon {
      margin-left: unset;
      margin-right: auto;
    }
  }

  /**
   * Display children or not
   */

  .redsift-side-navigation-menu__menu-container > ul {
    list-style: none;
    margin: unset;
    padding: unset;
    margin-left: -4px;

    ${_ref5 => {
  let {
    $variant
  } = _ref5;
  return css`
      width: calc(100% + ${$variant === SideNavigationMenuBarVariant.shrinked ? '0px' : '4px'});
    `;
}}
    margin-bottom: 16px;
    max-width: 241px;
    transition: height 300ms ease-out;
    overflow: hidden;
    ${_ref6 => {
  let {
    $isExpanded,
    $numberOfChildren
  } = _ref6;
  return $isExpanded ? css`
            height: ${$numberOfChildren * 38}px;
          ` : css`
            height: 0;
          `;
}};
  }
`;

const SideNavigationMenuReducer = (state, action) => {
  switch (action.type) {
    case SideNavigationMenuReducerActionType.Expand:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        isExpanded: true
      });
    case SideNavigationMenuReducerActionType.Collapse:
      return {
        isExpanded: false,
        currentIndex: null,
        previousIndex: null
      };
    case SideNavigationMenuReducerActionType.Move:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        isExpanded: true,
        currentIndex: action.index,
        previousIndex: state.currentIndex
      });
  }
};

/**
 * Component style.
 */
const StyledAppContainer = styled.div`
  box-sizing: content-box;
`;

const _excluded$E = ["children", "className", "locale", "product"];
const COMPONENT_NAME$E = 'AppContainer';
const CLASSNAME$E = 'redsift-app-container';
const DEFAULT_PROPS$E = {};

/**
 * The AppContainer component.
 */
const AppContainer = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      locale,
      product
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$E);
  const [sidePanelVariant, setSidePanelVariant] = useState(AppSidePanelVariant.standard);
  const [breadcrumbs, setBreadcrumbs] = useState();
  const state = {
    collapseSidePanel() {
      setSidePanelVariant(AppSidePanelVariant.shrinked);
    },
    expandSidePanel() {
      setSidePanelVariant(AppSidePanelVariant.standard);
    },
    setBreadcrumbs(breadcrumbs) {
      setBreadcrumbs(breadcrumbs);
    },
    product,
    breadcrumbs,
    sidePanelVariant,
    setSidePanelVariant
  };
  return /*#__PURE__*/React__default.createElement(StyledAppContainer, _extends$1({}, forwardedProps, {
    id: "redsift-app-container",
    $product: product,
    className: classNames(AppContainer.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement(AppContainerContext.Provider, {
    value: state
  }, /*#__PURE__*/React__default.createElement(I18nProvider, {
    locale: locale
  }, children)));
});
AppContainer.className = CLASSNAME$E;
AppContainer.defaultProps = DEFAULT_PROPS$E;
AppContainer.displayName = COMPONENT_NAME$E;

const _excluded$D = ["aria-label", "buttonProps", "buttonRef", "children", "className", "hasBadge", "icon", "iconProps", "iconRef", "id", "isDisabled", "isExpanded", "menuProps", "menuRef", "tabIndex"];
const COMPONENT_NAME$D = 'SideNavigationMenu';
const CLASSNAME$D = 'redsift-side-navigation-menu';
const DEFAULT_PROPS$D = {};

/**
 * The SideNavigationMenu component.
 */
const SideNavigationMenu = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-label': ariaLabel,
      buttonProps = {},
      buttonRef = useRef(),
      children,
      className,
      hasBadge,
      icon,
      iconProps,
      iconRef = useRef(),
      id: propsId,
      isDisabled,
      isExpanded: propsIsExpanded,
      menuProps = {},
      menuRef = useRef(),
      tabIndex
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$D);
  const [_id] = useId$1();
  const id = propsId !== null && propsId !== void 0 ? propsId : _id;
  warnIfNoAccessibleLabelFound(props);
  const appContainerState = useContext(AppContainerContext);
  const sideNavigationMenuBarContext = useContext(SideNavigationMenuBarContext);
  const [isFirstChild, setIsFirstChild] = useState(false);
  const currentPosition = useRef(-1);
  const {
    menuItems: menubarItems
  } = sideNavigationMenuBarContext || {};
  useEffect(() => {
    currentPosition.current = -1;
  });

  /**
   * Create an array containing only valid children which can only be SideNavigationMenuItem.
   * This prevent the navigation menubar to have more than two levels.
   */
  const childArray = filterComponents(SideNavigationMenuItem)(children);
  const renderedMenuItems = childArray.map((child, index) => {
    var _child$key, _child$key2;
    if (child.props.isCurrent) {
      currentPosition.current = index;
    }
    return /*#__PURE__*/React__default.createElement("li", {
      key: (_child$key = child.key) !== null && _child$key !== void 0 ? _child$key : index,
      role: "none"
    }, /*#__PURE__*/React__default.cloneElement(child, {
      isDisabled: child.props.isDisabled || isDisabled || undefined,
      isSecondLevel: true,
      key: (_child$key2 = child.key) !== null && _child$key2 !== void 0 ? _child$key2 : index
    }));
  });
  const menuItems = useRef(new Set()).current;
  const [state, dispatch] = useReducer(SideNavigationMenuReducer, {
    currentIndex: null,
    isExpanded: Boolean(propsIsExpanded),
    previousIndex: null
  });
  const {
    isExpanded,
    currentIndex,
    previousIndex
  } = state;
  const first = useCallback(() => dispatch({
    type: SideNavigationMenuReducerActionType.Move,
    index: 0
  }), []);
  const last = useCallback(() => dispatch({
    type: SideNavigationMenuReducerActionType.Move,
    index: menuItems.size - 1
  }), [menuItems.size]);
  const move = useCallback(index => dispatch({
    type: SideNavigationMenuReducerActionType.Move,
    index
  }), []);
  const open = useCallback(() => {
    var _sideNavigationMenuBa;
    sideNavigationMenuBarContext === null || sideNavigationMenuBarContext === void 0 ? void 0 : (_sideNavigationMenuBa = sideNavigationMenuBarContext.setCurrentOpenMenuId) === null || _sideNavigationMenuBa === void 0 ? void 0 : _sideNavigationMenuBa.call(sideNavigationMenuBarContext, id);
    dispatch({
      type: SideNavigationMenuReducerActionType.Expand
    });
    appContainerState === null || appContainerState === void 0 ? void 0 : appContainerState.expandSidePanel();
  }, []);
  const close = useCallback(focusButton => {
    if (isExpanded) {
      if (focusButton) {
        buttonRef.current.focus();
      }
      dispatch({
        type: SideNavigationMenuReducerActionType.Collapse
      });
    }
  }, [isExpanded]);
  useEffect(() => {
    if (sideNavigationMenuBarContext !== null && sideNavigationMenuBarContext !== void 0 && sideNavigationMenuBarContext.currentOpenMenuId && sideNavigationMenuBarContext.currentOpenMenuId !== id) {
      close();
    }
  }, [sideNavigationMenuBarContext === null || sideNavigationMenuBarContext === void 0 ? void 0 : sideNavigationMenuBarContext.currentOpenMenuId]);
  const previous = () => {
    const index = currentIndex === 0 ? menuItems.size - 1 : currentIndex - 1;
    move(index);
  };
  const next = () => {
    const index = currentIndex === menuItems.size - 1 ? 0 : currentIndex + 1;
    move(index);
  };
  const match = e => {
    const items = Array.from(menuItems);

    // Reorder the array, starting with the currentNode
    const reorderedItems = [...items.slice(currentIndex), ...items.slice(0, currentIndex)];

    // Find all nodes that begin with the pressed letter
    const matches = reorderedItems.filter(menuItem => {
      const {
        textContent
      } = menuItem;
      // istanbul ignore next
      const firstLetter = textContent === null || textContent === void 0 ? void 0 : textContent.toLowerCase().charAt(0);
      return e.key === firstLetter;
    });

    // Exit early if there are no matches
    if (!matches.length) {
      return;
    }

    // If the focused item is a match, focus the next match.
    // Otherwise, focus the first match
    const currentNode = items[currentIndex];
    const nextMatch = matches.includes(currentNode) ? matches[1] : matches[0];
    const index = items.findIndex(item => {
      return item === nextMatch;
    });
    move(index);
  };
  useEffect(() => {
    if (appContainerState && (appContainerState === null || appContainerState === void 0 ? void 0 : appContainerState.sidePanelVariant) === AppSidePanelVariant.shrinked && isExpanded) {
      close();
    }
  }, [appContainerState === null || appContainerState === void 0 ? void 0 : appContainerState.sidePanelVariant]);
  const value = useMemo(() => ({
    menuItems
  }), [menuItems]);
  useEffect(() => {
    const items = Array.from(menuItems);
    if (currentIndex !== previousIndex) {
      const currentNode = items[currentIndex];
      currentNode === null || currentNode === void 0 ? void 0 : currentNode.focus();
    }
  }, [menuItems, currentIndex, previousIndex]);
  useEffect(() => {
    if (!sideNavigationMenuBarContext) {
      setIsFirstChild(true);
      return;
    }
    const menuItemNode = buttonRef.current;
    if (menuItemNode) {
      menubarItems.add(menuItemNode);
    }
    return () => {
      menubarItems.delete(menuItemNode);
    };
  }, [menubarItems]);
  const keyDownOnButton = e => {
    if (isDisabled) {
      return;
    }
    const currentNode = e.target;
    switch (e.code) {
      case 'Enter':
      case 'Space':
        e.stopPropagation();
        currentNode.click();
        first();
        break;
      case 'ArrowRight':
        e.stopPropagation();
        first();
        break;
      case 'ArrowLeft':
        last();
        break;
    }
  };
  const buttonEventHandler = {
    onClick: event => {
      var _buttonProps$onClick;
      if (isDisabled) {
        return;
      }
      (_buttonProps$onClick = buttonProps.onClick) === null || _buttonProps$onClick === void 0 ? void 0 : _buttonProps$onClick.call(buttonProps, event);
      event.stopPropagation();
      if (isExpanded) {
        close();
      } else {
        open();
      }
    },
    onKeyDown: event => {
      var _buttonProps$onKeyDow;
      (_buttonProps$onKeyDow = buttonProps.onKeyDown) === null || _buttonProps$onKeyDow === void 0 ? void 0 : _buttonProps$onKeyDow.call(buttonProps, event);
      keyDownOnButton(event);
    }
  };
  const keyDownOnMenuItemList = e => {
    const currentNode = e.target;
    switch (e.code) {
      case 'ArrowUp':
        e.stopPropagation();
        e.preventDefault();
        previous();
        break;
      case 'ArrowDown':
        e.stopPropagation();
        e.preventDefault();
        next();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        e.stopPropagation();
        close(true);
        break;
      case 'ArrowRight':
        e.preventDefault();
        close(!sideNavigationMenuBarContext);
        break;
      case 'Home':
        e.stopPropagation();
        e.preventDefault();
        first();
        break;
      case 'End':
        e.stopPropagation();
        e.preventDefault();
        last();
        break;
      case 'Enter':
      case 'Space':
        e.preventDefault();
        currentNode.click();
        break;
      case 'Escape':
        e.stopPropagation();
        e.preventDefault();
        close(true);
        break;
      default:
        e.stopPropagation();
        match(e);
        break;
    }
  };
  const menuItemListEventHandler = {
    onKeyDown: event => {
      var _menuProps$onKeyDown;
      (_menuProps$onKeyDown = menuProps.onKeyDown) === null || _menuProps$onKeyDown === void 0 ? void 0 : _menuProps$onKeyDown.call(menuProps, event);
      keyDownOnMenuItemList(event);
    },
    onClick: event => {
      var _menuProps$onClick;
      if (isDisabled) {
        return;
      }
      (_menuProps$onClick = menuProps.onClick) === null || _menuProps$onClick === void 0 ? void 0 : _menuProps$onClick.call(menuProps, event);
    }
  };
  const sideNavigationVariant = sideNavigationMenuBarContext === null || sideNavigationMenuBarContext === void 0 ? void 0 : sideNavigationMenuBarContext.sideNavigationMenuBarVariant;
  return /*#__PURE__*/React__default.createElement(StyledSideNavigationMenu, _extends$1({}, forwardedProps, {
    $isDisabled: isDisabled,
    $isExpanded: isExpanded,
    $variant: sideNavigationVariant,
    $numberOfChildren: renderedMenuItems.length,
    className: classNames(SideNavigationMenu.className, className),
    id: id,
    ref: ref,
    tabIndex: undefined
  }), /*#__PURE__*/React__default.createElement(SideNavigationMenuContext.Provider, {
    value: value
  }, /*#__PURE__*/React__default.createElement(Flexbox, {
    flexDirection: "row",
    gap: "0px"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: `${SideNavigationMenu.className}__current-indicator-container`
  }, currentPosition.current !== -1 ? /*#__PURE__*/React__default.createElement(StyledSideNavigationMenuItemIndicator, {
    style: {
      position: 'relative',
      top: isExpanded ? `${38 + currentPosition.current * 38}px` : 0,
      transition: 'top 300ms ease-out'
    }
  }) : null), /*#__PURE__*/React__default.createElement("div", {
    className: `${SideNavigationMenu.className}__menu-container`
  }, /*#__PURE__*/React__default.createElement("button", _extends$1({}, buttonProps, {
    "aria-disabled": isDisabled,
    "aria-expanded": isExpanded ? 'true' : 'false',
    "aria-haspopup": "true",
    ref: buttonRef,
    role: "menuitem",
    tabIndex: tabIndex !== undefined ? tabIndex : isFirstChild ? 0 : -1,
    type: "button"
  }, buttonEventHandler), /*#__PURE__*/React__default.createElement(Icon, _extends$1({
    color: `var(--redsift-side-navigation-color-menu-item-text-${isDisabled ? 'disabled' : 'resting'})`
  }, iconProps, {
    icon: icon,
    ref: iconRef,
    className: classNames(iconProps === null || iconProps === void 0 ? void 0 : iconProps.className, 'first'),
    size: IconSize.large
  })), /*#__PURE__*/React__default.createElement("span", {
    className: "content"
  }, ariaLabel), hasBadge && !isExpanded ? /*#__PURE__*/React__default.createElement(Badge, {
    color: "error"
  }) : null, /*#__PURE__*/React__default.createElement(Icon, {
    icon: mdiChevronDown,
    className: `${SideNavigationMenu.className}__expand-icon`,
    size: IconSize.medium
  })), /*#__PURE__*/React__default.createElement("ul", _extends$1({}, menuProps, {
    "aria-label": ariaLabel,
    "aria-orientation": "vertical",
    ref: menuRef,
    role: "menu"
  }, menuItemListEventHandler), renderedMenuItems)))));
});
SideNavigationMenu.className = CLASSNAME$D;
SideNavigationMenu.defaultProps = DEFAULT_PROPS$D;
SideNavigationMenu.displayName = COMPONENT_NAME$D;

/**
 * Component style.
 */
const StyledSideNavigationMenuBar = styled.nav`
  background-color: var(--redsift-side-navigation-color-background);
  box-sizing: content-box;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  transition: width 300ms ease-out;
  width: ${_ref => {
  let {
    $variant
  } = _ref;
  return $variant === SideNavigationMenuBarVariant.shrinked ? '64px' : '250px';
}};

  ${_ref2 => {
  let {
    $marginTop = 0
  } = _ref2;
  return $marginTop ? css`
          height: calc(100% - ${$marginTop}px);
        ` : '';
}}

  ul {
    list-style: none;
    margin: unset;
    padding: unset;
  }

  scrollbar-color: var(--redsift-side-navigation-color-scrollbar-resting) transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    block-size: 9px;
    inline-size: 9px;
  }

  &::-webkit-scrollbar-thumb {
    background-clip: padding-box;
    background-color: var(--redsift-side-navigation-color-scrollbar-resting);
    border-radius: 0px;
    border-left: 2px solid transparent;
    border-right: 2px solid transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb:horizontal,
  &::-webkit-scrollbar-thumb:vertical {
    &:hover {
      background-color: var(--redsift-side-navigation-color-scrollbar-hover);
    }
  }

  &::-webkit-scrollbar-corner {
    visibility: hidden;
  }
`;

const _excluded$C = ["aria-label", "aria-labelledby", "canHaveMultipleMenuOpenAtOnce", "children", "className", "isDisabled", "menubarProps", "menubarRef", "variant"];
const COMPONENT_NAME$C = 'SideNavigationMenuBar';
const CLASSNAME$C = 'redsift-side-navigation-menu-bar';
const DEFAULT_PROPS$C = {
  variant: SideNavigationMenuBarVariant.standard
};

/**
 * Hook to store the previous index for further use
 */
const usePrevious = value => {
  const ref = useRef(0);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

/**
 * The SideNavigationMenuBar component.
 */
const SideNavigationMenuBar = /*#__PURE__*/forwardRef((props, ref) => {
  const navRef = ref || useRef();
  const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      canHaveMultipleMenuOpenAtOnce,
      children,
      className,
      isDisabled,
      menubarProps = {},
      menubarRef = useRef(),
      variant: propsVariant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$C);
  warnIfNoAccessibleLabelFound(props);
  const appContainerState = useContext(AppContainerContext);
  const {
    top
  } = useBoundingClientRect(navRef, [children]);
  const [variant, setVariant] = useState(appContainerState ? appContainerState.sidePanelVariant : propsVariant);
  const [currentOpenMenuId, setCurrentOpenMenuId] = useState();
  useEffect(() => {
    setVariant(propsVariant);
  }, [propsVariant]);
  useEffect(() => {
    if (appContainerState && appContainerState.sidePanelVariant && appContainerState.sidePanelVariant !== variant) {
      setVariant(appContainerState.sidePanelVariant);
    }
  }, [appContainerState === null || appContainerState === void 0 ? void 0 : appContainerState.sidePanelVariant]);

  /**
   * Create an array containing only valid children being either SideNavigationMenu or SideNavigationMenuItem.
   */
  const childArray = filterComponents([SideNavigationMenu, SideNavigationMenuItem])(children);
  const navItems = childArray.map((child, index) => {
    var _child$key, _child$key2;
    return /*#__PURE__*/React__default.createElement("li", {
      key: (_child$key = child.key) !== null && _child$key !== void 0 ? _child$key : index,
      role: "none"
    }, /*#__PURE__*/React__default.cloneElement(child, {
      isDisabled: child.props.isDisabled || isDisabled || undefined,
      key: (_child$key2 = child.key) !== null && _child$key2 !== void 0 ? _child$key2 : index,
      tabIndex: index === 0 ? 0 : -1
    }));
  });
  const menuItems = useRef(new Set()).current;
  const [currentIndex, setCurrentIndex] = useState(0);
  const previousIndex = usePrevious(currentIndex);
  const first = () => setCurrentIndex(0);
  const last = () => setCurrentIndex(menuItems.size - 1);
  const next = () => {
    const index = currentIndex === menuItems.size - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(index);
  };
  const previous = () => {
    const index = currentIndex === 0 ? menuItems.size - 1 : currentIndex - 1;
    setCurrentIndex(index);
  };
  const match = event => {
    const items = Array.from(menuItems);

    // Reorder the array, starting with the currentNode
    const reorderedItems = [...items.slice(currentIndex), ...items.slice(0, currentIndex)];

    // Find all nodes that begin with the pressed letter
    const matches = reorderedItems.filter(menuItem => {
      const {
        textContent
      } = menuItem;
      // istanbul ignore next
      const firstLetter = textContent === null || textContent === void 0 ? void 0 : textContent.toLowerCase().charAt(0);
      return event.key === firstLetter;
    });

    // Exit early if there are no matches
    if (!matches.length) {
      return;
    }

    // If the focused item is a match, focus the next match.
    // Otherwise, focus the first match
    const currentNode = items[currentIndex];
    const nextMatch = matches.includes(currentNode) ? matches[1] : matches[0];
    const index = items.findIndex(item => {
      return item === nextMatch;
    });
    setCurrentIndex(index);
  };
  const keyDown = e => {
    var _menubarProps$onKeyDo, _currentNode$parentNo;
    (_menubarProps$onKeyDo = menubarProps.onKeyDown) === null || _menubarProps$onKeyDo === void 0 ? void 0 : _menubarProps$onKeyDo.call(menubarProps, e);
    e.stopPropagation();
    const currentNode = e.target;
    // istanbul ignore next
    const parentMenu = currentNode === null || currentNode === void 0 ? void 0 : (_currentNode$parentNo = currentNode.parentNode) === null || _currentNode$parentNo === void 0 ? void 0 : _currentNode$parentNo.parentNode;
    const isFromSubmenu = parentMenu.getAttribute('role') === 'menu';
    switch (e.code) {
      case 'ArrowUp':
        e.preventDefault();
        previous();
        break;
      case 'ArrowDown':
        e.preventDefault();
        next();
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (isFromSubmenu) {
          next();
        }
        break;
      case 'End':
        e.preventDefault();
        last();
        break;
      case 'Home':
        e.preventDefault();
        first();
        break;
      case 'Enter':
      case 'Space':
        e.preventDefault();
        currentNode.click();
        break;
      default:
        match(e);
        break;
    }
  };
  const value = useMemo(() => ({
    menuItems,
    sideNavigationMenuBarVariant: variant,
    setSideNavigationMenuBarVariant: setVariant,
    currentOpenMenuId,
    setCurrentOpenMenuId: value => {
      if (!canHaveMultipleMenuOpenAtOnce) {
        setCurrentOpenMenuId(value);
      }
    }
  }), [menuItems, variant, setVariant, currentOpenMenuId, setCurrentOpenMenuId]);
  useEffect(() => {
    if (currentIndex !== previousIndex) {
      const items = Array.from(menuItems);
      const currentNode = items[currentIndex];
      const previousNode = items[previousIndex];

      // istanbul ignore next
      previousNode === null || previousNode === void 0 ? void 0 : previousNode.setAttribute('tabindex', '-1');
      currentNode === null || currentNode === void 0 ? void 0 : currentNode.setAttribute('tabindex', '0');
      currentNode === null || currentNode === void 0 ? void 0 : currentNode.focus();
    }
  }, [currentIndex, previousIndex, menuItems]);
  return /*#__PURE__*/React__default.createElement(StyledSideNavigationMenuBar, _extends$1({}, forwardedProps, {
    $isDisabled: isDisabled,
    $marginTop: top,
    $variant: variant,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className: classNames(SideNavigationMenuBar.className, className),
    ref: navRef
  }), /*#__PURE__*/React__default.createElement(SideNavigationMenuBarContext.Provider, {
    value: value
  }, /*#__PURE__*/React__default.createElement("ul", _extends$1({}, menubarProps, {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-orientation": "vertical",
    onKeyDown: keyDown,
    ref: menubarRef,
    role: "menubar"
  }), navItems)));
});
SideNavigationMenuBar.className = CLASSNAME$C;
SideNavigationMenuBar.defaultProps = DEFAULT_PROPS$C;
SideNavigationMenuBar.displayName = COMPONENT_NAME$C;

const _excluded$B = ["title", "href", "ref"],
  _excluded2$1 = ["title", "children", "ref"],
  _excluded3 = ["title", "href", "ref"];
const isMenu = item => {
  return item.children !== undefined;
};
const useSideNavigationMenuBar = _ref => {
  let {
    items,
    isActive
  } = _ref;
  const menuBarChildren = [];
  items.map(item => {
    if (!isMenu(item)) {
      const {
          title,
          href,
          ref
        } = item,
        rest = _objectWithoutProperties(item, _excluded$B);
      menuBarChildren.push( /*#__PURE__*/React__default.createElement(SideNavigationMenuItem, _extends$1({
        key: title,
        isCurrent: isActive ? isActive(href) : undefined
      }, rest, {
        href: href,
        ref: ref
      }), title));
    } else {
      const {
          title,
          children,
          ref
        } = item,
        rest = _objectWithoutProperties(item, _excluded2$1);
      menuBarChildren.push( /*#__PURE__*/React__default.createElement(SideNavigationMenu, _extends$1({
        key: title,
        "aria-label": title,
        isExpanded: isActive ? children.filter(child => isActive(child.href)).length > 0 : undefined,
        hasBadge: children.filter(child => child.badge).length > 0
      }, rest, {
        ref: ref
      }), children.map(child => {
        const {
            title,
            href,
            ref
          } = child,
          rest = _objectWithoutProperties(child, _excluded3);
        return /*#__PURE__*/React__default.createElement(SideNavigationMenuItem, _extends$1({
          key: title,
          href: href,
          isCurrent: isActive ? isActive(href) : undefined
        }, rest, {
          ref: ref
        }), title);
      })));
    }
  });
  return {
    children: menuBarChildren
  };
};

const useAppSidePanel = _ref => {
  let {
    items,
    isActive
  } = _ref;
  const sideNavigationMenuBarProps = useSideNavigationMenuBar({
    items,
    isActive
  });
  return {
    children: /*#__PURE__*/React__default.createElement(SideNavigationMenuBar, _extends$1({}, sideNavigationMenuBarProps, {
      "aria-label": "Navigation"
    }))
  };
};

/**
 * Component style.
 */
const StyledBreadcrumbItem = styled.a`
  background: none;
  border: none;
  cursor: unset;
  font-family: var(--redsift-typography-body-font-family);
  font-size: var(--redsift-typography-body-font-size);
  font-weight: var(--redsift-typography-body-font-weight);
  line-height: var(--redsift-typography-body-line-height);
  padding: 4px;
  text-decoration: none;

  ${_ref => {
  let {
    $isDisabled,
    $isCurrent
  } = _ref;
  return css`
    ${$isDisabled ? `
        color: var(--redsift-color-neutral-midgrey);
      ` : $isCurrent ? `
        color: var(--redsift-color-default-primary);
      ` : `
        color: var(--redsift-color-neutral-darkgrey);
      `}

    ${!$isDisabled && !$isCurrent && css`
      &:hover,
      &:focus-visible {
        // cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    `}
  `;
}}

  &:focus-visible {
    outline: 2px solid var(--redsift-color-neutral-darkgrey);
  }
`;

const _excluded$A = ["as", "children", "className", "href", "isCurrent", "isDisabled"];
const COMPONENT_NAME$B = 'BreadcrumbItem';
const CLASSNAME$B = 'redsift-breadcrumb-item';
const DEFAULT_PROPS$B = {};

/**
 * The BreadcrumbItem components is a list of links to the parent pages of the current page in hierarchical order.
 */
const BreadcrumbItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as,
      children,
      className,
      href,
      isCurrent,
      isDisabled
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$A);
  warnIfNoAccessibleLabelFound(props, [children]);
  return /*#__PURE__*/React__default.createElement(StyledBreadcrumbItem, _extends$1({
    as: as || 'a',
    role: "link",
    tabIndex: !isDisabled && !isCurrent ? 0 : undefined
  }, forwardedProps, {
    $isCurrent: isCurrent,
    $isDisabled: isDisabled,
    "aria-current": isCurrent ? 'page' : undefined,
    "aria-disabled": isDisabled || isCurrent,
    className: classNames(BreadcrumbItem.className, className),
    href: !isDisabled && !isCurrent ? href : undefined,
    ref: ref
  }), children);
});
BreadcrumbItem.className = CLASSNAME$B;
BreadcrumbItem.defaultProps = DEFAULT_PROPS$B;
BreadcrumbItem.displayName = COMPONENT_NAME$B;

/**
 * Component style.
 */
const StyledBreadcrumbs = styled.nav`
  ${baseStyling}

  ol {
    display: inline-flex;
    list-style: none;
    padding: unset;
  }

  .redsift-breadcrumbs__separator {
    font-family: var(--redsift-typography-body-font-family);
    font-size: var(--redsift-typography-body-font-size);
    font-weight: var(--redsift-typography-body-font-weight);
    line-height: var(--redsift-typography-body-line-height);
    margin: 0px 4px;

    ${_ref => {
  let {
    $isDisabled
  } = _ref;
  return $isDisabled ? css`
            color: var(--redsift-color-neutral-midgrey);
          ` : css`
            color: var(--redsift-color-neutral-black);
          `;
}}
  }
`;

const _excluded$z = ["children", "className", "isDisabled"];
const COMPONENT_NAME$A = 'Breadcrumbs';
const CLASSNAME$A = 'redsift-breadcrumbs';
const DEFAULT_PROPS$A = {};

/**
 * The Breadcrumbs components is a list of links to the parent pages of the current page in hierarchical order.
 */
const BaseBreadcrumbs = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      isDisabled
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$z);
  warnIfNoAccessibleLabelFound(props);
  const childArray = filterComponents([Breadcrumbs.Item])(children);
  const breadcrumbItems = childArray.map((child, index) => {
    var _child$key;
    const isCurrent = index === childArray.length - 1;
    return /*#__PURE__*/React__default.createElement("li", {
      key: index
    }, /*#__PURE__*/React__default.createElement(Breadcrumbs.Item, _extends$1({
      isDisabled: child.props.isDisabled || isDisabled || undefined
    }, child.props, {
      key: (_child$key = child.key) !== null && _child$key !== void 0 ? _child$key : index,
      isCurrent: isCurrent
    })), isCurrent === false ? /*#__PURE__*/React__default.createElement("span", {
      className: `${BaseBreadcrumbs.className}__separator`
    }, '/') : null);
  });
  return /*#__PURE__*/React__default.createElement(StyledBreadcrumbs, _extends$1({}, forwardedProps, {
    $isDisabled: isDisabled,
    className: classNames(BaseBreadcrumbs.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement("ol", null, breadcrumbItems));
});
BaseBreadcrumbs.className = CLASSNAME$A;
BaseBreadcrumbs.defaultProps = DEFAULT_PROPS$A;
BaseBreadcrumbs.displayName = COMPONENT_NAME$A;
const Breadcrumbs = Object.assign(BaseBreadcrumbs, {
  Item: BreadcrumbItem
});

const _excluded$y = ["breadcrumbs", "children", "className", "iconButtonProps", "iconButtonRef", "fallbackTitle", "title"];
const COMPONENT_NAME$z = 'AppBar';
const CLASSNAME$z = 'redsift-app-bar';
const DEFAULT_PROPS$z = {};

/**
 * The AppBar component.
 */
const AppBar = /*#__PURE__*/forwardRef((props, ref) => {
  var _breadcrumbs$, _appContainerState$pr;
  const {
      breadcrumbs,
      children,
      className,
      iconButtonProps,
      iconButtonRef,
      fallbackTitle,
      title: propsTitle
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$y);
  const [scroll, setScroll] = useState(false);
  useEffect(() => {
    window.addEventListener('scroll', () => {
      setScroll(window.scrollY > 8);
    });
  }, []);
  const {
    isLoaded
  } = useIsLoaded();
  const appContainerState = useContext(AppContainerContext);
  const stringFormatter = useLocalizedStringFormatter(intlMessages$4);
  return /*#__PURE__*/React__default.createElement(StyledAppBar, _extends$1({}, forwardedProps, {
    $hasBorder: scroll,
    $isLoaded: isLoaded,
    $isSidePanelCollapsed: appContainerState ? appContainerState.sidePanelVariant === AppSidePanelVariant.shrinked : true,
    className: classNames(AppBar.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${AppBar.className}__left`
  }, appContainerState ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, appContainerState.sidePanelVariant === AppSidePanelVariant.shrinked ? /*#__PURE__*/React__default.createElement("div", {
    style: {
      width: '30px',
      height: '30px',
      marginRight: '3px'
    }
  }) : /*#__PURE__*/React__default.createElement(IconButton, _extends$1({
    className: `${AppBar.className}-left__collapse-button`
  }, iconButtonProps, {
    "aria-label": stringFormatter.format('collapse'),
    icon: mdiMenuOpen,
    onClick: appContainerState.collapseSidePanel,
    ref: iconButtonRef,
    color: "question"
  }))) : null, propsTitle && typeof propsTitle !== 'string' ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, propsTitle) : typeof propsTitle === 'string' ? /*#__PURE__*/React__default.createElement(Heading, {
    as: "h1"
  }, propsTitle) : breadcrumbs ? /*#__PURE__*/React__default.createElement(Breadcrumbs, {
    "aria-label": "Breadcrumb"
  }, appContainerState && appContainerState.sidePanelVariant === AppSidePanelVariant.shrinked ? /*#__PURE__*/React__default.createElement(Breadcrumbs.Item, {
    href: "/",
    as: ((_breadcrumbs$ = breadcrumbs[0]) === null || _breadcrumbs$ === void 0 ? void 0 : _breadcrumbs$.as) || 'a'
  }, typeof appContainerState.product === 'string' ? ProductName[appContainerState.product] : (_appContainerState$pr = appContainerState.product) === null || _appContainerState$pr === void 0 ? void 0 : _appContainerState$pr.name) : null, breadcrumbs.map((breadcrumbItem, index) => /*#__PURE__*/React__default.createElement(Breadcrumbs.Item, _extends$1({
    key: index
  }, breadcrumbItem, {
    isDisabled: breadcrumbItem.isDisabled || breadcrumbItem.href === undefined,
    as: index === breadcrumbs.length - 1 ? 'h1' : breadcrumbItem.href === undefined ? 'span' : breadcrumbItem.as || 'a'
  })))) : fallbackTitle ? /*#__PURE__*/React__default.createElement(Heading, {
    as: "h1"
  }, fallbackTitle) : null), children ? /*#__PURE__*/React__default.createElement("div", {
    className: `${AppBar.className}__right`
  }, children) : null);
});
AppBar.className = CLASSNAME$z;
AppBar.defaultProps = DEFAULT_PROPS$z;
AppBar.displayName = COMPONENT_NAME$z;

/**
 * Component style.
 */
const StyledAppContent = styled.main`
  padding: 16px 64px;
  ${baseContainer}

  box-sizing: border-box;
  min-height: calc(100vh - 92px);

  @media screen and (max-width: 768px) {
    padding: 0px;
  }

  ${_ref => {
  let {
    $isSidePanelCollapsed
  } = _ref;
  return !$isSidePanelCollapsed ? css`
          transform: translate(254px);
          width: calc(100% - 254px);
        ` : css`
          transform: translate(68px);
          width: calc(100% - 68px);
        `;
}}

  ${_ref2 => {
  let {
    $isLoaded
  } = _ref2;
  return $isLoaded ? css`
          transition: transform 0.25s ease-out, width 0.25s ease-out;
        ` : '';
}}
`;

const _excluded$x = ["children", "className"];
const COMPONENT_NAME$y = 'AppContent';
const CLASSNAME$y = 'redsift-app-content';
const DEFAULT_PROPS$y = {};

/**
 * The AppContent component.
 */
const AppContent = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$x);
  const {
    isLoaded
  } = useIsLoaded();
  const appContainerState = useContext(AppContainerContext);
  return /*#__PURE__*/React__default.createElement(StyledAppContent, _extends$1({}, forwardedProps, {
    $isLoaded: isLoaded,
    $isSidePanelCollapsed: appContainerState ? appContainerState.sidePanelVariant === AppSidePanelVariant.shrinked : true,
    className: classNames(AppContent.className, className),
    ref: ref
  }), children);
});
AppContent.className = CLASSNAME$y;
AppContent.defaultProps = DEFAULT_PROPS$y;
AppContent.displayName = COMPONENT_NAME$y;

/**
 * Component variant.
 */
const ButtonGroupVariant = {
  primary: 'primary',
  secondary: 'secondary',
  unstyled: 'unstyled'
};

/**
 * Component color.
 */

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledButtonGroup = styled.div`
  display: ${_ref => {
  let {
    inline
  } = _ref;
  return inline ? 'inline-flex' : 'flex';
}};

  ${baseStyling}
  ${baseFlexbox}

  ${_ref2 => {
  let {
    $color,
    $variant
  } = _ref2;
  return css`
    ${$variant === ButtonGroupVariant.secondary ? css`
          .first-button {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right-color: transparent;
          }

          .middle-button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            margin-left: -1px;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right-color: transparent;
          }

          .last-button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            margin-left: -1px;
          }
        ` : $variant === ButtonGroupVariant.unstyled ? css`
          .first-button {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right: 1px solid rgba(0, 0, 0, 0.23);
            border-color: ${`var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active)`};
          }

          .middle-button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right: 1px solid rgba(0, 0, 0, 0.23);
            border-color: ${`var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-active)`};
          }

          .last-button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        ` : css`
          .first-button {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right: 1px solid #bdbdbd;
            border-color: ${`var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-hover)`};
          }

          .middle-button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right: 1px solid #bdbdbd;
            border-color: ${`var(--redsift-color-${Object.keys(CtasColorPalette).includes($color) ? 'ctas' : 'notifications'}-${$color}-button-hover)`};
          }

          .last-button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        `}
  `;
}};
`;

const _excluded$w = ["children", "className", "color", "variant"];
const COMPONENT_NAME$x = 'ButtonGroup';
const CLASSNAME$x = 'redsift-button-group';
const DEFAULT_PROPS$x = {
  variant: 'primary'
};

/**
 * The ButtonGroup component.
 */
const ButtonGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      color,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$w);

  /**
   * Create an array containing only valid children being either Button, IconButton, ButtonLink or MenuButton.
   */
  const childArray = filterComponents(['Button', 'IconButton', 'ButtonLink', 'MenuButton'])(children);
  const buttons = childArray.map((child, index) => {
    var _key;
    if (isComponent('MenuButton')(child)) {
      var _child$key;
      return /*#__PURE__*/React__default.cloneElement(child, {
        color,
        variant,
        key: (_child$key = child.key) !== null && _child$key !== void 0 ? _child$key : index,
        triggerClassName: index === 0 ? 'first-button' : index === childArray.length - 1 ? 'last-button' : 'middle-button'
      });
    }
    return /*#__PURE__*/React__default.cloneElement(child, {
      color,
      variant,
      key: (_key = child.key) !== null && _key !== void 0 ? _key : index,
      className: index === 0 ? 'first-button' : index === childArray.length - 1 ? 'last-button' : 'middle-button'
    });
  });
  return /*#__PURE__*/React__default.createElement(StyledButtonGroup, _extends$1({}, forwardedProps, {
    className: classNames(ButtonGroup.className, className),
    ref: ref,
    $color: color,
    $variant: variant
  }), buttons);
});
ButtonGroup.className = CLASSNAME$x;
ButtonGroup.defaultProps = DEFAULT_PROPS$x;
ButtonGroup.displayName = COMPONENT_NAME$x;

const _excluded$v = ["as", "children", "className", "color", "href", "isActive", "isDisabled", "leftIcon", "rightIcon", "target", "variant"];
const COMPONENT_NAME$w = 'ButtonLink';
const CLASSNAME$w = 'redsift-button-link';
const DEFAULT_PROPS$w = {
  color: CtasColorPalette.primary,
  height: 'fit-content',
  variant: ButtonVariant.primary
};

/**
 * The ButtonLink is a semantic link that looks like a button.
 *
 * For a semantic button that looks like a button, please use the Button component.
 * For a semantic link that looks like a link, please use the Link component.
 * For a semantic button that looks like a link, please use the LinkButton component.
 */
const ButtonLink = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as,
      children,
      className,
      color,
      href,
      isActive,
      isDisabled,
      leftIcon,
      rightIcon,
      target,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$v);
  return /*#__PURE__*/React__default.createElement(StyledButton, _extends$1({
    as: as || 'a',
    role: "link",
    tabIndex: !isDisabled ? 0 : undefined
  }, forwardedProps, {
    $color: color,
    $isActive: isActive,
    $isDisabled: isDisabled,
    $variant: variant,
    className: classNames(ButtonLink.className, className),
    href: !isDisabled ? href : undefined,
    ref: ref,
    target: target
  }), leftIcon ? /*#__PURE__*/React__default.createElement(Icon, {
    icon: leftIcon,
    "aria-hidden": "true",
    className: "left"
  }) : null, children, rightIcon ? /*#__PURE__*/React__default.createElement(Icon, {
    icon: rightIcon,
    "aria-hidden": "true",
    className: "right"
  }) : null);
});
ButtonLink.className = CLASSNAME$w;
ButtonLink.defaultProps = DEFAULT_PROPS$w;
ButtonLink.displayName = COMPONENT_NAME$w;

var collapse$3 = "Collapse";
var expand$3 = "Expand";
var enUS$2 = {
	collapse: collapse$3,
	expand: expand$3
};

var collapse$2 = "Réduire";
var expand$2 = "Développer";
var frFR$2 = {
	collapse: collapse$2,
	expand: expand$2
};

var intlMessages$2 = {
  'en-US': enUS$2,
  'fr-FR': frFR$2
};

/**
 * Component style.
 */
const StyledCard = styled.div`
  ${baseStyling}

  display: flex;
  flex-direction: column;

  .redsift-card__header {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
  }

  .redsift-card-header__icon-button {
    margin-top: 8px;
    margin-left: auto;
    margin-right: 8px;

    [dir='rtl'] & {
      margin-left: 8px;
      margin-right: auto;
    }
  }

  .redsift-card__content {
    visibility: ${_ref => {
  let {
    $isCollapsed
  } = _ref;
  return !$isCollapsed ? 'visible' : 'hidden';
}};
    overflow: ${_ref2 => {
  let {
    $isCollapsed
  } = _ref2;
  return !$isCollapsed ? 'visible' : 'hidden';
}};
    height: ${_ref3 => {
  let {
    $isCollapsed
  } = _ref3;
  return !$isCollapsed ? 'auto' : '0px';
}};
  }
`;

/**
 * Component style.
 */
const StyledCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${baseStyling}
  ${baseFlexbox}

  color: var(--redsift-color-neutral-black);

  .redsift-card-header__header {
    align-items: center;
    display: flex;
    gap: 8px;
    padding: 10px 0px;
    margin: 0px;
  }

  .redsift-card-header__subheader {
    font-family: var(--redsift-typography-body-font-family);
    font-size: var(--redsift-typography-body-font-size);
    font-weight: var(--redsift-typography-body-font-weight);
    line-height: var(--redsift-typography-body-line-height);
  }
`;

/**
 * Component style.
 */
const wave = keyframes`
  0% {
    -webkit-transform: translateX(-100%);
    -moz-transform: translateX(-100%);
    -ms-transform: translateX(-100%);
    transform: translateX(-100%);
  }

  50% {
    -webkit-transform: translateX(100%);
    -moz-transform: translateX(100%);
    -ms-transform: translateX(100%);
    transform: translateX(100%);
  }

  100% {
    -webkit-transform: translateX(100%);
    -moz-transform: translateX(100%);
    -ms-transform: translateX(100%);
    transform: translateX(100%);
  }
`;
const StyledSkeleton = styled.div`
  height: fit-content;
  width: fit-content;

  * {
    visibility: hidden;
  }

  flex: 0 0 auto;

  ${baseStyling}
  ${baseInternalSpacing}

  display: block;
  background-color: rgba(0, 0, 0, 0.11);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  -webkit-mask-image: -webkit-radial-gradient(white, black);

  &::after {
    -webkit-animation: ${wave} 1.6s linear 0.5s infinite;
    animation: ${wave} 1.6s linear 0.5s infinite;
    background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.04), transparent);
    background-clip: content-box;
    content: '';
    position: absolute;
    -webkit-transform: translateX(-100%);
    -moz-transform: translateX(-100%);
    -ms-transform: translateX(-100%);
    transform: translateX(-100%);
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
  }
`;

/**
 * Component style.
 */
const StyledSkeletonCircle = styled(StyledSkeleton)`
  border-radius: 50%;

  ${_ref => {
  let {
    height,
    width
  } = _ref;
  return css`
    &,
    &::after {
      ${height ? `height: ${height};` : width ? `height: ${width};` : ''}
      ${width ? `width: ${width};` : height ? `width: ${height};` : ''}
    }
  `;
}}
`;

const _excluded$u = ["children", "className", "isLoaded"];
const COMPONENT_NAME$v = 'SkeletonCircle';
const CLASSNAME$v = 'redsift-skeleton-circle';
const DEFAULT_PROPS$v = {};

/**
 * The SkeletonCircle component.
 */
const SkeletonCircle = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      isLoaded
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$u);
  if (isLoaded) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }
  return /*#__PURE__*/React__default.createElement(StyledSkeletonCircle, _extends$1({}, forwardedProps, {
    className: classNames(SkeletonCircle.className, className),
    ref: ref
  }), typeof children === 'string' ? /*#__PURE__*/React__default.createElement("span", null, children) : children);
});
SkeletonCircle.className = CLASSNAME$v;
SkeletonCircle.defaultProps = DEFAULT_PROPS$v;
SkeletonCircle.displayName = COMPONENT_NAME$v;

/**
 * Component variant.
 */
const SkeletonTextVariant = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  body: 'body',
  button: 'button',
  caption: 'caption'
};

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledSkeletonText = styled(StyledSkeleton)`
  background-clip: content-box;

  ${_ref => {
  let {
    height,
    $fontSize,
    $lineHeight,
    $variant
  } = _ref;
  return height ? css`
          &,
          &::after {
            height: ${height};
          }
        ` : css`
          &,
          &::after {
            height: ${$fontSize ? $fontSize : `var(--redsift-typography-${$variant}-font-size)`};
            padding: calc(
                (
                    ${$lineHeight ? $lineHeight : `var(--redsift-typography-${$variant}-line-height)`} -
                      ${$fontSize ? $fontSize : `var(--redsift-typography-${$variant}-font-size)`}
                  ) / 2
              )
              0px;
          }
        `;
}}}
`;

const _excluded$t = ["children", "className", "fontSize", "isLoaded", "lineHeight", "variant"];
const COMPONENT_NAME$u = 'SkeletonText';
const CLASSNAME$u = 'redsift-skeleton-text';
const DEFAULT_PROPS$u = {
  variant: 'body'
};

/**
 * The SkeletonText component.
 */
const SkeletonText = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      fontSize,
      isLoaded,
      lineHeight,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$t);
  if (isLoaded) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }
  return /*#__PURE__*/React__default.createElement(StyledSkeletonText, _extends$1({}, forwardedProps, {
    className: classNames(SkeletonText.className, className),
    ref: ref,
    $fontSize: fontSize,
    $lineHeight: lineHeight,
    $variant: variant
  }), typeof children === 'string' ? /*#__PURE__*/React__default.createElement("span", null, children) : children);
});
SkeletonText.className = CLASSNAME$u;
SkeletonText.defaultProps = DEFAULT_PROPS$u;
SkeletonText.displayName = COMPONENT_NAME$u;

const _excluded$s = ["children", "className", "isLoaded"];
const COMPONENT_NAME$t = 'Skeleton';
const CLASSNAME$t = 'redsift-skeleton';
const DEFAULT_PROPS$t = {};

/**
 * The Skeleton component.
 */
const BaseSkeleton = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      isLoaded
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$s);
  if (isLoaded) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }
  return /*#__PURE__*/React__default.createElement(StyledSkeleton, _extends$1({}, forwardedProps, {
    className: classNames(BaseSkeleton.className, className),
    ref: ref
  }), typeof children === 'string' ? /*#__PURE__*/React__default.createElement("span", null, children) : children);
});
BaseSkeleton.className = CLASSNAME$t;
BaseSkeleton.defaultProps = DEFAULT_PROPS$t;
BaseSkeleton.displayName = COMPONENT_NAME$t;
const Skeleton = Object.assign(BaseSkeleton, {
  Circle: SkeletonCircle,
  Text: SkeletonText
});

const _excluded$r = ["children", "className", "header", "headingProps", "icon", "iconProps", "isLoading", "subheader"];
const COMPONENT_NAME$s = 'CardHeader';
const CLASSNAME$s = 'redsift-card-header';
const DEFAULT_PROPS$s = {};

/**
 * The CardHeader component.
 */
const CardHeader = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      header,
      headingProps,
      icon,
      iconProps,
      isLoading,
      subheader
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$r);
  return /*#__PURE__*/React__default.createElement(StyledCardHeader, _extends$1({}, forwardedProps, {
    className: classNames(CardHeader.className, className),
    ref: ref
  }), subheader ? /*#__PURE__*/React__default.createElement(Skeleton.Text, {
    variant: "body",
    isLoaded: !isLoading
  }, /*#__PURE__*/React__default.createElement("div", {
    className: `${CardHeader.className}__subheader`
  }, subheader)) : null, header ? /*#__PURE__*/React__default.createElement(Skeleton.Text, {
    variant: (headingProps === null || headingProps === void 0 ? void 0 : headingProps.variant) || 'h2',
    isLoaded: !isLoading,
    marginTop: "10px",
    marginBottom: "10px"
  }, /*#__PURE__*/React__default.createElement(Heading, _extends$1({
    as: "h2",
    color: "black",
    className: `${CardHeader.className}__header`,
    variant: "h2"
  }, headingProps), icon ? /*#__PURE__*/React__default.createElement(Icon, _extends$1({
    icon: icon,
    "aria-hidden": "true",
    size: "large"
  }, iconProps)) : null, header)) : null, children);
});
CardHeader.className = CLASSNAME$s;
CardHeader.defaultProps = DEFAULT_PROPS$s;
CardHeader.displayName = COMPONENT_NAME$s;

/**
 * Component style.
 */
const StyledCardBody = styled.div`
  ${baseContainer}

  color: var(--redsift-color-neutral-darkgrey);
  font-family: var(--redsift-typography-body-font-family);
  font-size: var(--redsift-typography-body-font-size);
  font-weight: var(--redsift-typography-body-font-weight);
  line-height: var(--redsift-typography-body-line-height);
  padding: 16px 0px;
`;

const _excluded$q = ["children", "className"];
const COMPONENT_NAME$r = 'CardBody';
const CLASSNAME$r = 'redsift-card-body';
const DEFAULT_PROPS$r = {};

/**
 * The CardBody component.
 */
const CardBody = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$q);
  return /*#__PURE__*/React__default.createElement(StyledCardBody, _extends$1({}, forwardedProps, {
    className: classNames(CardBody.className, className),
    ref: ref
  }), children);
});
CardBody.className = CLASSNAME$r;
CardBody.defaultProps = DEFAULT_PROPS$r;
CardBody.displayName = COMPONENT_NAME$r;

/**
 * Component style.
 */
const StyledCardActions = styled.div`
  ${baseContainer}

  margin-top: 8px;
  margin-bottom: 8px;
`;

const _excluded$p = ["children", "className"];
const COMPONENT_NAME$q = 'CardActions';
const CLASSNAME$q = 'redsift-card-actions';
const DEFAULT_PROPS$q = {
  display: 'flex',
  flexDirection: 'row'
};

/**
 * The CardActions component.
 */
const CardActions = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$p);
  return /*#__PURE__*/React__default.createElement(StyledCardActions, _extends$1({}, forwardedProps, {
    className: classNames(CardActions.className, className),
    ref: ref
  }), children);
});
CardActions.className = CLASSNAME$q;
CardActions.defaultProps = DEFAULT_PROPS$q;
CardActions.displayName = COMPONENT_NAME$q;

const _excluded$o = ["children", "className", "defaultCollapsed", "isCollapsed", "isCollapsible", "onCollapse"];
const COMPONENT_NAME$p = 'Card';
const CLASSNAME$p = 'redsift-card';
const DEFAULT_PROPS$p = {};

/**
 * The Card component.
 */
const BaseCard = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      defaultCollapsed,
      isCollapsed: propsIsCollapsed,
      isCollapsible,
      onCollapse
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$o);
  const stringFormatter = useLocalizedStringFormatter(intlMessages$2);
  const [isCollapsed, setIsCollapsed] = useState(propsIsCollapsed !== null && propsIsCollapsed !== void 0 ? propsIsCollapsed : defaultCollapsed);
  useEffect(() => {
    setIsCollapsed(propsIsCollapsed !== null && propsIsCollapsed !== void 0 ? propsIsCollapsed : defaultCollapsed);
  }, [propsIsCollapsed, defaultCollapsed]);
  const handleCollapse = useCallback(collapsed => {
    if (onCollapse) {
      onCollapse(collapsed);
    }
    if (propsIsCollapsed === undefined || propsIsCollapsed === null) {
      setIsCollapsed(collapsed);
    }
  }, [onCollapse]);
  const [[header], [body], [actions]] = partitionComponents(React__default.Children.toArray(children), [isComponent('CardHeader'), isComponent('CardBody'), isComponent('CardActions')]);
  return /*#__PURE__*/React__default.createElement(StyledCard, _extends$1({}, forwardedProps, {
    className: classNames(BaseCard.className, className),
    ref: ref,
    $isCollapsed: isCollapsed
  }), header || isCollapsible ? /*#__PURE__*/React__default.createElement("div", {
    className: `${BaseCard.className}__header`
  }, header ? header : null, isCollapsible ? /*#__PURE__*/React__default.createElement(IconButton, {
    "aria-label": stringFormatter.format(isCollapsed ? 'expand' : 'collapse'),
    className: `${BaseCard.className}-header__icon-button`,
    color: "question",
    icon: isCollapsed ? mdiChevronDown : mdiChevronUp,
    onClick: () => handleCollapse(!isCollapsed)
  }) : null) : null, body || actions ? /*#__PURE__*/React__default.createElement("div", {
    className: `${BaseCard.className}__content`
  }, body, actions) : null);
});
BaseCard.className = CLASSNAME$p;
BaseCard.defaultProps = DEFAULT_PROPS$p;
BaseCard.displayName = COMPONENT_NAME$p;
const Card = Object.assign(BaseCard, {
  Header: CardHeader,
  Body: CardBody,
  Actions: CardActions
});

/**
 * Context props.
 */

/**
 * Component orientation.
 */
const CheckboxGroupOrientation = {
  vertical: 'vertical',
  horizontal: 'horizontal'
};

/**
 * Component props.
 */

const CheckboxGroupContext = /*#__PURE__*/React__default.createContext(null);

/**
 * Component style.
 */
const StyledCheckboxGroup = styled.div`
  ${baseStyling}

  display: flex;
  flex-direction: column;

  ${_ref => {
  let {
    $orientation
  } = _ref;
  return $orientation === CheckboxGroupOrientation.vertical ? `
    div.redsift-checkbox-group-boxes {
      display: flex;
      flex-direction: column;
    }
  ` : `
    div.redsift-checkbox-group-boxes {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }
  `;
}}

  span.redsift-checkbox-group-label {
    font-family: var(--redsift-typography-body-font-family);
    font-weight: var(--redsift-typography-body-font-weight);
    font-size: var(--redsift-typography-body-font-size);
    line-height: var(--redsift-typography-body-line-height);

    color: var(--redsift-color-neutral-black);
    ${_ref2 => {
  let {
    $isDisabled
  } = _ref2;
  return $isDisabled ? `
      color: var(--redsift-color-neutral-midgrey);
    ` : '';
}}
    ${_ref3 => {
  let {
    $isInvalid
  } = _ref3;
  return $isInvalid ? `
      color: var(--redsift-color-notifications-error-primary);
    ` : '';
}}
  }

  span.redsift-checkbox-group-description {
    font-family: var(--redsift-typography-helper-font-family);
    font-weight: var(--redsift-typography-helper-font-weight);
    font-size: var(--redsift-typography-helper-font-size);
    line-height: var(--redsift-typography-helper-line-height);

    color: var(--redsift-color-neutral-darkgrey);
    ${_ref4 => {
  let {
    $isDisabled
  } = _ref4;
  return $isDisabled ? `
      color: var(--redsift-color-neutral-midgrey);
    ` : '';
}}
    ${_ref5 => {
  let {
    $isInvalid
  } = _ref5;
  return $isInvalid ? `
      color: var(--redsift-color-notifications-error-primary);
    ` : '';
}}
  }
`;

const _excluded$n = ["children", "className", "defaultValues", "description", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "label", "onChange", "orientation", "value"];
const COMPONENT_NAME$o = 'CheckboxGroup';
const CLASSNAME$o = 'redsift-checkbox-group';
const DEFAULT_PROPS$o = {
  color: CtasColorPalette.primary,
  orientation: CheckboxGroupOrientation.vertical
};

/**
 * The CheckboxGroup component.
 * Can be used as controlled or uncontrolled.
 */
const CheckboxGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      defaultValues,
      description,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
      onChange,
      orientation,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$n);
  const [selectedValues, setValue] = useState(value || defaultValues || []);
  useEffect(() => {
    if (value) {
      state.setValue(value);
    }
  }, [value]);
  const state = {
    value: selectedValues,
    isDisabled: isDisabled || false,
    isReadOnly: isReadOnly || false,
    setValue(values) {
      if (isReadOnly || isDisabled) {
        return;
      }
      setValue(values);
    },
    addValue(value) {
      if (!selectedValues.includes(value)) {
        setValue(selectedValues.concat(value));
        if (onChange) {
          onChange(selectedValues.concat(value));
        }
      }
    },
    removeValue(value) {
      if (selectedValues.includes(value)) {
        setValue(selectedValues.filter(existingValue => existingValue !== value));
        if (onChange) {
          onChange(selectedValues.filter(existingValue => existingValue !== value));
        }
      }
    }
  };
  return /*#__PURE__*/React__default.createElement(StyledCheckboxGroup, _extends$1({}, forwardedProps, {
    $isDisabled: isDisabled,
    $isInvalid: isInvalid || isRequired && selectedValues.length === 0,
    $isReadOnly: isReadOnly,
    $orientation: orientation,
    "aria-disabled": isDisabled,
    className: classNames(CheckboxGroup.className, className),
    ref: ref,
    role: "group"
  }), /*#__PURE__*/React__default.createElement("span", {
    className: `${CheckboxGroup.className}-label`
  }, label, isRequired ? ' *' : null), /*#__PURE__*/React__default.createElement("div", {
    className: `${CheckboxGroup.className}-boxes`
  }, /*#__PURE__*/React__default.createElement(CheckboxGroupContext.Provider, {
    value: state
  }, children)), /*#__PURE__*/React__default.createElement("span", {
    className: `${CheckboxGroup.className}-description`
  }, description));
});
CheckboxGroup.className = CLASSNAME$o;
CheckboxGroup.defaultProps = DEFAULT_PROPS$o;
CheckboxGroup.displayName = COMPONENT_NAME$o;

/**
 * Component style.
 */
const StyledCheckbox = styled.label`
  height: fit-content;
  position: relative;
  width: fit-content;
  ${baseStyling}

  align-items: flex-start;
  background: none;
  border: none;
  display: inline-flex;

  input {
    height: 100%;
    margin: 0;
    opacity: 0;
    overflow: visible;
    padding: 0;
    position: absolute;
    width: 100%;
  }

  span.label {
    ${_ref => {
  let {
    $isDisabled,
    $isInvalid
  } = _ref;
  return $isDisabled ? css`
            color: var(--redsift-color-neutral-lightgrey);
          ` : $isInvalid ? css`
            color: var(--redsift-color-notifications-error-primary);
          ` : css`
            color: var(--redsift-color-neutral-black);
          `;
}}

    font-family: var(--redsift-typography-body-font-family);
    font-size: var(--redsift-typography-body-font-size);
    font-weight: var(--redsift-typography-body-font-weight);
    line-height: var(--redsift-typography-body-line-height);
    padding-left: 4px;
    padding-right: 16px;
    padding-top: 2px;

    [dir='rtl'] & {
      padding-left: 16px;
      padding-right: 4px;
    }
  }

  .redsift-icon {
    box-sizing: content-box;
    padding: 2px;
    width: 24px;

    ${_ref2 => {
  let {
    $isColored,
    $isDisabled,
    $isInvalid,
    $isFocusVisible
  } = _ref2;
  return css`
      ${$isDisabled ? css`
            color: var(--redsift-color-neutral-lightgrey);
          ` : $isInvalid ? css`
            color: var(--redsift-color-notifications-error-primary);
          ` : css`
            color: ${$isColored ? 'var(--redsift-color-ctas-primary-primary)' : 'var(--redsift-color-notifications-question-primary)'};
          `}

      ${$isFocusVisible && !$isDisabled ? css`
            background-color: ${$isColored ? 'var(--redsift-color-ctas-primary-hover)' : 'var(--redsift-color-notifications-question-hover)'};
          ` : ''}
    `;
}}
  }

  &:hover,
  &:focus-visible {
    outline: none;

    ${_ref3 => {
  let {
    $isColored,
    $isDisabled
  } = _ref3;
  return !$isDisabled ? css`
            .redsift-icon {
              background-color: ${$isColored ? 'var(--redsift-color-ctas-primary-hover)' : 'var(--redsift-color-notifications-question-hover)'};
              border-radius: 4px;
            }
          ` : '';
}}
  }
`;

const _excluded$m = ["aria-label", "aria-labelledby", "autoFocus", "children", "className", "defaultSelected", "inputProps", "inputRef", "isColored", "isDisabled", "isIndeterminate", "isInvalid", "isReadOnly", "isRequired", "isSelected", "name", "onChange", "value"];
const COMPONENT_NAME$n = 'Checkbox';
const CLASSNAME$n = 'redsift-checkbox';
const DEFAULT_PROPS$n = {
  isColored: true
};

/**
 * The Checkbox component.
 * Works both inside a CheckboxGroup or as standalone.
 * Can be used as controlled or uncontrolled.
 */
const Checkbox = /*#__PURE__*/forwardRef((props, ref) => {
  var _isDisabled, _isReadOnly, _ref;
  const groupState = useContext(CheckboxGroupContext);
  let {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      autoFocus,
      children,
      className,
      defaultSelected,
      inputProps,
      inputRef,
      isColored,
      isDisabled,
      isIndeterminate,
      isInvalid,
      isReadOnly,
      isRequired,
      isSelected: propsIsSelected,
      name,
      onChange,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$m);
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing({
    autoFocus
  });
  isDisabled = (_isDisabled = isDisabled) !== null && _isDisabled !== void 0 ? _isDisabled : groupState === null || groupState === void 0 ? void 0 : groupState.isDisabled;
  isReadOnly = (_isReadOnly = isReadOnly) !== null && _isReadOnly !== void 0 ? _isReadOnly : groupState === null || groupState === void 0 ? void 0 : groupState.isReadOnly;
  const [isSelected, setSelected] = useState(Boolean((_ref = propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : defaultSelected) !== null && _ref !== void 0 ? _ref : groupState === null || groupState === void 0 ? void 0 : groupState.value.includes(value)));
  const isControlled = propsIsSelected !== null && propsIsSelected !== undefined;
  useEffect(() => {
    setSelected(Boolean(propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : defaultSelected));
  }, [propsIsSelected, defaultSelected]);
  useEffect(() => {
    if (groupState) {
      setSelected(groupState.value.includes(value));
    }
  }, [groupState === null || groupState === void 0 ? void 0 : groupState.value]);
  warnIfNoAccessibleLabelFound(props, [children]);
  if (groupState) {
    if (propsIsSelected != null) {
      console.warn('isSelected is unsupported on individual <Checkbox> elements within a <CheckboxGroup> unless the <Checkbox> is controlled. Please apply this prop to the group instead or pass isControlled to the component.');
    }
    if (defaultSelected != null) {
      console.warn('defaultSelected is unsupported on individual <Checkbox> elements within a <CheckboxGroup> unless the <Checkbox> is controlled. Please apply this prop to the group instead or pass isControlled to the component.');
    }
    if (props.value == null) {
      console.warn('A <Checkbox> element within a <CheckboxGroup> requires a `value` property unless the <Checkbox> is controlled.');
    }
  }
  const handleChange = useCallback(event => {
    if (isDisabled || isReadOnly) {
      return;
    }
    if (groupState) {
      if (!groupState.value.includes(value)) {
        groupState.addValue(value);
      } else {
        groupState.removeValue(value);
      }
    }
    if (isControlled) {
      onChange === null || onChange === void 0 ? void 0 : onChange(!propsIsSelected, value, name, event, groupState);
    } else {
      setSelected(!isSelected);
      onChange === null || onChange === void 0 ? void 0 : onChange(!isSelected, value, name, event, groupState);
    }
  }, [onChange, groupState, isSelected, propsIsSelected]);
  return /*#__PURE__*/React__default.createElement(StyledCheckbox, _extends$1({}, forwardedProps, {
    $isColored: isColored,
    $isDisabled: isDisabled,
    $isFocusVisible: isFocusVisible,
    $isInvalid: isInvalid || isRequired && !(isSelected || isControlled && propsIsSelected),
    $isRequired: isRequired,
    $isSelected: isSelected || isControlled && propsIsSelected,
    className: classNames(Checkbox.className, className),
    ref: ref
  }), isSelected || isControlled && propsIsSelected ? /*#__PURE__*/React__default.createElement(Icon, {
    icon: mdiCheckboxMarked
  }) : isIndeterminate ? /*#__PURE__*/React__default.createElement(Icon, {
    icon: mdiMinusBox
  }) : /*#__PURE__*/React__default.createElement(Icon, {
    icon: mdiCheckboxBlankOutline
  }), children ? /*#__PURE__*/React__default.createElement("span", {
    className: "label"
  }, children) : null, /*#__PURE__*/React__default.createElement("input", _extends$1({}, inputProps, focusProps, {
    "aria-checked": isSelected || isControlled && propsIsSelected ? 'true' : isIndeterminate ? 'mixed' : 'false',
    "aria-disabled": isDisabled,
    "aria-invalid": isInvalid || isRequired && !(isSelected || isControlled && propsIsSelected),
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-readonly": isReadOnly,
    "aria-required": isRequired,
    autoFocus: autoFocus,
    checked: isSelected || isControlled && propsIsSelected ? true : false,
    disabled: isDisabled,
    name: name,
    onChange: handleChange,
    ref: inputRef,
    type: "checkbox",
    value: value
  })));
});
Checkbox.className = CLASSNAME$n;
Checkbox.defaultProps = DEFAULT_PROPS$n;
Checkbox.displayName = COMPONENT_NAME$n;

const ConditionalWrapper = _ref => {
  let {
    children,
    condition,
    wrapper
  } = _ref;
  return condition ? wrapper(children) : /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
};

var enUS$1 = {
	"collapse-all": "Collapse all details",
	"expand-all": "Expand all details"
};

var frFR$1 = {
	"collapse-all": "Réduire tous les détails",
	"expand-all": "Développer tous les détails"
};

var intlMessages$1 = {
  'en-US': enUS$1,
  'fr-FR': frFR$1
};

/**
 * Component variant.
 */
const ShieldVariant = {
  success: 'success',
  successLocked: 'successLocked',
  successUnlocked: 'successUnlocked',
  fail: 'fail',
  failLocked: 'failLocked',
  failUnlocked: 'failUnlocked',
  warning: 'warning',
  warningLocked: 'warningLocked',
  warningUnlocked: 'warningUnlocked',
  ignored: 'ignored',
  noData: 'noData',
  question: 'question',
  email: 'email'
};

/**
 * Component props.
 */

const StyledShield = styled.div`
  height: 24px;
  width: 20px;
  ${baseStyling}

  box-sizing: content-box;

  ${_ref => {
  let {
    $color,
    $isOutlined,
    $isReversed
  } = _ref;
  if ($isReversed && $isOutlined) {
    return css`
        & .shield {
          fill: var(--redsift-color-notifications-${$color}-primary);
          stroke: #ffffff;
          stroke-width: 2;
        }
        & .icon {
          fill-rule: evenodd;
          clip-rule: evenodd;
          fill: #ffffff;
        }
      `;
  } else if ($isOutlined) {
    return css`
        & .shield {
          fill: #ffffff;
          stroke: var(--redsift-color-notifications-${$color}-primary);
          stroke-width: 2;
        }
        & .icon {
          fill-rule: evenodd;
          clip-rule: evenodd;
          fill: var(--redsift-color-notifications-${$color}-primary);
        }
      `;
  } else if ($isReversed) {
    return css`
        & .shield {
          fill: #ffffff;
        }
        & .icon {
          fill-rule: evenodd;
          clip-rule: evenodd;
          fill: var(--redsift-color-notifications-${$color}-primary);
        }
      `;
  } else {
    return css`
        & .shield {
          fill: var(--redsift-color-notifications-${$color}-primary);
        }
        & .icon {
          fill-rule: evenodd;
          clip-rule: evenodd;
          fill: #ffffff;
        }
      `;
  }
}}
`;

const _excluded$l = ["aria-hidden", "aria-label", "className", "svgProps", "isOutlined", "isReversed", "variant"];
const COMPONENT_NAME$m = 'Shield';
const CLASSNAME$m = 'redsift-shield';
const DEFAULT_PROPS$m = {
  variant: ShieldVariant.success
};
const getVariant = variant => {
  switch (variant) {
    case ShieldVariant.email:
      return {
        color: NotificationsColorPalette.info,
        icon: 'M14.7,6.3H5.3c-0.6,0-1.2,0.5-1.2,1.2l0,7c0,0.6,0.5,1.2,1.2,1.2h9.3c0.6,0,1.2-0.5,1.2-1.2v-7 C15.8,6.9,15.3,6.3,14.7,6.3z M14.7,14.5H5.3V8.7l4.7,2.9l4.7-2.9V14.5z M10,10.4L5.3,7.5h9.3L10,10.4z'
      };
    case ShieldVariant.fail:
      return {
        color: NotificationsColorPalette.error,
        icon: 'M9,15V5h2v10H9z M9,19v-2h2v2H9z'
      };
    case ShieldVariant.failLocked:
      return {
        color: NotificationsColorPalette.error,
        icon: 'M13.5,9.7h-0.6V8.5c0-1.6-1.3-2.9-2.9-2.9S7.1,6.9,7.1,8.5v1.2H6.5c-0.6,0-1.2,0.5-1.2,1.2v5.8 c0,0.6,0.5,1.2,1.2,1.2h7c0.6,0,1.2-0.5,1.2-1.2v-5.8C14.7,10.2,14.1,9.7,13.5,9.7z M10,14.9c-0.6,0-1.2-0.5-1.2-1.2 s0.5-1.2,1.2-1.2c0.6,0,1.2,0.5,1.2,1.2S10.6,14.9,10,14.9z M11.8,9.7H8.2V8.5c0-1,0.8-1.8,1.8-1.8s1.8,0.8,1.8,1.8V9.7z'
      };
    case ShieldVariant.failUnlocked:
      return {
        color: NotificationsColorPalette.error,
        icon: 'M10,14.9c0.6,0,1.2-0.5,1.2-1.2s-0.5-1.2-1.2-1.2c-0.6,0-1.2,0.5-1.2,1.2S9.4,14.9,10,14.9z M13.5,9.7h-0.6V8.5 c0-1.6-1.3-2.9-2.9-2.9S7.1,6.9,7.1,8.5h1.1c0-1,0.8-1.8,1.8-1.8s1.8,0.8,1.8,1.8v1.2H6.5c-0.6,0-1.2,0.5-1.2,1.2v5.8 c0,0.6,0.5,1.2,1.2,1.2h7c0.6,0,1.2-0.5,1.2-1.2v-5.8C14.7,10.2,14.1,9.7,13.5,9.7z M13.5,16.7h-7v-5.8h7V16.7z'
      };
    case ShieldVariant.ignored:
      return {
        color: NotificationsColorPalette['no-data'],
        icon: 'M10,6.2c-3.2,0-5.8,2.6-5.8,5.8c0,3.2,2.6,5.8,5.8,5.8s5.8-2.6,5.8-5.8C15.8,8.8,13.2,6.2,10,6.2z M5.3,12 c0-2.6,2.1-4.7,4.7-4.7c1.1,0,2.1,0.4,2.9,1l-6.5,6.5C5.7,14.1,5.3,13.1,5.3,12z M10,16.7c-1.1,0-2.1-0.4-2.9-1l6.5-6.5 c0.6,0.8,1,1.8,1,2.9C14.7,14.6,12.6,16.7,10,16.7z'
      };
    case ShieldVariant.noData:
      return {
        color: NotificationsColorPalette['no-data'],
        icon: 'M16,13H4v-2h12V13z'
      };
    case ShieldVariant.question:
      return {
        color: NotificationsColorPalette.question,
        icon: 'M9,17h2v-2H9V17z M10,5C7.8,5,6,6.8,6,9h2c0-1.1,0.9-2,2-2s2,0.9,2,2c0,2-3,1.8-3,5h2c0-2.2,3-2.5,3-5 C14,6.8,12.2,5,10,5z'
      };
    case ShieldVariant.success:
      return {
        color: NotificationsColorPalette.success,
        icon: 'M8,16.6l8.5-8.5l-1.4-1.4L8,13.7l-2.8-2.8l-1.4,1.4L8,16.6z'
      };
    case ShieldVariant.successLocked:
      return {
        color: NotificationsColorPalette.success,
        icon: 'M13.5,9.7h-0.6V8.5c0-1.6-1.3-2.9-2.9-2.9S7.1,6.9,7.1,8.5v1.2H6.5c-0.6,0-1.2,0.5-1.2,1.2v5.8 c0,0.6,0.5,1.2,1.2,1.2h7c0.6,0,1.2-0.5,1.2-1.2v-5.8C14.7,10.2,14.1,9.7,13.5,9.7z M10,14.9c-0.6,0-1.2-0.5-1.2-1.2 s0.5-1.2,1.2-1.2c0.6,0,1.2,0.5,1.2,1.2S10.6,14.9,10,14.9z M11.8,9.7H8.2V8.5c0-1,0.8-1.8,1.8-1.8s1.8,0.8,1.8,1.8V9.7z'
      };
    case ShieldVariant.successUnlocked:
      return {
        color: NotificationsColorPalette.success,
        icon: 'M10,14.9c0.6,0,1.2-0.5,1.2-1.2s-0.5-1.2-1.2-1.2c-0.6,0-1.2,0.5-1.2,1.2S9.4,14.9,10,14.9z M13.5,9.7h-0.6V8.5 c0-1.6-1.3-2.9-2.9-2.9S7.1,6.9,7.1,8.5h1.1c0-1,0.8-1.8,1.8-1.8s1.8,0.8,1.8,1.8v1.2H6.5c-0.6,0-1.2,0.5-1.2,1.2v5.8 c0,0.6,0.5,1.2,1.2,1.2h7c0.6,0,1.2-0.5,1.2-1.2v-5.8C14.7,10.2,14.1,9.7,13.5,9.7z M13.5,16.7h-7v-5.8h7V16.7z'
      };
    case ShieldVariant.warning:
      return {
        color: NotificationsColorPalette.warning,
        icon: 'M10,5c0.7,0,1.4,0.4,1.7,1l4,7c0.4,0.6,0.4,1.4,0,2c-0.4,0.6-1,1-1.7,1H6c-0.7,0-1.4-0.4-1.7-1 c-0.4-0.6-0.4-1.4,0-2l4-7C8.6,5.4,9.3,5,10,5z M10,7l-4,7h8L10,7z'
      };
    case ShieldVariant.warningLocked:
      return {
        color: NotificationsColorPalette.warning,
        icon: 'M13.5,9.7h-0.6V8.5c0-1.6-1.3-2.9-2.9-2.9S7.1,6.9,7.1,8.5v1.2H6.5c-0.6,0-1.2,0.5-1.2,1.2v5.8 c0,0.6,0.5,1.2,1.2,1.2h7c0.6,0,1.2-0.5,1.2-1.2v-5.8C14.7,10.2,14.1,9.7,13.5,9.7z M10,14.9c-0.6,0-1.2-0.5-1.2-1.2 s0.5-1.2,1.2-1.2c0.6,0,1.2,0.5,1.2,1.2S10.6,14.9,10,14.9z M11.8,9.7H8.2V8.5c0-1,0.8-1.8,1.8-1.8s1.8,0.8,1.8,1.8V9.7z'
      };
    case ShieldVariant.warningUnlocked:
      return {
        color: NotificationsColorPalette.warning,
        icon: 'M10,14.9c0.6,0,1.2-0.5,1.2-1.2s-0.5-1.2-1.2-1.2c-0.6,0-1.2,0.5-1.2,1.2S9.4,14.9,10,14.9z M13.5,9.7h-0.6V8.5 c0-1.6-1.3-2.9-2.9-2.9S7.1,6.9,7.1,8.5h1.1c0-1,0.8-1.8,1.8-1.8s1.8,0.8,1.8,1.8v1.2H6.5c-0.6,0-1.2,0.5-1.2,1.2v5.8 c0,0.6,0.5,1.2,1.2,1.2h7c0.6,0,1.2-0.5,1.2-1.2v-5.8C14.7,10.2,14.1,9.7,13.5,9.7z M13.5,16.7h-7v-5.8h7V16.7z'
      };
  }
};

/**
 * The Shield component.
 */
const Shield = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-hidden': ariaHidden,
      'aria-label': ariaLabel,
      className,
      svgProps,
      isOutlined,
      isReversed,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$l);
  const {
    color,
    icon
  } = getVariant(variant);
  return /*#__PURE__*/React__default.createElement(StyledShield, _extends$1({}, forwardedProps, {
    "aria-hidden": ariaLabel ? ariaHidden ? ariaHidden : undefined : true,
    "aria-label": ariaLabel,
    className: classNames(Shield.className, className),
    ref: ref,
    $color: color,
    $isOutlined: isOutlined,
    $isReversed: isReversed,
    $variant: variant
  }), /*#__PURE__*/React__default.createElement("svg", _extends$1({
    "aria-hidden": ariaLabel ? ariaHidden ? ariaHidden : undefined : true,
    "aria-label": ariaLabel,
    focusable: "false",
    preserveAspectRatio: "xMidYMid meet",
    role: "img",
    style: {
      verticalAlign: '-0.125em'
    },
    viewBox: "0 0 20 24",
    width: "20",
    height: "24"
  }, svgProps), /*#__PURE__*/React__default.createElement("path", {
    d: isOutlined ? 'M1,10.9V5l9-3.9L19,5v5.9c0,5.5-3.9,10.7-9,12.1C4.9,21.6,1,16.4,1,10.9z' : 'M10,0L0,4.4v6.5C0,17,4.3,22.6,10,24c5.7-1.4,10-7,10-13.1V4.4L10,0z',
    className: "shield"
  }), /*#__PURE__*/React__default.createElement("path", {
    d: icon,
    className: "icon"
  })));
});
Shield.className = CLASSNAME$m;
Shield.defaultProps = DEFAULT_PROPS$m;
Shield.displayName = COMPONENT_NAME$m;

var collapse$1 = "Collapse details";
var expand$1 = "Expand details";
var enUS = {
	collapse: collapse$1,
	expand: expand$1
};

var collapse = "Réduire les détails";
var expand = "Développer les détails";
var frFR = {
	collapse: collapse,
	expand: expand
};

var intlMessages = {
  'en-US': enUS,
  'fr-FR': frFR
};

/**
 * Component style.
 */
const StyledDetailedCardCollapsibleSectionItems = styled.div`
  ${_ref => {
  let {
    $hideBackground
  } = _ref;
  return $hideBackground ? css`
          background: none;
        ` : css`
          background-color: var(--redsift-color-neutral-xlightgrey);
          margin: 8px -16px;
          padding: 8px 16px;
        `;
}}

  .redsift-detailed-card-collapsible-section-items__caption {
    color: var(--redsift-color-neutral-black);
    display: flex;
    font-family: var(--redsift-typography-font-family-poppins);
    font-size: 12px;
    font-weight: 300;
    justify-content: flex-end;
    line-height: 14px;
    margin: 8px 0px;
    padding-top: 8px;
    border-top: 1px solid var(--redsift-color-neutral-midgrey);
  }
`;

const _excluded$k = ["caption", "children", "className", "hideBackground"];
const COMPONENT_NAME$l = 'DetailedCardCollapsibleSectionItems';
const CLASSNAME$l = 'redsift-detailed-card-collapsible-section-items';
const DEFAULT_PROPS$l = {};

/**
 * The DetailedCardCollapsibleSectionItems Section component.
 */
const DetailedCardCollapsibleSectionItems = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      caption,
      children,
      className,
      hideBackground
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$k);
  return /*#__PURE__*/React__default.createElement(StyledDetailedCardCollapsibleSectionItems, _extends$1({}, forwardedProps, {
    className: classNames(DetailedCardCollapsibleSectionItems.className, className),
    ref: ref,
    $hideBackground: hideBackground
  }), children, caption ? /*#__PURE__*/React__default.createElement("div", {
    className: `${DetailedCardCollapsibleSectionItems.className}__caption`
  }, caption) : null);
});
DetailedCardCollapsibleSectionItems.className = CLASSNAME$l;
DetailedCardCollapsibleSectionItems.defaultProps = DEFAULT_PROPS$l;
DetailedCardCollapsibleSectionItems.displayName = COMPONENT_NAME$l;

/**
 * Component style.
 */
const StyledDetailedCardSection = styled.div`
  margin: 8px 0;

  .redsift-detailed-card-section__header {
    ${_ref => {
  let {
    $color
  } = _ref;
  return $color && [...Object.keys(NotificationsColorPalette), ...Object.keys(ProductColorPalette)].indexOf($color) !== -1 ? css`
            border-bottom: 1px solid var(--redsift-color-${$color}-primary);
          ` : css`
            border-bottom: 1px solid ${$color || css`var(--redsift-color-neutral-midgrey)`};
          `;
}}
    margin-bottom: 8px;
  }

  .redsift-detailed-card-section-header__title {
    font-size: 18px;
    font-weight: 500;
    lineheight: 22px;
    padding: 6px 0px;
  }

  .redsift-detailed-card-section-header__collapse-button {
    color: var(--redsift-color-neutral-black);
  }

  .redsift-detailed-card-collapsible-section-items {
    visibility: ${_ref2 => {
  let {
    $isCollapsed
  } = _ref2;
  return !$isCollapsed ? 'visible' : 'hidden';
}};
    overflow: ${_ref3 => {
  let {
    $isCollapsed
  } = _ref3;
  return !$isCollapsed ? 'visible' : 'hidden';
}};
    height: ${_ref4 => {
  let {
    $isCollapsed
  } = _ref4;
  return !$isCollapsed ? 'auto' : '0px';
}};
    ${_ref5 => {
  let {
    $isCollapsed
  } = _ref5;
  return $isCollapsed ? css`
            margin: unset;
            padding: unset;
          ` : '';
}};
  }
`;

const _excluded$j = ["badge", "color", "children", "className", "header", "isCollapsed", "isCollapsible", "isLoading"];
const COMPONENT_NAME$k = 'DetailedCardSection';
const CLASSNAME$k = 'redsift-detailed-card-section';
const DEFAULT_PROPS$k = {
  isCollapsible: true
};
const hasCollapsibleChildren = children => {
  let hasCollapsible = false;
  React__default.Children.map(children, child => {
    if (isComponent(DetailedCardCollapsibleSectionItems)(child)) {
      hasCollapsible = true;
    }
  });
  return hasCollapsible;
};

/**
 * The DetailedCardSection component.
 */
const DetailedCardSection = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      badge,
      color,
      children,
      className,
      header,
      isCollapsed: propsIsCollapsed,
      isCollapsible: propsIsCollapsible,
      isLoading
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$j);
  const stringFormatter = useLocalizedStringFormatter(intlMessages);
  const isCollapsible = propsIsCollapsible && hasCollapsibleChildren(children);
  const [isCollapsed, setIsCollapsed] = useState(Boolean(propsIsCollapsed));
  return /*#__PURE__*/React__default.createElement(StyledDetailedCardSection, _extends$1({}, forwardedProps, {
    className: classNames(DetailedCardSection.className, className),
    ref: ref,
    $color: color,
    $isCollapsed: isCollapsed
  }), /*#__PURE__*/React__default.createElement(Flexbox, {
    justifyContent: "space-between",
    className: `${DetailedCardSection.className}__header`
  }, header ? /*#__PURE__*/React__default.createElement(Skeleton.Text, {
    variant: "body",
    isLoaded: !isLoading,
    fontSize: "18px",
    lineHeight: "22px",
    marginTop: "6px",
    marginBottom: "12px"
  }, /*#__PURE__*/React__default.createElement(Flexbox, {
    alignItems: "center",
    gap: "8px"
  }, typeof header === 'string' ? /*#__PURE__*/React__default.createElement(Text, {
    className: `${DetailedCardSection.className}-header__title`,
    color: "black",
    fontSize: "18px",
    lineHeight: "22px"
  }, header) : /*#__PURE__*/React__default.createElement("div", {
    className: `${DetailedCardSection.className}-header__title`
  }, header), badge ? /*#__PURE__*/React__default.createElement(Badge, _extends$1({
    variant: BadgeVariant.standard
  }, badge)) : null)) : null, isCollapsible && !isLoading ? /*#__PURE__*/React__default.createElement(IconButton, {
    marginLeft: "auto",
    "aria-label": stringFormatter.format(isCollapsed ? 'expand' : 'collapse'),
    className: `${DetailedCardSection.className}-header__collapse-button`,
    color: "question",
    icon: isCollapsed ? mdiChevronDown : mdiChevronUp,
    onClick: () => {
      setIsCollapsed(isCollapsed => !isCollapsed);
    }
  }) : null), children);
});
DetailedCardSection.className = CLASSNAME$k;
DetailedCardSection.defaultProps = DEFAULT_PROPS$k;
DetailedCardSection.displayName = COMPONENT_NAME$k;

/**
 * Component style.
 */
const StyledDetailedCard = styled.div`
  ${baseStyling}

  background-color: var(--redsift-color-neutral-white);
  border-radius: 4px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);
  box-sizing: content-box;
  padding: 16px;

  .redsift-detailed-card__banner {
    background-color: ${_ref => {
  let {
    $color
  } = _ref;
  return css`var(--redsift-color-notifications-${$color}-primary)`;
}};
  }

  .redsift-detailed-card__collapse-buttons {
    width: 100%;
  }
`;

/**
 * Component style.
 */
const StyledDetailedCardHeader = styled.div`
  margin: 8px 0;

  .redsift-detailed-card-header__header {
    font-family: var(--redsift-typography-font-family-poppins);
    font-size: 32px;
    font-weight: 500;
    line-height: 32px;
    margin-bottom: 16px;
  }
`;

const _excluded$i = ["children", "className", "header", "headingProps", "isLoading"];
const COMPONENT_NAME$j = 'DetailedCardHeader';
const CLASSNAME$j = 'redsift-detailed-card-header';
const DEFAULT_PROPS$j = {};

/**
 * The DetailedCardHeader component.
 */
const DetailedCardHeader = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      header,
      headingProps,
      isLoading
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$i);
  return /*#__PURE__*/React__default.createElement(StyledDetailedCardHeader, _extends$1({}, forwardedProps, {
    className: classNames(DetailedCardHeader.className, className),
    ref: ref
  }), header ? /*#__PURE__*/React__default.createElement(Skeleton.Text, {
    variant: "h2",
    isLoaded: !isLoading,
    height: "32px",
    marginBottom: "16px"
  }, /*#__PURE__*/React__default.createElement(Heading, _extends$1({
    as: "h2",
    className: `${DetailedCardHeader.className}__header`,
    variant: "h2"
  }, headingProps), header)) : null, children);
});
DetailedCardHeader.className = CLASSNAME$j;
DetailedCardHeader.defaultProps = DEFAULT_PROPS$j;
DetailedCardHeader.displayName = COMPONENT_NAME$j;

/**
 * Component size.
 */
const PillSize = {
  small: 'small',
  large: 'large'
};

/**
 * Component props.
 */

const pickTextColorBasedOnBgColorAdvanced = (bgColor, lightColor, darkColor) => {
  var color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
  var r = parseInt(color.substring(0, 2), 16); // hexToR
  var g = parseInt(color.substring(2, 4), 16); // hexToG
  var b = parseInt(color.substring(4, 6), 16); // hexToB
  var uicolors = [r / 255, g / 255, b / 255];
  var c = uicolors.map(col => {
    if (col <= 0.03928) {
      return col / 12.92;
    }
    return Math.pow((col + 0.055) / 1.055, 2.4);
  });
  var L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return L > 0.179 ? darkColor : lightColor;
};
const nameToHex = color => {
  return [...Object.keys(PresentationColorPalette), 'white', 'black', 'no-data', 'error', 'warning', 'success', 'error-dark', 'warning-dark', 'success-dark'].includes(color) ? {
    green: RedsiftColorPresentationGreenLight,
    pink: RedsiftColorPresentationPinkLight,
    aqua: RedsiftColorPresentationAquaLight,
    brown: RedsiftColorPresentationBrownLight,
    red: RedsiftColorPresentationRedLight,
    yellow: RedsiftColorPresentationYellowLight,
    purple: RedsiftColorPresentationPurpleLight,
    orange: RedsiftColorPresentationOrangeLight,
    blue: RedsiftColorPresentationBlueLight,
    grey: RedsiftColorPresentationGreyLight,
    white: RedsiftColorNeutralWhite,
    black: RedsiftColorNeutralBlack,
    'no-data': RedsiftColorNeutralMidgrey,
    error: RedsiftColorPresentationRedLighter,
    warning: RedsiftColorPresentationOrangeLighter,
    success: RedsiftColorPresentationGreenLighter,
    'error-dark': RedsiftColorPresentationRedDefault,
    'warning-dark': RedsiftColorPresentationOrangeDefault,
    'success-dark': RedsiftColorPresentationGreenDefault
  }[color] : color;
};

/**
 * Component style.
 */
const StyledPill = styled.div`
  align-items: stretch;
  display: inline-flex;
  gap: 4px;
  width: fit-content;
  ${baseStyling}
  
  border-radius: 2px;
  padding: 2px 8px;
  text-align: center;

  .redsift-pill-content {
    ${_ref => {
  let {
    $autoBreak
  } = _ref;
  return !$autoBreak ? css`
            text-wrap: nowrap;
          ` : css`
            word-break: break-word;
          `;
}};

    align-items: center;
    display: inline-flex;
    gap: 4px;
    width: fit-content;
  }

  ${_ref2 => {
  let {
    $color,
    $hoverColor,
    $size
  } = _ref2;
  return css`
    ${$color === 'black' ? 'background-color: var(--redsift-color-neutral-black);' : $color === 'white' ? css`
          background-color: var(--redsift-color-neutral-white);
          border: 1px solid #d6d6d6;
        ` : $color === 'no-data' ? 'background-color: var(--redsift-color-neutral-midgrey);' : $color === 'error' ? 'background-color: var(--redsift-color-presentation-red-lighter);' : $color === 'warning' ? 'background-color: var(--redsift-color-presentation-orange-lighter);' : $color === 'success' ? 'background-color: var(--redsift-color-presentation-green-lighter);' : $color === 'error-dark' ? 'background-color: var(--redsift-color-presentation-red-default);' : $color === 'warning-dark' ? 'background-color: var(--redsift-color-presentation-orange-default);' : $color === 'success-dark' ? 'background-color: var(--redsift-color-presentation-green-default);' : Object.keys(PresentationColorPalette).includes($color) ? `background-color: var(--redsift-color-presentation-${$color}-light);` : `background-color: ${$color};`}

    &,
    .redsift-icon {
      color: ${pickTextColorBasedOnBgColorAdvanced(nameToHex($color), RedsiftColorNeutralWhite, RedsiftColorNeutralBlack)};
    }

    button {
      cursor: pointer;
      background: none;
      border: none;
      border-radius: 0 2px 2px 0;
      margin: -2px -8px -2px 2px;
      border-left: 1px solid ${$color === 'white' ? '#d6d6d6' : css`var(--redsift-color-neutral-white)`};
      font-size: ${$size === PillSize.large ? css`15px` : css`14px`};
    }

    button:hover {
      ${$color === 'black' ? 'background-color: var(--redsift-color-neutral-darkgrey);' : $color === 'white' ? 'background-color: var(--redsift-color-neutral-xlightgrey);' : $color === 'no-data' ? 'background-color: var(--redsift-color-neutral-darkgrey);' : $color === 'error' ? 'background-color: var(--redsift-color-presentation-red-light);' : $color === 'warning' ? 'background-color: var(--redsift-color-presentation-orange-light);' : $color === 'success' ? 'background-color: var(--redsift-color-presentation-green-light);' : $color === 'error-dark' ? 'background-color: var(--redsift-color-presentation-red-dark);' : $color === 'warning-dark' ? 'background-color: var(--redsift-color-presentation-orange-dark);' : $color === 'success-dark' ? 'background-color: var(--redsift-color-presentation-green-dark);' : Object.keys(PresentationColorPalette).includes($color) ? `background-color: var(--redsift-color-presentation-${$color}-default);` : ''}
      background-color: ${$hoverColor};
    }
  `;
}}}

  &,
  .redsift-pill-content {
    &,
    .redsift-icon,
    .redsift-text,
    .redsift-number {
      font-family: var(--redsift-typography-pill-font-family);
      font-size: ${_ref3 => {
  let {
    $size
  } = _ref3;
  return $size === PillSize.large ? css`var(--redsift-typography-chip-font-size)` : css`var(--redsift-typography-pill-font-size)`;
}}};
      font-weight: var(--redsift-typography-pill-font-weight);
      line-height: ${_ref4 => {
  let {
    $size
  } = _ref4;
  return $size === PillSize.large ? css`var(--redsift-typography-chip-line-height)` : css`var(--redsift-typography-pill-line-height)`;
}}};
    }

    .redsift-icon {
      width: unset;
      height: unset;

      > svg {
        width: 1em;
        height: 1em;
      }
    }
  }
`;

const _excluded$h = ["autoBreak", "children", "className", "color", "hoverColor", "rightButton", "size"];
const COMPONENT_NAME$i = 'Pill';
const CLASSNAME$i = 'redsift-pill';
const DEFAULT_PROPS$i = {
  color: PresentationColorPalette.pink,
  height: 'fit-content',
  size: PillSize.large
};

/**
 * The Pill component.
 */
const Pill = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      autoBreak,
      children,
      className,
      color,
      hoverColor,
      rightButton,
      size
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$h);
  return /*#__PURE__*/React__default.createElement(StyledPill, _extends$1({}, forwardedProps, {
    $autoBreak: autoBreak,
    $color: color,
    $hoverColor: hoverColor,
    $size: size,
    className: classNames(Pill.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${Pill.className}-content`
  }, children), rightButton ? /*#__PURE__*/React__default.createElement("button", _extends$1({
    "aria-label": "Clear",
    color: "question"
  }, rightButton, {
    className: "right clear"
  }), /*#__PURE__*/React__default.createElement(Icon, {
    icon: mdiClose$1
  })) : null);
});
Pill.className = CLASSNAME$i;
Pill.defaultProps = DEFAULT_PROPS$i;
Pill.displayName = COMPONENT_NAME$i;

/**
 * Component style.
 */
const StyledDetailedCardSectionItem = styled.div`
  padding: 8px 0;

  .redsift-detailed-card-section-item-header__shield {
    align-items: center;
    display: flex;
    height: 28px;
    justify-content: center;
    padding: 0 4px;
  }

  .redsift-detailed-card-section-item-header__icon {
    height: 28px;
  }

  .redsift-detailed-card-section-item-header__pill {
    display: inline-block;
    margin-right: 8px;
  }

  .redsift-detailed-card-section-item-header__description {
    line-height: 28px;
    display: inline-flex;
    align-items: center;
  }
`;

const _excluded$g = ["children", "className", "description", "icon", "iconProps", "isLoading", "pill", "pillProps", "shield"];
const COMPONENT_NAME$h = 'DetailedCardSectionItem';
const CLASSNAME$h = 'redsift-detailed-card-section-item';
const DEFAULT_PROPS$h = {};

/**
 * The DetailedCardSectionItems component.
 */
const DetailedCardSectionItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      description,
      icon,
      iconProps,
      isLoading,
      pill,
      pillProps,
      shield
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$g);
  return /*#__PURE__*/React__default.createElement(StyledDetailedCardSectionItem, _extends$1({}, forwardedProps, {
    className: classNames(DetailedCardSectionItem.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement(Flexbox, {
    alignItems: "flex-start",
    className: `${DetailedCardSectionItem.className}__header`
  }, shield ? /*#__PURE__*/React__default.createElement(Skeleton, {
    isLoaded: !isLoading
  }, /*#__PURE__*/React__default.createElement(Shield, {
    variant: shield,
    className: `${DetailedCardSectionItem.className}-header__shield`
  })) : icon ? /*#__PURE__*/React__default.createElement(Skeleton, {
    isLoaded: !isLoading
  }, /*#__PURE__*/React__default.createElement("div", {
    className: `${DetailedCardSectionItem.className}-header__icon`
  }, /*#__PURE__*/React__default.createElement(Icon, _extends$1({
    icon: icon
  }, iconProps, {
    size: "large"
  })))) : null, description || pill ? /*#__PURE__*/React__default.createElement("div", {
    className: `${DetailedCardSectionItem.className}-header__description`
  }, /*#__PURE__*/React__default.createElement(Skeleton, {
    isLoaded: !isLoading
  }, pill && /*#__PURE__*/React__default.createElement(Pill, _extends$1({
    className: `${DetailedCardSectionItem.className}-header__pill`
  }, pillProps), pill), typeof description === 'string' ? /*#__PURE__*/React__default.createElement(Text, null, description) : description)) : null), children);
});
DetailedCardSectionItem.className = CLASSNAME$h;
DetailedCardSectionItem.defaultProps = DEFAULT_PROPS$h;
DetailedCardSectionItem.displayName = COMPONENT_NAME$h;

/*!
* tabbable 6.1.2
* @license MIT, https://github.com/focus-trap/tabbable/blob/master/LICENSE
*/
// NOTE: separate `:not()` selectors has broader browser support than the newer
//  `:not([inert], [inert] *)` (Feb 2023)
// CAREFUL: JSDom does not support `:not([inert] *)` as a selector; using it causes
//  the entire query to fail, resulting in no nodes found, which will break a lot
//  of things... so we have to rely on JS to identify nodes inside an inert container
var candidateSelectors = ['input:not([inert])', 'select:not([inert])', 'textarea:not([inert])', 'a[href]:not([inert])', 'button:not([inert])', '[tabindex]:not(slot):not([inert])', 'audio[controls]:not([inert])', 'video[controls]:not([inert])', '[contenteditable]:not([contenteditable="false"]):not([inert])', 'details>summary:first-of-type:not([inert])', 'details:not([inert])'];
var candidateSelector = /* #__PURE__ */candidateSelectors.join(',');
var NoElement = typeof Element === 'undefined';
var matches = NoElement ? function () {} : Element.prototype.matches || Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
var getRootNode = !NoElement && Element.prototype.getRootNode ? function (element) {
  var _element$getRootNode;
  return element === null || element === void 0 ? void 0 : (_element$getRootNode = element.getRootNode) === null || _element$getRootNode === void 0 ? void 0 : _element$getRootNode.call(element);
} : function (element) {
  return element === null || element === void 0 ? void 0 : element.ownerDocument;
};

/**
 * Determines if a node is inert or in an inert ancestor.
 * @param {Element} [node]
 * @param {boolean} [lookUp] If true and `node` is not inert, looks up at ancestors to
 *  see if any of them are inert. If false, only `node` itself is considered.
 * @returns {boolean} True if inert itself or by way of being in an inert ancestor.
 *  False if `node` is falsy.
 */
var isInert = function isInert(node, lookUp) {
  var _node$getAttribute;
  if (lookUp === void 0) {
    lookUp = true;
  }
  // CAREFUL: JSDom does not support inert at all, so we can't use the `HTMLElement.inert`
  //  JS API property; we have to check the attribute, which can either be empty or 'true';
  //  if it's `null` (not specified) or 'false', it's an active element
  var inertAtt = node === null || node === void 0 ? void 0 : (_node$getAttribute = node.getAttribute) === null || _node$getAttribute === void 0 ? void 0 : _node$getAttribute.call(node, 'inert');
  var inert = inertAtt === '' || inertAtt === 'true';

  // NOTE: this could also be handled with `node.matches('[inert], :is([inert] *)')`
  //  if it weren't for `matches()` not being a function on shadow roots; the following
  //  code works for any kind of node
  // CAREFUL: JSDom does not appear to support certain selectors like `:not([inert] *)`
  //  so it likely would not support `:is([inert] *)` either...
  var result = inert || lookUp && node && isInert(node.parentNode); // recursive

  return result;
};

/**
 * Determines if a node's content is editable.
 * @param {Element} [node]
 * @returns True if it's content-editable; false if it's not or `node` is falsy.
 */
var isContentEditable = function isContentEditable(node) {
  var _node$getAttribute2;
  // CAREFUL: JSDom does not support the `HTMLElement.isContentEditable` API so we have
  //  to use the attribute directly to check for this, which can either be empty or 'true';
  //  if it's `null` (not specified) or 'false', it's a non-editable element
  var attValue = node === null || node === void 0 ? void 0 : (_node$getAttribute2 = node.getAttribute) === null || _node$getAttribute2 === void 0 ? void 0 : _node$getAttribute2.call(node, 'contenteditable');
  return attValue === '' || attValue === 'true';
};

/**
 * @param {Element} el container to check in
 * @param {boolean} includeContainer add container to check
 * @param {(node: Element) => boolean} filter filter candidates
 * @returns {Element[]}
 */
var getCandidates = function getCandidates(el, includeContainer, filter) {
  // even if `includeContainer=false`, we still have to check it for inertness because
  //  if it's inert, all its children are inert
  if (isInert(el)) {
    return [];
  }
  var candidates = Array.prototype.slice.apply(el.querySelectorAll(candidateSelector));
  if (includeContainer && matches.call(el, candidateSelector)) {
    candidates.unshift(el);
  }
  candidates = candidates.filter(filter);
  return candidates;
};

/**
 * @callback GetShadowRoot
 * @param {Element} element to check for shadow root
 * @returns {ShadowRoot|boolean} ShadowRoot if available or boolean indicating if a shadowRoot is attached but not available.
 */

/**
 * @callback ShadowRootFilter
 * @param {Element} shadowHostNode the element which contains shadow content
 * @returns {boolean} true if a shadow root could potentially contain valid candidates.
 */

/**
 * @typedef {Object} CandidateScope
 * @property {Element} scopeParent contains inner candidates
 * @property {Element[]} candidates list of candidates found in the scope parent
 */

/**
 * @typedef {Object} IterativeOptions
 * @property {GetShadowRoot|boolean} getShadowRoot true if shadow support is enabled; falsy if not;
 *  if a function, implies shadow support is enabled and either returns the shadow root of an element
 *  or a boolean stating if it has an undisclosed shadow root
 * @property {(node: Element) => boolean} filter filter candidates
 * @property {boolean} flatten if true then result will flatten any CandidateScope into the returned list
 * @property {ShadowRootFilter} shadowRootFilter filter shadow roots;
 */

/**
 * @param {Element[]} elements list of element containers to match candidates from
 * @param {boolean} includeContainer add container list to check
 * @param {IterativeOptions} options
 * @returns {Array.<Element|CandidateScope>}
 */
var getCandidatesIteratively = function getCandidatesIteratively(elements, includeContainer, options) {
  var candidates = [];
  var elementsToCheck = Array.from(elements);
  while (elementsToCheck.length) {
    var element = elementsToCheck.shift();
    if (isInert(element, false)) {
      // no need to look up since we're drilling down
      // anything inside this container will also be inert
      continue;
    }
    if (element.tagName === 'SLOT') {
      // add shadow dom slot scope (slot itself cannot be focusable)
      var assigned = element.assignedElements();
      var content = assigned.length ? assigned : element.children;
      var nestedCandidates = getCandidatesIteratively(content, true, options);
      if (options.flatten) {
        candidates.push.apply(candidates, nestedCandidates);
      } else {
        candidates.push({
          scopeParent: element,
          candidates: nestedCandidates
        });
      }
    } else {
      // check candidate element
      var validCandidate = matches.call(element, candidateSelector);
      if (validCandidate && options.filter(element) && (includeContainer || !elements.includes(element))) {
        candidates.push(element);
      }

      // iterate over shadow content if possible
      var shadowRoot = element.shadowRoot ||
      // check for an undisclosed shadow
      typeof options.getShadowRoot === 'function' && options.getShadowRoot(element);

      // no inert look up because we're already drilling down and checking for inertness
      //  on the way down, so all containers to this root node should have already been
      //  vetted as non-inert
      var validShadowRoot = !isInert(shadowRoot, false) && (!options.shadowRootFilter || options.shadowRootFilter(element));
      if (shadowRoot && validShadowRoot) {
        // add shadow dom scope IIF a shadow root node was given; otherwise, an undisclosed
        //  shadow exists, so look at light dom children as fallback BUT create a scope for any
        //  child candidates found because they're likely slotted elements (elements that are
        //  children of the web component element (which has the shadow), in the light dom, but
        //  slotted somewhere _inside_ the undisclosed shadow) -- the scope is created below,
        //  _after_ we return from this recursive call
        var _nestedCandidates = getCandidatesIteratively(shadowRoot === true ? element.children : shadowRoot.children, true, options);
        if (options.flatten) {
          candidates.push.apply(candidates, _nestedCandidates);
        } else {
          candidates.push({
            scopeParent: element,
            candidates: _nestedCandidates
          });
        }
      } else {
        // there's not shadow so just dig into the element's (light dom) children
        //  __without__ giving the element special scope treatment
        elementsToCheck.unshift.apply(elementsToCheck, element.children);
      }
    }
  }
  return candidates;
};
var getTabindex = function getTabindex(node, isScope) {
  if (node.tabIndex < 0) {
    // in Chrome, <details/>, <audio controls/> and <video controls/> elements get a default
    // `tabIndex` of -1 when the 'tabindex' attribute isn't specified in the DOM,
    // yet they are still part of the regular tab order; in FF, they get a default
    // `tabIndex` of 0; since Chrome still puts those elements in the regular tab
    // order, consider their tab index to be 0.
    // Also browsers do not return `tabIndex` correctly for contentEditable nodes;
    // so if they don't have a tabindex attribute specifically set, assume it's 0.
    //
    // isScope is positive for custom element with shadow root or slot that by default
    // have tabIndex -1, but need to be sorted by document order in order for their
    // content to be inserted in the correct position
    if ((isScope || /^(AUDIO|VIDEO|DETAILS)$/.test(node.tagName) || isContentEditable(node)) && isNaN(parseInt(node.getAttribute('tabindex'), 10))) {
      return 0;
    }
  }
  return node.tabIndex;
};
var sortOrderedTabbables = function sortOrderedTabbables(a, b) {
  return a.tabIndex === b.tabIndex ? a.documentOrder - b.documentOrder : a.tabIndex - b.tabIndex;
};
var isInput = function isInput(node) {
  return node.tagName === 'INPUT';
};
var isHiddenInput = function isHiddenInput(node) {
  return isInput(node) && node.type === 'hidden';
};
var isDetailsWithSummary = function isDetailsWithSummary(node) {
  var r = node.tagName === 'DETAILS' && Array.prototype.slice.apply(node.children).some(function (child) {
    return child.tagName === 'SUMMARY';
  });
  return r;
};
var getCheckedRadio = function getCheckedRadio(nodes, form) {
  for (var i = 0; i < nodes.length; i++) {
    if (nodes[i].checked && nodes[i].form === form) {
      return nodes[i];
    }
  }
};
var isTabbableRadio = function isTabbableRadio(node) {
  if (!node.name) {
    return true;
  }
  var radioScope = node.form || getRootNode(node);
  var queryRadios = function queryRadios(name) {
    return radioScope.querySelectorAll('input[type="radio"][name="' + name + '"]');
  };
  var radioSet;
  if (typeof window !== 'undefined' && typeof window.CSS !== 'undefined' && typeof window.CSS.escape === 'function') {
    radioSet = queryRadios(window.CSS.escape(node.name));
  } else {
    try {
      radioSet = queryRadios(node.name);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Looks like you have a radio button with a name attribute containing invalid CSS selector characters and need the CSS.escape polyfill: %s', err.message);
      return false;
    }
  }
  var checked = getCheckedRadio(radioSet, node.form);
  return !checked || checked === node;
};
var isRadio = function isRadio(node) {
  return isInput(node) && node.type === 'radio';
};
var isNonTabbableRadio = function isNonTabbableRadio(node) {
  return isRadio(node) && !isTabbableRadio(node);
};

// determines if a node is ultimately attached to the window's document
var isNodeAttached = function isNodeAttached(node) {
  var _nodeRoot;
  // The root node is the shadow root if the node is in a shadow DOM; some document otherwise
  //  (but NOT _the_ document; see second 'If' comment below for more).
  // If rootNode is shadow root, it'll have a host, which is the element to which the shadow
  //  is attached, and the one we need to check if it's in the document or not (because the
  //  shadow, and all nodes it contains, is never considered in the document since shadows
  //  behave like self-contained DOMs; but if the shadow's HOST, which is part of the document,
  //  is hidden, or is not in the document itself but is detached, it will affect the shadow's
  //  visibility, including all the nodes it contains). The host could be any normal node,
  //  or a custom element (i.e. web component). Either way, that's the one that is considered
  //  part of the document, not the shadow root, nor any of its children (i.e. the node being
  //  tested).
  // To further complicate things, we have to look all the way up until we find a shadow HOST
  //  that is attached (or find none) because the node might be in nested shadows...
  // If rootNode is not a shadow root, it won't have a host, and so rootNode should be the
  //  document (per the docs) and while it's a Document-type object, that document does not
  //  appear to be the same as the node's `ownerDocument` for some reason, so it's safer
  //  to ignore the rootNode at this point, and use `node.ownerDocument`. Otherwise,
  //  using `rootNode.contains(node)` will _always_ be true we'll get false-positives when
  //  node is actually detached.
  // NOTE: If `nodeRootHost` or `node` happens to be the `document` itself (which is possible
  //  if a tabbable/focusable node was quickly added to the DOM, focused, and then removed
  //  from the DOM as in https://github.com/focus-trap/focus-trap-react/issues/905), then
  //  `ownerDocument` will be `null`, hence the optional chaining on it.
  var nodeRoot = node && getRootNode(node);
  var nodeRootHost = (_nodeRoot = nodeRoot) === null || _nodeRoot === void 0 ? void 0 : _nodeRoot.host;

  // in some cases, a detached node will return itself as the root instead of a document or
  //  shadow root object, in which case, we shouldn't try to look further up the host chain
  var attached = false;
  if (nodeRoot && nodeRoot !== node) {
    var _nodeRootHost, _nodeRootHost$ownerDo, _node$ownerDocument;
    attached = !!((_nodeRootHost = nodeRootHost) !== null && _nodeRootHost !== void 0 && (_nodeRootHost$ownerDo = _nodeRootHost.ownerDocument) !== null && _nodeRootHost$ownerDo !== void 0 && _nodeRootHost$ownerDo.contains(nodeRootHost) || node !== null && node !== void 0 && (_node$ownerDocument = node.ownerDocument) !== null && _node$ownerDocument !== void 0 && _node$ownerDocument.contains(node));
    while (!attached && nodeRootHost) {
      var _nodeRoot2, _nodeRootHost2, _nodeRootHost2$ownerD;
      // since it's not attached and we have a root host, the node MUST be in a nested shadow DOM,
      //  which means we need to get the host's host and check if that parent host is contained
      //  in (i.e. attached to) the document
      nodeRoot = getRootNode(nodeRootHost);
      nodeRootHost = (_nodeRoot2 = nodeRoot) === null || _nodeRoot2 === void 0 ? void 0 : _nodeRoot2.host;
      attached = !!((_nodeRootHost2 = nodeRootHost) !== null && _nodeRootHost2 !== void 0 && (_nodeRootHost2$ownerD = _nodeRootHost2.ownerDocument) !== null && _nodeRootHost2$ownerD !== void 0 && _nodeRootHost2$ownerD.contains(nodeRootHost));
    }
  }
  return attached;
};
var isZeroArea = function isZeroArea(node) {
  var _node$getBoundingClie = node.getBoundingClientRect(),
    width = _node$getBoundingClie.width,
    height = _node$getBoundingClie.height;
  return width === 0 && height === 0;
};
var isHidden = function isHidden(node, _ref) {
  var displayCheck = _ref.displayCheck,
    getShadowRoot = _ref.getShadowRoot;
  // NOTE: visibility will be `undefined` if node is detached from the document
  //  (see notes about this further down), which means we will consider it visible
  //  (this is legacy behavior from a very long way back)
  // NOTE: we check this regardless of `displayCheck="none"` because this is a
  //  _visibility_ check, not a _display_ check
  if (getComputedStyle(node).visibility === 'hidden') {
    return true;
  }
  var isDirectSummary = matches.call(node, 'details>summary:first-of-type');
  var nodeUnderDetails = isDirectSummary ? node.parentElement : node;
  if (matches.call(nodeUnderDetails, 'details:not([open]) *')) {
    return true;
  }
  if (!displayCheck || displayCheck === 'full' || displayCheck === 'legacy-full') {
    if (typeof getShadowRoot === 'function') {
      // figure out if we should consider the node to be in an undisclosed shadow and use the
      //  'non-zero-area' fallback
      var originalNode = node;
      while (node) {
        var parentElement = node.parentElement;
        var rootNode = getRootNode(node);
        if (parentElement && !parentElement.shadowRoot && getShadowRoot(parentElement) === true // check if there's an undisclosed shadow
        ) {
          // node has an undisclosed shadow which means we can only treat it as a black box, so we
          //  fall back to a non-zero-area test
          return isZeroArea(node);
        } else if (node.assignedSlot) {
          // iterate up slot
          node = node.assignedSlot;
        } else if (!parentElement && rootNode !== node.ownerDocument) {
          // cross shadow boundary
          node = rootNode.host;
        } else {
          // iterate up normal dom
          node = parentElement;
        }
      }
      node = originalNode;
    }
    // else, `getShadowRoot` might be true, but all that does is enable shadow DOM support
    //  (i.e. it does not also presume that all nodes might have undisclosed shadows); or
    //  it might be a falsy value, which means shadow DOM support is disabled

    // Since we didn't find it sitting in an undisclosed shadow (or shadows are disabled)
    //  now we can just test to see if it would normally be visible or not, provided it's
    //  attached to the main document.
    // NOTE: We must consider case where node is inside a shadow DOM and given directly to
    //  `isTabbable()` or `isFocusable()` -- regardless of `getShadowRoot` option setting.

    if (isNodeAttached(node)) {
      // this works wherever the node is: if there's at least one client rect, it's
      //  somehow displayed; it also covers the CSS 'display: contents' case where the
      //  node itself is hidden in place of its contents; and there's no need to search
      //  up the hierarchy either
      return !node.getClientRects().length;
    }

    // Else, the node isn't attached to the document, which means the `getClientRects()`
    //  API will __always__ return zero rects (this can happen, for example, if React
    //  is used to render nodes onto a detached tree, as confirmed in this thread:
    //  https://github.com/facebook/react/issues/9117#issuecomment-284228870)
    //
    // It also means that even window.getComputedStyle(node).display will return `undefined`
    //  because styles are only computed for nodes that are in the document.
    //
    // NOTE: THIS HAS BEEN THE CASE FOR YEARS. It is not new, nor is it caused by tabbable
    //  somehow. Though it was never stated officially, anyone who has ever used tabbable
    //  APIs on nodes in detached containers has actually implicitly used tabbable in what
    //  was later (as of v5.2.0 on Apr 9, 2021) called `displayCheck="none"` mode -- essentially
    //  considering __everything__ to be visible because of the innability to determine styles.
    //
    // v6.0.0: As of this major release, the default 'full' option __no longer treats detached
    //  nodes as visible with the 'none' fallback.__
    if (displayCheck !== 'legacy-full') {
      return true; // hidden
    }
    // else, fallback to 'none' mode and consider the node visible
  } else if (displayCheck === 'non-zero-area') {
    // NOTE: Even though this tests that the node's client rect is non-zero to determine
    //  whether it's displayed, and that a detached node will __always__ have a zero-area
    //  client rect, we don't special-case for whether the node is attached or not. In
    //  this mode, we do want to consider nodes that have a zero area to be hidden at all
    //  times, and that includes attached or not.
    return isZeroArea(node);
  }

  // visible, as far as we can tell, or per current `displayCheck=none` mode, we assume
  //  it's visible
  return false;
};

// form fields (nested) inside a disabled fieldset are not focusable/tabbable
//  unless they are in the _first_ <legend> element of the top-most disabled
//  fieldset
var isDisabledFromFieldset = function isDisabledFromFieldset(node) {
  if (/^(INPUT|BUTTON|SELECT|TEXTAREA)$/.test(node.tagName)) {
    var parentNode = node.parentElement;
    // check if `node` is contained in a disabled <fieldset>
    while (parentNode) {
      if (parentNode.tagName === 'FIELDSET' && parentNode.disabled) {
        // look for the first <legend> among the children of the disabled <fieldset>
        for (var i = 0; i < parentNode.children.length; i++) {
          var child = parentNode.children.item(i);
          // when the first <legend> (in document order) is found
          if (child.tagName === 'LEGEND') {
            // if its parent <fieldset> is not nested in another disabled <fieldset>,
            // return whether `node` is a descendant of its first <legend>
            return matches.call(parentNode, 'fieldset[disabled] *') ? true : !child.contains(node);
          }
        }
        // the disabled <fieldset> containing `node` has no <legend>
        return true;
      }
      parentNode = parentNode.parentElement;
    }
  }

  // else, node's tabbable/focusable state should not be affected by a fieldset's
  //  enabled/disabled state
  return false;
};
var isNodeMatchingSelectorFocusable = function isNodeMatchingSelectorFocusable(options, node) {
  if (node.disabled ||
  // we must do an inert look up to filter out any elements inside an inert ancestor
  //  because we're limited in the type of selectors we can use in JSDom (see related
  //  note related to `candidateSelectors`)
  isInert(node) || isHiddenInput(node) || isHidden(node, options) ||
  // For a details element with a summary, the summary element gets the focus
  isDetailsWithSummary(node) || isDisabledFromFieldset(node)) {
    return false;
  }
  return true;
};
var isNodeMatchingSelectorTabbable = function isNodeMatchingSelectorTabbable(options, node) {
  if (isNonTabbableRadio(node) || getTabindex(node) < 0 || !isNodeMatchingSelectorFocusable(options, node)) {
    return false;
  }
  return true;
};
var isValidShadowRootTabbable = function isValidShadowRootTabbable(shadowHostNode) {
  var tabIndex = parseInt(shadowHostNode.getAttribute('tabindex'), 10);
  if (isNaN(tabIndex) || tabIndex >= 0) {
    return true;
  }
  // If a custom element has an explicit negative tabindex,
  // browsers will not allow tab targeting said element's children.
  return false;
};

/**
 * @param {Array.<Element|CandidateScope>} candidates
 * @returns Element[]
 */
var sortByOrder = function sortByOrder(candidates) {
  var regularTabbables = [];
  var orderedTabbables = [];
  candidates.forEach(function (item, i) {
    var isScope = !!item.scopeParent;
    var element = isScope ? item.scopeParent : item;
    var candidateTabindex = getTabindex(element, isScope);
    var elements = isScope ? sortByOrder(item.candidates) : element;
    if (candidateTabindex === 0) {
      isScope ? regularTabbables.push.apply(regularTabbables, elements) : regularTabbables.push(element);
    } else {
      orderedTabbables.push({
        documentOrder: i,
        tabIndex: candidateTabindex,
        item: item,
        isScope: isScope,
        content: elements
      });
    }
  });
  return orderedTabbables.sort(sortOrderedTabbables).reduce(function (acc, sortable) {
    sortable.isScope ? acc.push.apply(acc, sortable.content) : acc.push(sortable.content);
    return acc;
  }, []).concat(regularTabbables);
};
var tabbable = function tabbable(el, options) {
  options = options || {};
  var candidates;
  if (options.getShadowRoot) {
    candidates = getCandidatesIteratively([el], options.includeContainer, {
      filter: isNodeMatchingSelectorTabbable.bind(null, options),
      flatten: false,
      getShadowRoot: options.getShadowRoot,
      shadowRootFilter: isValidShadowRootTabbable
    });
  } else {
    candidates = getCandidates(el, options.includeContainer, isNodeMatchingSelectorTabbable.bind(null, options));
  }
  return sortByOrder(candidates);
};

function getAlignment(placement) {
  return placement.split('-')[1];
}

function getLengthFromAxis(axis) {
  return axis === 'y' ? 'height' : 'width';
}

function getSide(placement) {
  return placement.split('-')[0];
}

function getMainAxisFromPlacement(placement) {
  return ['top', 'bottom'].includes(getSide(placement)) ? 'x' : 'y';
}

function computeCoordsFromPlacement(_ref, placement, rtl) {
  let {
    reference,
    floating
  } = _ref;
  const commonX = reference.x + reference.width / 2 - floating.width / 2;
  const commonY = reference.y + reference.height / 2 - floating.height / 2;
  const mainAxis = getMainAxisFromPlacement(placement);
  const length = getLengthFromAxis(mainAxis);
  const commonAlign = reference[length] / 2 - floating[length] / 2;
  const side = getSide(placement);
  const isVertical = mainAxis === 'x';
  let coords;
  switch (side) {
    case 'top':
      coords = {
        x: commonX,
        y: reference.y - floating.height
      };
      break;
    case 'bottom':
      coords = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case 'right':
      coords = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case 'left':
      coords = {
        x: reference.x - floating.width,
        y: commonY
      };
      break;
    default:
      coords = {
        x: reference.x,
        y: reference.y
      };
  }
  switch (getAlignment(placement)) {
    case 'start':
      coords[mainAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
      break;
    case 'end':
      coords[mainAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
      break;
  }
  return coords;
}

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a reference element when it is given a certain positioning strategy.
 *
 * This export does not have any `platform` interface logic. You will need to
 * write one for the platform you are using Floating UI with.
 */
const computePosition$1 = async (reference, floating, config) => {
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform
  } = config;
  const validMiddleware = middleware.filter(Boolean);
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(floating));
  let rects = await platform.getElementRects({
    reference,
    floating,
    strategy
  });
  let {
    x,
    y
  } = computeCoordsFromPlacement(rects, placement, rtl);
  let statefulPlacement = placement;
  let middlewareData = {};
  let resetCount = 0;
  for (let i = 0; i < validMiddleware.length; i++) {
    const {
      name,
      fn
    } = validMiddleware[i];
    const {
      x: nextX,
      y: nextY,
      data,
      reset
    } = await fn({
      x,
      y,
      initialPlacement: placement,
      placement: statefulPlacement,
      strategy,
      middlewareData,
      rects,
      platform,
      elements: {
        reference,
        floating
      }
    });
    x = nextX != null ? nextX : x;
    y = nextY != null ? nextY : y;
    middlewareData = {
      ...middlewareData,
      [name]: {
        ...middlewareData[name],
        ...data
      }
    };
    if (reset && resetCount <= 50) {
      resetCount++;
      if (typeof reset === 'object') {
        if (reset.placement) {
          statefulPlacement = reset.placement;
        }
        if (reset.rects) {
          rects = reset.rects === true ? await platform.getElementRects({
            reference,
            floating,
            strategy
          }) : reset.rects;
        }
        ({
          x,
          y
        } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
      }
      i = -1;
      continue;
    }
  }
  return {
    x,
    y,
    placement: statefulPlacement,
    strategy,
    middlewareData
  };
};

function evaluate(value, param) {
  return typeof value === 'function' ? value(param) : value;
}

function expandPaddingObject(padding) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...padding
  };
}

function getSideObjectFromPadding(padding) {
  return typeof padding !== 'number' ? expandPaddingObject(padding) : {
    top: padding,
    right: padding,
    bottom: padding,
    left: padding
  };
}

function rectToClientRect(rect) {
  return {
    ...rect,
    top: rect.y,
    left: rect.x,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  };
}

/**
 * Resolves with an object of overflow side offsets that determine how much the
 * element is overflowing a given clipping boundary on each side.
 * - positive = overflowing the boundary by that number of pixels
 * - negative = how many pixels left before it will overflow
 * - 0 = lies flush with the boundary
 * @see https://floating-ui.com/docs/detectOverflow
 */
async function detectOverflow(state, options) {
  var _await$platform$isEle;
  if (options === void 0) {
    options = {};
  }
  const {
    x,
    y,
    platform,
    rects,
    elements,
    strategy
  } = state;
  const {
    boundary = 'clippingAncestors',
    rootBoundary = 'viewport',
    elementContext = 'floating',
    altBoundary = false,
    padding = 0
  } = evaluate(options, state);
  const paddingObject = getSideObjectFromPadding(padding);
  const altContext = elementContext === 'floating' ? 'reference' : 'floating';
  const element = elements[altBoundary ? altContext : elementContext];
  const clippingClientRect = rectToClientRect(await platform.getClippingRect({
    element: ((_await$platform$isEle = await (platform.isElement == null ? void 0 : platform.isElement(element))) != null ? _await$platform$isEle : true) ? element : element.contextElement || (await (platform.getDocumentElement == null ? void 0 : platform.getDocumentElement(elements.floating))),
    boundary,
    rootBoundary,
    strategy
  }));
  const rect = elementContext === 'floating' ? {
    ...rects.floating,
    x,
    y
  } : rects.reference;
  const offsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(elements.floating));
  const offsetScale = (await (platform.isElement == null ? void 0 : platform.isElement(offsetParent))) ? (await (platform.getScale == null ? void 0 : platform.getScale(offsetParent))) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  };
  const elementClientRect = rectToClientRect(platform.convertOffsetParentRelativeRectToViewportRelativeRect ? await platform.convertOffsetParentRelativeRectToViewportRelativeRect({
    rect,
    offsetParent,
    strategy
  }) : rect);
  return {
    top: (clippingClientRect.top - elementClientRect.top + paddingObject.top) / offsetScale.y,
    bottom: (elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom) / offsetScale.y,
    left: (clippingClientRect.left - elementClientRect.left + paddingObject.left) / offsetScale.x,
    right: (elementClientRect.right - clippingClientRect.right + paddingObject.right) / offsetScale.x
  };
}

const min$1 = Math.min;
const max$1 = Math.max;

function within(min$1$1, value, max$1$1) {
  return max$1(min$1$1, min$1(value, max$1$1));
}

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow$1 = options => ({
  name: 'arrow',
  options,
  async fn(state) {
    const {
      x,
      y,
      placement,
      rects,
      platform,
      elements
    } = state;
    // Since `element` is required, we don't Partial<> the type.
    const {
      element,
      padding = 0
    } = evaluate(options, state) || {};
    if (element == null) {
      return {};
    }
    const paddingObject = getSideObjectFromPadding(padding);
    const coords = {
      x,
      y
    };
    const axis = getMainAxisFromPlacement(placement);
    const length = getLengthFromAxis(axis);
    const arrowDimensions = await platform.getDimensions(element);
    const isYAxis = axis === 'y';
    const minProp = isYAxis ? 'top' : 'left';
    const maxProp = isYAxis ? 'bottom' : 'right';
    const clientProp = isYAxis ? 'clientHeight' : 'clientWidth';
    const endDiff = rects.reference[length] + rects.reference[axis] - coords[axis] - rects.floating[length];
    const startDiff = coords[axis] - rects.reference[axis];
    const arrowOffsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(element));
    let clientSize = arrowOffsetParent ? arrowOffsetParent[clientProp] : 0;

    // DOM platform can return `window` as the `offsetParent`.
    if (!clientSize || !(await (platform.isElement == null ? void 0 : platform.isElement(arrowOffsetParent)))) {
      clientSize = elements.floating[clientProp] || rects.floating[length];
    }
    const centerToReference = endDiff / 2 - startDiff / 2;

    // If the padding is large enough that it causes the arrow to no longer be
    // centered, modify the padding so that it is centered.
    const largestPossiblePadding = clientSize / 2 - arrowDimensions[length] / 2 - 1;
    const minPadding = min$1(paddingObject[minProp], largestPossiblePadding);
    const maxPadding = min$1(paddingObject[maxProp], largestPossiblePadding);

    // Make sure the arrow doesn't overflow the floating element if the center
    // point is outside the floating element's bounds.
    const min$1$1 = minPadding;
    const max = clientSize - arrowDimensions[length] - maxPadding;
    const center = clientSize / 2 - arrowDimensions[length] / 2 + centerToReference;
    const offset = within(min$1$1, center, max);

    // If the reference is small enough that the arrow's padding causes it to
    // to point to nothing for an aligned placement, adjust the offset of the
    // floating element itself. This stops `shift()` from taking action, but can
    // be worked around by calling it again after the `arrow()` if desired.
    const shouldAddOffset = getAlignment(placement) != null && center != offset && rects.reference[length] / 2 - (center < min$1$1 ? minPadding : maxPadding) - arrowDimensions[length] / 2 < 0;
    const alignmentOffset = shouldAddOffset ? center < min$1$1 ? min$1$1 - center : max - center : 0;
    return {
      [axis]: coords[axis] - alignmentOffset,
      data: {
        [axis]: offset,
        centerOffset: center - offset
      }
    };
  }
});

const oppositeSideMap = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, side => oppositeSideMap[side]);
}

function getAlignmentSides(placement, rects, rtl) {
  if (rtl === void 0) {
    rtl = false;
  }
  const alignment = getAlignment(placement);
  const mainAxis = getMainAxisFromPlacement(placement);
  const length = getLengthFromAxis(mainAxis);
  let mainAlignmentSide = mainAxis === 'x' ? alignment === (rtl ? 'end' : 'start') ? 'right' : 'left' : alignment === 'start' ? 'bottom' : 'top';
  if (rects.reference[length] > rects.floating[length]) {
    mainAlignmentSide = getOppositePlacement(mainAlignmentSide);
  }
  return {
    main: mainAlignmentSide,
    cross: getOppositePlacement(mainAlignmentSide)
  };
}

const oppositeAlignmentMap = {
  start: 'end',
  end: 'start'
};
function getOppositeAlignmentPlacement(placement) {
  return placement.replace(/start|end/g, alignment => oppositeAlignmentMap[alignment]);
}

function getExpandedPlacements(placement) {
  const oppositePlacement = getOppositePlacement(placement);
  return [getOppositeAlignmentPlacement(placement), oppositePlacement, getOppositeAlignmentPlacement(oppositePlacement)];
}

function getSideList(side, isStart, rtl) {
  const lr = ['left', 'right'];
  const rl = ['right', 'left'];
  const tb = ['top', 'bottom'];
  const bt = ['bottom', 'top'];
  switch (side) {
    case 'top':
    case 'bottom':
      if (rtl) return isStart ? rl : lr;
      return isStart ? lr : rl;
    case 'left':
    case 'right':
      return isStart ? tb : bt;
    default:
      return [];
  }
}
function getOppositeAxisPlacements(placement, flipAlignment, direction, rtl) {
  const alignment = getAlignment(placement);
  let list = getSideList(getSide(placement), direction === 'start', rtl);
  if (alignment) {
    list = list.map(side => side + "-" + alignment);
    if (flipAlignment) {
      list = list.concat(list.map(getOppositeAlignmentPlacement));
    }
  }
  return list;
}

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'flip',
    options,
    async fn(state) {
      var _middlewareData$flip;
      const {
        placement,
        middlewareData,
        rects,
        initialPlacement,
        platform,
        elements
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = true,
        fallbackPlacements: specifiedFallbackPlacements,
        fallbackStrategy = 'bestFit',
        fallbackAxisSideDirection = 'none',
        flipAlignment = true,
        ...detectOverflowOptions
      } = evaluate(options, state);
      const side = getSide(placement);
      const isBasePlacement = getSide(initialPlacement) === initialPlacement;
      const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
      const fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipAlignment ? [getOppositePlacement(initialPlacement)] : getExpandedPlacements(initialPlacement));
      if (!specifiedFallbackPlacements && fallbackAxisSideDirection !== 'none') {
        fallbackPlacements.push(...getOppositeAxisPlacements(initialPlacement, flipAlignment, fallbackAxisSideDirection, rtl));
      }
      const placements = [initialPlacement, ...fallbackPlacements];
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const overflows = [];
      let overflowsData = ((_middlewareData$flip = middlewareData.flip) == null ? void 0 : _middlewareData$flip.overflows) || [];
      if (checkMainAxis) {
        overflows.push(overflow[side]);
      }
      if (checkCrossAxis) {
        const {
          main,
          cross
        } = getAlignmentSides(placement, rects, rtl);
        overflows.push(overflow[main], overflow[cross]);
      }
      overflowsData = [...overflowsData, {
        placement,
        overflows
      }];

      // One or more sides is overflowing.
      if (!overflows.every(side => side <= 0)) {
        var _middlewareData$flip2, _overflowsData$filter;
        const nextIndex = (((_middlewareData$flip2 = middlewareData.flip) == null ? void 0 : _middlewareData$flip2.index) || 0) + 1;
        const nextPlacement = placements[nextIndex];
        if (nextPlacement) {
          // Try next placement and re-run the lifecycle.
          return {
            data: {
              index: nextIndex,
              overflows: overflowsData
            },
            reset: {
              placement: nextPlacement
            }
          };
        }

        // First, find the candidates that fit on the mainAxis side of overflow,
        // then find the placement that fits the best on the main crossAxis side.
        let resetPlacement = (_overflowsData$filter = overflowsData.filter(d => d.overflows[0] <= 0).sort((a, b) => a.overflows[1] - b.overflows[1])[0]) == null ? void 0 : _overflowsData$filter.placement;

        // Otherwise fallback.
        if (!resetPlacement) {
          switch (fallbackStrategy) {
            case 'bestFit':
              {
                var _overflowsData$map$so;
                const placement = (_overflowsData$map$so = overflowsData.map(d => [d.placement, d.overflows.filter(overflow => overflow > 0).reduce((acc, overflow) => acc + overflow, 0)]).sort((a, b) => a[1] - b[1])[0]) == null ? void 0 : _overflowsData$map$so[0];
                if (placement) {
                  resetPlacement = placement;
                }
                break;
              }
            case 'initialPlacement':
              resetPlacement = initialPlacement;
              break;
          }
        }
        if (placement !== resetPlacement) {
          return {
            reset: {
              placement: resetPlacement
            }
          };
        }
      }
      return {};
    }
  };
};

async function convertValueToCoords(state, options) {
  const {
    placement,
    platform,
    elements
  } = state;
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
  const side = getSide(placement);
  const alignment = getAlignment(placement);
  const isVertical = getMainAxisFromPlacement(placement) === 'x';
  const mainAxisMulti = ['left', 'top'].includes(side) ? -1 : 1;
  const crossAxisMulti = rtl && isVertical ? -1 : 1;
  const rawValue = evaluate(options, state);

  // eslint-disable-next-line prefer-const
  let {
    mainAxis,
    crossAxis,
    alignmentAxis
  } = typeof rawValue === 'number' ? {
    mainAxis: rawValue,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: 0,
    crossAxis: 0,
    alignmentAxis: null,
    ...rawValue
  };
  if (alignment && typeof alignmentAxis === 'number') {
    crossAxis = alignment === 'end' ? alignmentAxis * -1 : alignmentAxis;
  }
  return isVertical ? {
    x: crossAxis * crossAxisMulti,
    y: mainAxis * mainAxisMulti
  } : {
    x: mainAxis * mainAxisMulti,
    y: crossAxis * crossAxisMulti
  };
}

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset = function (options) {
  if (options === void 0) {
    options = 0;
  }
  return {
    name: 'offset',
    options,
    async fn(state) {
      const {
        x,
        y
      } = state;
      const diffCoords = await convertValueToCoords(state, options);
      return {
        x: x + diffCoords.x,
        y: y + diffCoords.y,
        data: diffCoords
      };
    }
  };
};

function getCrossAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'shift',
    options,
    async fn(state) {
      const {
        x,
        y,
        placement
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = false,
        limiter = {
          fn: _ref => {
            let {
              x,
              y
            } = _ref;
            return {
              x,
              y
            };
          }
        },
        ...detectOverflowOptions
      } = evaluate(options, state);
      const coords = {
        x,
        y
      };
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const mainAxis = getMainAxisFromPlacement(getSide(placement));
      const crossAxis = getCrossAxis(mainAxis);
      let mainAxisCoord = coords[mainAxis];
      let crossAxisCoord = coords[crossAxis];
      if (checkMainAxis) {
        const minSide = mainAxis === 'y' ? 'top' : 'left';
        const maxSide = mainAxis === 'y' ? 'bottom' : 'right';
        const min = mainAxisCoord + overflow[minSide];
        const max = mainAxisCoord - overflow[maxSide];
        mainAxisCoord = within(min, mainAxisCoord, max);
      }
      if (checkCrossAxis) {
        const minSide = crossAxis === 'y' ? 'top' : 'left';
        const maxSide = crossAxis === 'y' ? 'bottom' : 'right';
        const min = crossAxisCoord + overflow[minSide];
        const max = crossAxisCoord - overflow[maxSide];
        crossAxisCoord = within(min, crossAxisCoord, max);
      }
      const limitedCoords = limiter.fn({
        ...state,
        [mainAxis]: mainAxisCoord,
        [crossAxis]: crossAxisCoord
      });
      return {
        ...limitedCoords,
        data: {
          x: limitedCoords.x - x,
          y: limitedCoords.y - y
        }
      };
    }
  };
};

function getWindow$1(node) {
  var _node$ownerDocument;
  return ((_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
}

function getComputedStyle$1(element) {
  return getWindow$1(element).getComputedStyle(element);
}

function isNode(value) {
  return value instanceof getWindow$1(value).Node;
}
function getNodeName(node) {
  return isNode(node) ? (node.nodeName || '').toLowerCase() : '';
}

function isHTMLElement$1(value) {
  return value instanceof getWindow$1(value).HTMLElement;
}
function isElement$1(value) {
  return value instanceof getWindow$1(value).Element;
}
function isShadowRoot$1(node) {
  // Browsers without `ShadowRoot` support.
  if (typeof ShadowRoot === 'undefined') {
    return false;
  }
  const OwnElement = getWindow$1(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}
function isOverflowElement(element) {
  const {
    overflow,
    overflowX,
    overflowY,
    display
  } = getComputedStyle$1(element);
  return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && !['inline', 'contents'].includes(display);
}
function isTableElement(element) {
  return ['table', 'td', 'th'].includes(getNodeName(element));
}
function isContainingBlock(element) {
  const safari = isSafari$1();
  const css = getComputedStyle$1(element);

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  return css.transform !== 'none' || css.perspective !== 'none' || !safari && (css.backdropFilter ? css.backdropFilter !== 'none' : false) || !safari && (css.filter ? css.filter !== 'none' : false) || ['transform', 'perspective', 'filter'].some(value => (css.willChange || '').includes(value)) || ['paint', 'layout', 'strict', 'content'].some(value => (css.contain || '').includes(value));
}
function isSafari$1() {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('-webkit-backdrop-filter', 'none');
}
function isLastTraversableNode(node) {
  return ['html', 'body', '#document'].includes(getNodeName(node));
}

const min = Math.min;
const max = Math.max;
const round = Math.round;

function getCssDimensions(element) {
  const css = getComputedStyle$1(element);
  // In testing environments, the `width` and `height` properties are empty
  // strings for SVG elements, returning NaN. Fallback to `0` in this case.
  let width = parseFloat(css.width) || 0;
  let height = parseFloat(css.height) || 0;
  const hasOffset = isHTMLElement$1(element);
  const offsetWidth = hasOffset ? element.offsetWidth : width;
  const offsetHeight = hasOffset ? element.offsetHeight : height;
  const shouldFallback = round(width) !== offsetWidth || round(height) !== offsetHeight;
  if (shouldFallback) {
    width = offsetWidth;
    height = offsetHeight;
  }
  return {
    width,
    height,
    fallback: shouldFallback
  };
}

function unwrapElement(element) {
  return !isElement$1(element) ? element.contextElement : element;
}

const FALLBACK_SCALE = {
  x: 1,
  y: 1
};
function getScale(element) {
  const domElement = unwrapElement(element);
  if (!isHTMLElement$1(domElement)) {
    return FALLBACK_SCALE;
  }
  const rect = domElement.getBoundingClientRect();
  const {
    width,
    height,
    fallback
  } = getCssDimensions(domElement);
  let x = (fallback ? round(rect.width) : rect.width) / width;
  let y = (fallback ? round(rect.height) : rect.height) / height;

  // 0, NaN, or Infinity should always fallback to 1.

  if (!x || !Number.isFinite(x)) {
    x = 1;
  }
  if (!y || !Number.isFinite(y)) {
    y = 1;
  }
  return {
    x,
    y
  };
}

const noOffsets = {
  x: 0,
  y: 0
};
function getVisualOffsets(element, isFixed, floatingOffsetParent) {
  var _win$visualViewport, _win$visualViewport2;
  if (isFixed === void 0) {
    isFixed = true;
  }
  if (!isSafari$1()) {
    return noOffsets;
  }
  const win = element ? getWindow$1(element) : window;
  if (!floatingOffsetParent || isFixed && floatingOffsetParent !== win) {
    return noOffsets;
  }
  return {
    x: ((_win$visualViewport = win.visualViewport) == null ? void 0 : _win$visualViewport.offsetLeft) || 0,
    y: ((_win$visualViewport2 = win.visualViewport) == null ? void 0 : _win$visualViewport2.offsetTop) || 0
  };
}

function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  const clientRect = element.getBoundingClientRect();
  const domElement = unwrapElement(element);
  let scale = FALLBACK_SCALE;
  if (includeScale) {
    if (offsetParent) {
      if (isElement$1(offsetParent)) {
        scale = getScale(offsetParent);
      }
    } else {
      scale = getScale(element);
    }
  }
  const visualOffsets = getVisualOffsets(domElement, isFixedStrategy, offsetParent);
  let x = (clientRect.left + visualOffsets.x) / scale.x;
  let y = (clientRect.top + visualOffsets.y) / scale.y;
  let width = clientRect.width / scale.x;
  let height = clientRect.height / scale.y;
  if (domElement) {
    const win = getWindow$1(domElement);
    const offsetWin = offsetParent && isElement$1(offsetParent) ? getWindow$1(offsetParent) : offsetParent;
    let currentIFrame = win.frameElement;
    while (currentIFrame && offsetParent && offsetWin !== win) {
      const iframeScale = getScale(currentIFrame);
      const iframeRect = currentIFrame.getBoundingClientRect();
      const css = getComputedStyle(currentIFrame);
      iframeRect.x += (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
      iframeRect.y += (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
      x *= iframeScale.x;
      y *= iframeScale.y;
      width *= iframeScale.x;
      height *= iframeScale.y;
      x += iframeRect.x;
      y += iframeRect.y;
      currentIFrame = getWindow$1(currentIFrame).frameElement;
    }
  }
  return rectToClientRect({
    width,
    height,
    x,
    y
  });
}

function getDocumentElement(node) {
  return ((isNode(node) ? node.ownerDocument : node.document) || window.document).documentElement;
}

function getNodeScroll(element) {
  if (isElement$1(element)) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }
  return {
    scrollLeft: element.pageXOffset,
    scrollTop: element.pageYOffset
  };
}

function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
  let {
    rect,
    offsetParent,
    strategy
  } = _ref;
  const isOffsetParentAnElement = isHTMLElement$1(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  if (offsetParent === documentElement) {
    return rect;
  }
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  let scale = {
    x: 1,
    y: 1
  };
  const offsets = {
    x: 0,
    y: 0
  };
  if (isOffsetParentAnElement || !isOffsetParentAnElement && strategy !== 'fixed') {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement$1(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent);
      scale = getScale(offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    }
  }
  return {
    width: rect.width * scale.x,
    height: rect.height * scale.y,
    x: rect.x * scale.x - scroll.scrollLeft * scale.x + offsets.x,
    y: rect.y * scale.y - scroll.scrollTop * scale.y + offsets.y
  };
}

function getWindowScrollBarX(element) {
  // If <html> has a CSS width greater than the viewport, then this will be
  // incorrect for RTL.
  return getBoundingClientRect(getDocumentElement(element)).left + getNodeScroll(element).scrollLeft;
}

// Gets the entire size of the scrollable document area, even extending outside
// of the `<html>` and `<body>` rect bounds if horizontally scrollable.
function getDocumentRect(element) {
  const html = getDocumentElement(element);
  const scroll = getNodeScroll(element);
  const body = element.ownerDocument.body;
  const width = max(html.scrollWidth, html.clientWidth, body.scrollWidth, body.clientWidth);
  const height = max(html.scrollHeight, html.clientHeight, body.scrollHeight, body.clientHeight);
  let x = -scroll.scrollLeft + getWindowScrollBarX(element);
  const y = -scroll.scrollTop;
  if (getComputedStyle$1(body).direction === 'rtl') {
    x += max(html.clientWidth, body.clientWidth) - width;
  }
  return {
    width,
    height,
    x,
    y
  };
}

function getParentNode(node) {
  if (getNodeName(node) === 'html') {
    return node;
  }
  const result =
  // Step into the shadow DOM of the parent of a slotted node.
  node.assignedSlot ||
  // DOM Element detected.
  node.parentNode ||
  // ShadowRoot detected.
  isShadowRoot$1(node) && node.host ||
  // Fallback.
  getDocumentElement(node);
  return isShadowRoot$1(result) ? result.host : result;
}

function getNearestOverflowAncestor(node) {
  const parentNode = getParentNode(node);
  if (isLastTraversableNode(parentNode)) {
    // `getParentNode` will never return a `Document` due to the fallback
    // check, so it's either the <html> or <body> element.
    return parentNode.ownerDocument.body;
  }
  if (isHTMLElement$1(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }
  return getNearestOverflowAncestor(parentNode);
}

function getOverflowAncestors(node, list) {
  var _node$ownerDocument;
  if (list === void 0) {
    list = [];
  }
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === ((_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.body);
  const win = getWindow$1(scrollableAncestor);
  if (isBody) {
    return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : []);
  }
  return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor));
}

function getViewportRect(element, strategy) {
  const win = getWindow$1(element);
  const html = getDocumentElement(element);
  const visualViewport = win.visualViewport;
  let width = html.clientWidth;
  let height = html.clientHeight;
  let x = 0;
  let y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    const visualViewportBased = isSafari$1();
    if (!visualViewportBased || visualViewportBased && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  return {
    width,
    height,
    x,
    y
  };
}

// Returns the inner client rect, subtracting scrollbars if present.
function getInnerBoundingClientRect(element, strategy) {
  const clientRect = getBoundingClientRect(element, true, strategy === 'fixed');
  const top = clientRect.top + element.clientTop;
  const left = clientRect.left + element.clientLeft;
  const scale = isHTMLElement$1(element) ? getScale(element) : {
    x: 1,
    y: 1
  };
  const width = element.clientWidth * scale.x;
  const height = element.clientHeight * scale.y;
  const x = left * scale.x;
  const y = top * scale.y;
  return {
    width,
    height,
    x,
    y
  };
}
function getClientRectFromClippingAncestor(element, clippingAncestor, strategy) {
  let rect;
  if (clippingAncestor === 'viewport') {
    rect = getViewportRect(element, strategy);
  } else if (clippingAncestor === 'document') {
    rect = getDocumentRect(getDocumentElement(element));
  } else if (isElement$1(clippingAncestor)) {
    rect = getInnerBoundingClientRect(clippingAncestor, strategy);
  } else {
    const visualOffsets = getVisualOffsets(element);
    rect = {
      ...clippingAncestor,
      x: clippingAncestor.x - visualOffsets.x,
      y: clippingAncestor.y - visualOffsets.y
    };
  }
  return rectToClientRect(rect);
}
function hasFixedPositionAncestor(element, stopNode) {
  const parentNode = getParentNode(element);
  if (parentNode === stopNode || !isElement$1(parentNode) || isLastTraversableNode(parentNode)) {
    return false;
  }
  return getComputedStyle$1(parentNode).position === 'fixed' || hasFixedPositionAncestor(parentNode, stopNode);
}

// A "clipping ancestor" is an `overflow` element with the characteristic of
// clipping (or hiding) child elements. This returns all clipping ancestors
// of the given element up the tree.
function getClippingElementAncestors(element, cache) {
  const cachedResult = cache.get(element);
  if (cachedResult) {
    return cachedResult;
  }
  let result = getOverflowAncestors(element).filter(el => isElement$1(el) && getNodeName(el) !== 'body');
  let currentContainingBlockComputedStyle = null;
  const elementIsFixed = getComputedStyle$1(element).position === 'fixed';
  let currentNode = elementIsFixed ? getParentNode(element) : element;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  while (isElement$1(currentNode) && !isLastTraversableNode(currentNode)) {
    const computedStyle = getComputedStyle$1(currentNode);
    const currentNodeIsContaining = isContainingBlock(currentNode);
    if (!currentNodeIsContaining && computedStyle.position === 'fixed') {
      currentContainingBlockComputedStyle = null;
    }
    const shouldDropCurrentNode = elementIsFixed ? !currentNodeIsContaining && !currentContainingBlockComputedStyle : !currentNodeIsContaining && computedStyle.position === 'static' && !!currentContainingBlockComputedStyle && ['absolute', 'fixed'].includes(currentContainingBlockComputedStyle.position) || isOverflowElement(currentNode) && !currentNodeIsContaining && hasFixedPositionAncestor(element, currentNode);
    if (shouldDropCurrentNode) {
      // Drop non-containing blocks.
      result = result.filter(ancestor => ancestor !== currentNode);
    } else {
      // Record last containing block for next iteration.
      currentContainingBlockComputedStyle = computedStyle;
    }
    currentNode = getParentNode(currentNode);
  }
  cache.set(element, result);
  return result;
}

// Gets the maximum area that the element is visible in due to any number of
// clipping ancestors.
function getClippingRect(_ref) {
  let {
    element,
    boundary,
    rootBoundary,
    strategy
  } = _ref;
  const elementClippingAncestors = boundary === 'clippingAncestors' ? getClippingElementAncestors(element, this._c) : [].concat(boundary);
  const clippingAncestors = [...elementClippingAncestors, rootBoundary];
  const firstClippingAncestor = clippingAncestors[0];
  const clippingRect = clippingAncestors.reduce((accRect, clippingAncestor) => {
    const rect = getClientRectFromClippingAncestor(element, clippingAncestor, strategy);
    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromClippingAncestor(element, firstClippingAncestor, strategy));
  return {
    width: clippingRect.right - clippingRect.left,
    height: clippingRect.bottom - clippingRect.top,
    x: clippingRect.left,
    y: clippingRect.top
  };
}

function getDimensions(element) {
  return getCssDimensions(element);
}

function getTrueOffsetParent(element, polyfill) {
  if (!isHTMLElement$1(element) || getComputedStyle$1(element).position === 'fixed') {
    return null;
  }
  if (polyfill) {
    return polyfill(element);
  }
  return element.offsetParent;
}
function getContainingBlock(element) {
  let currentNode = getParentNode(element);
  while (isHTMLElement$1(currentNode) && !isLastTraversableNode(currentNode)) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    } else {
      currentNode = getParentNode(currentNode);
    }
  }
  return null;
}

// Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.
function getOffsetParent(element, polyfill) {
  const window = getWindow$1(element);
  if (!isHTMLElement$1(element)) {
    return window;
  }
  let offsetParent = getTrueOffsetParent(element, polyfill);
  while (offsetParent && isTableElement(offsetParent) && getComputedStyle$1(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent, polyfill);
  }
  if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle$1(offsetParent).position === 'static' && !isContainingBlock(offsetParent))) {
    return window;
  }
  return offsetParent || getContainingBlock(element) || window;
}

function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
  const isOffsetParentAnElement = isHTMLElement$1(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  const isFixed = strategy === 'fixed';
  const rect = getBoundingClientRect(element, true, isFixed, offsetParent);
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = {
    x: 0,
    y: 0
  };
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement$1(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent, true, isFixed, offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }
  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

const platform = {
  getClippingRect,
  convertOffsetParentRelativeRectToViewportRelativeRect,
  isElement: isElement$1,
  getDimensions,
  getOffsetParent,
  getDocumentElement,
  getScale,
  async getElementRects(_ref) {
    let {
      reference,
      floating,
      strategy
    } = _ref;
    const getOffsetParentFn = this.getOffsetParent || getOffsetParent;
    const getDimensionsFn = this.getDimensions;
    return {
      reference: getRectRelativeToOffsetParent(reference, await getOffsetParentFn(floating), strategy),
      floating: {
        x: 0,
        y: 0,
        ...(await getDimensionsFn(floating))
      }
    };
  },
  getClientRects: element => Array.from(element.getClientRects()),
  isRTL: element => getComputedStyle$1(element).direction === 'rtl'
};

/**
 * Automatically updates the position of the floating element when necessary.
 * Should only be called when the floating element is mounted on the DOM or
 * visible on the screen.
 * @returns cleanup function that should be invoked when the floating element is
 * removed from the DOM or hidden from the screen.
 * @see https://floating-ui.com/docs/autoUpdate
 */
function autoUpdate(reference, floating, update, options) {
  if (options === void 0) {
    options = {};
  }
  const {
    ancestorScroll = true,
    ancestorResize = true,
    elementResize = true,
    animationFrame = false
  } = options;
  const ancestors = ancestorScroll || ancestorResize ? [...(isElement$1(reference) ? getOverflowAncestors(reference) : reference.contextElement ? getOverflowAncestors(reference.contextElement) : []), ...getOverflowAncestors(floating)] : [];
  ancestors.forEach(ancestor => {
    // ignores Window, checks for [object VisualViewport]
    const isVisualViewport = !isElement$1(ancestor) && ancestor.toString().includes('V');
    if (ancestorScroll && (animationFrame ? isVisualViewport : true)) {
      ancestor.addEventListener('scroll', update, {
        passive: true
      });
    }
    ancestorResize && ancestor.addEventListener('resize', update);
  });
  let observer = null;
  if (elementResize) {
    observer = new ResizeObserver(() => {
      update();
    });
    isElement$1(reference) && !animationFrame && observer.observe(reference);
    if (!isElement$1(reference) && reference.contextElement && !animationFrame) {
      observer.observe(reference.contextElement);
    }
    observer.observe(floating);
  }
  let frameId;
  let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
  if (animationFrame) {
    frameLoop();
  }
  function frameLoop() {
    const nextRefRect = getBoundingClientRect(reference);
    if (prevRefRect && (nextRefRect.x !== prevRefRect.x || nextRefRect.y !== prevRefRect.y || nextRefRect.width !== prevRefRect.width || nextRefRect.height !== prevRefRect.height)) {
      update();
    }
    prevRefRect = nextRefRect;
    frameId = requestAnimationFrame(frameLoop);
  }
  update();
  return () => {
    var _observer;
    ancestors.forEach(ancestor => {
      ancestorScroll && ancestor.removeEventListener('scroll', update);
      ancestorResize && ancestor.removeEventListener('resize', update);
    });
    (_observer = observer) == null ? void 0 : _observer.disconnect();
    observer = null;
    if (animationFrame) {
      cancelAnimationFrame(frameId);
    }
  };
}

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a reference element when it is given a certain CSS positioning
 * strategy.
 */
const computePosition = (reference, floating, options) => {
  // This caches the expensive `getClippingElementAncestors` function so that
  // multiple lifecycle resets re-use the same result. It only lives for a
  // single call. If other functions become expensive, we can add them as well.
  const cache = new Map();
  const mergedOptions = {
    platform,
    ...options
  };
  const platformWithCache = {
    ...mergedOptions.platform,
    _c: cache
  };
  return computePosition$1(reference, floating, {
    ...mergedOptions,
    platform: platformWithCache
  });
};

/**
 * A data provider that provides data to position an inner element of the
 * floating element (usually a triangle or caret) so that it is centered to the
 * reference element.
 * This wraps the core `arrow` middleware to allow React refs as the element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow = options => {
  const {
    element,
    padding
  } = options;
  function isRef(value) {
    return Object.prototype.hasOwnProperty.call(value, 'current');
  }
  return {
    name: 'arrow',
    options,
    fn(args) {
      if (isRef(element)) {
        if (element.current != null) {
          return arrow$1({
            element: element.current,
            padding
          }).fn(args);
        }
        return {};
      } else if (element) {
        return arrow$1({
          element,
          padding
        }).fn(args);
      }
      return {};
    }
  };
};

var index$1 = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

// Fork of `fast-deep-equal` that only does the comparisons we need and compares
// functions
function deepEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === 'function' && a.toString() === b.toString()) {
    return true;
  }
  let length, i, keys;
  if (a && b && typeof a == 'object') {
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;) {
        if (!deepEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) {
      return false;
    }
    for (i = length; i-- !== 0;) {
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) {
        return false;
      }
    }
    for (i = length; i-- !== 0;) {
      const key = keys[i];
      if (key === '_owner' && a.$$typeof) {
        continue;
      }
      if (!deepEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }
  return a !== a && b !== b;
}

function useLatestRef$1(value) {
  const ref = React.useRef(value);
  index$1(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Provides data to position a floating element.
 * @see https://floating-ui.com/docs/react
 */
function useFloating$1(options) {
  if (options === void 0) {
    options = {};
  }
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform,
    whileElementsMounted,
    open
  } = options;
  const [data, setData] = React.useState({
    x: null,
    y: null,
    strategy,
    placement,
    middlewareData: {},
    isPositioned: false
  });
  const [latestMiddleware, setLatestMiddleware] = React.useState(middleware);
  if (!deepEqual(latestMiddleware, middleware)) {
    setLatestMiddleware(middleware);
  }
  const referenceRef = React.useRef(null);
  const floatingRef = React.useRef(null);
  const dataRef = React.useRef(data);
  const whileElementsMountedRef = useLatestRef$1(whileElementsMounted);
  const platformRef = useLatestRef$1(platform);
  const [reference, _setReference] = React.useState(null);
  const [floating, _setFloating] = React.useState(null);
  const setReference = React.useCallback(node => {
    if (referenceRef.current !== node) {
      referenceRef.current = node;
      _setReference(node);
    }
  }, []);
  const setFloating = React.useCallback(node => {
    if (floatingRef.current !== node) {
      floatingRef.current = node;
      _setFloating(node);
    }
  }, []);
  const update = React.useCallback(() => {
    if (!referenceRef.current || !floatingRef.current) {
      return;
    }
    const config = {
      placement,
      strategy,
      middleware: latestMiddleware
    };
    if (platformRef.current) {
      config.platform = platformRef.current;
    }
    computePosition(referenceRef.current, floatingRef.current, config).then(data => {
      const fullData = {
        ...data,
        isPositioned: true
      };
      if (isMountedRef.current && !deepEqual(dataRef.current, fullData)) {
        dataRef.current = fullData;
        ReactDOM.flushSync(() => {
          setData(fullData);
        });
      }
    });
  }, [latestMiddleware, placement, strategy, platformRef]);
  index$1(() => {
    if (open === false && dataRef.current.isPositioned) {
      dataRef.current.isPositioned = false;
      setData(data => ({
        ...data,
        isPositioned: false
      }));
    }
  }, [open]);
  const isMountedRef = React.useRef(false);
  index$1(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  index$1(() => {
    if (reference && floating) {
      if (whileElementsMountedRef.current) {
        return whileElementsMountedRef.current(reference, floating, update);
      } else {
        update();
      }
    }
  }, [reference, floating, update, whileElementsMountedRef]);
  const refs = React.useMemo(() => ({
    reference: referenceRef,
    floating: floatingRef,
    setReference,
    setFloating
  }), [setReference, setFloating]);
  const elements = React.useMemo(() => ({
    reference,
    floating
  }), [reference, floating]);
  return React.useMemo(() => ({
    ...data,
    update,
    refs,
    elements,
    reference: setReference,
    floating: setFloating
  }), [data, update, refs, elements, setReference, setFloating]);
}

var index = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

let serverHandoffComplete = false;
let count = 0;
const genId = () => "floating-ui-" + count++;
function useFloatingId() {
  const [id, setId] = React.useState(() => serverHandoffComplete ? genId() : undefined);
  index(() => {
    if (id == null) {
      setId(genId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    if (!serverHandoffComplete) {
      serverHandoffComplete = true;
    }
  }, []);
  return id;
}

// `toString()` prevents bundlers from trying to `import { useId } from 'react'`
const useReactId = React[/*#__PURE__*/'useId'.toString()];

/**
 * Uses React 18's built-in `useId()` when available, or falls back to a
 * slightly less performant (requiring a double render) implementation for
 * earlier React versions.
 * @see https://floating-ui.com/docs/useId
 */
const useId = useReactId || useFloatingId;

function createPubSub() {
  const map = new Map();
  return {
    emit(event, data) {
      var _map$get;
      (_map$get = map.get(event)) == null ? void 0 : _map$get.forEach(handler => handler(data));
    },
    on(event, listener) {
      map.set(event, [...(map.get(event) || []), listener]);
    },
    off(event, listener) {
      map.set(event, (map.get(event) || []).filter(l => l !== listener));
    }
  };
}

const FloatingNodeContext = /*#__PURE__*/React.createContext(null);
const FloatingTreeContext = /*#__PURE__*/React.createContext(null);
const useFloatingParentNodeId = () => {
  var _React$useContext;
  return ((_React$useContext = React.useContext(FloatingNodeContext)) == null ? void 0 : _React$useContext.id) || null;
};
const useFloatingTree = () => React.useContext(FloatingTreeContext);

function getDocument(node) {
  return (node == null ? void 0 : node.ownerDocument) || document;
}

// Avoid Chrome DevTools blue warning.
function getPlatform() {
  const uaData = navigator.userAgentData;
  if (uaData != null && uaData.platform) {
    return uaData.platform;
  }
  return navigator.platform;
}
function getUserAgent() {
  const uaData = navigator.userAgentData;
  if (uaData && Array.isArray(uaData.brands)) {
    return uaData.brands.map(_ref => {
      let {
        brand,
        version
      } = _ref;
      return brand + "/" + version;
    }).join(' ');
  }
  return navigator.userAgent;
}

function getWindow(value) {
  return getDocument(value).defaultView || window;
}
function isElement(value) {
  return value ? value instanceof getWindow(value).Element : false;
}
function isHTMLElement(value) {
  return value ? value instanceof getWindow(value).HTMLElement : false;
}
function isShadowRoot(node) {
  // Browsers without `ShadowRoot` support
  if (typeof ShadowRoot === 'undefined') {
    return false;
  }
  const OwnElement = getWindow(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}

// License: https://github.com/adobe/react-spectrum/blob/b35d5c02fe900badccd0cf1a8f23bb593419f238/packages/@react-aria/utils/src/isVirtualEvent.ts
function isVirtualClick(event) {
  if (event.mozInputSource === 0 && event.isTrusted) {
    return true;
  }
  const androidRe = /Android/i;
  if ((androidRe.test(getPlatform()) || androidRe.test(getUserAgent())) && event.pointerType) {
    return event.type === 'click' && event.buttons === 1;
  }
  return event.detail === 0 && !event.pointerType;
}
function isVirtualPointerEvent(event) {
  return event.width === 0 && event.height === 0 || event.width === 1 && event.height === 1 && event.pressure === 0 && event.detail === 0 && event.pointerType !== 'mouse' ||
  // iOS VoiceOver returns 0.333• for width/height.
  event.width < 1 && event.height < 1 && event.pressure === 0 && event.detail === 0;
}
function isSafari() {
  // Chrome DevTools does not complain about navigator.vendor
  return /apple/i.test(navigator.vendor);
}
function isMac() {
  return getPlatform().toLowerCase().startsWith('mac') && !navigator.maxTouchPoints;
}
function isMouseLikePointerType(pointerType, strict) {
  // On some Linux machines with Chromium, mouse inputs return a `pointerType`
  // of "pen": https://github.com/floating-ui/floating-ui/issues/2015
  const values = ['mouse', 'pen'];
  if (!strict) {
    values.push('', undefined);
  }
  return values.includes(pointerType);
}

function useLatestRef(value) {
  const ref = useRef(value);
  index(() => {
    ref.current = value;
  });
  return ref;
}

const safePolygonIdentifier = 'data-floating-ui-safe-polygon';
function getDelay(value, prop, pointerType) {
  if (pointerType && !isMouseLikePointerType(pointerType)) {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  return value == null ? void 0 : value[prop];
}
/**
 * Opens the floating element while hovering over the reference element, like
 * CSS `:hover`.
 * @see https://floating-ui.com/docs/useHover
 */
const useHover = function (context, _temp) {
  let {
    enabled = true,
    delay = 0,
    handleClose = null,
    mouseOnly = false,
    restMs = 0,
    move = true
  } = _temp === void 0 ? {} : _temp;
  const {
    open,
    onOpenChange,
    dataRef,
    events,
    elements: {
      domReference,
      floating
    },
    refs
  } = context;
  const tree = useFloatingTree();
  const parentId = useFloatingParentNodeId();
  const handleCloseRef = useLatestRef(handleClose);
  const delayRef = useLatestRef(delay);
  const pointerTypeRef = React.useRef();
  const timeoutRef = React.useRef();
  const handlerRef = React.useRef();
  const restTimeoutRef = React.useRef();
  const blockMouseMoveRef = React.useRef(true);
  const performedPointerEventsMutationRef = React.useRef(false);
  const unbindMouseMoveRef = React.useRef(() => {});
  const isHoverOpen = React.useCallback(() => {
    var _dataRef$current$open;
    const type = (_dataRef$current$open = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open.type;
    return (type == null ? void 0 : type.includes('mouse')) && type !== 'mousedown';
  }, [dataRef]);

  // When dismissing before opening, clear the delay timeouts to cancel it
  // from showing.
  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    function onDismiss() {
      clearTimeout(timeoutRef.current);
      clearTimeout(restTimeoutRef.current);
      blockMouseMoveRef.current = true;
    }
    events.on('dismiss', onDismiss);
    return () => {
      events.off('dismiss', onDismiss);
    };
  }, [enabled, events]);
  React.useEffect(() => {
    if (!enabled || !handleCloseRef.current || !open) {
      return;
    }
    function onLeave() {
      if (isHoverOpen()) {
        onOpenChange(false);
      }
    }
    const html = getDocument(floating).documentElement;
    html.addEventListener('mouseleave', onLeave);
    return () => {
      html.removeEventListener('mouseleave', onLeave);
    };
  }, [floating, open, onOpenChange, enabled, handleCloseRef, dataRef, isHoverOpen]);
  const closeWithDelay = React.useCallback(function (runElseBranch) {
    if (runElseBranch === void 0) {
      runElseBranch = true;
    }
    const closeDelay = getDelay(delayRef.current, 'close', pointerTypeRef.current);
    if (closeDelay && !handlerRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => onOpenChange(false), closeDelay);
    } else if (runElseBranch) {
      clearTimeout(timeoutRef.current);
      onOpenChange(false);
    }
  }, [delayRef, onOpenChange]);
  const cleanupMouseMoveHandler = React.useCallback(() => {
    unbindMouseMoveRef.current();
    handlerRef.current = undefined;
  }, []);
  const clearPointerEvents = React.useCallback(() => {
    if (performedPointerEventsMutationRef.current) {
      const body = getDocument(refs.floating.current).body;
      body.style.pointerEvents = '';
      body.removeAttribute(safePolygonIdentifier);
      performedPointerEventsMutationRef.current = false;
    }
  }, [refs]);

  // Registering the mouse events on the reference directly to bypass React's
  // delegation system. If the cursor was on a disabled element and then entered
  // the reference (no gap), `mouseenter` doesn't fire in the delegation system.
  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    function isClickLikeOpenEvent() {
      return dataRef.current.openEvent ? ['click', 'mousedown'].includes(dataRef.current.openEvent.type) : false;
    }
    function onMouseEnter(event) {
      clearTimeout(timeoutRef.current);
      blockMouseMoveRef.current = false;
      if (mouseOnly && !isMouseLikePointerType(pointerTypeRef.current) || restMs > 0 && getDelay(delayRef.current, 'open') === 0) {
        return;
      }
      dataRef.current.openEvent = event;
      const openDelay = getDelay(delayRef.current, 'open', pointerTypeRef.current);
      if (openDelay) {
        timeoutRef.current = setTimeout(() => {
          onOpenChange(true);
        }, openDelay);
      } else {
        onOpenChange(true);
      }
    }
    function onMouseLeave(event) {
      if (isClickLikeOpenEvent()) {
        return;
      }
      unbindMouseMoveRef.current();
      const doc = getDocument(floating);
      clearTimeout(restTimeoutRef.current);
      if (handleCloseRef.current) {
        // Prevent clearing `onScrollMouseLeave` timeout.
        if (!open) {
          clearTimeout(timeoutRef.current);
        }
        handlerRef.current = handleCloseRef.current({
          ...context,
          tree,
          x: event.clientX,
          y: event.clientY,
          onClose() {
            clearPointerEvents();
            cleanupMouseMoveHandler();
            closeWithDelay();
          }
        });
        const handler = handlerRef.current;
        doc.addEventListener('mousemove', handler);
        unbindMouseMoveRef.current = () => {
          doc.removeEventListener('mousemove', handler);
        };
        return;
      }
      closeWithDelay();
    }

    // Ensure the floating element closes after scrolling even if the pointer
    // did not move.
    // https://github.com/floating-ui/floating-ui/discussions/1692
    function onScrollMouseLeave(event) {
      if (isClickLikeOpenEvent()) {
        return;
      }
      handleCloseRef.current == null ? void 0 : handleCloseRef.current({
        ...context,
        tree,
        x: event.clientX,
        y: event.clientY,
        onClose() {
          clearPointerEvents();
          cleanupMouseMoveHandler();
          closeWithDelay();
        }
      })(event);
    }
    if (isElement(domReference)) {
      const ref = domReference;
      open && ref.addEventListener('mouseleave', onScrollMouseLeave);
      floating == null ? void 0 : floating.addEventListener('mouseleave', onScrollMouseLeave);
      move && ref.addEventListener('mousemove', onMouseEnter, {
        once: true
      });
      ref.addEventListener('mouseenter', onMouseEnter);
      ref.addEventListener('mouseleave', onMouseLeave);
      return () => {
        open && ref.removeEventListener('mouseleave', onScrollMouseLeave);
        floating == null ? void 0 : floating.removeEventListener('mouseleave', onScrollMouseLeave);
        move && ref.removeEventListener('mousemove', onMouseEnter);
        ref.removeEventListener('mouseenter', onMouseEnter);
        ref.removeEventListener('mouseleave', onMouseLeave);
      };
    }
  }, [domReference, floating, enabled, context, mouseOnly, restMs, move, closeWithDelay, cleanupMouseMoveHandler, clearPointerEvents, onOpenChange, open, tree, delayRef, handleCloseRef, dataRef]);

  // Block pointer-events of every element other than the reference and floating
  // while the floating element is open and has a `handleClose` handler. Also
  // handles nested floating elements.
  // https://github.com/floating-ui/floating-ui/issues/1722
  index(() => {
    var _handleCloseRef$curre;
    if (!enabled) {
      return;
    }
    if (open && (_handleCloseRef$curre = handleCloseRef.current) != null && _handleCloseRef$curre.__options.blockPointerEvents && isHoverOpen()) {
      const body = getDocument(floating).body;
      body.setAttribute(safePolygonIdentifier, '');
      body.style.pointerEvents = 'none';
      performedPointerEventsMutationRef.current = true;
      if (isElement(domReference) && floating) {
        var _tree$nodesRef$curren, _tree$nodesRef$curren2;
        const ref = domReference;
        const parentFloating = tree == null ? void 0 : (_tree$nodesRef$curren = tree.nodesRef.current.find(node => node.id === parentId)) == null ? void 0 : (_tree$nodesRef$curren2 = _tree$nodesRef$curren.context) == null ? void 0 : _tree$nodesRef$curren2.elements.floating;
        if (parentFloating) {
          parentFloating.style.pointerEvents = '';
        }
        ref.style.pointerEvents = 'auto';
        floating.style.pointerEvents = 'auto';
        return () => {
          ref.style.pointerEvents = '';
          floating.style.pointerEvents = '';
        };
      }
    }
  }, [enabled, open, parentId, floating, domReference, tree, handleCloseRef, dataRef, isHoverOpen]);
  index(() => {
    if (!open) {
      pointerTypeRef.current = undefined;
      cleanupMouseMoveHandler();
      clearPointerEvents();
    }
  }, [open, cleanupMouseMoveHandler, clearPointerEvents]);
  React.useEffect(() => {
    return () => {
      cleanupMouseMoveHandler();
      clearTimeout(timeoutRef.current);
      clearTimeout(restTimeoutRef.current);
      clearPointerEvents();
    };
  }, [enabled, cleanupMouseMoveHandler, clearPointerEvents]);
  return React.useMemo(() => {
    if (!enabled) {
      return {};
    }
    function setPointerRef(event) {
      pointerTypeRef.current = event.pointerType;
    }
    return {
      reference: {
        onPointerDown: setPointerRef,
        onPointerEnter: setPointerRef,
        onMouseMove() {
          if (open || restMs === 0) {
            return;
          }
          clearTimeout(restTimeoutRef.current);
          restTimeoutRef.current = setTimeout(() => {
            if (!blockMouseMoveRef.current) {
              onOpenChange(true);
            }
          }, restMs);
        }
      },
      floating: {
        onMouseEnter() {
          clearTimeout(timeoutRef.current);
        },
        onMouseLeave() {
          events.emit('dismiss', {
            type: 'mouseLeave',
            data: {
              returnFocus: false
            }
          });
          closeWithDelay(false);
        }
      }
    };
  }, [events, enabled, restMs, open, onOpenChange, closeWithDelay]);
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

/**
 * Find the real active element. Traverses into shadowRoots.
 */
function activeElement$1(doc) {
  let activeElement = doc.activeElement;
  while (((_activeElement = activeElement) == null ? void 0 : (_activeElement$shadow = _activeElement.shadowRoot) == null ? void 0 : _activeElement$shadow.activeElement) != null) {
    var _activeElement, _activeElement$shadow;
    activeElement = activeElement.shadowRoot.activeElement;
  }
  return activeElement;
}

function contains(parent, child) {
  if (!parent || !child) {
    return false;
  }
  const rootNode = child.getRootNode && child.getRootNode();

  // First, attempt with faster native method
  if (parent.contains(child)) {
    return true;
  }
  // then fallback to custom implementation with Shadow DOM support
  else if (rootNode && isShadowRoot(rootNode)) {
    let next = child;
    do {
      if (next && parent === next) {
        return true;
      }
      // @ts-ignore
      next = next.parentNode || next.host;
    } while (next);
  }

  // Give up, the result is false
  return false;
}

function getChildren(nodes, id) {
  let allChildren = nodes.filter(node => {
    var _node$context;
    return node.parentId === id && ((_node$context = node.context) == null ? void 0 : _node$context.open);
  }) || [];
  let currentChildren = allChildren;
  while (currentChildren.length) {
    currentChildren = nodes.filter(node => {
      var _currentChildren;
      return (_currentChildren = currentChildren) == null ? void 0 : _currentChildren.some(n => {
        var _node$context2;
        return node.parentId === n.id && ((_node$context2 = node.context) == null ? void 0 : _node$context2.open);
      });
    }) || [];
    allChildren = allChildren.concat(currentChildren);
  }
  return allChildren;
}

function getTarget(event) {
  if ('composedPath' in event) {
    return event.composedPath()[0];
  }

  // TS thinks `event` is of type never as it assumes all browsers support
  // `composedPath()`, but browsers without shadow DOM don't.
  return event.target;
}

const getTabbableOptions = () => ({
  getShadowRoot: true,
  displayCheck:
  // JSDOM does not support the `tabbable` library. To solve this we can
  // check if `ResizeObserver` is a real function (not polyfilled), which
  // determines if the current environment is JSDOM-like.
  typeof ResizeObserver === 'function' && ResizeObserver.toString().includes('[native code]') ? 'full' : 'none'
});
function getTabbableIn(container, direction) {
  const allTabbable = tabbable(container, getTabbableOptions());
  if (direction === 'prev') {
    allTabbable.reverse();
  }
  const activeIndex = allTabbable.indexOf(activeElement$1(getDocument(container)));
  const nextTabbableElements = allTabbable.slice(activeIndex + 1);
  return nextTabbableElements[0];
}
function getNextTabbable() {
  return getTabbableIn(document.body, 'next');
}
function getPreviousTabbable() {
  return getTabbableIn(document.body, 'prev');
}
function isOutsideEvent(event, container) {
  const containerElement = container || event.currentTarget;
  const relatedTarget = event.relatedTarget;
  return !relatedTarget || !contains(containerElement, relatedTarget);
}
function disableFocusInside(container) {
  const tabbableElements = tabbable(container, getTabbableOptions());
  tabbableElements.forEach(element => {
    element.dataset.tabindex = element.getAttribute('tabindex') || '';
    element.setAttribute('tabindex', '-1');
  });
}
function enableFocusInside(container) {
  const elements = container.querySelectorAll('[data-tabindex]');
  elements.forEach(element => {
    const tabindex = element.dataset.tabindex;
    delete element.dataset.tabindex;
    if (tabindex) {
      element.setAttribute('tabindex', tabindex);
    } else {
      element.removeAttribute('tabindex');
    }
  });
}

// `toString()` prevents bundlers from trying to `import { useInsertionEffect } from 'react'`
const useInsertionEffect = React[/*#__PURE__*/'useInsertionEffect'.toString()];
const useSafeInsertionEffect = useInsertionEffect || (fn => fn());
function useEvent(callback) {
  const ref = React.useRef(() => {
    if (process.env.NODE_ENV !== "production") {
      throw new Error('Cannot call an event handler while rendering.');
    }
  });
  useSafeInsertionEffect(() => {
    ref.current = callback;
  });
  return React.useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return ref.current == null ? void 0 : ref.current(...args);
  }, []);
}

// See Diego Haz's Sandbox for making this logic work well on Safari/iOS:
// https://codesandbox.io/s/tabbable-portal-f4tng?file=/src/FocusTrap.tsx

const HIDDEN_STYLES = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'fixed',
  whiteSpace: 'nowrap',
  width: '1px',
  top: 0,
  left: 0
};
let activeElement;
let timeoutId;
function setActiveElementOnTab(event) {
  if (event.key === 'Tab') {
    activeElement = event.target;
    clearTimeout(timeoutId);
  }
}
function isTabFocus(event) {
  const result = activeElement === event.relatedTarget;
  activeElement = event.relatedTarget;
  clearTimeout(timeoutId);
  return result;
}
const FocusGuard = /*#__PURE__*/React.forwardRef(function FocusGuard(props, ref) {
  const onFocus = useEvent(props.onFocus);
  const [role, setRole] = React.useState();
  index(() => {
    if (isSafari()) {
      // Unlike other screen readers such as NVDA and JAWS, the virtual cursor
      // on VoiceOver does trigger the onFocus event, so we can use the focus
      // trap element. On Safari, only buttons trigger the onFocus event.
      // NB: "group" role in the Sandbox no longer appears to work, must be a
      // button role.
      setRole('button');
    }
    document.addEventListener('keydown', setActiveElementOnTab);
    return () => {
      document.removeEventListener('keydown', setActiveElementOnTab);
    };
  }, []);
  return /*#__PURE__*/React.createElement("span", _extends({}, props, {
    ref: ref,
    tabIndex: 0
    // Role is only for VoiceOver
    ,
    role: role,
    "aria-hidden": role ? undefined : true,
    "data-floating-ui-focus-guard": "",
    style: HIDDEN_STYLES,
    onFocus: event => {
      if (isSafari() && isMac() && !isTabFocus(event)) {
        // On macOS we need to wait a little bit before moving
        // focus again.
        event.persist();
        timeoutId = window.setTimeout(() => {
          onFocus(event);
        }, 50);
      } else {
        onFocus(event);
      }
    }
  }));
});

const PortalContext = /*#__PURE__*/React.createContext(null);
const useFloatingPortalNode = function (_temp) {
  let {
    id,
    enabled = true
  } = _temp === void 0 ? {} : _temp;
  const [portalEl, setPortalEl] = React.useState(null);
  const uniqueId = useId();
  const portalContext = usePortalContext();
  index(() => {
    if (!enabled) {
      return;
    }
    const rootNode = id ? document.getElementById(id) : null;
    if (rootNode) {
      rootNode.setAttribute('data-floating-ui-portal', '');
      setPortalEl(rootNode);
    } else {
      const newPortalEl = document.createElement('div');
      if (id !== '') {
        newPortalEl.id = id || uniqueId;
      }
      newPortalEl.setAttribute('data-floating-ui-portal', '');
      setPortalEl(newPortalEl);
      const container = (portalContext == null ? void 0 : portalContext.portalNode) || document.body;
      container.appendChild(newPortalEl);
      return () => {
        container.removeChild(newPortalEl);
      };
    }
  }, [id, portalContext, uniqueId, enabled]);
  return portalEl;
};

/**
 * Portals the floating element into a given container element — by default,
 * outside of the app root and into the body.
 * @see https://floating-ui.com/docs/FloatingPortal
 */
const FloatingPortal = _ref => {
  let {
    children,
    id,
    root = null,
    preserveTabOrder = true
  } = _ref;
  const portalNode = useFloatingPortalNode({
    id,
    enabled: !root
  });
  const [focusManagerState, setFocusManagerState] = React.useState(null);
  const beforeOutsideRef = React.useRef(null);
  const afterOutsideRef = React.useRef(null);
  const beforeInsideRef = React.useRef(null);
  const afterInsideRef = React.useRef(null);
  const shouldRenderGuards =
  // The FocusManager and therefore floating element are currently open/
  // rendered.
  !!focusManagerState &&
  // Guards are only for non-modal focus management.
  !focusManagerState.modal && !!(root || portalNode) && preserveTabOrder;

  // https://codesandbox.io/s/tabbable-portal-f4tng?file=/src/TabbablePortal.tsx
  React.useEffect(() => {
    if (!portalNode || !preserveTabOrder || focusManagerState != null && focusManagerState.modal) {
      return;
    }

    // Make sure elements inside the portal element are tabbable only when the
    // portal has already been focused, either by tabbing into a focus trap
    // element outside or using the mouse.
    function onFocus(event) {
      if (portalNode && isOutsideEvent(event)) {
        const focusing = event.type === 'focusin';
        const manageFocus = focusing ? enableFocusInside : disableFocusInside;
        manageFocus(portalNode);
      }
    }
    // Listen to the event on the capture phase so they run before the focus
    // trap elements onFocus prop is called.
    portalNode.addEventListener('focusin', onFocus, true);
    portalNode.addEventListener('focusout', onFocus, true);
    return () => {
      portalNode.removeEventListener('focusin', onFocus, true);
      portalNode.removeEventListener('focusout', onFocus, true);
    };
  }, [portalNode, preserveTabOrder, focusManagerState == null ? void 0 : focusManagerState.modal]);
  return /*#__PURE__*/React.createElement(PortalContext.Provider, {
    value: React.useMemo(() => ({
      preserveTabOrder,
      beforeOutsideRef,
      afterOutsideRef,
      beforeInsideRef,
      afterInsideRef,
      portalNode,
      setFocusManagerState
    }), [preserveTabOrder, portalNode])
  }, shouldRenderGuards && portalNode && /*#__PURE__*/React.createElement(FocusGuard, {
    "data-type": "outside",
    ref: beforeOutsideRef,
    onFocus: event => {
      if (isOutsideEvent(event, portalNode)) {
        var _beforeInsideRef$curr;
        (_beforeInsideRef$curr = beforeInsideRef.current) == null ? void 0 : _beforeInsideRef$curr.focus();
      } else {
        const prevTabbable = getPreviousTabbable() || (focusManagerState == null ? void 0 : focusManagerState.refs.domReference.current);
        prevTabbable == null ? void 0 : prevTabbable.focus();
      }
    }
  }), shouldRenderGuards && portalNode && /*#__PURE__*/React.createElement("span", {
    "aria-owns": portalNode.id,
    style: HIDDEN_STYLES
  }), root ? /*#__PURE__*/createPortal(children, root) : portalNode ? /*#__PURE__*/createPortal(children, portalNode) : null, shouldRenderGuards && portalNode && /*#__PURE__*/React.createElement(FocusGuard, {
    "data-type": "outside",
    ref: afterOutsideRef,
    onFocus: event => {
      if (isOutsideEvent(event, portalNode)) {
        var _afterInsideRef$curre;
        (_afterInsideRef$curre = afterInsideRef.current) == null ? void 0 : _afterInsideRef$curre.focus();
      } else {
        const nextTabbable = getNextTabbable() || (focusManagerState == null ? void 0 : focusManagerState.refs.domReference.current);
        nextTabbable == null ? void 0 : nextTabbable.focus();
        (focusManagerState == null ? void 0 : focusManagerState.closeOnFocusOut) && (focusManagerState == null ? void 0 : focusManagerState.onOpenChange(false));
      }
    }
  }));
};
const usePortalContext = () => React.useContext(PortalContext);

/**
 * Check whether the event.target is within the provided node. Uses event.composedPath if available for custom element support.
 *
 * @param event The event whose target/composedPath to check
 * @param node The node to check against
 * @returns Whether the event.target/composedPath is within the node.
 */
function isEventTargetWithin(event, node) {
  if (node == null) {
    return false;
  }
  if ('composedPath' in event) {
    return event.composedPath().includes(node);
  }

  // TS thinks `event` is of type never as it assumes all browsers support composedPath, but browsers without shadow dom don't
  const e = event;
  return e.target != null && node.contains(e.target);
}

const bubbleHandlerKeys = {
  pointerdown: 'onPointerDown',
  mousedown: 'onMouseDown',
  click: 'onClick'
};
const captureHandlerKeys = {
  pointerdown: 'onPointerDownCapture',
  mousedown: 'onMouseDownCapture',
  click: 'onClickCapture'
};
const normalizeBubblesProp = function (bubbles) {
  var _bubbles$escapeKey, _bubbles$outsidePress;
  if (bubbles === void 0) {
    bubbles = true;
  }
  return {
    escapeKeyBubbles: typeof bubbles === 'boolean' ? bubbles : (_bubbles$escapeKey = bubbles.escapeKey) != null ? _bubbles$escapeKey : true,
    outsidePressBubbles: typeof bubbles === 'boolean' ? bubbles : (_bubbles$outsidePress = bubbles.outsidePress) != null ? _bubbles$outsidePress : true
  };
};
/**
 * Closes the floating element when a dismissal is requested — by default, when
 * the user presses the `escape` key or outside of the floating element.
 * @see https://floating-ui.com/docs/useDismiss
 */
const useDismiss = function (_ref, _temp) {
  let {
    open,
    onOpenChange,
    events,
    nodeId,
    elements: {
      reference,
      domReference,
      floating
    },
    dataRef
  } = _ref;
  let {
    enabled = true,
    escapeKey = true,
    outsidePress: unstable_outsidePress = true,
    outsidePressEvent = 'pointerdown',
    referencePress = false,
    referencePressEvent = 'pointerdown',
    ancestorScroll = false,
    bubbles = true
  } = _temp === void 0 ? {} : _temp;
  const tree = useFloatingTree();
  const nested = useFloatingParentNodeId() != null;
  const outsidePressFn = useEvent(typeof unstable_outsidePress === 'function' ? unstable_outsidePress : () => false);
  const outsidePress = typeof unstable_outsidePress === 'function' ? outsidePressFn : unstable_outsidePress;
  const insideReactTreeRef = React.useRef(false);
  const {
    escapeKeyBubbles,
    outsidePressBubbles
  } = normalizeBubblesProp(bubbles);
  React.useEffect(() => {
    if (!open || !enabled) {
      return;
    }
    dataRef.current.__escapeKeyBubbles = escapeKeyBubbles;
    dataRef.current.__outsidePressBubbles = outsidePressBubbles;
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        const children = tree ? getChildren(tree.nodesRef.current, nodeId) : [];
        if (children.length > 0) {
          let shouldDismiss = true;
          children.forEach(child => {
            var _child$context;
            if ((_child$context = child.context) != null && _child$context.open && !child.context.dataRef.current.__escapeKeyBubbles) {
              shouldDismiss = false;
              return;
            }
          });
          if (!shouldDismiss) {
            return;
          }
        }
        events.emit('dismiss', {
          type: 'escapeKey',
          data: {
            returnFocus: {
              preventScroll: false
            }
          }
        });
        onOpenChange(false);
      }
    }
    function onOutsidePress(event) {
      // Given developers can stop the propagation of the synthetic event,
      // we can only be confident with a positive value.
      const insideReactTree = insideReactTreeRef.current;
      insideReactTreeRef.current = false;
      if (insideReactTree) {
        return;
      }
      if (typeof outsidePress === 'function' && !outsidePress(event)) {
        return;
      }
      const target = getTarget(event);

      // Check if the click occurred on the scrollbar
      if (isHTMLElement(target) && floating) {
        const win = floating.ownerDocument.defaultView || window;
        const canScrollX = target.scrollWidth > target.clientWidth;
        const canScrollY = target.scrollHeight > target.clientHeight;
        let xCond = canScrollY && event.offsetX > target.clientWidth;

        // In some browsers it is possible to change the <body> (or window)
        // scrollbar to the left side, but is very rare and is difficult to
        // check for. Plus, for modal dialogs with backdrops, it is more
        // important that the backdrop is checked but not so much the window.
        if (canScrollY) {
          const isRTL = win.getComputedStyle(target).direction === 'rtl';
          if (isRTL) {
            xCond = event.offsetX <= target.offsetWidth - target.clientWidth;
          }
        }
        if (xCond || canScrollX && event.offsetY > target.clientHeight) {
          return;
        }
      }
      const targetIsInsideChildren = tree && getChildren(tree.nodesRef.current, nodeId).some(node => {
        var _node$context;
        return isEventTargetWithin(event, (_node$context = node.context) == null ? void 0 : _node$context.elements.floating);
      });
      if (isEventTargetWithin(event, floating) || isEventTargetWithin(event, domReference) || targetIsInsideChildren) {
        return;
      }
      const children = tree ? getChildren(tree.nodesRef.current, nodeId) : [];
      if (children.length > 0) {
        let shouldDismiss = true;
        children.forEach(child => {
          var _child$context2;
          if ((_child$context2 = child.context) != null && _child$context2.open && !child.context.dataRef.current.__outsidePressBubbles) {
            shouldDismiss = false;
            return;
          }
        });
        if (!shouldDismiss) {
          return;
        }
      }
      events.emit('dismiss', {
        type: 'outsidePress',
        data: {
          returnFocus: nested ? {
            preventScroll: true
          } : isVirtualClick(event) || isVirtualPointerEvent(event)
        }
      });
      onOpenChange(false);
    }
    function onScroll() {
      onOpenChange(false);
    }
    const doc = getDocument(floating);
    escapeKey && doc.addEventListener('keydown', onKeyDown);
    outsidePress && doc.addEventListener(outsidePressEvent, onOutsidePress);
    let ancestors = [];
    if (ancestorScroll) {
      if (isElement(domReference)) {
        ancestors = getOverflowAncestors(domReference);
      }
      if (isElement(floating)) {
        ancestors = ancestors.concat(getOverflowAncestors(floating));
      }
      if (!isElement(reference) && reference && reference.contextElement) {
        ancestors = ancestors.concat(getOverflowAncestors(reference.contextElement));
      }
    }

    // Ignore the visual viewport for scrolling dismissal (allow pinch-zoom)
    ancestors = ancestors.filter(ancestor => {
      var _doc$defaultView;
      return ancestor !== ((_doc$defaultView = doc.defaultView) == null ? void 0 : _doc$defaultView.visualViewport);
    });
    ancestors.forEach(ancestor => {
      ancestor.addEventListener('scroll', onScroll, {
        passive: true
      });
    });
    return () => {
      escapeKey && doc.removeEventListener('keydown', onKeyDown);
      outsidePress && doc.removeEventListener(outsidePressEvent, onOutsidePress);
      ancestors.forEach(ancestor => {
        ancestor.removeEventListener('scroll', onScroll);
      });
    };
  }, [dataRef, floating, domReference, reference, escapeKey, outsidePress, outsidePressEvent, events, tree, nodeId, open, onOpenChange, ancestorScroll, enabled, escapeKeyBubbles, outsidePressBubbles, nested]);
  React.useEffect(() => {
    insideReactTreeRef.current = false;
  }, [outsidePress, outsidePressEvent]);
  return React.useMemo(() => {
    if (!enabled) {
      return {};
    }
    return {
      reference: {
        [bubbleHandlerKeys[referencePressEvent]]: () => {
          if (referencePress) {
            events.emit('dismiss', {
              type: 'referencePress',
              data: {
                returnFocus: false
              }
            });
            onOpenChange(false);
          }
        }
      },
      floating: {
        [captureHandlerKeys[outsidePressEvent]]: () => {
          insideReactTreeRef.current = true;
        }
      }
    };
  }, [enabled, events, referencePress, outsidePressEvent, referencePressEvent, onOpenChange]);
};

/**
 * Opens the floating element while the reference element has focus, like CSS
 * `:focus`.
 * @see https://floating-ui.com/docs/useFocus
 */
const useFocus = function (_ref, _temp) {
  let {
    open,
    onOpenChange,
    dataRef,
    events,
    refs,
    elements: {
      floating,
      domReference
    }
  } = _ref;
  let {
    enabled = true,
    keyboardOnly = true
  } = _temp === void 0 ? {} : _temp;
  const pointerTypeRef = React.useRef('');
  const blockFocusRef = React.useRef(false);
  const timeoutRef = React.useRef();
  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    const doc = getDocument(floating);
    const win = doc.defaultView || window;

    // If the reference was focused and the user left the tab/window, and the
    // floating element was not open, the focus should be blocked when they
    // return to the tab/window.
    function onBlur() {
      if (!open && isHTMLElement(domReference) && domReference === activeElement$1(getDocument(domReference))) {
        blockFocusRef.current = true;
      }
    }
    win.addEventListener('blur', onBlur);
    return () => {
      win.removeEventListener('blur', onBlur);
    };
  }, [floating, domReference, open, enabled]);
  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    function onDismiss(payload) {
      if (payload.type === 'referencePress' || payload.type === 'escapeKey') {
        blockFocusRef.current = true;
      }
    }
    events.on('dismiss', onDismiss);
    return () => {
      events.off('dismiss', onDismiss);
    };
  }, [events, enabled]);
  React.useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);
  return React.useMemo(() => {
    if (!enabled) {
      return {};
    }
    return {
      reference: {
        onPointerDown(_ref2) {
          let {
            pointerType
          } = _ref2;
          pointerTypeRef.current = pointerType;
          blockFocusRef.current = !!(pointerType && keyboardOnly);
        },
        onMouseLeave() {
          blockFocusRef.current = false;
        },
        onFocus(event) {
          var _dataRef$current$open;
          if (blockFocusRef.current) {
            return;
          }

          // Dismiss with click should ignore the subsequent `focus` trigger,
          // but only if the click originated inside the reference element.
          if (event.type === 'focus' && ((_dataRef$current$open = dataRef.current.openEvent) == null ? void 0 : _dataRef$current$open.type) === 'mousedown' && dataRef.current.openEvent && isEventTargetWithin(dataRef.current.openEvent, domReference)) {
            return;
          }
          dataRef.current.openEvent = event.nativeEvent;
          onOpenChange(true);
        },
        onBlur(event) {
          blockFocusRef.current = false;
          const relatedTarget = event.relatedTarget;

          // Hit the non-modal focus management portal guard. Focus will be
          // moved into the floating element immediately after.
          const movedToFocusGuard = isElement(relatedTarget) && relatedTarget.hasAttribute('data-floating-ui-focus-guard') && relatedTarget.getAttribute('data-type') === 'outside';

          // Wait for the window blur listener to fire.
          timeoutRef.current = setTimeout(() => {
            // When focusing the reference element (e.g. regular click), then
            // clicking into the floating element, prevent it from hiding.
            // Note: it must be focusable, e.g. `tabindex="-1"`.
            if (contains(refs.floating.current, relatedTarget) || contains(domReference, relatedTarget) || movedToFocusGuard) {
              return;
            }
            onOpenChange(false);
          });
        }
      }
    };
  }, [enabled, keyboardOnly, domReference, refs, dataRef, onOpenChange]);
};

/**
 * Merges an array of refs into a single memoized callback ref or `null`.
 * @see https://floating-ui.com/docs/useMergeRefs
 */
function useMergeRefs(refs) {
  return React.useMemo(() => {
    if (refs.every(ref => ref == null)) {
      return null;
    }
    return value => {
      refs.forEach(ref => {
        if (typeof ref === 'function') {
          ref(value);
        } else if (ref != null) {
          ref.current = value;
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refs);
}

/**
 * Adds base screen reader props to the reference and floating elements for a
 * given floating element `role`.
 * @see https://floating-ui.com/docs/useRole
 */
const useRole = function (_ref, _temp) {
  let {
    open
  } = _ref;
  let {
    enabled = true,
    role = 'dialog'
  } = _temp === void 0 ? {} : _temp;
  const rootId = useId();
  const referenceId = useId();
  return React.useMemo(() => {
    const floatingProps = {
      id: rootId,
      role
    };
    if (!enabled) {
      return {};
    }
    if (role === 'tooltip') {
      return {
        reference: {
          'aria-describedby': open ? rootId : undefined
        },
        floating: floatingProps
      };
    }
    return {
      reference: {
        'aria-expanded': open ? 'true' : 'false',
        'aria-haspopup': role === 'alertdialog' ? 'dialog' : role,
        'aria-controls': open ? rootId : undefined,
        ...(role === 'listbox' && {
          role: 'combobox'
        }),
        ...(role === 'menu' && {
          id: referenceId
        })
      },
      floating: {
        ...floatingProps,
        ...(role === 'menu' && {
          'aria-labelledby': referenceId
        })
      }
    };
  }, [enabled, role, open, rootId, referenceId]);
};

function isPointInPolygon(point, polygon) {
  const [x, y] = point;
  let isInside = false;
  const length = polygon.length;
  for (let i = 0, j = length - 1; i < length; j = i++) {
    const [xi, yi] = polygon[i] || [0, 0];
    const [xj, yj] = polygon[j] || [0, 0];
    const intersect = yi >= y !== yj >= y && x <= (xj - xi) * (y - yi) / (yj - yi) + xi;
    if (intersect) {
      isInside = !isInside;
    }
  }
  return isInside;
}
function isInside(point, rect) {
  return point[0] >= rect.x && point[0] <= rect.x + rect.width && point[1] >= rect.y && point[1] <= rect.y + rect.height;
}
function safePolygon(_temp) {
  let {
    restMs = 0,
    buffer = 0.5,
    blockPointerEvents = false
  } = _temp === void 0 ? {} : _temp;
  let timeoutId;
  let isInsideRect = false;
  let hasLanded = false;
  const fn = _ref => {
    let {
      x,
      y,
      placement,
      elements,
      onClose,
      nodeId,
      tree
    } = _ref;
    return function onMouseMove(event) {
      function close() {
        clearTimeout(timeoutId);
        onClose();
      }
      clearTimeout(timeoutId);
      if (!elements.domReference || !elements.floating || placement == null || x == null || y == null) {
        return;
      }
      const {
        clientX,
        clientY
      } = event;
      const clientPoint = [clientX, clientY];
      const target = getTarget(event);
      const isLeave = event.type === 'mouseleave';
      const isOverFloatingEl = contains(elements.floating, target);
      const isOverReferenceEl = contains(elements.domReference, target);
      const refRect = elements.domReference.getBoundingClientRect();
      const rect = elements.floating.getBoundingClientRect();
      const side = placement.split('-')[0];
      const cursorLeaveFromRight = x > rect.right - rect.width / 2;
      const cursorLeaveFromBottom = y > rect.bottom - rect.height / 2;
      const isOverReferenceRect = isInside(clientPoint, refRect);
      if (isOverFloatingEl) {
        hasLanded = true;
        if (!isLeave) {
          return;
        }
      }
      if (isOverReferenceEl) {
        hasLanded = false;
      }
      if (isOverReferenceEl && !isLeave) {
        hasLanded = true;
        return;
      }

      // Prevent overlapping floating element from being stuck in an open-close
      // loop: https://github.com/floating-ui/floating-ui/issues/1910
      if (isLeave && isElement(event.relatedTarget) && contains(elements.floating, event.relatedTarget)) {
        return;
      }

      // If any nested child is open, abort.
      if (tree && getChildren(tree.nodesRef.current, nodeId).some(_ref2 => {
        let {
          context
        } = _ref2;
        return context == null ? void 0 : context.open;
      })) {
        return;
      }

      // If the pointer is leaving from the opposite side, the "buffer" logic
      // creates a point where the floating element remains open, but should be
      // ignored.
      // A constant of 1 handles floating point rounding errors.
      if (side === 'top' && y >= refRect.bottom - 1 || side === 'bottom' && y <= refRect.top + 1 || side === 'left' && x >= refRect.right - 1 || side === 'right' && x <= refRect.left + 1) {
        return close();
      }

      // Ignore when the cursor is within the rectangular trough between the
      // two elements. Since the triangle is created from the cursor point,
      // which can start beyond the ref element's edge, traversing back and
      // forth from the ref to the floating element can cause it to close. This
      // ensures it always remains open in that case.
      let rectPoly = [];
      switch (side) {
        case 'top':
          rectPoly = [[rect.left, refRect.top + 1], [rect.left, rect.bottom - 1], [rect.right, rect.bottom - 1], [rect.right, refRect.top + 1]];
          isInsideRect = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= refRect.top + 1;
          break;
        case 'bottom':
          rectPoly = [[rect.left, rect.top + 1], [rect.left, refRect.bottom - 1], [rect.right, refRect.bottom - 1], [rect.right, rect.top + 1]];
          isInsideRect = clientX >= rect.left && clientX <= rect.right && clientY >= refRect.bottom - 1 && clientY <= rect.bottom;
          break;
        case 'left':
          rectPoly = [[rect.right - 1, rect.bottom], [rect.right - 1, rect.top], [refRect.left + 1, rect.top], [refRect.left + 1, rect.bottom]];
          isInsideRect = clientX >= rect.left && clientX <= refRect.left + 1 && clientY >= rect.top && clientY <= rect.bottom;
          break;
        case 'right':
          rectPoly = [[refRect.right - 1, rect.bottom], [refRect.right - 1, rect.top], [rect.left + 1, rect.top], [rect.left + 1, rect.bottom]];
          isInsideRect = clientX >= refRect.right - 1 && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
          break;
      }
      function getPolygon(_ref3) {
        let [x, y] = _ref3;
        const isFloatingWider = rect.width > refRect.width;
        const isFloatingTaller = rect.height > refRect.height;
        switch (side) {
          case 'top':
            {
              const cursorPointOne = [isFloatingWider ? x + buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y + buffer + 1];
              const cursorPointTwo = [isFloatingWider ? x - buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y + buffer + 1];
              const commonPoints = [[rect.left, cursorLeaveFromRight ? rect.bottom - buffer : isFloatingWider ? rect.bottom - buffer : rect.top], [rect.right, cursorLeaveFromRight ? isFloatingWider ? rect.bottom - buffer : rect.top : rect.bottom - buffer]];
              return [cursorPointOne, cursorPointTwo, ...commonPoints];
            }
          case 'bottom':
            {
              const cursorPointOne = [isFloatingWider ? x + buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y - buffer];
              const cursorPointTwo = [isFloatingWider ? x - buffer / 2 : cursorLeaveFromRight ? x + buffer * 4 : x - buffer * 4, y - buffer];
              const commonPoints = [[rect.left, cursorLeaveFromRight ? rect.top + buffer : isFloatingWider ? rect.top + buffer : rect.bottom], [rect.right, cursorLeaveFromRight ? isFloatingWider ? rect.top + buffer : rect.bottom : rect.top + buffer]];
              return [cursorPointOne, cursorPointTwo, ...commonPoints];
            }
          case 'left':
            {
              const cursorPointOne = [x + buffer + 1, isFloatingTaller ? y + buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              const cursorPointTwo = [x + buffer + 1, isFloatingTaller ? y - buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              const commonPoints = [[cursorLeaveFromBottom ? rect.right - buffer : isFloatingTaller ? rect.right - buffer : rect.left, rect.top], [cursorLeaveFromBottom ? isFloatingTaller ? rect.right - buffer : rect.left : rect.right - buffer, rect.bottom]];
              return [...commonPoints, cursorPointOne, cursorPointTwo];
            }
          case 'right':
            {
              const cursorPointOne = [x - buffer, isFloatingTaller ? y + buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              const cursorPointTwo = [x - buffer, isFloatingTaller ? y - buffer / 2 : cursorLeaveFromBottom ? y + buffer * 4 : y - buffer * 4];
              const commonPoints = [[cursorLeaveFromBottom ? rect.left + buffer : isFloatingTaller ? rect.left + buffer : rect.right, rect.top], [cursorLeaveFromBottom ? isFloatingTaller ? rect.left + buffer : rect.right : rect.left + buffer, rect.bottom]];
              return [cursorPointOne, cursorPointTwo, ...commonPoints];
            }
        }
      }
      const poly = isInsideRect ? rectPoly : getPolygon([x, y]);
      if (isInsideRect) {
        return;
      } else if (hasLanded && !isOverReferenceRect) {
        return close();
      }
      if (!isPointInPolygon([clientX, clientY], poly)) {
        close();
      } else if (restMs && !hasLanded) {
        timeoutId = setTimeout(close, restMs);
      }
    };
  };
  fn.__options = {
    blockPointerEvents
  };
  return fn;
}

/**
 * Provides data to position a floating element and context to add interactions.
 * @see https://floating-ui.com/docs/react
 */
function useFloating(options) {
  if (options === void 0) {
    options = {};
  }
  const {
    open = false,
    onOpenChange: unstable_onOpenChange,
    nodeId
  } = options;
  const position = useFloating$1(options);
  const tree = useFloatingTree();
  const domReferenceRef = React.useRef(null);
  const dataRef = React.useRef({});
  const events = React.useState(() => createPubSub())[0];
  const [domReference, setDomReference] = React.useState(null);
  const setPositionReference = React.useCallback(node => {
    const positionReference = isElement(node) ? {
      getBoundingClientRect: () => node.getBoundingClientRect(),
      contextElement: node
    } : node;
    position.refs.setReference(positionReference);
  }, [position.refs]);
  const setReference = React.useCallback(node => {
    if (isElement(node) || node === null) {
      domReferenceRef.current = node;
      setDomReference(node);
    }

    // Backwards-compatibility for passing a virtual element to `reference`
    // after it has set the DOM reference.
    if (isElement(position.refs.reference.current) || position.refs.reference.current === null ||
    // Don't allow setting virtual elements using the old technique back to
    // `null` to support `positionReference` + an unstable `reference`
    // callback ref.
    node !== null && !isElement(node)) {
      position.refs.setReference(node);
    }
  }, [position.refs]);
  const refs = React.useMemo(() => ({
    ...position.refs,
    setReference,
    setPositionReference,
    domReference: domReferenceRef
  }), [position.refs, setReference, setPositionReference]);
  const elements = React.useMemo(() => ({
    ...position.elements,
    domReference: domReference
  }), [position.elements, domReference]);
  const onOpenChange = useEvent(unstable_onOpenChange);
  const context = React.useMemo(() => ({
    ...position,
    refs,
    elements,
    dataRef,
    nodeId,
    events,
    open,
    onOpenChange
  }), [position, nodeId, events, open, onOpenChange, refs, elements]);
  index(() => {
    const node = tree == null ? void 0 : tree.nodesRef.current.find(node => node.id === nodeId);
    if (node) {
      node.context = context;
    }
  });
  return React.useMemo(() => ({
    ...position,
    context,
    refs,
    reference: setReference,
    positionReference: setPositionReference
  }), [position, refs, context, setReference, setPositionReference]);
}

function mergeProps(userProps, propsList, elementKey) {
  const map = new Map();
  return {
    ...(elementKey === 'floating' && {
      tabIndex: -1
    }),
    ...userProps,
    ...propsList.map(value => value ? value[elementKey] : null).concat(userProps).reduce((acc, props) => {
      if (!props) {
        return acc;
      }
      Object.entries(props).forEach(_ref => {
        let [key, value] = _ref;
        if (key.indexOf('on') === 0) {
          if (!map.has(key)) {
            map.set(key, []);
          }
          if (typeof value === 'function') {
            var _map$get;
            (_map$get = map.get(key)) == null ? void 0 : _map$get.push(value);
            acc[key] = function () {
              var _map$get2;
              for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
                args[_key] = arguments[_key];
              }
              (_map$get2 = map.get(key)) == null ? void 0 : _map$get2.forEach(fn => fn(...args));
            };
          }
        } else {
          acc[key] = value;
        }
      });
      return acc;
    }, {})
  };
}
const useInteractions = function (propsList) {
  if (propsList === void 0) {
    propsList = [];
  }
  // The dependencies are a dynamic array, so we can't use the linter's
  // suggestion to add it to the deps array.
  const deps = propsList;
  const getReferenceProps = React.useCallback(userProps => mergeProps(userProps, propsList, 'reference'),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);
  const getFloatingProps = React.useCallback(userProps => mergeProps(userProps, propsList, 'floating'),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);
  const getItemProps = React.useCallback(userProps => mergeProps(userProps, propsList, 'item'),
  // Granularly check for `item` changes, because the `getItemProps` getter
  // should be as referentially stable as possible since it may be passed as
  // a prop to many components. All `item` key values must therefore be
  // memoized.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  propsList.map(key => key == null ? void 0 : key.item));
  return React.useMemo(() => ({
    getReferenceProps,
    getFloatingProps,
    getItemProps
  }), [getReferenceProps, getFloatingProps, getItemProps]);
};

const TooltipContext = /*#__PURE__*/React__default.createContext(null);

/**
 * Context props.
 */

/**
 * Component variant.
 */
const TooltipPlacement = {
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
  'top-start': 'top-start',
  'top-end': 'top-end',
  'right-start': 'right-start',
  'right-end': 'right-end',
  'bottom-start': 'bottom-start',
  'bottom-end': 'bottom-end',
  'left-start': 'left-start',
  'left-end': 'left-end'
};

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledTooltipContent = styled.div`
  ${baseContainer}

  align-items: center;
  background-color: var(--redsift-color-neutral-white);
  border-radius: 4px;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2);
  color: var(--redsift-color-neutral-black);
  display: flex;
  filter: drop-shadow(0px 4px 5px rgba(0, 0, 0, 0.14)) drop-shadow(0px 1px 10px rgba(0, 0, 0, 0.12));
  font-family: var(--redsift-typography-tooltip-font-family);
  font-size: var(--redsift-typography-tooltip-font-size);
  font-weight: var(--redsift-typography-tooltip-font-weight);
  line-height: var(--redsift-typography-tooltip-line-height);
  max-width: calc(100vw - 48px);
  padding: 4px 8px;
  z-index: var(--redsift-layout-z-index-tooltip);

  .redsift-tooltip-content__arrow {
    position: absolute;
    width: 0;
    height: 0;
    border-color: var(--redsift-color-neutral-white);
    border-style: solid;
  }

  ${_ref => {
  let {
    $placement
  } = _ref;
  switch ($placement) {
    case TooltipPlacement.left:
    case TooltipPlacement['left-end']:
    case TooltipPlacement['left-start']:
      return css`
          .redsift-tooltip-content__arrow {
            border-width: 6px 0 6px 6px;
            border-top-color: transparent;
            border-right-color: transparent;
            border-bottom-color: transparent;
          }
        `;
    case TooltipPlacement.top:
    case TooltipPlacement['top-end']:
    case TooltipPlacement['top-start']:
      return css`
          .redsift-tooltip-content__arrow {
            border-width: 6px 6px 0;
            border-right-color: transparent;
            border-bottom-color: transparent;
            border-left-color: transparent;
          }
        `;
    case TooltipPlacement.right:
    case TooltipPlacement['right-end']:
    case TooltipPlacement['right-start']:
      return css`
          .redsift-tooltip-content__arrow {
            border-width: 6px 6px 6px 0;
            border-top-color: transparent;
            border-bottom-color: transparent;
            border-left-color: transparent;
          }
        `;
    case TooltipPlacement.bottom:
    case TooltipPlacement['bottom-end']:
    case TooltipPlacement['bottom-start']:
    default:
      return css`
          .redsift-tooltip-content__arrow {
            border-width: 0 6px 6px;
            border-top-color: transparent;
            border-right-color: transparent;
            border-left-color: transparent;
          }
        `;
  }
}}
`;

const COMPONENT_NAME$g = 'TooltipContent';
const CLASSNAME$g = 'redsift-tooltip-content';
const DEFAULT_PROPS$g = {};

/**
 * The TooltipContent component.
 */
const TooltipContent = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    className,
    style
  } = props;
  const {
    getFloatingProps,
    isOpen,
    placement,
    refs,
    strategy,
    x,
    y,
    middlewareData: {
      arrow: {
        x: arrowX,
        y: arrowY
      } = {}
    }
  } = useTooltipContext();
  const popoverRef = useMergeRefs([refs.setFloating, ref]);
  const {
    arrowRef
  } = useTooltipContext();
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[placement.split('-')[0]];
  return /*#__PURE__*/React__default.createElement(FloatingPortal, {
    id: "redsift-app-container"
  }, isOpen && /*#__PURE__*/React__default.createElement(StyledTooltipContent, _extends$1({
    className: classNames(TooltipContent.className, className),
    ref: popoverRef,
    style: _objectSpread2({
      position: strategy,
      top: y !== null && y !== void 0 ? y : 0,
      left: x !== null && x !== void 0 ? x : 0,
      visibility: x == null ? 'hidden' : 'visible'
    }, style)
  }, getFloatingProps(props), {
    $placement: placement
  }), /*#__PURE__*/React__default.createElement("div", {
    ref: arrowRef,
    className: `${TooltipContent.className}__arrow`,
    style: {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      [staticSide]: '-6px'
    }
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${TooltipContent.className}__inner`
  }, children)));
});
TooltipContent.className = CLASSNAME$g;
TooltipContent.defaultProps = DEFAULT_PROPS$g;
TooltipContent.displayName = COMPONENT_NAME$g;

const COMPONENT_NAME$f = 'TooltipTrigger';
const CLASSNAME$f = 'redsift-tooltip-trigger';
const DEFAULT_PROPS$f = {};

/**
 * The TooltipTrigger component.
 */
const TooltipTrigger = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children
  } = props;
  const {
    getReferenceProps,
    refs,
    tooltipId
  } = useTooltipContext();
  const childrenRef = children.ref;
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);
  if ( /*#__PURE__*/React__default.isValidElement(children)) {
    var _children$props$child;
    return /*#__PURE__*/React__default.cloneElement(children, _objectSpread2({}, getReferenceProps(_objectSpread2(_objectSpread2(_objectSpread2({
      ref: triggerRef
    }, props), {}, {
      'aria-describedby': tooltipId
    }, children.props), {}, {
      children: (_children$props$child = children.props.children) !== null && _children$props$child !== void 0 ? _children$props$child : ''
    }))));
  }
  return /*#__PURE__*/React__default.createElement("span", _extends$1({
    ref: triggerRef
  }, getReferenceProps(props)), children);
});
TooltipTrigger.className = CLASSNAME$f;
TooltipTrigger.defaultProps = DEFAULT_PROPS$f;
TooltipTrigger.displayName = COMPONENT_NAME$f;

function useTooltip(_ref) {
  let {
    defaultOpen,
    delay,
    placement,
    isOpen: propsIsOpen,
    onOpen,
    tooltipId: propsTooltipId
  } = _ref;
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(propsIsOpen !== null && propsIsOpen !== void 0 ? propsIsOpen : defaultOpen);
  const [_id] = useId$1();
  const tooltipId = propsTooltipId !== null && propsTooltipId !== void 0 ? propsTooltipId : _id;
  useEffect(() => {
    setIsOpen(propsIsOpen !== null && propsIsOpen !== void 0 ? propsIsOpen : defaultOpen);
  }, [propsIsOpen, defaultOpen]);
  const handleOpen = useCallback(collapsed => {
    if (onOpen) {
      onOpen(collapsed);
    }
    if (propsIsOpen === undefined || propsIsOpen === null) {
      setIsOpen(collapsed);
    }
  }, [onOpen]);
  const data = useFloating({
    placement,
    open: isOpen,
    onOpenChange: handleOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({
      fallbackAxisSideDirection: 'start'
    }), shift({
      padding: 8
    }), arrow({
      element: arrowRef
    })]
  });
  const context = data.context;
  const hover = useHover(context, {
    move: false,
    delay: {
      open: delay,
      close: 0
    },
    handleClose: safePolygon()
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'tooltip'
  });
  const interactions = useInteractions([hover, focus, dismiss, role]);
  return React__default.useMemo(() => _objectSpread2(_objectSpread2(_objectSpread2({
    isOpen,
    handleOpen
  }, interactions), data), {}, {
    arrowRef,
    tooltipId
  }), [isOpen, handleOpen, interactions, data, arrowRef, tooltipId]);
}

const COMPONENT_NAME$e = 'Tooltip';
const CLASSNAME$e = 'redsift-tooltip';
const DEFAULT_PROPS$e = {
  delay: 500,
  placement: TooltipPlacement.top
};

/**
 * The Tooltip component.
 */
const BaseTooltip = props => {
  const {
    children,
    defaultOpen,
    delay,
    isOpen,
    onOpen,
    placement,
    tooltipId
  } = props;
  const tooltip = useTooltip({
    defaultOpen,
    delay,
    placement,
    isOpen,
    onOpen,
    tooltipId
  });
  const [[trigger], [content]] = partitionComponents(React__default.Children.toArray(children), [isComponent('TooltipTrigger'), isComponent('TooltipContent')]);
  return /*#__PURE__*/React__default.createElement(TooltipContext.Provider, {
    value: tooltip
  }, trigger, content);
};
BaseTooltip.className = CLASSNAME$e;
BaseTooltip.defaultProps = DEFAULT_PROPS$e;
BaseTooltip.displayName = COMPONENT_NAME$e;
const Tooltip = Object.assign(BaseTooltip, {
  Trigger: TooltipTrigger,
  Content: TooltipContent
});

const useTooltipContext = () => {
  const context = React__default.useContext(TooltipContext);
  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }
  return context;
};

const _excluded$f = ["areAllCollapsed", "bannerVariant", "children", "className", "color", "defaultAllCollapsed", "icon", "isCollapsible", "isLoading", "onCollapseAll", "shield"];
const COMPONENT_NAME$d = 'DetailedCard';
const CLASSNAME$d = 'redsift-detailed-card';
const DEFAULT_PROPS$d = {
  isCollapsible: true,
  width: '400px'
};
const hasMultipleCollapsibleChildren = children => {
  const collapsibleChildren = [];
  const childrenArray = React__default.Children.toArray(children);
  for (const child of childrenArray) {
    if (isComponent(DetailedCardSection)(child)) {
      let hasCollapsibleItems = Boolean(child.isCollapsible);
      React__default.Children.forEach(child.props.children, sectionChild => {
        if (isComponent(DetailedCardCollapsibleSectionItems)(sectionChild)) {
          hasCollapsibleItems = true;
        }
      });
      if (hasCollapsibleItems) {
        collapsibleChildren.push(child);
      }
    }
  }
  return collapsibleChildren.length > 1;
};

/**
 * The DetailedCard component.
 */
const BaseDetailedCard = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      areAllCollapsed: propsAreAllCollapsed,
      bannerVariant,
      children,
      className,
      color,
      defaultAllCollapsed,
      icon,
      isCollapsible: propsIsCollapsible,
      isLoading,
      onCollapseAll,
      shield
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$f);
  const stringFormatter = useLocalizedStringFormatter(intlMessages$1);
  const isCollapsible = propsIsCollapsible && hasMultipleCollapsibleChildren(children);
  const [allCollapsed, setAllCollapsed] = useState({
    state: propsAreAllCollapsed !== null && propsAreAllCollapsed !== void 0 ? propsAreAllCollapsed : defaultAllCollapsed
  });
  useEffect(() => {
    setAllCollapsed({
      state: propsAreAllCollapsed !== null && propsAreAllCollapsed !== void 0 ? propsAreAllCollapsed : defaultAllCollapsed
    });
  }, [propsAreAllCollapsed, defaultAllCollapsed]);
  const handleCollapse = useCallback(allCollapsed => {
    if (onCollapseAll) {
      onCollapseAll(allCollapsed);
    }
    if (propsAreAllCollapsed === undefined || propsAreAllCollapsed === null) {
      setAllCollapsed({
        state: allCollapsed
      });
    }
  }, [onCollapseAll]);
  const [[header], content] = partitionComponents(React__default.Children.toArray(children), [isComponent('DetailedCardHeader')]);
  return /*#__PURE__*/React__default.createElement(StyledDetailedCard, _extends$1({}, forwardedProps, {
    className: classNames(BaseDetailedCard.className, className),
    ref: ref,
    $color: color
  }), color ? isLoading ? /*#__PURE__*/React__default.createElement(Skeleton, {
    height: bannerVariant === 'condensed' ? '8px' : '80px',
    width: "calc(100% + 32px)",
    margin: "-16px -16px 16px -16px"
  }) : /*#__PURE__*/React__default.createElement(Flexbox, {
    className: `${BaseDetailedCard.className}__banner`,
    alignItems: "center",
    height: bannerVariant === 'condensed' ? '8px' : '80px',
    justifyContent: "center",
    margin: "-16px -16px 16px -16px",
    width: "calc(100% + 32px)"
  }, bannerVariant === 'condensed' ? null : shield ? /*#__PURE__*/React__default.createElement(Shield, {
    variant: shield,
    isOutlined: true,
    isReversed: true,
    svgProps: {
      width: '40px',
      height: '40px'
    },
    style: {
      width: '40px',
      height: '40px'
    }
  }) : icon ? /*#__PURE__*/React__default.createElement(Icon, {
    icon: icon,
    color: "white",
    size: "xlarge"
  }) : null) : null, header ? /*#__PURE__*/React__default.createElement("div", {
    className: `${BaseDetailedCard.className}__header`
  }, header) : null, isCollapsible ? /*#__PURE__*/React__default.createElement(Flexbox, {
    justifyContent: "flex-end",
    className: `${BaseDetailedCard.className}__collapse-buttons`
  }, /*#__PURE__*/React__default.createElement(Tooltip, null, /*#__PURE__*/React__default.createElement(Tooltip.Trigger, null, /*#__PURE__*/React__default.createElement(IconButton, {
    "aria-label": stringFormatter.format('collapse-all'),
    "aria-describedby": undefined,
    icon: mdiPageLast,
    onClick: () => handleCollapse(true),
    style: {
      transform: 'rotate(-90deg)'
    },
    color: "question"
  })), /*#__PURE__*/React__default.createElement(Tooltip.Content, null, stringFormatter.format('collapse-all'))), /*#__PURE__*/React__default.createElement(Tooltip, null, /*#__PURE__*/React__default.createElement(Tooltip.Trigger, null, /*#__PURE__*/React__default.createElement(IconButton, {
    "aria-label": stringFormatter.format('expand-all'),
    "aria-describedby": undefined,
    icon: mdiKeyboardCaps,
    onClick: () => handleCollapse(false),
    style: {
      transform: 'rotate(180deg)'
    },
    color: "question"
  })), /*#__PURE__*/React__default.createElement(Tooltip.Content, null, stringFormatter.format('expand-all')))) : null, React__default.Children.map(content, child => {
    if (isComponent(DetailedCardSection)(child)) {
      return /*#__PURE__*/React__default.cloneElement(child, {
        isCollapsed: allCollapsed.state,
        key: Math.random()
      });
    }
    return child;
  }));
});
BaseDetailedCard.className = CLASSNAME$d;
BaseDetailedCard.defaultProps = DEFAULT_PROPS$d;
BaseDetailedCard.displayName = COMPONENT_NAME$d;
const DetailedCard = Object.assign(BaseDetailedCard, {
  Header: DetailedCardHeader,
  Section: DetailedCardSection,
  CollapsibleSectionItems: DetailedCardCollapsibleSectionItems,
  SectionItem: DetailedCardSectionItem
});

let EventKey = /*#__PURE__*/function (EventKey) {
  EventKey["ArrowLeft"] = "ArrowLeft";
  EventKey["ArrowRight"] = "ArrowRight";
  EventKey["ArrowUp"] = "ArrowUp";
  EventKey["ArrowDown"] = "ArrowDown";
  EventKey["Home"] = "Home";
  EventKey["End"] = "End";
  EventKey["Enter"] = "Enter";
  return EventKey;
}({});
let Navigation = /*#__PURE__*/function (Navigation) {
  Navigation["PREVIOUS"] = "PREVIOUS";
  Navigation["NEXT"] = "NEXT";
  Navigation["VERY_FIRST"] = "VERY_FIRST";
  Navigation["VERY_LAST"] = "VERY_LAST";
  Navigation["PREVIOUS_ROW"] = "PREVIOUS_ROW";
  Navigation["NEXT_ROW"] = "NEXT_ROW";
  Navigation["FIRST_IN_ROW"] = "FIRST_IN_ROW";
  Navigation["LAST_IN_ROW"] = "LAST_IN_ROW";
  return Navigation;
}({});
let FocusWithinGroupActionType = /*#__PURE__*/function (FocusWithinGroupActionType) {
  FocusWithinGroupActionType["REGISTER_TAB_STOP"] = "REGISTER_TAB_STOP";
  FocusWithinGroupActionType["UNREGISTER_TAB_STOP"] = "UNREGISTER_TAB_STOP";
  FocusWithinGroupActionType["KEY_DOWN_ON_ITEM"] = "KEY_DOWN_ON_ITEM";
  FocusWithinGroupActionType["CLICK_ON_ITEM"] = "CLICK_ON_ITEM";
  FocusWithinGroupActionType["KEY_DOWN_ON_LIST"] = "KEY_DOWN_ON_LIST";
  FocusWithinGroupActionType["FOCUS_ON_LIST"] = "FOCUS_ON_LIST";
  FocusWithinGroupActionType["BLUR_ON_LIST"] = "BLUR_ON_LIST";
  FocusWithinGroupActionType["TAB_STOP_UPDATED"] = "TAB_STOP_UPDATED";
  FocusWithinGroupActionType["OPTIONS_UPDATED"] = "OPTIONS_UPDATED";
  FocusWithinGroupActionType["FILTER_LIST"] = "FILTER_LIST";
  FocusWithinGroupActionType["DELAY_ACTION"] = "DELAY_ACTION";
  return FocusWithinGroupActionType;
}({});

/**
 * Component props.
 */

const FOCUS_WITHING_GROUP_INITIAL_STATE = {
  selectedId: null,
  allowFocusing: false,
  tabStops: [],
  direction: 'vertical',
  focusOnClick: false,
  focusOnInit: true,
  loopAround: false,
  rowStartMap: null,
  focusType: 'roving-tabindex',
  listRole: 'listbox'
};
const FocusWithinGroupContext = /*#__PURE__*/React__default.createContext({
  state: FOCUS_WITHING_GROUP_INITIAL_STATE,
  dispatch: () => {}
});

const DOCUMENT_POSITION_FOLLOWING = 4;
const FocusWithinGroupReducer = (state, action) => {
  switch (action.type) {
    case FocusWithinGroupActionType.REGISTER_TAB_STOP:
      {
        const newTabStop = action.payload;
        if (!newTabStop.domElementRef.current) {
          return state;
        }

        // Iterate backwards through state.tabStops since it is
        // most likely that the tab stop will need to be inserted
        // at the end of that array.
        let indexToInsertAt = -1;
        for (let i = state.tabStops.length - 1; i >= 0; --i) {
          const loopTabStop = state.tabStops[i];
          if (loopTabStop.id === newTabStop.id) {
            // console.warn(false, `'${newTabStop.id}' tab stop already registered`);
            return state;
          }
          // The compareDocumentPosition condition is true
          // if newTabStop follows loopTabStop:
          if (indexToInsertAt === -1 && loopTabStop.domElementRef.current && !!(loopTabStop.domElementRef.current.compareDocumentPosition(newTabStop.domElementRef.current) & DOCUMENT_POSITION_FOLLOWING)) {
            indexToInsertAt = i + 1;
            break;
          }
        }

        // The indexToInsertAt is -1 when newTabStop should be inserted
        // at the start of tabStops (the compareDocumentPosition condition
        // always returns false in that case).
        if (indexToInsertAt === -1) {
          indexToInsertAt = 0;
        }
        const newTabStops = state.tabStops.slice();
        newTabStops.splice(indexToInsertAt, 0, newTabStop);
        return _objectSpread2(_objectSpread2({}, state), {}, {
          selectedId: state.focusOnInit ? getUpdatedSelectedId(newTabStops, state.selectedId) : null,
          tabStops: newTabStops,
          rowStartMap: null
        });
      }
    case FocusWithinGroupActionType.UNREGISTER_TAB_STOP:
      {
        const id = action.payload.id;
        const newTabStops = state.tabStops.filter(tabStop => tabStop.id !== id);
        if (newTabStops.length === state.tabStops.length) {
          // console.warn(false, `'${id}' tab stop already unregistered`);
          return state;
        }
        return _objectSpread2(_objectSpread2({}, state), {}, {
          selectedId: state.focusOnInit ? getUpdatedSelectedId(newTabStops, state.selectedId) : null,
          tabStops: newTabStops,
          rowStartMap: null
        });
      }
    case FocusWithinGroupActionType.TAB_STOP_UPDATED:
      {
        const {
          id,
          rowIndex,
          isDisabled
        } = action.payload;
        const index = state.tabStops.findIndex(tabStop => tabStop.id === id);
        if (index === -1) {
          console.warn(false, `'${id}' tab stop not registered`);
          return state;
        }
        const tabStop = state.tabStops[index];
        if (tabStop.isDisabled === isDisabled && tabStop.rowIndex === rowIndex) {
          // Nothing to do so short-circuit.
          return state;
        }
        const newTabStop = _objectSpread2(_objectSpread2({}, tabStop), {}, {
          rowIndex,
          isDisabled
        });
        const newTabStops = state.tabStops.slice();
        newTabStops.splice(index, 1, newTabStop);
        return _objectSpread2(_objectSpread2({}, state), {}, {
          selectedId: state.focusOnInit ? getUpdatedSelectedId(newTabStops, state.selectedId) : null,
          tabStops: newTabStops,
          rowStartMap: null
        });
      }
    case FocusWithinGroupActionType.KEY_DOWN_ON_ITEM:
      {
        const {
          id,
          key,
          ctrlKey
        } = action.payload;
        const index = state.tabStops.findIndex(tabStop => tabStop.id === id);
        if (index === -1) {
          console.warn(false, `'${id}' tab stop not registered`);
          return state;
        }
        return moveSelectionFromKey(state, index, key, ctrlKey);
      }
    case FocusWithinGroupActionType.CLICK_ON_ITEM:
      {
        const id = action.payload.id;
        const index = state.tabStops.findIndex(tabStop => tabStop.id === id);
        if (index === -1) {
          console.warn(false, `'${id}' tab stop not registered`);
          return state;
        }
        const currentTabStop = state.tabStops[index];
        return currentTabStop.isDisabled ? state : selectTabStop(state, currentTabStop, undefined, state.focusOnClick);
      }
    case FocusWithinGroupActionType.FOCUS_ON_LIST:
      {
        var _action$payload$id, _state$tabStops$;
        const id = (_action$payload$id = action.payload.id) !== null && _action$payload$id !== void 0 ? _action$payload$id : (_state$tabStops$ = state.tabStops[0]) === null || _state$tabStops$ === void 0 ? void 0 : _state$tabStops$.id;
        const index = state.tabStops.findIndex(tabStop => tabStop.id === id);
        if (index === -1) {
          return state;
        }
        const currentTabStop = state.tabStops[index];
        return currentTabStop.isDisabled ? _objectSpread2(_objectSpread2({}, state), {}, {
          delayedAction: undefined
        }, action.payload) : selectTabStop(_objectSpread2(_objectSpread2({}, state), {}, {
          delayedAction: undefined
        }, action.payload), currentTabStop, undefined, state.focusOnClick);
      }
    case FocusWithinGroupActionType.BLUR_ON_LIST:
      {
        return _objectSpread2(_objectSpread2({}, state), {}, {
          activedescendant: ['', '']
        });
      }
    case FocusWithinGroupActionType.KEY_DOWN_ON_LIST:
      {
        const {
          key,
          ctrlKey
        } = action.payload;
        let index = state.tabStops.findIndex(tabStop => tabStop.id === state.selectedId);
        if (index === -1) {
          if (state.focusOnInit) {
            console.warn(false, `'${state.selectedId}' tab stop not registered`);
            return _objectSpread2(_objectSpread2({}, state), {}, {
              delayedAction: undefined
            });
          } else {
            index = 0;
          }
        }
        return moveSelectionFromKey(_objectSpread2(_objectSpread2({}, state), {}, {
          delayedAction: undefined
        }), index, key, ctrlKey);
      }
    case FocusWithinGroupActionType.OPTIONS_UPDATED:
      return _objectSpread2(_objectSpread2({}, state), action.payload);
    case FocusWithinGroupActionType.FILTER_LIST:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        selectedId: null
      }, action.payload);
    case FocusWithinGroupActionType.DELAY_ACTION:
      return _objectSpread2(_objectSpread2({}, state), {}, {
        delayedAction: action.payload
      });
    /* istanbul ignore next */
    default:
      return state;
  }
};
function findIndexOfVeryFirstActiveTabStop(tabStops) {
  return tabStops.findIndex(tabStop => !tabStop.isDisabled);
}
function findIndexOfVeryLastActiveTabStop(tabStops) {
  for (let i = tabStops.length - 1; i >= 0; --i) {
    const tabStop = tabStops[i];
    if (!tabStop.isDisabled) {
      return i;
    }
  }
  return -1;
}

// Determine the updated value for selectedId:
function getUpdatedSelectedId(tabStops, currentSelectedId) {
  if (currentSelectedId === null) {
    // There is not currently selected tab stop, so find
    // the first tab stop that is not isDisabled and return
    // its id, otherwise return null.
    const index = tabStops.findIndex(tabStop => !tabStop.isDisabled);
    return index === -1 ? null : tabStops[index].id;
  }
  let index = tabStops.findIndex(tabStop => tabStop.id === currentSelectedId);
  if (index !== -1 && !tabStops[index].isDisabled) {
    // The current selected id is still valid, so return it.
    return currentSelectedId;
  }

  // Finds the first tab stop that is not isDisabled and return
  // its id, otherwise return null.
  index = tabStops.findIndex(tabStop => !tabStop.isDisabled);
  return index === -1 ? null : tabStops[index].id;
}

// Translates the user key down event info into a navigation instruction.
function getNavigationValue(key, ctrlKey, isGrid, direction, loopAround, isFirst, isLast) {
  switch (key) {
    case EventKey.ArrowLeft:
      if (isGrid || direction === 'horizontal' || direction === 'both') {
        return !isGrid && loopAround && isFirst ? Navigation.VERY_LAST : Navigation.PREVIOUS;
      }
      return null;
    case EventKey.ArrowRight:
      if (isGrid || direction === 'horizontal' || direction === 'both') {
        return !isGrid && loopAround && isLast ? Navigation.VERY_FIRST : Navigation.NEXT;
      }
      return null;
    case EventKey.ArrowUp:
      if (isGrid) {
        return Navigation.PREVIOUS_ROW;
      } else if (direction === 'vertical' || direction === 'both') {
        return loopAround && isFirst ? Navigation.VERY_LAST : Navigation.PREVIOUS;
      }
      return null;
    case EventKey.ArrowDown:
      if (isGrid) {
        return Navigation.NEXT_ROW;
      } else if (direction === 'vertical' || direction === 'both') {
        return loopAround && isLast ? Navigation.VERY_FIRST : Navigation.NEXT;
      }
      return null;
    case EventKey.Home:
      return !isGrid || ctrlKey ? Navigation.VERY_FIRST : Navigation.FIRST_IN_ROW;
    case EventKey.End:
      return !isGrid || ctrlKey ? Navigation.VERY_LAST : Navigation.LAST_IN_ROW;
    /* istanbul ignore next */
    default:
      return null;
  }
}

// Creates the new state for a tab stop when it becomes the selected one.
function selectTabStop(state, tabStop, rowStartMap) {
  let allowFocusing = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  return _objectSpread2(_objectSpread2({}, state), {}, {
    allowFocusing,
    selectedId: tabStop.id,
    rowStartMap: rowStartMap || state.rowStartMap,
    activedescendant: state.focusType === 'virtual-focus' ? [tabStop.id, tabStop.value] : undefined
  });
}

// Creates the row start index lookup map
// for the currently registered tab stops.
function createRowStartMap(state) {
  const map = new Map();
  for (let i = 0; i < state.tabStops.length; ++i) {
    const {
      rowIndex
    } = state.tabStops[i];
    if (rowIndex !== null && !map.has(rowIndex)) {
      map.set(rowIndex, i);
    }
  }
  return map;
}
function moveSelectionFromKey(state, index, key, ctrlKey) {
  const currentTabStop = state.tabStops[index];
  if (currentTabStop.isDisabled) {
    return state;
  }
  const isGrid = currentTabStop.rowIndex !== null;
  const isFirst = index === findIndexOfVeryFirstActiveTabStop(state.tabStops);
  const isLast = index === findIndexOfVeryLastActiveTabStop(state.tabStops);
  const navigation = getNavigationValue(key, ctrlKey, isGrid, state.direction, state.loopAround, isFirst, isLast);
  if (!navigation) {
    return state;
  }
  switch (navigation) {
    case Navigation.NEXT:
      {
        for (let i = index + 1; i < state.tabStops.length; ++i) {
          const tabStop = state.tabStops[i];
          if (isGrid && tabStop.rowIndex !== currentTabStop.rowIndex) {
            break;
          }
          if (!tabStop.isDisabled) {
            return selectTabStop(state, tabStop);
          }
        }
      }
      break;
    case Navigation.PREVIOUS:
      {
        for (let i = index - 1; i >= 0; --i) {
          const tabStop = state.tabStops[i];
          if (isGrid && tabStop.rowIndex !== currentTabStop.rowIndex) {
            break;
          }
          if (!tabStop.isDisabled) {
            return selectTabStop(state, tabStop);
          }
        }
      }
      break;
    case Navigation.VERY_FIRST:
      {
        const index = findIndexOfVeryFirstActiveTabStop(state.tabStops);
        if (index > -1) {
          return selectTabStop(state, state.tabStops[index]);
        }
      }
      break;
    case Navigation.VERY_LAST:
      {
        const index = findIndexOfVeryLastActiveTabStop(state.tabStops);
        if (index > -1) {
          return selectTabStop(state, state.tabStops[index]);
        }
      }
      break;
    case Navigation.PREVIOUS_ROW:
      {
        if (currentTabStop.rowIndex === null || currentTabStop.rowIndex === 0) {
          return state;
        }
        const rowStartMap = state.rowStartMap || createRowStartMap(state);
        const rowStartIndex = rowStartMap.get(currentTabStop.rowIndex);
        if (rowStartIndex === undefined) {
          return state;
        }
        const columnOffset = index - rowStartIndex;
        for (let i = currentTabStop.rowIndex - 1; i >= 0; --i) {
          const rowStartIndex = rowStartMap.get(i);
          if (rowStartIndex === undefined) {
            return state;
          }
          const rowTabStop = state.tabStops[rowStartIndex + columnOffset];
          if (rowTabStop && !rowTabStop.isDisabled) {
            return selectTabStop(state, rowTabStop, rowStartMap);
          }
        }
        return _objectSpread2(_objectSpread2({}, state), {}, {
          allowFocusing: true,
          rowStartMap
        });
      }
    case Navigation.NEXT_ROW:
      {
        const maxRowIndex = state.tabStops[state.tabStops.length - 1].rowIndex;
        if (currentTabStop.rowIndex === null || maxRowIndex === null || currentTabStop.rowIndex === maxRowIndex) {
          return state;
        }
        const rowStartMap = state.rowStartMap || createRowStartMap(state);
        const rowStartIndex = rowStartMap.get(currentTabStop.rowIndex);
        if (rowStartIndex === undefined) {
          return state;
        }
        const columnOffset = index - rowStartIndex;
        for (let i = currentTabStop.rowIndex + 1; i <= maxRowIndex; ++i) {
          const rowStartIndex = rowStartMap.get(i);
          if (rowStartIndex === undefined) {
            return state;
          }
          const rowTabStop = state.tabStops[rowStartIndex + columnOffset];
          if (rowTabStop && !rowTabStop.isDisabled) {
            return selectTabStop(state, rowTabStop, rowStartMap);
          }
        }
        return _objectSpread2(_objectSpread2({}, state), {}, {
          allowFocusing: true,
          rowStartMap
        });
      }
    case Navigation.FIRST_IN_ROW:
      {
        if (currentTabStop.rowIndex === null) {
          return state;
        }
        const rowStartMap = state.rowStartMap || createRowStartMap(state);
        const rowStartIndex = rowStartMap.get(currentTabStop.rowIndex);
        if (rowStartIndex === undefined) {
          return state;
        }
        for (let i = rowStartIndex; i < state.tabStops.length; ++i) {
          const tabStop = state.tabStops[i];
          if (tabStop.rowIndex !== currentTabStop.rowIndex) {
            break;
          } else if (!tabStop.isDisabled) {
            return selectTabStop(state, state.tabStops[i], rowStartMap);
          }
        }
      }
      break;
    case Navigation.LAST_IN_ROW:
      {
        if (currentTabStop.rowIndex === null) {
          return state;
        }
        const rowStartMap = state.rowStartMap || createRowStartMap(state);
        const rowEndIndex = rowStartMap.has(currentTabStop.rowIndex + 1) ? (rowStartMap.get(currentTabStop.rowIndex + 1) || 0) - 1 : state.tabStops.length - 1;
        for (let i = rowEndIndex; i >= 0; --i) {
          const tabStop = state.tabStops[i];
          if (tabStop.rowIndex !== currentTabStop.rowIndex) {
            break;
          } else if (!tabStop.isDisabled) {
            return selectTabStop(state, state.tabStops[i], rowStartMap);
          }
        }
      }
      break;
  }
  return state;
}

/**
 * The FocusWithinGroup component.
 *
 * @param {ReactNode} children - The child content, which will
 * include the DOM elements to rove between using the tab key.
 * @param {KeyDirection} direction - An optional direction value
 * that only applies when the roving tabindex is not being
 * used within a grid. This value specifies the arrow key behaviour.
 * The default value is 'horizontal'.
 * When set to 'horizontal' then only the ArrowLeft and ArrowRight
 * keys move to the previous and next tab stop respectively.
 * When set to 'vertical' then only the ArrowUp and ArrowDown keys
 * move to the previous and next tab stop respectively. When set
 * to 'both' then both the ArrowLeft and ArrowUp keys can be used
 * to move to the previous tab stop, and both the ArrowRight
 * and ArrowDown keys can be used to move to the next tab stop.
 * If you do not pass an explicit value then the 'horizontal'
 * behaviour applies.
 * @param {boolean} focusOnClick - An optional flag for indicating
 * if `focus()` should invoked on an item in the roving tabindex
 * when it is clicked. The default value for this flag is `false`,
 * meaning that `focus()` will not be invoked on click.
 * Browsers are
 * [inconsistent in their behaviour](https://zellwk.com/blog/inconsistent-button-behavior/)
 * when a button is clicked so you will see some variation between
 * the browsers with the default value. Prior to version 3 of this library,
 * the behaviour was to always invoke `focus()` on click; this behaviour
 * can be maintained by passing `true`.
 * @param {boolean} loopAround - An optional flag that,
 * when set to `true`, will loop the tabindex around when the user
 * tries to tab to the first or last elements in the roving tabindex,
 * rather than stopping. The default value is `false` (no looping).
 * Note that this option does not apply if the roving tabindex
 * is being used on a grid.
 */
const FocusWithinGroup = props => {
  const {
    children,
    direction,
    focusOnClick,
    loopAround,
    focusType,
    listRole,
    focusOnInit,
    activedescendant,
    maxOptionsLength
  } = props;
  const [state, dispatch] = useReducer(FocusWithinGroupReducer, _objectSpread2(_objectSpread2({}, FOCUS_WITHING_GROUP_INITIAL_STATE), {}, {
    direction,
    focusOnClick,
    loopAround,
    focusType,
    listRole,
    focusOnInit,
    activedescendant,
    maxOptionsLength
  }));

  // Update the options whenever they change:
  useEffect(() => {
    dispatch({
      type: FocusWithinGroupActionType.OPTIONS_UPDATED,
      payload: {
        direction: direction !== null && direction !== void 0 ? direction : FOCUS_WITHING_GROUP_INITIAL_STATE.direction,
        focusOnClick: focusOnClick !== null && focusOnClick !== void 0 ? focusOnClick : FOCUS_WITHING_GROUP_INITIAL_STATE.focusOnClick,
        loopAround: loopAround !== null && loopAround !== void 0 ? loopAround : FOCUS_WITHING_GROUP_INITIAL_STATE.loopAround,
        focusType: focusType !== null && focusType !== void 0 ? focusType : FOCUS_WITHING_GROUP_INITIAL_STATE.focusType,
        listRole: listRole !== null && listRole !== void 0 ? listRole : FOCUS_WITHING_GROUP_INITIAL_STATE.listRole,
        focusOnInit: focusOnInit !== null && focusOnInit !== void 0 ? focusOnInit : FOCUS_WITHING_GROUP_INITIAL_STATE.focusOnInit,
        activedescendant: activedescendant !== null && activedescendant !== void 0 ? activedescendant : FOCUS_WITHING_GROUP_INITIAL_STATE.activedescendant,
        maxOptionsLength: maxOptionsLength !== null && maxOptionsLength !== void 0 ? maxOptionsLength : FOCUS_WITHING_GROUP_INITIAL_STATE.maxOptionsLength
      }
    });
  }, [direction, focusOnClick, focusOnInit, loopAround, focusType, listRole, activedescendant, maxOptionsLength]);
  const context = useMemo(() => ({
    state,
    dispatch
  }), [state]);
  return /*#__PURE__*/React__default.createElement(FocusWithinGroupContext.Provider, {
    value: context
  }, children);
};

let counter = 0;
function uniqueId() {
  return `rti_${++counter}`;
}

/**
 * Includes the given DOM element in the current roving tabindex.
 * @param {RefObject<Element>} domElementRef The DOM element to include.
 * This must be the same DOM element for the lifetime of the containing
 * component.
 * @param {boolean} isDisabled Whether or not the DOM element is currently
 * enabled. This value can be updated as appropriate throughout the lifetime
 * of the containing component.
 * @param {number?} rowIndex An optional integer value that must be
 * populated if the roving tabindex is being used in a grid. In that case,
 * set it to the zero-based index of the row that the given DOM element
 * is currently part of. You can update this row index as appropriate
 * throughout the lifetime of the containing component, for example if
 * the shape of the grid can change dynamically.
 * @returns A tuple of values to be applied by the containing
 * component for the roving tabindex to work correctly.
 * First tuple value: The tabIndex value to apply to the tab stop
 * element.
 * Second tuple value: Whether or not focus() should be invoked on the
 * tab stop element.
 * Third tuple value: The onKeyDown callback to apply to the tab
 * stop element. If the key press is relevant to the hook then
 * event.preventDefault() will be invoked on the event.
 * Fourth tuple value: The onClick callback to apply to the tab
 * stop element.
 */
function useFocusOnListItem(props) {
  var _context$state$active;
  const {
    domElementRef,
    isDisabled,
    id,
    value,
    rowIndex = null
  } = props;
  // Create a stable ID for the lifetime of the component:
  const idRef = useRef(null);
  function getId() {
    if (id) {
      return id;
    }
    if (!idRef.current) {
      idRef.current = uniqueId();
    }
    return idRef.current;
  }
  const isMounted = useRef(false);
  const context = useContext(FocusWithinGroupContext);

  // Register the tab stop on mount and unregister it on unmount:
  useEffect(() => {
    const id = getId();
    context.dispatch({
      type: FocusWithinGroupActionType.REGISTER_TAB_STOP,
      payload: {
        id,
        domElementRef,
        rowIndex,
        isDisabled,
        value: value
      }
    });
    return () => {
      context.dispatch({
        type: FocusWithinGroupActionType.UNREGISTER_TAB_STOP,
        payload: {
          id
        }
      });
    };
  }, []);

  // Update the tab stop data if rowIndex or isDisabled change.
  // The isMounted flag is used to prevent this effect running
  // on mount, which is benign but redundant (as the
  // REGISTER_TAB_STOP action would have just been dispatched).
  useEffect(() => {
    if (isMounted.current) {
      context.dispatch({
        type: FocusWithinGroupActionType.TAB_STOP_UPDATED,
        payload: {
          id: getId(),
          rowIndex,
          isDisabled
        }
      });
    } else {
      isMounted.current = true;
    }
  }, [rowIndex, isDisabled]);

  // Create a stable callback function for handling key down events:
  const handleKeyDown = useCallback(event => {
    const key = EventKey[event.key];
    if (!key) {
      return;
    }
    context.dispatch({
      type: FocusWithinGroupActionType.KEY_DOWN_ON_ITEM,
      payload: {
        id: getId(),
        key,
        ctrlKey: event.ctrlKey
      }
    });
    event.preventDefault();
  }, []);

  // Create a stable callback function for handling click events:
  const handleClick = useCallback(() => {
    context.dispatch({
      type: FocusWithinGroupActionType.CLICK_ON_ITEM,
      payload: {
        id: getId()
      }
    });
  }, []);

  // Determine if the current tab stop is the currently active one:
  const selected = getId() === context.state.selectedId;
  const tabIndex = context.state.focusType === 'roving-tabindex' ? selected ? 0 : -1 : undefined;
  const isFocused = selected && context.state.allowFocusing;
  useEffect(() => {
    if (isFocused && domElementRef.current && context.state.focusType === 'roving-tabindex') {
      var _current;
      (_current = domElementRef.current) === null || _current === void 0 ? void 0 : _current.focus();
    }
  }, [isFocused]);
  return {
    tabIndex,
    isFocused: context.state.focusType === 'roving-tabindex' ? isFocused && document.activeElement === domElementRef.current : selected && ((_context$state$active = context.state.activedescendant) === null || _context$state$active === void 0 ? void 0 : _context$state$active[0]) === getId(),
    handleKeyDown,
    handleClick
  };
}

function useFocusOnList() {
  const context = useContext(FocusWithinGroupContext);
  const handleKeyDown = useCallback(event => {
    const key = EventKey[event.key];
    if (!key) {
      return;
    }
    context.dispatch({
      type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
      payload: {
        key,
        ctrlKey: event.ctrlKey
      }
    });
    event.preventDefault();
  }, []);
  const handleFocus = useCallback(() => {
    context.dispatch({
      type: FocusWithinGroupActionType.FOCUS_ON_LIST,
      payload: {
        id: context.state.selectedId
      }
    });
  }, [context.state.selectedId]);
  const handleBlur = useCallback(() => {
    context.dispatch({
      type: FocusWithinGroupActionType.BLUR_ON_LIST,
      payload: {}
    });
  }, []);
  return {
    activedescendant: context.state.activedescendant,
    handleKeyDown,
    handleFocus,
    handleBlur
  };
}

/**
 * Component style.
 */
const StyledGrid = styled.div`
  display: ${_ref => {
  let {
    inline
  } = _ref;
  return inline ? 'inline-grid' : 'grid';
}};
  flex: 1;

  ${baseStyling}
  ${baseInternalSpacing}
  ${baseGrid}

  ${_ref2 => {
  let {
    $divider
  } = _ref2;
  return $divider ? css`
          overflow: hidden;
          .redsift-grid-item:not(:empty) {
            position: relative;

            ::before {
              content: '';
              position: absolute;
              background-color: var(--redsift-color-neutral-lightgrey);
              inline-size: 1px;
              block-size: 100%;
              inset-inline-start: calc(${Math.max(1, $divider.colGap / 2)}px * -1);
            }

            ::after {
              content: '';
              position: absolute;
              background-color: var(--redsift-color-neutral-lightgrey);
              inline-size: 200vw;
              block-size: 1px;
              inset-inline-start: -100vw;
              inset-block-start: calc(${Math.max(1, $divider.rowGap / 2)}px * -1);
              z-index: 0;
            }
          }
        ` : '';
}}
`;

/**
 * Component style.
 */
const StyledGridItem = styled.div`
  display: inherit;

  ${baseStyling}
  ${baseInternalSpacing}
`;

const _excluded$e = ["children", "className"];
const COMPONENT_NAME$c = 'GridItem';
const CLASSNAME$c = 'redsift-grid-item';
const DEFAULT_PROPS$c = {};

/**
 * The GridItem component.
 */
const GridItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$e);
  return /*#__PURE__*/React__default.createElement(StyledGridItem, _extends$1({}, forwardedProps, {
    className: classNames(GridItem.className, className),
    ref: ref
  }), children);
});
GridItem.className = CLASSNAME$c;
GridItem.defaultProps = DEFAULT_PROPS$c;
GridItem.displayName = COMPONENT_NAME$c;

const _excluded$d = ["children", "className", "divider", "gap"];
const COMPONENT_NAME$b = 'Grid';
const CLASSNAME$b = 'redsift-grid';
const DEFAULT_PROPS$b = {};

/**
 * The Grid component.
 */
const BaseGrid = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      divider,
      gap
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$d);
  return /*#__PURE__*/React__default.createElement(StyledGrid, _extends$1({}, forwardedProps, {
    gap: divider ? `${divider.rowGap}px ${divider.colGap}px` : gap,
    className: classNames(BaseGrid.className, className),
    ref: ref,
    $divider: divider
  }), children);
});
BaseGrid.className = CLASSNAME$b;
BaseGrid.defaultProps = DEFAULT_PROPS$b;
BaseGrid.displayName = COMPONENT_NAME$b;
const Grid = Object.assign(BaseGrid, {
  Item: GridItem
});

/**
 * Component style.
 */
const StyledItem = styled.div`
  height: 36px;
  position: relative;
  width: 100%;
  padding: 8px 16px;

  ${baseContainer}

  align-items: center;
  background: none;
  border: none;
  box-sizing: border-box;
  display: inline-flex;

  ${_ref => {
  let {
    $isActive,
    $isSelected,
    $isDisabled,
    $isHovered,
    $isFocused
  } = _ref;
  return css`
    background-color: ${($isActive || $isSelected) && ($isHovered || $isFocused) ? 'var(--redsift-color-ctas-primary-active-hover)' : $isActive ? 'var(--redsift-color-ctas-primary-active)' : $isHovered ? 'var(--redsift-color-ctas-primary-hover)' : $isFocused ? 'var(--redsift-color-ctas-primary-hover)' : $isSelected && !$isDisabled ? 'var(--redsift-color-ctas-primary-active)' : $isSelected && $isDisabled ? 'var(--redsift-color-neutral-lightgrey)' : $isDisabled ? 'none' : 'none'};
  `;
}}

  &:hover,
  &:focus-visible {
    outline: none;

    ${_ref2 => {
  let {
    $isActive,
    $isSelected,
    $isDisabled
  } = _ref2;
  return css`
      cursor: ${$isDisabled ? 'default' : 'pointer'};
      background-color: ${$isActive ? 'var(--redsift-color-ctas-primary-active-hover)' : $isSelected && !$isDisabled ? 'var(--redsift-color-ctas-primary-active-hover)' : $isDisabled ? 'none' : 'var(--redsift-color-ctas-primary-hover)'};
    `;
}};
  }

  &:active {
    outline: none;

    ${_ref3 => {
  let {
    $isHovered,
    $isDisabled
  } = _ref3;
  return css`
      background-color: ${$isHovered ? 'var(--redsift-color-ctas-primary-active-hover)' : $isDisabled ? 'none' : 'var(--redsift-color-ctas-primary-active)'};
    `;
}};
  }
`;

const LISTBOX_INITIAL_STATE = {
  isDisabled: false,
  isReadOnly: false,
  selectedValues: [],
  selectionMode: 'single'
};
const ListboxContext = /*#__PURE__*/React__default.createContext({
  state: LISTBOX_INITIAL_STATE,
  dispatch: () => {}
});

/**
 * Context props.
 */

let ListboxActionType = /*#__PURE__*/function (ListboxActionType) {
  ListboxActionType["TOGGLE"] = "TOGGLE";
  ListboxActionType["SET"] = "SET";
  ListboxActionType["UPDATE_OPTIONS"] = "UPDATE_OPTIONS";
  return ListboxActionType;
}({});
/**
 * Component variant.
 */
const ListboxVariant = {
  standalone: 'standalone',
  unstyled: 'unstyled'
};

/**
 * Component props.
 */

function useListboxItem(props) {
  const {
    domElementRef,
    value,
    id,
    isDisabled,
    propsIsSelected
  } = props;
  const {
    tabIndex,
    isFocused,
    handleKeyDown: handleFocusKeyDown,
    handleClick: handleFocusClick
  } = useFocusOnListItem({
    domElementRef,
    isDisabled: isDisabled,
    id,
    value
  });
  const context = useContext(ListboxContext);
  const handleKeyDown = useCallback(event => {
    handleFocusKeyDown(event);
    const code = event.code;
    if (code === 'Enter' || code === 'Space') {
      if (context.state.selectionMode === 'multiple') {
        context.dispatch({
          type: ListboxActionType.TOGGLE,
          payload: {
            value,
            activedescendant: [id, value]
          }
        });
      } else {
        context.dispatch({
          type: ListboxActionType.SET,
          payload: {
            values: [value],
            activedescendant: [id, value]
          }
        });
      }
    }
  }, []);
  const handleClick = useCallback(() => {
    handleFocusClick();
    if (context.state.selectionMode === 'multiple') {
      context.dispatch({
        type: ListboxActionType.TOGGLE,
        payload: {
          value,
          activedescendant: [id, value]
        }
      });
    } else {
      context.dispatch({
        type: ListboxActionType.SET,
        payload: {
          values: [value],
          activedescendant: [id, value]
        }
      });
    }
  }, []);
  const isSelected = propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : context.state.selectedValues.find(selectedValue => selectedValue === value) !== undefined;
  useEffect(() => {
    if (isFocused && domElementRef.current) {
      var _current;
      (_current = domElementRef.current) === null || _current === void 0 ? void 0 : _current.focus();
    }
  }, [isFocused]);
  return {
    tabIndex,
    isFocused,
    isSelected,
    isDisabled,
    handleKeyDown,
    handleClick
  };
}

function useMenuItem(props) {
  const {
    domElementRef,
    id,
    isDisabled,
    onClick
  } = props;
  const {
    tabIndex,
    isFocused,
    handleKeyDown: handleFocusKeyDown,
    handleClick: handleFocusClick
  } = useFocusOnListItem({
    domElementRef,
    isDisabled: isDisabled,
    id
  });
  const handleKeyDown = useCallback(event => {
    const code = event.code;
    if (code === 'Enter' || code === 'Space') {
      onClick === null || onClick === void 0 ? void 0 : onClick();
    }
    handleFocusKeyDown(event);
  }, []);
  const handleClick = useCallback(() => {
    handleFocusClick();
    onClick === null || onClick === void 0 ? void 0 : onClick();
  }, []);
  useEffect(() => {
    if (isFocused && domElementRef.current) {
      var _current;
      (_current = domElementRef.current) === null || _current === void 0 ? void 0 : _current.focus();
    }
  }, [isFocused]);
  return {
    tabIndex,
    isFocused,
    isDisabled,
    handleKeyDown,
    handleClick
  };
}

const _excluded$c = ["children", "className", "defaultSelected", "id", "isActive", "isDisabled", "isHovered", "isSelected", "value"],
  _excluded2 = ["children", "className", "id", "isActive", "isDisabled", "isHovered", "onClick"];
const COMPONENT_NAME$a = 'Item';
const CLASSNAME$a = 'redsift-item';
const DEFAULT_PROPS$a = {};
const RenderedListboxItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      defaultSelected,
      id: propsId,
      isActive,
      isDisabled: propsIsDisabled,
      isHovered,
      isSelected: propsIsSelected,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$c);
  const id = propsId !== null && propsId !== void 0 ? propsId : value;
  const _ref = useRef();
  const divRef = ref !== null && ref !== void 0 ? ref : _ref;
  const {
    tabIndex,
    isFocused,
    isSelected,
    isDisabled,
    handleKeyDown,
    handleClick
  } = useListboxItem({
    domElementRef: divRef,
    value,
    id,
    isDisabled: propsIsDisabled,
    propsIsSelected: propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : defaultSelected
  });
  return /*#__PURE__*/React__default.createElement(StyledItem, _extends$1({
    role: "option"
  }, forwardedProps, {
    id: id,
    $isActive: isActive,
    $isDisabled: isDisabled,
    $isFocused: isFocused,
    $isHovered: isHovered,
    $isSelected: isSelected,
    "aria-selected": isSelected,
    className: classNames(Item.className, className),
    onClick: handleClick,
    ref: divRef,
    onKeyDown: handleKeyDown,
    tabIndex: tabIndex
  }), children !== null && children !== void 0 ? children : value);
});
const RenderedMenuItem = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      id: propsId,
      isActive,
      isDisabled: propsIsDisabled,
      isHovered,
      onClick
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded2);
  const [_id] = useId$1();
  const id = propsId !== null && propsId !== void 0 ? propsId : _id;
  const _ref = useRef();
  const divRef = ref !== null && ref !== void 0 ? ref : _ref;
  const {
    tabIndex,
    isFocused,
    isDisabled,
    handleKeyDown,
    handleClick
  } = useMenuItem({
    domElementRef: divRef,
    id,
    isDisabled: propsIsDisabled,
    onClick: onClick
  });
  return /*#__PURE__*/React__default.createElement(StyledItem, _extends$1({
    role: "option"
  }, forwardedProps, {
    id: id,
    $isActive: isActive,
    $isDisabled: isDisabled,
    $isFocused: isFocused,
    $isHovered: isHovered,
    $isSelected: false,
    className: classNames(Item.className, className),
    onClick: handleClick,
    ref: divRef,
    onKeyDown: handleKeyDown,
    tabIndex: tabIndex
  }), children);
});

/**
 * The Item component.
 */
const Item = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    value
  } = props;
  const focusContext = useContext(FocusWithinGroupContext);
  const isListbox = focusContext.state.listRole === 'listbox';
  if (!focusContext.state.filter) {
    return isListbox ? /*#__PURE__*/React__default.createElement(RenderedListboxItem, _extends$1({}, props, {
      value: props.value,
      ref: ref
    })) : /*#__PURE__*/React__default.createElement(RenderedMenuItem, _extends$1({}, props, {
      ref: ref
    }));
  }
  const {
    value: filterValue,
    type,
    caseSensitive
  } = focusContext.state.filter;
  if (!filterValue) {
    return isListbox ? /*#__PURE__*/React__default.createElement(RenderedListboxItem, _extends$1({}, props, {
      value: props.value,
      ref: ref
    })) : /*#__PURE__*/React__default.createElement(RenderedMenuItem, _extends$1({}, props, {
      ref: ref
    }));
  } else if (caseSensitive && (type === 'startsWith' && value.startsWith(filterValue) || type === 'contains' && value.includes(filterValue) || type === 'endsWith' && value.endsWith(filterValue))) {
    return isListbox ? /*#__PURE__*/React__default.createElement(RenderedListboxItem, _extends$1({}, props, {
      value: props.value,
      ref: ref
    })) : /*#__PURE__*/React__default.createElement(RenderedMenuItem, _extends$1({}, props, {
      ref: ref
    }));
  } else if (!caseSensitive && (type === 'startsWith' && value.toLowerCase().startsWith(filterValue.toLowerCase()) || type === 'contains' && value.toLowerCase().includes(filterValue.toLowerCase()) || type === 'endsWith' && value.toLowerCase().endsWith(filterValue.toLowerCase()))) {
    return isListbox ? /*#__PURE__*/React__default.createElement(RenderedListboxItem, _extends$1({}, props, {
      value: props.value,
      ref: ref
    })) : /*#__PURE__*/React__default.createElement(RenderedMenuItem, _extends$1({}, props, {
      ref: ref
    }));
  }
  return null;
});
Item.className = CLASSNAME$a;
Item.defaultProps = DEFAULT_PROPS$a;
Item.displayName = COMPONENT_NAME$a;

/**
 * Component style.
 */
const StyledLink = styled.a`
  ${baseStyling}

  background: none;
  border: none;
  color: inherit;
  cursor: text !important;
  font-family: var(--redsift-typography-link-font-family);
  font-size: var(--redsift-typography-link-font-size);
  font-weight: var(--redsift-typography-link-font-weight);
  line-height: var(--redsift-typography-link-line-height);
  padding: unset;
  text-decoration: none;

  ${_ref => {
  let {
    $isDisabled
  } = _ref;
  return !$isDisabled ? css`
          color: var(--redsift-color-ctas-primary-primary);
          text-decoration: underline;
          text-decoration-color: var(--redsift-color-ctas-primary-hover);
          text-underline-offset: 2px;

          &:hover,
          &:focus-visible {
            cursor: pointer !important;
            text-decoration-color: var(--redsift-color-ctas-primary-primary);
          }
        ` : '';
}}

  &:focus-visible {
    outline: 2px solid var(--redsift-color-ctas-primary-primary);
  }
`;

const _excluded$b = ["as", "children", "className", "href", "isDisabled"];
const COMPONENT_NAME$9 = 'Link';
const CLASSNAME$9 = 'redsift-link';
const DEFAULT_PROPS$9 = {};

/**
 * The Link is a semantic link that looks like a link.
 * Shoud be used for internal and external navigation only.
 *
 * For a semantic link that looks like a button, please use the ButtonLink component.
 * For a semantic button that looks like a button, please use the Button component.
 * For a semantic button that looks like a link, please use the LinkButton component.
 */
const Link = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as,
      children,
      className,
      href,
      isDisabled
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$b);
  return /*#__PURE__*/React__default.createElement(StyledLink, _extends$1({
    as: as,
    "aria-disabled": isDisabled,
    role: "link",
    tabIndex: !isDisabled ? 0 : undefined
  }, forwardedProps, {
    $isDisabled: isDisabled,
    className: classNames(Link.className, className),
    href: !isDisabled ? href : undefined,
    ref: ref
  }), children);
});
Link.className = CLASSNAME$9;
Link.defaultProps = DEFAULT_PROPS$9;
Link.displayName = COMPONENT_NAME$9;

const _excluded$a = ["children", "className", "disabled", "isDisabled"];
const COMPONENT_NAME$8 = 'LinkButton';
const CLASSNAME$8 = 'redsift-link-button';
const DEFAULT_PROPS$8 = {};

/**
 * The LinkButton is a semantic button that looks like a link.
 *
 * For a semantic button that looks like a button, please use the Button component.
 * For a semantic link that looks like a link, please use the Link component.
 * For a semantic link that looks like a button, please use the ButtonLink component.
 */
const LinkButton = /*#__PURE__*/forwardRef((props, ref) => {
  const buttonRef = ref || useRef();
  const {
      children,
      className,
      disabled,
      isDisabled: propsIsDisabled
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$a);
  const isDisabled = propsIsDisabled || disabled;
  return /*#__PURE__*/React__default.createElement(StyledLink, _extends$1({
    as: "button"
  }, forwardedProps, {
    $isDisabled: isDisabled || disabled,
    "aria-disabled": isDisabled || disabled,
    className: classNames(LinkButton.className, className),
    disabled: isDisabled || disabled
    // @ts-ignore
    ,
    ref: buttonRef
  }), children);
});
LinkButton.className = CLASSNAME$8;
LinkButton.defaultProps = DEFAULT_PROPS$8;
LinkButton.displayName = COMPONENT_NAME$8;

const _excluded$9 = ["children", "id", "isDisabled", "context"];
const ActiveDescendantListbox = /*#__PURE__*/forwardRef((props, ref) => {
  var _context$state$active;
  const {
      children,
      id,
      isDisabled,
      context
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$9);
  const {
    activedescendant,
    handleKeyDown: handleFocusKeyDown,
    handleFocus,
    handleBlur
  } = useFocusOnList();
  const handleKeyDown = useCallback(event => {
    handleFocusKeyDown(event);
    const code = event.code;
    if (code === 'Enter' || code === 'Space') {
      if (context.state.selectionMode === 'multiple') {
        var _activedescendant$;
        context.dispatch({
          type: ListboxActionType.TOGGLE,
          payload: {
            value: (_activedescendant$ = activedescendant === null || activedescendant === void 0 ? void 0 : activedescendant[1]) !== null && _activedescendant$ !== void 0 ? _activedescendant$ : '',
            activedescendant
          }
        });
      } else {
        var _activedescendant$2;
        context.dispatch({
          type: ListboxActionType.SET,
          payload: {
            values: [(_activedescendant$2 = activedescendant === null || activedescendant === void 0 ? void 0 : activedescendant[1]) !== null && _activedescendant$2 !== void 0 ? _activedescendant$2 : ''],
            activedescendant
          }
        });
      }
    }
  }, [activedescendant]);
  return /*#__PURE__*/React__default.createElement(Flexbox, _extends$1({
    flexDirection: "column",
    gap: "8px"
  }, forwardedProps, {
    "aria-disabled": isDisabled,
    ref: ref,
    id: id,
    role: "listbox",
    "aria-activedescendant": (_context$state$active = context.state.activedescendant) === null || _context$state$active === void 0 ? void 0 : _context$state$active[0],
    "aria-multiselectable": context.state.selectionMode === 'multiple',
    tabIndex: 0,
    style: {
      outline: 'none'
    },
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseDown: event => event.preventDefault(),
    onMouseLeave: handleBlur,
    onKeyDown: handleKeyDown
  }), /*#__PURE__*/React__default.createElement(ListboxContext.Provider, {
    value: context
  }, children));
});

const _excluded$8 = ["children", "id", "isDisabled", "context"];
const RovingTabindexListbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      id,
      isDisabled,
      context
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$8);
  return /*#__PURE__*/React__default.createElement(Flexbox, _extends$1({
    flexDirection: "column",
    gap: "8px"
  }, forwardedProps, {
    "aria-disabled": isDisabled,
    ref: ref,
    id: id,
    role: "listbox",
    "aria-multiselectable": context.state.selectionMode === 'multiple'
  }), /*#__PURE__*/React__default.createElement(ListboxContext.Provider, {
    value: context
  }, children));
});

/**
 * Component style.
 */
const StyledListbox = styled(Flexbox)`
  outline: none;

  ${_ref => {
  let {
    $variant,
    $isDisabled,
    $isInvalid
  } = _ref;
  return $variant === ListboxVariant.standalone ? css`
          span.redsift-listbox-label {
            font-family: var(--redsift-typography-body-font-family);
            font-weight: var(--redsift-typography-body-font-weight);
            font-size: var(--redsift-typography-body-font-size);
            line-height: var(--redsift-typography-body-line-height);

            color: var(--redsift-color-neutral-black);
            ${$isDisabled ? `
          color: var(--redsift-color-neutral-midgrey);
        ` : ''}
            ${$isInvalid ? `
          color: var(--redsift-color-notifications-error-primary);
        ` : ''}
          }

          .redsift-listbox-list {
            border: 1px solid var(--redsift-color-neutral-midgrey);
            padding: 4px;
            min-width: 200px;
          }
        ` : '';
}}
`;

const ListboxReducer = (state, action) => {
  switch (action.type) {
    case ListboxActionType.TOGGLE:
      {
        if (state.isReadOnly || state.isDisabled) {
          return state;
        }
        if (!state.selectedValues.includes(action.payload.value)) {
          return _objectSpread2(_objectSpread2({}, state), {}, {
            selectedValues: state.selectedValues.concat(action.payload.value)
          }, action.payload.activedescendant && {
            activedescendant: action.payload.activedescendant
          });
        }
        return _objectSpread2(_objectSpread2({}, state), {}, {
          selectedValues: state.selectedValues.filter(existingValue => existingValue !== action.payload.value)
        }, action.payload.activedescendant && {
          activedescendant: action.payload.activedescendant
        });
      }
    case ListboxActionType.SET:
      {
        if (state.isReadOnly || state.isDisabled) {
          return state;
        }
        return _objectSpread2(_objectSpread2({}, state), {}, {
          selectedValues: action.payload.values
        }, action.payload.activedescendant && {
          activedescendant: action.payload.activedescendant
        });
      }
    case ListboxActionType.UPDATE_OPTIONS:
      {
        return _objectSpread2(_objectSpread2({}, state), action.payload);
      }
    default:
      return state;
  }
};

const _excluded$7 = ["children", "className", "defaultValues", "direction", "focusOnClick", "focusType", "id", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "label", "loopAround", "onChange", "selectionMode", "values", "variant"];
const COMPONENT_NAME$7 = 'Listbox';
const CLASSNAME$7 = 'redsift-listbox';
const DEFAULT_PROPS$7 = {
  focusOnClick: false,
  focusType: 'roving-tabindex',
  selectionMode: 'single',
  variant: ListboxVariant.standalone
};

/**
 * The Listbox component.
 * Can be used as controlled or uncontrolled.
 */
const Listbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      defaultValues,
      direction,
      focusOnClick,
      focusType,
      id: propsId,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
      loopAround,
      onChange,
      selectionMode,
      values,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$7);
  const [_id] = useId$1();
  const id = propsId !== null && propsId !== void 0 ? propsId : _id;
  const _ref = useRef();
  const divRef = ref !== null && ref !== void 0 ? ref : _ref;
  const [state, dispatch] = useReducer(ListboxReducer, _objectSpread2(_objectSpread2({}, LISTBOX_INITIAL_STATE), {}, {
    isDisabled,
    isReadOnly,
    selectedValues: values || defaultValues || [],
    selectionMode
  }));
  useEffect(() => {
    if (values && values.length >= 0) {
      dispatch({
        type: ListboxActionType.SET,
        payload: {
          values
        }
      });
    }
  }, [values]);
  const context = useMemo(() => ({
    state,
    dispatch
  }), [state]);
  useEffect(() => {
    onChange === null || onChange === void 0 ? void 0 : onChange(state.selectedValues);
  }, [state.selectedValues]);
  return /*#__PURE__*/React__default.createElement(ConditionalWrapper, {
    wrapper: children => /*#__PURE__*/React__default.createElement(StyledListbox, _extends$1({
      flexDirection: "column",
      gap: "8px"
    }, forwardedProps, {
      $isDisabled: isDisabled,
      $isInvalid: isInvalid || isRequired && state.selectedValues.length === 0,
      $isReadOnly: isReadOnly,
      className: classNames(Listbox.className, className),
      ref: divRef,
      id: id,
      $variant: variant
    }), children),
    condition: variant === ListboxVariant.standalone
  }, /*#__PURE__*/React__default.createElement(FocusWithinGroup, {
    direction: direction,
    focusOnClick: focusOnClick,
    loopAround: loopAround,
    focusType: focusType
  }, label ? /*#__PURE__*/React__default.createElement("span", {
    id: `${id}-label`,
    className: `${Listbox.className}-label`
  }, label, isRequired ? /*#__PURE__*/React__default.createElement("span", {
    "aria-hidden": "true"
  }, ' *') : null) : null, focusType === 'roving-tabindex' ? /*#__PURE__*/React__default.createElement(RovingTabindexListbox, _extends$1({}, forwardedProps, {
    className: `${Listbox.className}-list`,
    context: context,
    ref: divRef
  }), children) : /*#__PURE__*/React__default.createElement(ActiveDescendantListbox, _extends$1({}, forwardedProps, {
    className: `${Listbox.className}-list`,
    context: context,
    ref: divRef
  }), children)));
});
Listbox.className = CLASSNAME$7;
Listbox.defaultProps = DEFAULT_PROPS$7;
Listbox.displayName = COMPONENT_NAME$7;

/**
 * Component style.
 */
const StyledNumber = styled(StyledText)`
  font-family: var(--redsift-typography-font-family-poppins);
`;

const _excluded$6 = ["as", "className", "color", "compactDisplay", "currency", "currencyDisplay", "currencySign", "fontSize", "fontWeight", "lineHeight", "localeMatcher", "maximumFractionDigits", "maximumSignificantDigits", "minimumFractionDigits", "minimumIntegerDigits", "minimumSignificantDigits", "notation", "noWrap", "numberingSystem", "roundingIncrement", "roundingMode", "roundingPriority", "signDisplay", "trailingZeroDisplay", "type", "unit", "unitDisplay", "useGrouping", "value", "variant"];
const COMPONENT_NAME$6 = 'Number';
const CLASSNAME$6 = 'redsift-number';
const DEFAULT_PROPS$6 = {
  compactDisplay: 'short',
  currencyDisplay: 'symbol',
  currencySign: 'standard',
  localeMatcher: 'best fit',
  notation: 'standard',
  signDisplay: 'auto',
  type: 'decimal',
  unitDisplay: 'short',
  roundingMode: 'halfExpand',
  roundingPriority: 'auto',
  trailingZeroDisplay: 'auto'
};

/**
 * The Number component.
 */
const Number$1 = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      as,
      className,
      color,
      compactDisplay,
      currency,
      currencyDisplay,
      currencySign,
      fontSize,
      fontWeight,
      lineHeight,
      localeMatcher,
      maximumFractionDigits,
      maximumSignificantDigits,
      minimumFractionDigits,
      minimumIntegerDigits,
      minimumSignificantDigits,
      notation,
      noWrap,
      numberingSystem,
      roundingIncrement,
      roundingMode,
      roundingPriority,
      signDisplay,
      trailingZeroDisplay,
      type,
      unit,
      unitDisplay,
      useGrouping,
      value,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$6);
  const formatter = useNumberFormatter(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
    compactDisplay,
    currency,
    currencyDisplay,
    currencySign,
    localeMatcher
  }, maximumFractionDigits !== undefined && {
    maximumFractionDigits
  }), maximumSignificantDigits !== undefined && {
    maximumSignificantDigits
  }), minimumFractionDigits !== undefined && {
    minimumFractionDigits
  }), minimumIntegerDigits !== undefined && {
    minimumIntegerDigits
  }), minimumSignificantDigits !== undefined && {
    minimumSignificantDigits
  }), {}, {
    notation,
    numberingSystem,
    // @ts-ignore
    roundingIncrement,
    // @ts-ignore
    roundingMode,
    // @ts-ignore
    roundingPriority,
    signDisplay,
    style: type,
    // @ts-ignore
    trailingZeroDisplay,
    unit,
    unitDisplay,
    useGrouping
  }));
  return /*#__PURE__*/React__default.createElement(StyledNumber, _extends$1({
    as: as
  }, forwardedProps, {
    $as: as,
    className: classNames(Number$1.className, className),
    ref: ref,
    $color: color,
    $lineHeight: lineHeight,
    $fontSize: fontSize,
    $fontWeight: fontWeight,
    $noWrap: noWrap,
    $variant: variant
  }), formatter.format(value));
});
Number$1.className = CLASSNAME$6;
Number$1.defaultProps = DEFAULT_PROPS$6;
Number$1.displayName = COMPONENT_NAME$6;

/**
 * Component style.
 */
const StyledProgressBar = styled.progress`
    ${_ref => {
  let {
    backgroundColor,
    height,
    progressColor,
    width
  } = _ref;
  return css`

        &[value] { // Target only state with a value
            -webkit-appearance: none;
            appearance: none;
            height: ${height || '8px'};
            width: ${width || '100%'};
            color: var(--redsift-color-presentation-red-default);

            &::-webkit-progress-bar {
                background-color: ${backgroundColor || 'var(--redsift-color-neutral-xlightgrey)'};
                box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1) inset;
            }

            &::-webkit-progress-value {
                background-color: ${progressColor || 'var(--redsift-color-ctas-primary-primary)'};
                box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1) inset;
            }
        }
    `;
}}
`;

const _excluded$5 = ["className", "value"];
const COMPONENT_NAME$5 = 'ProgressBar';
const CLASSNAME$5 = 'redsift-progress-bar';
const DEFAULT_PROPS$5 = {};

/**
 * The Progress bar component.
 */
const ProgressBar = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      className,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$5);
  return /*#__PURE__*/React__default.createElement(StyledProgressBar, _extends$1({}, forwardedProps, {
    className: classNames(ProgressBar.className, className),
    ref: ref,
    variant: "determinate",
    max: "100",
    value: value || 0
  }));
});
ProgressBar.className = CLASSNAME$5;
ProgressBar.defaultProps = DEFAULT_PROPS$5;
ProgressBar.displayName = COMPONENT_NAME$5;

/**
 * Context props.
 */

/**
 * Component orientation.
 */
const RadioGroupOrientation = {
  horizontal: 'horizontal',
  vertical: 'vertical'
};

/**
 * Component props.
 */

const RadioGroupContext = /*#__PURE__*/React__default.createContext(null);

/**
 * Component style.
 */
const StyledRadioGroup = styled.div`
  ${baseStyling}

  display: flex;
  flex-direction: column;

  ${_ref => {
  let {
    $orientation
  } = _ref;
  return $orientation === RadioGroupOrientation.vertical ? `
    div.redsift-radio-group-boxes {
      display: flex;
      flex-direction: column;
    }
  ` : `
    div.redsift-radio-group-boxes {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }
  `;
}}

  span.redsift-radio-group-label {
    font-family: var(--redsift-typography-body-font-family);
    font-weight: var(--redsift-typography-body-font-weight);
    font-size: var(--redsift-typography-body-font-size);
    line-height: var(--redsift-typography-body-line-height);

    color: var(--redsift-color-neutral-black);
    ${_ref2 => {
  let {
    $isDisabled
  } = _ref2;
  return $isDisabled ? `
      color: var(--redsift-color-neutral-midgrey);
    ` : '';
}}
    ${_ref3 => {
  let {
    $isInvalid
  } = _ref3;
  return $isInvalid ? `
      color: var(--redsift-color-notifications-error-primary);
    ` : '';
}}
  }

  span.redsift-radio-group-description {
    font-family: var(--redsift-typography-helper-font-family);
    font-weight: var(--redsift-typography-helper-font-weight);
    font-size: var(--redsift-typography-helper-font-size);
    line-height: var(--redsift-typography-helper-line-height);

    color: var(--redsift-color-neutral-darkgrey);
    ${_ref4 => {
  let {
    $isDisabled
  } = _ref4;
  return $isDisabled ? `
      color: var(--redsift-color-neutral-midgrey);
    ` : '';
}}
    ${_ref5 => {
  let {
    $isInvalid
  } = _ref5;
  return $isInvalid ? `
      color: var(--redsift-color-notifications-error-primary);
    ` : '';
}}
  }
`;

const _excluded$4 = ["children", "className", "defaultValue", "description", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "label", "onChange", "orientation", "value"];
const COMPONENT_NAME$4 = 'RadioGroup';
const CLASSNAME$4 = 'redsift-radio-group';
const DEFAULT_PROPS$4 = {
  color: CtasColorPalette.primary,
  orientation: RadioGroupOrientation.vertical
};

/**
 * The RadioGroup component.
 * Can be used as controlled or uncontrolled.
 */
const RadioGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      defaultValue,
      description,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
      onChange,
      orientation,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$4);
  const [selectedValue, setValue] = useState(value || defaultValue || '');
  useEffect(() => {
    if (value) {
      state.setValue(value);
    }
  }, [value]);
  const state = {
    value: selectedValue,
    isDisabled: isDisabled || false,
    isReadOnly: isReadOnly || false,
    setValue(value) {
      if (isReadOnly || isDisabled) {
        return;
      }
      const previousValue = selectedValue;
      setValue(value);
      if (value !== previousValue && onChange) {
        onChange(value);
      }
    }
  };
  return /*#__PURE__*/React__default.createElement(StyledRadioGroup, _extends$1({}, forwardedProps, {
    $isDisabled: isDisabled,
    $isInvalid: isInvalid || isRequired && selectedValue.length === 0,
    $isReadOnly: isReadOnly,
    $orientation: orientation,
    "aria-disabled": isDisabled,
    className: classNames(RadioGroup.className, className),
    ref: ref,
    role: "radiogroup"
  }), /*#__PURE__*/React__default.createElement("span", {
    className: `${RadioGroup.className}-label`
  }, label, isRequired ? ' *' : null), /*#__PURE__*/React__default.createElement("div", {
    className: `${RadioGroup.className}-boxes`
  }, /*#__PURE__*/React__default.createElement(RadioGroupContext.Provider, {
    value: state
  }, children)), /*#__PURE__*/React__default.createElement("span", {
    className: `${RadioGroup.className}-description`
  }, description));
});
RadioGroup.className = CLASSNAME$4;
RadioGroup.defaultProps = DEFAULT_PROPS$4;
RadioGroup.displayName = COMPONENT_NAME$4;

/**
 * Component style.
 */
const StyledRadio = styled.label`
  height: fit-content;
  position: relative;
  width: fit-content;
  ${baseStyling}

  align-items: center;
  background: none;
  border: none;
  display: inline-flex;

  input {
    height: 100%;
    margin: 0;
    opacity: 0;
    overflow: visible;
    padding: 0;
    position: absolute;
    width: 100%;
  }

  span.label {
    ${_ref => {
  let {
    $isDisabled,
    $isInvalid
  } = _ref;
  return $isDisabled ? css`
            color: var(--redsift-color-neutral-midgrey);
          ` : $isInvalid ? css`
            color: var(--redsift-color-notifications-error-primary);
          ` : css`
            color: var(--redsift-color-neutral-black);
          `;
}}

    font-family: var(--redsift-typography-body-font-family);
    font-size: var(--redsift-typography-body-font-size);
    font-weight: var(--redsift-typography-body-font-weight);
    line-height: var(--redsift-typography-body-line-height);
    padding-left: 4px;
    padding-right: 16px;

    [dir='rtl'] & {
      padding-left: 16px;
      padding-right: 4px;
    }
  }

  .redsift-icon {
    box-sizing: content-box;
    padding: 2px;
    width: 24px;

    ${_ref2 => {
  let {
    $isColored,
    $isDisabled,
    $isInvalid,
    $isFocusVisible
  } = _ref2;
  return css`
      ${$isDisabled ? css`
            color: var(--redsift-color-neutral-midgrey);
          ` : $isInvalid ? css`
            color: var(--redsift-color-notifications-error-primary);
          ` : css`
            color: ${$isColored ? 'var(--redsift-color-ctas-primary-primary)' : 'var(--redsift-color-notifications-question-primary)'};
          `}

      ${$isFocusVisible && !$isDisabled ? css`
            background-color: ${$isColored ? 'var(--redsift-color-ctas-primary-hover)' : 'var(--redsift-color-notifications-question-hover)'};
            border-radius: 16px;
          ` : ''}
    `;
}}
  }

  &:hover,
  &:focus-visible {
    outline: none;

    ${_ref3 => {
  let {
    $isColored,
    $isDisabled
  } = _ref3;
  return !$isDisabled ? css`
            .redsift-icon {
              background-color: ${$isColored ? 'var(--redsift-color-ctas-primary-hover)' : 'var(--redsift-color-notifications-question-hover)'};
              border-radius: 16px;
            }
          ` : '';
}}
  }
`;

const _excluded$3 = ["aria-label", "aria-labelledby", "autoFocus", "children", "className", "defaultSelected", "inputProps", "inputRef", "isColored", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "isSelected", "name", "onChange", "value"];
const COMPONENT_NAME$3 = 'Radio';
const CLASSNAME$3 = 'redsift-radio';
const DEFAULT_PROPS$3 = {
  isColored: true
};

/**
 * The Radio component.
 * Works both inside a RadioGroup or as standalone.
 * Can be used as controlled or uncontrolled.
 */
const Radio = /*#__PURE__*/forwardRef((props, ref) => {
  var _isDisabled, _isReadOnly, _ref;
  const groupState = useContext(RadioGroupContext);
  let {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      autoFocus,
      children,
      className,
      defaultSelected,
      inputProps,
      inputRef,
      isColored,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      isSelected: propsIsSelected,
      name,
      onChange,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$3);
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing({
    autoFocus
  });
  isDisabled = (_isDisabled = isDisabled) !== null && _isDisabled !== void 0 ? _isDisabled : groupState === null || groupState === void 0 ? void 0 : groupState.isDisabled;
  isReadOnly = (_isReadOnly = isReadOnly) !== null && _isReadOnly !== void 0 ? _isReadOnly : groupState === null || groupState === void 0 ? void 0 : groupState.isReadOnly;
  const [isSelected, setSelected] = useState(Boolean((_ref = propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : defaultSelected) !== null && _ref !== void 0 ? _ref : Boolean((groupState === null || groupState === void 0 ? void 0 : groupState.value) && groupState.value === value)));
  const isControlled = propsIsSelected !== null && propsIsSelected !== undefined;
  useEffect(() => {
    setSelected(Boolean(propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : defaultSelected));
  }, [propsIsSelected, defaultSelected]);
  useEffect(() => {
    if (groupState) {
      setSelected(groupState.value === value);
    }
  }, [groupState === null || groupState === void 0 ? void 0 : groupState.value]);
  warnIfNoAccessibleLabelFound(props, [children]);
  if (groupState) {
    if (propsIsSelected != null) {
      console.warn('isSelected is unsupported on individual <Radio> elements within a <RadioGroup> unless the <Radio> is controlled. Please apply this prop to the group instead.');
    }
    if (defaultSelected != null) {
      console.warn('defaultSelected is unsupported on individual <Radio> elements within a <RadioGroup> unless the <Radio> is controlled. Please apply this prop to the group instead.');
    }
    if (props.value == null) {
      console.warn('A <Radio> element within a <RadioGroup> requires a `value` property unless the <Radio> is controlled.');
    }
  }
  const handleChange = useCallback(event => {
    if (isDisabled || isReadOnly) {
      return;
    }
    if (groupState) {
      groupState.setValue(value);
    }
    if (isControlled) {
      onChange === null || onChange === void 0 ? void 0 : onChange(!propsIsSelected, value, name, event, groupState);
    } else {
      setSelected(!isSelected);
      onChange === null || onChange === void 0 ? void 0 : onChange(!isSelected, value, name, event, groupState);
    }
  }, [onChange, groupState, isSelected, propsIsSelected]);
  return /*#__PURE__*/React__default.createElement(StyledRadio, _extends$1({}, forwardedProps, {
    $isColored: isColored,
    $isDisabled: isDisabled,
    $isFocusVisible: isFocusVisible,
    $isInvalid: isInvalid || isRequired && !(isSelected || isControlled && propsIsSelected),
    $isRequired: isRequired,
    $isSelected: isSelected || isControlled && propsIsSelected,
    className: classNames(Radio.className, className),
    ref: ref
  }), isSelected || isControlled && propsIsSelected ? /*#__PURE__*/React__default.createElement(Icon, {
    icon: mdiRadioboxMarked
  }) : /*#__PURE__*/React__default.createElement(Icon, {
    icon: mdiRadioboxBlank
  }), children ? /*#__PURE__*/React__default.createElement("span", {
    className: "label"
  }, children) : null, /*#__PURE__*/React__default.createElement("input", _extends$1({}, inputProps, focusProps, {
    "aria-checked": isSelected || isControlled && propsIsSelected ? 'true' : 'false',
    "aria-disabled": isDisabled,
    "aria-invalid": isInvalid || isRequired && !(isSelected || isControlled && propsIsSelected),
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-readonly": isReadOnly,
    "aria-required": isRequired,
    autoFocus: autoFocus,
    checked: isSelected || isControlled && propsIsSelected ? true : false,
    disabled: isDisabled,
    name: name,
    onChange: handleChange,
    ref: inputRef,
    type: "radio",
    value: value
  })));
});
Radio.className = CLASSNAME$3;
Radio.defaultProps = DEFAULT_PROPS$3;
Radio.displayName = COMPONENT_NAME$3;

const SwitchGroupContext = /*#__PURE__*/React__default.createContext(null);

/**
 * Component style.
 */
const StyledSwitch = styled.label`
  height: fit-content;
  position: relative;
  width: fit-content;
  ${baseStyling}

  align-items: center;
  background: none;
  border: none;
  display: inline-flex;
  margin: 4px;

  input {
    height: 100%;
    margin: 0;
    opacity: 0;
    overflow: visible;
    padding: 0;
    position: absolute;
    width: 100%;
  }

  span.label {
    ${_ref => {
  let {
    $isDisabled,
    $isInvalid
  } = _ref;
  return $isDisabled ? css`
            color: var(--redsift-color-neutral-midgrey);
          ` : $isInvalid ? css`
            color: var(--redsift-color-notifications-error-primary);
          ` : css`
            color: var(--redsift-color-neutral-black);
          `;
}}

    font-family: var(--redsift-typography-body-font-family);
    font-size: var(--redsift-typography-body-font-size);
    font-weight: var(--redsift-typography-body-font-weight);
    line-height: var(--redsift-typography-body-line-height);
    padding-inline-start: 12px;
    padding-inline-end: 24px;
  }

  .redsift-switch__slide {
    height: 20px;
    position: relative;
    min-width: 40px;
  }

  .redsift-switch-slide__inner {
    background-color: var(--redsift-color-neutral-midgrey);
    border-radius: 20px;
    height: 24px;
    position: absolute;
    top: calc(50% - 24px / 2);
    width: 46px;
    z-index: 2;
  }

  .redsift-switch-slide__knob-wrapper {
    border-radius: 50%;
    height: 20px;
    margin-inline-start: ${_ref2 => {
  let {
    $isSelected
  } = _ref2;
  return !$isSelected ? '2px' : '24px';
}};
    position: absolute;
    top: -6px;
    width: 20px;
  }

  .redsift-switch-slide-knob-wrapper__elevation {
    ${_ref3 => {
  let {
    $isColored,
    $isInvalid
  } = _ref3;
  return css`
      background-color: var(
        --redsift-color-${$isInvalid ? 'notifications-error-hover' : $isColored ? 'ctas-primary-hover' : 'notifications-no-data-button-hover'}
      );
    `;
}}
    border-radius: 50%;
    height: 32px;
    margin-inline-start: ${_ref4 => {
  let {
    $isSelected
  } = _ref4;
  return !$isSelected ? '-6px' : '-6px';
}};
    opacity: 0;
    position: absolute;
    width: 32px;
    z-index: 3;
  }

  .redsift-switch-slide-knob-wrapper__knob {
    background-color: var(--redsift-color-neutral-white);
    border-radius: 50%;
    box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12);
    height: 20px;
    position: absolute;
    top: 6px;
    width: 20px;
    z-index: 4;
  }

  .redsift-switch-slide__inner {
    ${_ref5 => {
  let {
    $isColored,
    $isDisabled,
    $isInvalid,
    $isSelected
  } = _ref5;
  return css`
      ${$isDisabled ? css`
            background-color: var(--redsift-color-neutral-lightgrey);
          ` : $isSelected ? css`
            background-color: var(
              --redsift-color-${$isInvalid ? 'notifications-error-primary' : $isColored ? 'ctas-primary-primary' : 'notifications-no-data-button-active'}
            );
          ` : css`
            background-color: var(
              --redsift-color-${$isInvalid ? 'notifications-error-hover' : $isColored ? 'ctas-primary-hover' : 'notifications-no-data-button-hover'}
            );
          `}
    `;
}}
  }

  &:hover,
  &:focus-visible {
    outline: none;
    cursor: pointer;

    ${_ref6 => {
  let {
    $isDisabled
  } = _ref6;
  return !$isDisabled ? css`
            .redsift-switch-slide-knob-wrapper__elevation {
              opacity: 0.5;
            }
          ` : '';
}}
  }

  ${_ref7 => {
  let {
    $isDisabled,
    $isFocusVisible
  } = _ref7;
  return $isFocusVisible && !$isDisabled ? css`
          .redsift-switch-slide-knob-wrapper__elevation {
            opacity: 0.5;
          }
        ` : '';
}}
`;

const _excluded$2 = ["aria-label", "aria-labelledby", "autoFocus", "children", "className", "defaultSelected", "inputProps", "inputRef", "isColored", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "isSelected", "name", "onChange", "value"];
const COMPONENT_NAME$2 = 'Switch';
const CLASSNAME$2 = 'redsift-switch';
const DEFAULT_PROPS$2 = {
  isColored: true
};

/**
 * The Switch component.
 * Works both inside a SwitchGroup or as standalone.
 * Can be used as controlled or uncontrolled.
 */
const Switch = /*#__PURE__*/forwardRef((props, ref) => {
  var _isDisabled, _isReadOnly, _ref;
  const groupState = useContext(SwitchGroupContext);
  let {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      autoFocus,
      children,
      className,
      defaultSelected,
      inputProps,
      inputRef,
      isColored,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      isSelected: propsIsSelected,
      name,
      onChange,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$2);
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing({
    autoFocus
  });
  isDisabled = (_isDisabled = isDisabled) !== null && _isDisabled !== void 0 ? _isDisabled : groupState === null || groupState === void 0 ? void 0 : groupState.isDisabled;
  isReadOnly = (_isReadOnly = isReadOnly) !== null && _isReadOnly !== void 0 ? _isReadOnly : groupState === null || groupState === void 0 ? void 0 : groupState.isReadOnly;
  const [isSelected, setSelected] = useState(Boolean((_ref = propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : defaultSelected) !== null && _ref !== void 0 ? _ref : groupState === null || groupState === void 0 ? void 0 : groupState.value.includes(value)));
  const isControlled = propsIsSelected !== null && propsIsSelected !== undefined;
  useEffect(() => {
    setSelected(Boolean(propsIsSelected !== null && propsIsSelected !== void 0 ? propsIsSelected : defaultSelected));
  }, [propsIsSelected, defaultSelected]);
  useEffect(() => {
    if (groupState) {
      setSelected(groupState.value.includes(value));
    }
  }, [groupState === null || groupState === void 0 ? void 0 : groupState.value]);
  warnIfNoAccessibleLabelFound(props, [children]);
  if (groupState) {
    if (propsIsSelected != null) {
      console.warn('isSelected is unsupported on individual <Switch> elements within a <SwitchGroup> unless the <Switch> is controlled. Please apply this prop to the group instead.');
    }
    if (defaultSelected != null) {
      console.warn('defaultSelected is unsupported on individual <Switch> elements within a <SwitchGroup> unless the <Switch> is controlled. Please apply this prop to the group instead.');
    }
    if (props.value == null) {
      console.warn('A <Switch> element within a <SwitchGroup> requires a `value` property unless the <Switch> is controlled.');
    }
  }
  const handleChange = useCallback(event => {
    if (isDisabled || isReadOnly) {
      return;
    }
    if (groupState) {
      if (!groupState.value.includes(value)) {
        groupState.addValue(value);
      } else {
        groupState.removeValue(value);
      }
    }
    if (isControlled) {
      onChange === null || onChange === void 0 ? void 0 : onChange(!propsIsSelected, value, name, event, groupState);
    } else {
      setSelected(!isSelected);
      onChange === null || onChange === void 0 ? void 0 : onChange(!isSelected, value, name, event, groupState);
    }
  }, [onChange, groupState, isSelected, propsIsSelected]);
  return /*#__PURE__*/React__default.createElement(StyledSwitch, _extends$1({}, forwardedProps, {
    $isColored: isColored,
    $isDisabled: isDisabled,
    $isFocusVisible: isFocusVisible,
    $isInvalid: isInvalid || isRequired && !(isSelected || isControlled && propsIsSelected),
    $isRequired: isRequired,
    $isSelected: isSelected || isControlled && propsIsSelected,
    className: classNames(Switch.className, className),
    ref: ref
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${Switch.className}__slide`
  }, /*#__PURE__*/React__default.createElement("div", {
    className: `${Switch.className}-slide__inner`
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${Switch.className}-slide__knob-wrapper`
  }, /*#__PURE__*/React__default.createElement("div", {
    className: `${Switch.className}-slide-knob-wrapper__elevation`
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${Switch.className}-slide-knob-wrapper__knob`
  }))), children ? /*#__PURE__*/React__default.createElement("span", {
    className: "label"
  }, children) : null, /*#__PURE__*/React__default.createElement("input", _extends$1({}, inputProps, focusProps, {
    "aria-checked": isSelected || isControlled && propsIsSelected ? 'true' : 'false',
    "aria-disabled": isDisabled,
    "aria-invalid": isInvalid || isRequired && !(isSelected || isControlled && propsIsSelected),
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-readonly": isReadOnly,
    "aria-required": isRequired,
    autoFocus: autoFocus,
    checked: isSelected || isControlled && propsIsSelected ? true : false,
    disabled: isDisabled,
    name: name,
    onChange: handleChange,
    ref: inputRef,
    role: "switch",
    type: "checkbox",
    value: value
  })));
});
Switch.className = CLASSNAME$2;
Switch.defaultProps = DEFAULT_PROPS$2;
Switch.displayName = COMPONENT_NAME$2;

/**
 * Context props.
 */

/**
 * Component orientation.
 */
const SwitchGroupOrientation = {
  horizontal: 'horizontal',
  vertical: 'vertical'
};

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledSwitchGroup = styled.div`
  ${baseStyling}

  display: flex;
  flex-direction: column;

  ${_ref => {
  let {
    $orientation
  } = _ref;
  return $orientation === SwitchGroupOrientation.vertical ? `
    div.redsift-switch-group-switches {
      display: flex;
      flex-direction: column;
    }
  ` : `
    div.redsift-switch-group-switches {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }
  `;
}}

  span.redsift-switch-group-label {
    font-family: var(--redsift-typography-body-font-family);
    font-weight: var(--redsift-typography-body-font-weight);
    font-size: var(--redsift-typography-body-font-size);
    line-height: var(--redsift-typography-body-line-height);

    color: var(--redsift-color-neutral-black);
    ${_ref2 => {
  let {
    $isDisabled
  } = _ref2;
  return $isDisabled ? `
      color: var(--redsift-color-neutral-midgrey);
    ` : '';
}}
    ${_ref3 => {
  let {
    $isInvalid
  } = _ref3;
  return $isInvalid ? `
      color: var(--redsift-color-notifications-error-primary);
    ` : '';
}}
  }

  span.redsift-switch-group-description {
    font-family: var(--redsift-typography-helper-font-family);
    font-weight: var(--redsift-typography-helper-font-weight);
    font-size: var(--redsift-typography-helper-font-size);
    line-height: var(--redsift-typography-helper-line-height);

    color: var(--redsift-color-neutral-darkgrey);
    ${_ref4 => {
  let {
    $isDisabled
  } = _ref4;
  return $isDisabled ? `
      color: var(--redsift-color-neutral-midgrey);
    ` : '';
}}
    ${_ref5 => {
  let {
    $isInvalid
  } = _ref5;
  return $isInvalid ? `
      color: var(--redsift-color-notifications-error-primary);
    ` : '';
}}
  }
`;

const _excluded$1 = ["children", "className", "defaultValues", "description", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "label", "onChange", "orientation", "value"];
const COMPONENT_NAME$1 = 'SwitchGroup';
const CLASSNAME$1 = 'redsift-switch-group';
const DEFAULT_PROPS$1 = {
  color: CtasColorPalette.primary,
  orientation: SwitchGroupOrientation.vertical
};

/**
 * The SwitchGroup component.
 * Can be used as controlled or uncontrolled.
 */
const SwitchGroup = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className,
      defaultValues,
      description,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
      onChange,
      orientation,
      value
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1);
  const [selectedValues, setValue] = useState(value || defaultValues || []);
  useEffect(() => {
    if (value) {
      state.setValue(value);
    }
  }, [value]);
  const state = {
    value: selectedValues,
    isDisabled: isDisabled || false,
    isReadOnly: isReadOnly || false,
    setValue(values) {
      if (isReadOnly || isDisabled) {
        return;
      }
      setValue(values);
    },
    addValue(value) {
      if (!selectedValues.includes(value)) {
        setValue(selectedValues.concat(value));
        if (onChange) {
          onChange(selectedValues.concat(value));
        }
      }
    },
    removeValue(value) {
      if (selectedValues.includes(value)) {
        setValue(selectedValues.filter(existingValue => existingValue !== value));
        if (onChange) {
          onChange(selectedValues.filter(existingValue => existingValue !== value));
        }
      }
    }
  };
  return /*#__PURE__*/React__default.createElement(StyledSwitchGroup, _extends$1({}, forwardedProps, {
    $isDisabled: isDisabled,
    $isInvalid: isInvalid || isRequired && selectedValues.length === 0,
    $isReadOnly: isReadOnly,
    $orientation: orientation,
    "aria-disabled": isDisabled,
    className: classNames(SwitchGroup.className, className),
    ref: ref,
    role: "group"
  }), /*#__PURE__*/React__default.createElement("span", {
    className: `${SwitchGroup.className}-label`
  }, label, isRequired ? ' *' : null), /*#__PURE__*/React__default.createElement("div", {
    className: `${SwitchGroup.className}-switches`
  }, /*#__PURE__*/React__default.createElement(SwitchGroupContext.Provider, {
    value: state
  }, children)), /*#__PURE__*/React__default.createElement("span", {
    className: `${SwitchGroup.className}-description`
  }, description));
});
SwitchGroup.className = CLASSNAME$1;
SwitchGroup.defaultProps = DEFAULT_PROPS$1;
SwitchGroup.displayName = COMPONENT_NAME$1;

/**
 * Component variant.
 */
const TextFieldVariant = {
  default: 'default',
  underline: 'underline'
};

/**
 * Component props.
 */

/**
 * Component style.
 */
const StyledTextField = styled.div`
  height: fit-content;
  position: relative;
  width: fit-content;
  margin: 0;
  min-width: 0;
  padding: 0;
  ${baseStyling}

  border: 0;
  display: inline-flex;
  flex-direction: column;
  vertical-align: top;

  ${_ref => {
  let {
    $hasLeftIcon,
    $hasContent,
    $isFocused,
    $isDisabled,
    $isInvalid,
    $isColored,
    $variant
  } = _ref;
  return css`
    ${$isDisabled ? css`
          pointer-events: none;
        ` : ''}

    .redsift-text-field__label {
      font-family: var(--redsift-typography-input-text-font-family);
      font-size: var(--redsift-typography-input-text-font-size);
      font-weight: var(--redsift-typography-input-text-font-weight);
      line-height: var(--redsift-typography-input-text-line-height);
      left: ${$hasLeftIcon && !$hasContent ? '32px' : '0'};
      overflow: hidden;
      pointer-events: none;
      position: absolute;
      text-overflow: ellipsis;
      top: ${$hasContent ? '-8px' : '-5px'};
      transform-origin: top left;
      transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
        max-width 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
      white-space: nowrap;
      z-index: 1;

      ${!$hasContent ? css`
            max-width: calc(100% - 24px);
            transform: translate(${$variant === TextFieldVariant.underline ? '0px' : '14px'}, 16px) scale(1);
          ` : css`
            max-width: calc(133% - 32px);
            transform: translate(${$variant === TextFieldVariant.underline ? '0px' : '14px'}, 1px) scale(0.733);
          `}

      ${$isDisabled ? css`
            color: var(--redsift-color-notifications-no-data-primary);
          ` : $isInvalid ? css`
            color: var(--redsift-color-notifications-error-primary);
          ` : $isFocused ? css`
            color: ${$isColored ? 'var(--redsift-color-ctas-primary-primary)' : 'var(--redsift-color-notifications-question-primary)'};
          ` : css`
            color: var(--redsift-color-neutral-darkgrey);
          `}
    }

    .redsift-text-field__input-wrapper {
      align-items: flex-start;
      box-sizing: border-box;
      cursor: text;
      display: inline-flex;
      height: 42px;
      position: relative;
    }

    .redsift-text-field__pills {
      align-items: center;
      box-sizing: border-box;
      display: flex;
      flex-wrap: wrap;
      flex: 1 1 auto;
      padding-bottom: 6px;
      ${$variant !== TextFieldVariant.underline && !$hasLeftIcon ? css`
            padding-left: 16px;
          ` : ''}
      padding-top: 10px;

      .redsift-pill {
        margin: 3px 6px 3px 0;
      }
    }

    .redsift-text-field-input-wrapper__input {
      background: none;
      border: 0;
      box-sizing: content-box;
      display: flex;
      flex: 1 1 auto;
      font-family: var(--redsift-typography-input-text-font-family);
      font-size: var(--redsift-typography-input-text-font-size);
      font-weight: var(--redsift-typography-input-text-font-weight);
      line-height: var(--redsift-typography-input-text-line-height);
      min-width: 0;
      min-width: 100px;
      padding: 2px 0;
      width: auto;

      ${$isDisabled ? css`
            color: var(--redsift-color-notifications-no-data-primary);
          ` : css`
            color: var(--redsift-color-neutral-black);
          `}
    }

    .redsift-text-field-input-wrapper__input::placeholder {
      color: var(--redsift-color-neutral-midgrey);
    }

    .redsift-text-field-input-wrapper__input:focus {
      outline: 0;
    }

    .redsift-text-field-input-wrapper__fieldset {
      border-style: solid;
      bottom: 0;
      left: 1px;
      margin: 0;
      min-width: 0%;
      overflow: hidden;
      padding: 0 8px;
      pointer-events: none;
      position: absolute;
      right: 0;
      text-align: left;
      top: -5px;

      ${$variant === TextFieldVariant.underline ? css`
            border-bottom-width: 2px;
          ` : css`
            border-radius: 4px;
            border-width: 2px;
          `}

      ${$isDisabled ? css`
            border-color: var(--redsift-color-notifications-no-data-primary);
          ` : $isInvalid ? css`
            border-color: var(--redsift-color-notifications-error-primary);
          ` : $isFocused ? css`
            border-color: ${$isColored ? 'var(--redsift-color-ctas-primary-primary)' : 'var(--redsift-color-notifications-question-primary)'};
          ` : css`
            border-color: var(--redsift-color-neutral-midgrey);
          `}
    }

    .redsift-text-field-input-wrapper-fieldset__legend {
      display: block;
      float: unset;
      font-size: 11px;
      height: 11px;
      overflow: hidden;
      padding: 0;
      visibility: hidden;
      white-space: nowrap;
      width: auto;

      ${!$hasContent ? css`
            max-width: 0.01px;
            transition: max-width 50ms cubic-bezier(0, 0, 0.2, 1) 0ms;
          ` : css`
            max-width: 100%;
            transition: max-width 100ms cubic-bezier(0, 0, 0.2, 1) 50ms;
          `}
    }

    .redsift-text-field-input-wrapper-fieldset__legend > span {
      display: inline-block;
      opacity: 0;
      padding-left: 5px;
      padding-right: 5px;
      visibility: visible;
    }

    .redsift-icon-button {
      padding: 2px;
    }

    .redsift-text-field-input-wrapper__toolbar {
      position: relative;
      top: 9px;
    }

    ${$variant !== TextFieldVariant.underline ? css`
          .redsift-icon.left {
            padding-left: 12px;
            padding-right: 8px;
            line-height: 28px;
            top: 10px;
          }

          .redsift-text-field-input-wrapper__toolbar {
            padding-right: 8px;
          }
        ` : css`
          .redsift-icon.left {
            padding-right: 8px;
            top: 10px;
          }
        `}
  `;
}}
`;

// Material Design Icons v7.2.96
var mdiClose = "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z";

const _excluded = ["aria-label", "aria-labelledby", "autoFocus", "className", "defaultValue", "hasClearButton", "after", "internal", "inputProps", "inputRef", "isColored", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "label", "leftIcon", "name", "onBlur", "onChange", "onClear", "onFocus", "pills", "placeholder", "type", "value", "variant"];
const COMPONENT_NAME = 'TextField';
const CLASSNAME = 'redsift-text-field';
const DEFAULT_PROPS = {
  defaultValue: '',
  isColored: true,
  variant: TextFieldVariant.default
};

/**
 * The TextField component.
 * Can be used as controlled or uncontrolled.
 */
const TextField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      autoFocus,
      className,
      defaultValue,
      hasClearButton,
      after,
      internal,
      inputProps,
      inputRef,
      isColored,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
      leftIcon,
      name,
      onBlur: onBlurProps,
      onChange,
      onClear,
      onFocus: onFocusProps,
      pills,
      placeholder,
      type,
      value: propsValue,
      variant
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded);
  const {
    isFocusVisible,
    isFocused,
    focusProps: {
      onFocus,
      onBlur
    }
  } = useFocusRing({
    autoFocus
  });
  const [isFocusWithin, setFocusWithin] = useState(Boolean(autoFocus));
  const {
    focusWithinProps
  } = useFocusWithin({
    onFocusWithinChange: isFocusWithin => setFocusWithin(isFocusWithin)
  });
  const [value, setValue] = useState(defaultValue);
  const isControlled = propsValue !== null && propsValue !== undefined;
  warnIfNoAccessibleLabelFound(props, [label]);
  const handleChange = useCallback(event => {
    if (isDisabled || isReadOnly) {
      return;
    }
    if (onChange) {
      onChange(event.target.value, name, event);
    } else {
      setValue(event.target.value);
    }
  }, [onChange]);
  const handleClear = useCallback(() => {
    if (isDisabled || isReadOnly) {
      return;
    }
    if (onChange) {
      onChange('');
    }
    if (onClear) {
      onClear();
    }
    if (hasClearButton && hasClearButton !== true && hasClearButton.onClick) {
      hasClearButton.onClick({});
    }
    setValue('');
  }, [onChange]);
  return /*#__PURE__*/React__default.createElement(StyledTextField, _extends$1({}, focusWithinProps, forwardedProps, {
    $hasContent: isFocusWithin || Boolean(isControlled ? propsValue : value) || Boolean(placeholder) || Boolean(pills && pills.length > 0),
    $hasLeftIcon: Boolean(leftIcon),
    $isColored: isColored,
    $isDisabled: isDisabled,
    $isFocusVisible: isFocusVisible,
    $isInvalid: isInvalid || isRequired && !(isControlled ? propsValue : value),
    $isRequired: isRequired,
    $isFocused: isFocused,
    $variant: variant,
    className: classNames(TextField.className, className),
    ref: ref
  }), label ? /*#__PURE__*/React__default.createElement("label", {
    className: `${TextField.className}__label`
  }, /*#__PURE__*/React__default.createElement("span", null, label)) : null, /*#__PURE__*/React__default.createElement("div", {
    className: `${TextField.className}__input-wrapper`
  }, leftIcon ? /*#__PURE__*/React__default.createElement(Icon, _extends$1({
    color: isDisabled ? 'question' : 'black'
  }, leftIcon, {
    "aria-hidden": "true",
    className: "left"
  })) : null, /*#__PURE__*/React__default.createElement("div", {
    className: `${TextField.className}__pills`
  }, pills === null || pills === void 0 ? void 0 : pills.map(pill => /*#__PURE__*/React__default.createElement(Pill, {
    key: pill.value,
    color: "blue",
    size: "small"
  }, pill.label)), /*#__PURE__*/React__default.createElement("input", _extends$1({}, inputProps, {
    onBlur: event => {
      onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
      onBlurProps === null || onBlurProps === void 0 ? void 0 : onBlurProps(event);
    },
    onFocus: event => {
      onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
      onFocusProps === null || onFocusProps === void 0 ? void 0 : onFocusProps(event);
    },
    "aria-disabled": isDisabled,
    "aria-invalid": isInvalid || isRequired && !(isControlled ? propsValue : value),
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-readonly": isReadOnly,
    "aria-required": isRequired,
    autoFocus: autoFocus,
    className: `${TextField.className}-input-wrapper__input`,
    disabled: isDisabled,
    name: name,
    onChange: handleChange,
    placeholder: placeholder,
    ref: inputRef,
    type: type,
    value: isControlled ? propsValue : value
  }))), hasClearButton || internal || after ? /*#__PURE__*/React__default.createElement(Flexbox, {
    className: `${TextField.className}-input-wrapper__toolbar`,
    gap: "8px"
  }, hasClearButton ? /*#__PURE__*/React__default.createElement(IconButton, _extends$1({
    "aria-label": "Clear",
    color: "question",
    isDisabled: isDisabled,
    icon: mdiClose
  }, hasClearButton !== true && hasClearButton, {
    onClick: handleClear,
    className: "right clear"
  })) : null, typeof internal === 'function' ? internal(isControlled ? propsValue : value, isDisabled, isInvalid || isRequired && !(isControlled ? propsValue : value), isRequired) : internal, (hasClearButton || internal) && after ? /*#__PURE__*/React__default.createElement("span", {
    style: {
      borderLeft: '1px solid var(--redsift-color-neutral-midgrey)'
    }
  }) : null, typeof after === 'function' ? after(isControlled ? propsValue : value, isDisabled, isInvalid || isRequired && !(isControlled ? propsValue : value), isRequired) : after) : null, /*#__PURE__*/React__default.createElement("fieldset", {
    "aria-hidden": "true",
    className: `${TextField.className}-input-wrapper__fieldset`
  }, /*#__PURE__*/React__default.createElement("legend", {
    className: `${TextField.className}-input-wrapper-fieldset__legend`
  }, label ? /*#__PURE__*/React__default.createElement("span", null, label) : null))));
});
TextField.className = CLASSNAME;
TextField.defaultProps = DEFAULT_PROPS;
TextField.displayName = COMPONENT_NAME;

export { ActiveDescendantListbox, Alert, AlertVariant, AlignContent, AlignItems, AlignSelf, AppBar, AppContainer, AppContainerContext, AppContent, AppSidePanel, AppSidePanelVariant, Badge, BadgeVariant, BaseBreadcrumbs, BaseGrid, BaseSkeleton, BreadcrumbItem, Breadcrumbs, Button, ButtonGroup, ButtonGroupVariant, ButtonLink, ButtonVariant, Card, CardActions, CardBody, CardHeader, Checkbox, CheckboxGroup, CheckboxGroupOrientation, ConditionalWrapper, CtasColorPalette, DetailedCard, DetailedCardCollapsibleSectionItems, DetailedCardHeader, DetailedCardSection, DetailedCardSectionItem, EventKey, FOCUS_WITHING_GROUP_INITIAL_STATE, FlexDirection, FlexWrap, Flexbox, FocusWithinGroup, FocusWithinGroupActionType, FocusWithinGroupContext, FocusWithinGroupReducer, FontFamily, Grid, GridItem, Heading, HeadingComponent, HeadingVariant, Icon, IconButton, IconButtonVariant, IconSize, Item, JustifyContent, JustifyItems, JustifySelf, LISTBOX_INITIAL_STATE, Link, LinkButton, Listbox, ListboxActionType, ListboxContext, ListboxReducer, ListboxVariant, Navigation, NeutralColorPalette, NotificationsColorPalette, Number$1 as Number, Pill, PillSize, PresentationColorPalette, Product, ProductColorPalette, ProductLogo, ProductName, ProgressBar, Radio, RadioGroup, RadioGroupOrientation, RedsiftAppBarColorBackground, RedsiftAppBarColorExpandIconBackgroundHover, RedsiftAppBarColorExpandIconHover, RedsiftAppBarColorExpandIconResting, RedsiftAppBarColorText, RedsiftColorCtasPrimaryActive, RedsiftColorCtasPrimaryActiveHover, RedsiftColorCtasPrimaryButtonActive, RedsiftColorCtasPrimaryButtonActiveHover, RedsiftColorCtasPrimaryButtonHover, RedsiftColorCtasPrimaryHover, RedsiftColorCtasPrimaryPrimary, RedsiftColorCtasSecondaryActive, RedsiftColorCtasSecondaryActiveHover, RedsiftColorCtasSecondaryButtonActive, RedsiftColorCtasSecondaryButtonActiveHover, RedsiftColorCtasSecondaryButtonHover, RedsiftColorCtasSecondaryHover, RedsiftColorCtasSecondaryPrimary, RedsiftColorNeutralBlack, RedsiftColorNeutralDarkgrey, RedsiftColorNeutralLightgrey, RedsiftColorNeutralMidgrey, RedsiftColorNeutralWhite, RedsiftColorNeutralXlightgrey, RedsiftColorNotificationsErrorActive, RedsiftColorNotificationsErrorActiveHover, RedsiftColorNotificationsErrorButtonActive, RedsiftColorNotificationsErrorButtonActiveHover, RedsiftColorNotificationsErrorButtonHover, RedsiftColorNotificationsErrorHover, RedsiftColorNotificationsErrorPrimary, RedsiftColorNotificationsInfoActive, RedsiftColorNotificationsInfoActiveHover, RedsiftColorNotificationsInfoButtonActive, RedsiftColorNotificationsInfoButtonActiveHover, RedsiftColorNotificationsInfoButtonHover, RedsiftColorNotificationsInfoHover, RedsiftColorNotificationsInfoPrimary, RedsiftColorNotificationsNoDataActive, RedsiftColorNotificationsNoDataActiveHover, RedsiftColorNotificationsNoDataButtonActive, RedsiftColorNotificationsNoDataButtonActiveHover, RedsiftColorNotificationsNoDataButtonHover, RedsiftColorNotificationsNoDataHover, RedsiftColorNotificationsNoDataPrimary, RedsiftColorNotificationsQuestionActive, RedsiftColorNotificationsQuestionActiveHover, RedsiftColorNotificationsQuestionButtonActive, RedsiftColorNotificationsQuestionButtonActiveHover, RedsiftColorNotificationsQuestionButtonHover, RedsiftColorNotificationsQuestionHover, RedsiftColorNotificationsQuestionPrimary, RedsiftColorNotificationsSuccessActive, RedsiftColorNotificationsSuccessActiveHover, RedsiftColorNotificationsSuccessButtonActive, RedsiftColorNotificationsSuccessButtonActiveHover, RedsiftColorNotificationsSuccessButtonHover, RedsiftColorNotificationsSuccessHover, RedsiftColorNotificationsSuccessPrimary, RedsiftColorNotificationsWarningActive, RedsiftColorNotificationsWarningActiveHover, RedsiftColorNotificationsWarningButtonActive, RedsiftColorNotificationsWarningButtonActiveHover, RedsiftColorNotificationsWarningButtonHover, RedsiftColorNotificationsWarningHover, RedsiftColorNotificationsWarningPrimary, RedsiftColorPresentationAquaDark, RedsiftColorPresentationAquaDarker, RedsiftColorPresentationAquaDarkerer, RedsiftColorPresentationAquaDefault, RedsiftColorPresentationAquaLight, RedsiftColorPresentationAquaLighter, RedsiftColorPresentationAquaLighterer, RedsiftColorPresentationBlueDark, RedsiftColorPresentationBlueDarker, RedsiftColorPresentationBlueDarkerer, RedsiftColorPresentationBlueDefault, RedsiftColorPresentationBlueLight, RedsiftColorPresentationBlueLighter, RedsiftColorPresentationBlueLighterer, RedsiftColorPresentationBrownDark, RedsiftColorPresentationBrownDarker, RedsiftColorPresentationBrownDarkerer, RedsiftColorPresentationBrownDefault, RedsiftColorPresentationBrownLight, RedsiftColorPresentationBrownLighter, RedsiftColorPresentationBrownLighterer, RedsiftColorPresentationGreenDark, RedsiftColorPresentationGreenDarker, RedsiftColorPresentationGreenDarkerer, RedsiftColorPresentationGreenDefault, RedsiftColorPresentationGreenLight, RedsiftColorPresentationGreenLighter, RedsiftColorPresentationGreenLighterer, RedsiftColorPresentationGreyDark, RedsiftColorPresentationGreyDarker, RedsiftColorPresentationGreyDarkerer, RedsiftColorPresentationGreyDefault, RedsiftColorPresentationGreyLight, RedsiftColorPresentationGreyLighter, RedsiftColorPresentationGreyLighterer, RedsiftColorPresentationOrangeDark, RedsiftColorPresentationOrangeDarker, RedsiftColorPresentationOrangeDarkerer, RedsiftColorPresentationOrangeDefault, RedsiftColorPresentationOrangeLight, RedsiftColorPresentationOrangeLighter, RedsiftColorPresentationOrangeLighterer, RedsiftColorPresentationPinkDark, RedsiftColorPresentationPinkDarker, RedsiftColorPresentationPinkDarkerer, RedsiftColorPresentationPinkDefault, RedsiftColorPresentationPinkLight, RedsiftColorPresentationPinkLighter, RedsiftColorPresentationPinkLighterer, RedsiftColorPresentationPurpleDark, RedsiftColorPresentationPurpleDarker, RedsiftColorPresentationPurpleDarkerer, RedsiftColorPresentationPurpleDefault, RedsiftColorPresentationPurpleLight, RedsiftColorPresentationPurpleLighter, RedsiftColorPresentationPurpleLighterer, RedsiftColorPresentationRedDark, RedsiftColorPresentationRedDarker, RedsiftColorPresentationRedDarkerer, RedsiftColorPresentationRedDefault, RedsiftColorPresentationRedLight, RedsiftColorPresentationRedLighter, RedsiftColorPresentationRedLighterer, RedsiftColorPresentationYellowDark, RedsiftColorPresentationYellowDarker, RedsiftColorPresentationYellowDarkerer, RedsiftColorPresentationYellowDefault, RedsiftColorPresentationYellowLight, RedsiftColorPresentationYellowLighter, RedsiftColorPresentationYellowLighterer, RedsiftColorProductAsm, RedsiftColorProductBrandTrust, RedsiftColorProductCertificates, RedsiftColorProductOndmarc, RedsiftColorProductRadar, RedsiftColorProductRojoDs, RedsiftColorProductVendorSecure, RedsiftColorProductWebsite, RedsiftColorRedsift, RedsiftLayoutZIndexDialog, RedsiftLayoutZIndexDropdown, RedsiftLayoutZIndexFooter, RedsiftLayoutZIndexHeader, RedsiftLayoutZIndexOverlay, RedsiftLayoutZIndexPopover, RedsiftLayoutZIndexSidePanel, RedsiftLayoutZIndexTooltip, RedsiftSideNavigationColorBackground, RedsiftSideNavigationColorMenuItemActive, RedsiftSideNavigationColorMenuItemBackgroundActive, RedsiftSideNavigationColorMenuItemBackgroundHover, RedsiftSideNavigationColorMenuItemBackgroundSecondary, RedsiftSideNavigationColorMenuItemTextDisabled, RedsiftSideNavigationColorMenuItemTextHover, RedsiftSideNavigationColorMenuItemTextResting, RedsiftSideNavigationColorScrollbarHover, RedsiftSideNavigationColorScrollbarResting, RedsiftTypographyBadgeFontFamily, RedsiftTypographyBadgeFontSize, RedsiftTypographyBadgeFontWeight, RedsiftTypographyBadgeLineHeight, RedsiftTypographyBodyFontFamily, RedsiftTypographyBodyFontSize, RedsiftTypographyBodyFontWeight, RedsiftTypographyBodyLineHeight, RedsiftTypographyBodyTextTransform, RedsiftTypographyButtonFontFamily, RedsiftTypographyButtonFontSize, RedsiftTypographyButtonFontWeight, RedsiftTypographyButtonLineHeight, RedsiftTypographyButtonTextTransform, RedsiftTypographyCaptionFontFamily, RedsiftTypographyCaptionFontSize, RedsiftTypographyCaptionFontWeight, RedsiftTypographyCaptionLineHeight, RedsiftTypographyCaptionTextTransform, RedsiftTypographyChipFontFamily, RedsiftTypographyChipFontSize, RedsiftTypographyChipFontWeight, RedsiftTypographyChipLineHeight, RedsiftTypographyDatagridCellFontFamily, RedsiftTypographyDatagridCellFontSize, RedsiftTypographyDatagridCellFontWeight, RedsiftTypographyDatagridCellLineHeight, RedsiftTypographyDatagridHeaderFontFamily, RedsiftTypographyDatagridHeaderFontSize, RedsiftTypographyDatagridHeaderFontWeight, RedsiftTypographyDatagridHeaderLineHeight, RedsiftTypographyFontFamilyPoppins, RedsiftTypographyFontFamilySourceCodePro, RedsiftTypographyFontWeightBlack, RedsiftTypographyFontWeightBold, RedsiftTypographyFontWeightExtraBold, RedsiftTypographyFontWeightExtraLight, RedsiftTypographyFontWeightLight, RedsiftTypographyFontWeightMedium, RedsiftTypographyFontWeightRegular, RedsiftTypographyFontWeightSemiBold, RedsiftTypographyFontWeightThin, RedsiftTypographyH1FontFamily, RedsiftTypographyH1FontSize, RedsiftTypographyH1FontWeight, RedsiftTypographyH1LineHeight, RedsiftTypographyH1TextTransform, RedsiftTypographyH2FontFamily, RedsiftTypographyH2FontSize, RedsiftTypographyH2FontWeight, RedsiftTypographyH2LineHeight, RedsiftTypographyH2TextTransform, RedsiftTypographyH3FontFamily, RedsiftTypographyH3FontSize, RedsiftTypographyH3FontWeight, RedsiftTypographyH3LineHeight, RedsiftTypographyH3TextTransform, RedsiftTypographyH4FontFamily, RedsiftTypographyH4FontSize, RedsiftTypographyH4FontWeight, RedsiftTypographyH4LineHeight, RedsiftTypographyH4TextTransform, RedsiftTypographyH5FontFamily, RedsiftTypographyH5FontSize, RedsiftTypographyH5FontWeight, RedsiftTypographyH5LineHeight, RedsiftTypographyH5TextTransform, RedsiftTypographyHelperFontFamily, RedsiftTypographyHelperFontSize, RedsiftTypographyHelperFontWeight, RedsiftTypographyHelperLineHeight, RedsiftTypographyHelperTextTransform, RedsiftTypographyInputTextFontFamily, RedsiftTypographyInputTextFontSize, RedsiftTypographyInputTextFontWeight, RedsiftTypographyInputTextLineHeight, RedsiftTypographyLinkFontFamily, RedsiftTypographyLinkFontSize, RedsiftTypographyLinkFontWeight, RedsiftTypographyLinkLineHeight, RedsiftTypographyPillFontFamily, RedsiftTypographyPillFontSize, RedsiftTypographyPillFontWeight, RedsiftTypographyPillLineHeight, RedsiftTypographyTooltipFontFamily, RedsiftTypographyTooltipFontSize, RedsiftTypographyTooltipFontWeight, RedsiftTypographyTooltipLineHeight, RovingTabindexListbox, Shield, ShieldVariant, SideNavigationMenu, SideNavigationMenuBar, SideNavigationMenuBarVariant, SideNavigationMenuItem, SideNavigationMenuReducerActionType, Skeleton, SkeletonCircle, SkeletonText, SkeletonTextVariant, Spinner, SpinnerSize, StyledButton, StyledLink, Switch, SwitchGroup, SwitchGroupOrientation, Text, TextComponent, TextField, TextFieldVariant, TextVariant, baseContainer, baseFlexbox, baseGrid, baseInternalSpacing, baseLayout, basePositioning, baseSizing, baseSpacing, baseStyling, filterComponents, focusRing, isComponent, nextId, partitionComponents, resetId, setPrefix, sizeToDimension$1 as sizeToDimension, uniqueId, useAppSidePanel, useBoundingClientRect, useFocusOnList, useFocusOnListItem, useId$1 as useId, useSideNavigationMenuBar, useWindowSize, warnIfNoAccessibleLabelFound };
//# sourceMappingURL=index.js.map
